import React, { useState, useEffect } from 'react';
import md5 from 'md5';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MapPin,
  Monitor,
  Plus,
  Edit2,
  Trash2,
  Search,
  Cpu,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Activity,
  Wifi,
  WifiOff,
  AlertCircle,
  Users,
  X,
  Info,
  FileText,
  RefreshCw,
  User,
  Clock,
  AlertTriangle,
  Edit,
  DoorOpen,
  UserPlus,
  Upload,
  Car,
  Copy,
  Link,
  ExternalLink,
  Settings,
  ChevronDown,
  Loader2,
  Download,
  MoreVertical,
  CreditCard,
  Save,
  RotateCcw,
  Fingerprint,
  ArrowUpDown,
  FileDown,
} from 'lucide-react';
import { useKeycloak } from '../contexts/KeycloakContext';
import { useCustomer } from '../contexts/CustomerContext';
import { usePersonDetail } from '../hooks/usePersonDetail';
import { useDevicePhoto } from '../hooks/useDevicePhoto';
import { useDeviceHealthCheck, DeviceHealth } from '../hooks/useDeviceHealthCheck';
import { isElectron } from '../utils/platform';
import { canTcpProbe, smartSelectDeviceIp } from '../utils/deviceProbe';
import { guaritaCardDecimal } from '../utils/guaritaSerial';
import { deviceOperations } from '../services/deviceRouter';
import toast from 'react-hot-toast';
import { logger } from '../services/logger';
import { fetchWithAuth, getApiBaseUrl } from '../services/api';
import { ensureOk, fireAndForget, okJson } from '../services/httpError';
import { reportDeviceSyncOutcome } from '../utils/deviceSyncOutcome';
import { expectedHikWebhook, hikWebhookUrl, WEBHOOK_HOST, EVENT_WEBHOOK_PORT, EVENT_WEBHOOK_PROTOCOL } from '../utils/hikWebhook';
import { expectedControlIdMonitor, compareControlIdMonitor, controlIdWebhookUrl, type ControlIdMonitorComparison } from '../utils/controlIdMonitor';
import DeviceProbe from '../components/DeviceProbe';
import FuncionariosReport from '../components/FuncionariosReport';
import { SyncProgressState, SyncProgressItem } from '../components/SyncProgressModal';
import { useSyncProgress } from '../contexts/SyncProgressContext';
import { getAbsoluteImageUrl } from '../utils/imageUrl';
import { ensureHttpScheme, sanitizeDeviceAddress } from '../utils/deviceAddress';
import { friendlyDeviceError } from '../utils/errorParser';
import { getPrimaryPhotoUrl } from '../utils/photoUtils';
import { hasMinimumRoleLevel } from '../config/roles';
import { deviceCommunicationService, CommunicationMode, controlIDSecBoxId } from '../services/deviceCommunication';
import { DeviceOperations } from '../services/deviceCommunication';
import { DeviceIntegration } from '../integrations/devices';
import { triggerDeviceDoor } from '../utils/deviceDoorTrigger';
import { fingerprintService } from '../services/fingerprintService';
import { resolveForcedUserID, shouldPullDeviceBiometry } from './linkPersonHelpers';
import {
  listDeviceRegistrations,
  registerOnDevice,
  registerOnDeviceWithMask,
  deleteFromDevice,
  updateGuaritaIntegration,
  GuaritaDeviceRecord,
  syncVehiclesToGuarita,
} from '../services/guaritaIntegrationService';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { Printer } from 'lucide-react';
import { Button } from '../components/ui/Button';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:9080/api/v1';

// Translation functions to convert English values to Portuguese
const translateZoneType = (type: string): string => {
  const translations: Record<string, string> = {
    'entrance': 'Entrada',
    'common_area': 'Área Comum',
    'private': 'Privada',
    'restricted': 'Restrita',
    'parking': 'Estacionamento',
    'elevator': 'Elevador',
    'gate': 'Portão',
  };
  return translations[type] || type;
};

const formatManufacturer = (m?: string | null): string => {
  if (!m) return '';
  const map: Record<string, string> = {
    'controlid': 'ControlID',
    'control id': 'ControlID',
    'hikvision': 'Hikvision',
    'intelbras': 'Intelbras',
    'nice': 'Nice',
    'linear': 'Linear',
  };
  return map[m.toLowerCase()] ?? m;
};

const translateDeviceType = (type: string): string => {
  const translations: Record<string, string> = {
    'facial_reader': 'Facial',
    'facial_recognition': 'Facial',
    'rfid_reader': 'RFID',
    'controlid_rfid': 'RFID',
    'catraca': 'Catraca',
    'fingerprint': 'Digital',
    'lpr': 'LPR',
    'hikvision_lpr': 'LPR Hikvision',
    'alphadigi_lpr': 'LPR Alphadigi',
    'guarita': 'Guarita IP',
  };
  return translations[type] || type;
};

/**
 * Devices whose authorized list is VEHICLES rather than people.
 *
 * RFID readers hold vehicle tags; Hikvision LPR cameras hold vehicle plates. Both are
 * vehicle-keyed, so the device modal shows "Veículos Autorizados" for them. LPR was missing
 * here, so an LPR camera showed an empty "Pessoas Autorizadas" tab instead of its vehicles.
 *
 * 'lpr' and 'hikvision_lpr' are both accepted: the generic "Tipo: LPR" tab in the device form
 * produces the former, the Hikvision-specific create path the latter.
 */
const isVehicleListDevice = (deviceType?: string): boolean =>
  deviceType === 'rfid_reader' || deviceType === 'controlid_rfid' ||
  deviceType === 'hikvision_lpr' || deviceType === 'lpr';

const translateStatus = (status: string): string => {
  const translations: Record<string, string> = {
    'active': 'Ativo',
    'inactive': 'Inativo',
    'online': 'Online',
    'offline': 'Offline',
    'error': 'Erro',
    'maintenance': 'Manutenção',
  };
  return translations[status] || status;
};

export interface Zone {
  id: string;
  customer_id: string;
  name: string;
  description?: string;
  zone_type: string;
  entry_type: string; // 'entrada', 'passagem', 'saida'
  status: string;
  requires_authorization: boolean;
  device_count: number;
  created_at: string;
}

export interface IoTDevice {
  id: string;
  customer_id: string;
  name: string;
  device_type: string;
  status: string;
  zone_id: string;
  zone_name?: string;
  ip_address?: string;
  local_ip_address?: string;
  username?: string;
  password?: string;
  manufacturer?: string;
  model?: string;
  serial_number?: string;
  firmware_version?: string;
  port?: number;
  mac_address?: string;
  protocol?: string;
  last_heartbeat?: string;
  created_at?: string;
  updated_at?: string;
  is_active: boolean;
  webhook_url?: string;
  controlid_webhook_url?: string;
  config?: Record<string, any>;
}

// Module-level cache for Hikvision face photos fetched directly from device
// Key: directFaceUrl, Value: base64 data URL. Persists for the session.
const hikFacePhotoCache = new Map<string, string>();
// Pending fetches: avoid duplicate in-flight requests for the same URL
const hikFacePhotoPending = new Map<string, Promise<string | null>>();

/**
 * Component for displaying device person photo with smart routing
 * - Hikvision in Electron: fetches directly from device using direct_face_url
 * - ControlID / browser: uses cloud proxy via useDevicePhoto hook
 */
const DevicePersonPhoto: React.FC<{
  deviceId: string;
  employeeNo: string;
  device: IoTDevice;
  alt: string;
  directFaceUrl?: string | null;
  className?: string;
}> = ({ deviceId, employeeNo, device, alt, directFaceUrl, className }) => {
  const [hikPhotoUrl, setHikPhotoUrl] = React.useState<string | null>(null);
  const [hikLoading, setHikLoading] = React.useState(false);

  const isElectron = !!(window as any).electronAPI;
  const useHikPath = !!(directFaceUrl && isElectron);

  // Hikvision path: fetch photo directly from device via Electron IPC (with cache)
  React.useEffect(() => {
    if (!useHikPath || !directFaceUrl) return;

    // Already a base64 data URL (picData stored inline) — use directly, no fetch needed
    if (directFaceUrl.startsWith('data:')) {
      setHikPhotoUrl(directFaceUrl);
      return;
    }

    // Cache hit — show immediately without loading state
    const cached = hikFacePhotoCache.get(directFaceUrl);
    if (cached) {
      setHikPhotoUrl(cached);
      return;
    }

    let cancelled = false;
    setHikLoading(true);
    setHikPhotoUrl(null);

    // Deduplicate in-flight requests for the same URL
    let fetchPromise = hikFacePhotoPending.get(directFaceUrl);
    if (!fetchPromise) {
      fetchPromise = (window as any).electronAPI.deviceHttpRequest({
        url: directFaceUrl,
        method: 'GET',
        timeout: 10000,
        digestAuth: {
          username: device.username || '',
          password: device.password || '',
        },
      }).then((result: any) => {
        const dataUrl = result.success && typeof result.data === 'string' ? result.data : null;
        if (dataUrl) hikFacePhotoCache.set(directFaceUrl, dataUrl);
        hikFacePhotoPending.delete(directFaceUrl);
        return dataUrl;
      }).catch(() => {
        hikFacePhotoPending.delete(directFaceUrl);
        return null;
      });
      hikFacePhotoPending.set(directFaceUrl, fetchPromise);
    }

    fetchPromise.then((dataUrl: string | null) => {
      if (cancelled) return;
      if (dataUrl) setHikPhotoUrl(dataUrl);
      setHikLoading(false);
    });

    return () => { cancelled = true; };
  }, [directFaceUrl, useHikPath, device.username, device.password]);

  // ControlID / cloud path — always called (disabled by passing undefined when using Hik path)
  const { photoUrl: controlIdPhotoUrl, isLoading: controlIdLoading } = useDevicePhoto(
    useHikPath ? undefined : deviceId,
    useHikPath ? undefined : `/user_get_image.fcgi?user_id=${employeeNo}`,
    useHikPath ? undefined : {
      local_ip_address: device.local_ip_address,
      ip_address: device.ip_address,
      username: device.username,
      password: device.password,
      port: device.port,
    }
  );

  const photoUrl = useHikPath ? hikPhotoUrl : controlIdPhotoUrl;
  const isLoading = useHikPath ? hikLoading : controlIdLoading;

  if (isLoading) {
    return (
      <div className={`${className || 'w-10 h-10 rounded-full'} bg-gray-200 animate-pulse`} />
    );
  }

  if (!photoUrl) {
    return null;
  }

  return (
    <img
      src={photoUrl}
      alt={alt}
      className={className || 'w-8 h-10 rounded-md object-cover bg-gray-200'}
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );
};

const getRoleLabel = (roleType: string): string => {
  const labels: Record<string, string> = {
    resident: 'Morador',
    visitor: 'Visitante',
    service_provider: 'Prestador',
    staff: 'Funcionário',
    delivery: 'Entregador',
  };
  return labels[roleType] || 'Desconhecido';
};

// Visual receiver-bitmask indicator — one dot per possible receptor (NICE Guarita packs
// receivers into a single byte, so max 8), filled for the ones this tag actually has.
const ReceptorDots: React.FC<{ mask: number; maxReceptors?: number }> = ({ mask, maxReceptors = 8 }) => {
  const active: number[] = [];
  for (let i = 0; i < maxReceptors; i++) {
    if (mask & (1 << i)) active.push(i + 1);
  }
  const summary = active.length === maxReceptors ? 'Todos os receptores' : active.length === 0 ? 'Nenhum receptor' : `REC ${active.join(', ')}`;
  return (
    <div className="flex items-center gap-1 mt-1" title={summary}>
      {Array.from({ length: maxReceptors }, (_, i) => {
        const isActive = (mask & (1 << i)) !== 0;
        return (
          <span
            key={i}
            title={`REC ${i + 1}${isActive ? ' — ativo' : ''}`}
            className={`flex items-center justify-center w-4 h-4 rounded-full border text-[9px] font-bold leading-none transition-colors ${
              isActive ? 'bg-blue-500 border-blue-500 text-white' : 'bg-white border-gray-300 text-gray-400'
            }`}
          >
            {i + 1}
          </span>
        );
      })}
    </div>
  );
};

const ZonesDevices: React.FC = () => {
  const { token, roles: globalRoles, getRolesForCustomer } = useKeycloak();
  const { selectedCustomer } = useCustomer();
  const { openPersonDetail } = usePersonDetail();
  const showConfirm = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();
  const [webBannerDismissed, setWebBannerDismissed] = useState(false);

  // Get customer-specific roles (not global roles)
  const roles = selectedCustomer?.id ? getRolesForCustomer(selectedCustomer.id) : globalRoles;
  const isAdmin = hasMinimumRoleLevel(roles, 80); // Check if user is admin level (80+)

  // Debug: Log role information
  useEffect(() => {
    console.log('[ZonesDevices] Role Debug:', {
      selectedCustomer: selectedCustomer?.name,
      customerId: selectedCustomer?.id,
      roles,
      isAdmin,
      minRoleLevelRequired: 80
    });
  }, [roles, isAdmin, selectedCustomer]);

  const [zones, setZones] = useState<Zone[]>([]);
  const [devices, setDevices] = useState<IoTDevice[]>([]);

  // Real-time device health checking (Electron only)
  const { healthMap, isChecking: isHealthChecking } = useDeviceHealthCheck(devices, { interval: 10000 });
  const electronMode = isElectron();

  // Helper: is a device a Guarita-type device?
  const isGuaritaDevice = (d: IoTDevice) =>
    d.device_type === 'guarita' ||
    (d.manufacturer?.toLowerCase() === 'nice' && d.model?.toLowerCase().includes('guarita'));

  // Guarita health: check TCP connection status via cloud API (works in all modes)
  const [guaritaHealthMap, setGuaritaHealthMap] = useState<Map<string, DeviceHealth>>(new Map());
  useEffect(() => {
    const guaritaDevices = devices.filter(isGuaritaDevice);
    if (guaritaDevices.length === 0) return;
    const checkAll = async () => {
      const integIds = Array.from(new Set(
        guaritaDevices.map(d => d.config?.guarita_integration_id).filter(Boolean) as string[]
      ));
      const statusById = new Map<string, boolean>();
      await Promise.all(integIds.map(async (integId) => {
        try {
          const r = await fetchWithAuth(`/api/v1/iot-connect/guarita/${integId}/status`);
          if (r.ok) {
            const data = await r.json();
            statusById.set(integId, (data.connected_devices?.length ?? 0) > 0);
          } else {
            statusById.set(integId, false);
          }
        } catch { statusById.set(integId, false); }
      }));
      setGuaritaHealthMap(prev => {
        const next = new Map(prev);
        for (const d of guaritaDevices) {
          const integId = d.config?.guarita_integration_id;
          if (integId) {
            next.set(d.id, { status: statusById.get(integId) ? 'online' : 'offline', checkedAt: new Date() });
          }
        }
        return next;
      });
    };
    checkAll();
    const interval = setInterval(checkAll, 10000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices]);

  // Get effective status: Electron live check overrides server status; Guarita uses cloud check.
  // In web mode, non-Guarita devices can't be probed directly — return 'web_unavailable'.
  const getEffectiveStatus = (device: IoTDevice): 'online' | 'offline' | 'checking' | 'web_unavailable' | string => {
    if (isGuaritaDevice(device) && guaritaHealthMap.has(device.id)) {
      return guaritaHealthMap.get(device.id)!.status;
    }
    if (!electronMode && !isGuaritaDevice(device)) {
      return 'web_unavailable';
    }
    if (electronMode && healthMap.has(device.id)) {
      return healthMap.get(device.id)!.status;
    }
    return device.status;
  };

  // Effective counts for stats cards
  const effectiveOnlineCount = devices.filter(d => getEffectiveStatus(d) === 'online').length;
  const effectiveOfflineCount = devices.filter(d => getEffectiveStatus(d) === 'offline').length;

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [entryTypeFilter, setEntryTypeFilter] = useState<string>('all'); // 'all', 'entrada', 'passagem', 'saida'
  const [showCreateZoneModal, setShowCreateZoneModal] = useState(false);
  const [showFuncionariosReport, setShowFuncionariosReport] = useState(false);
  const [showCreateDeviceModal, setShowCreateDeviceModal] = useState(false);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<IoTDevice | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeviceListModal, setShowDeviceListModal] = useState<'all' | 'active' | 'offline' | null>(null);
  const [showDoorConfirmModal, setShowDoorConfirmModal] = useState(false);
  const [doorTriggeringZone, setDoorTriggeringZone] = useState<Zone | null>(null);
  const [isDoorTriggering, setIsDoorTriggering] = useState(false);
  const [showDeviceCardsModal, setShowDeviceCardsModal] = useState(false);
  const [deviceCards, setDeviceCards] = useState<any[]>([]);
  const [isLoadingCards, setIsLoadingCards] = useState(false);

  // Zone card menu state
  const [openMenuZoneId, setOpenMenuZoneId] = useState<string | null>(null);

  // Device action states
  const [restartingDeviceId, setRestartingDeviceId] = useState<string | null>(null);
  const [settingTimeDeviceId, setSettingTimeDeviceId] = useState<string | null>(null);

  // Sync state
  const [showSyncConfirmModal, setShowSyncConfirmModal] = useState(false);
  const [syncTarget, setSyncTarget] = useState<'all' | Zone | null>(null);
  const [isSyncingZone, setIsSyncingZone] = useState<string | null>(null);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const { setData: setSyncProgressData, pauseRef: syncPauseRef, stopRef: syncStopRef } = useSyncProgress();

  // Vehicle removal confirmation modal state
  const [showVehicleRemoveModal, setShowVehicleRemoveModal] = useState(false);
  const [vehicleToRemove, setVehicleToRemove] = useState<{
    vehicle: any;
    device: IoTDevice;
    cardNumber: string;
  } | null>(null);
  const [isRemovingVehicle, setIsRemovingVehicle] = useState(false);

  // Translate English role types to Portuguese labels
  const roleTypeLabels: Record<string, string> = {
    resident: 'Morador',
    visitor: 'Visitante',
    service_provider: 'Prestador',
    staff: 'Funcionário',
    delivery: 'Entregador',
    morador: 'Morador',
    visitante: 'Visitante',
    prestador: 'Prestador',
    funcionario: 'Funcionário',
    entregador: 'Entregador',
  };

  const getRoleLabel = React.useCallback((roleType: string) => {
    return roleTypeLabels[roleType] || roleType;
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      fetchZones();
      fetchDevices();
    }
  }, [selectedCustomer]);

  // Handle navigation from person modal with device ID
  useEffect(() => {
    const state = location.state as { selectedDeviceId?: string };
    if (state?.selectedDeviceId && devices.length > 0) {
      const device = devices.find(d => d.id === state.selectedDeviceId);
      if (device) {
        setSelectedDevice(device);
        setShowDetailsModal(true);
        // Clear the state to prevent reopening on refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, devices]);

  // Fetch cards from RFID device when modal opens
  useEffect(() => {
    if (showDeviceCardsModal && selectedDevice) {
      fetchDeviceCards();
    }
  }, [showDeviceCardsModal, selectedDevice]);

  const fetchDeviceCards = async () => {
    if (!selectedDevice) return;

    setIsLoadingCards(true);
    setDeviceCards([]);

    try {
      const response = await fetchWithAuth(
        `/api/v1/iot-connect/rfid/devices/${selectedDevice.id}/cards`
      );

      if (response.ok) {
        const data = await response.json();
        setDeviceCards(data.cards || []);
        toast.success(`${data.cards?.length || 0} cartões encontrados no dispositivo`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Falha ao carregar cartões do dispositivo');
        setDeviceCards([]);
      }
    } catch (error) {
      logger.error('Error fetching device cards:', error);
      toast.error('Erro ao carregar cartões do dispositivo');
      setDeviceCards([]);
    } finally {
      setIsLoadingCards(false);
    }
  };

  const fetchZones = async () => {
    if (!selectedCustomer) return;

    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `/api/v1/zones/customer/${selectedCustomer.id}`
      );

      if (response.ok) {
        const data = await response.json();
        setZones(data.zones || []);
      }
    } catch (error) {
      logger.error('zones-devices.page', 'Failed to fetch zones', error instanceof Error ? error : new Error(String(error)));
      toast.error('Falha ao carregar zonas');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDevices = async () => {
    if (!selectedCustomer) return;

    try {
      const response = await fetchWithAuth(
        `/api/v1/iot-devices/customer/${selectedCustomer.id}`
      );

      if (response.ok) {
        const data = await response.json();
        setDevices(data.devices || []);
      }
    } catch (error) {
      logger.error('zones-devices.page', 'Failed to fetch devices', error instanceof Error ? error : new Error(String(error)));
    }
  };

  const handleRefresh = async () => {
    if (!selectedCustomer) return;

    setIsRefreshing(true);
    try {
      await Promise.all([fetchZones(), fetchDevices()]);
      toast.success('Dados atualizados');
    } catch (error) {
      toast.error('Erro ao atualizar dados');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle vehicle removal from RFID device
  const handleRemoveVehicleFromDevice = async () => {
    if (!vehicleToRemove) return;

    setIsRemovingVehicle(true);
    try {
      const response = await fetchWithAuth(
        `/api/v1/iot-connect/rfid/devices/${vehicleToRemove.device.id}/cards/${vehicleToRemove.cardNumber}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      toast.success('Veículo removido do dispositivo com sucesso');
      setShowVehicleRemoveModal(false);
      setVehicleToRemove(null);
      // Refresh the vehicles list
      if (typeof (window as any).fetchAuthorizedVehicles === 'function') {
        (window as any).fetchAuthorizedVehicles();
      }
    } catch (error) {
      console.error('Failed to remove vehicle from device:', error);
      toast.error('Erro ao remover veículo do dispositivo');
    } finally {
      setIsRemovingVehicle(false);
    }
  };

  const handleDeleteZone = async (zoneId: string) => {
    if (!await showConfirm({ message: 'Tem certeza que deseja excluir esta zona? Dispositivos associados precisarão ser reatribuídos.', variant: 'danger', confirmLabel: 'Excluir' })) return;

    try {
      const response = await fetchWithAuth(
        `/api/v1/zones/${zoneId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        toast.success('Zona excluída com sucesso');
        fetchZones();
        fetchDevices();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Falha ao excluir zona');
      }
    } catch (error) {
      logger.error('zones-devices.page', 'Failed to delete zone', error instanceof Error ? error : new Error(String(error)));
      toast.error('Erro ao excluir zona');
    }
  };

  const handleDeleteDevice = async (deviceId: string, deviceName: string) => {
    if (!selectedCustomer) return;
    if (!await showConfirm({ message: `Tem certeza que deseja excluir o dispositivo "${deviceName}"?`, variant: 'danger', confirmLabel: 'Excluir' })) return;

    try {
      const response = await fetchWithAuth(
        `/api/v1/iot-devices/${deviceId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        toast.success('Dispositivo excluído com sucesso');
        fetchDevices();
        fetchZones();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Falha ao excluir dispositivo');
      }
    } catch (error) {
      logger.error('zones-devices.page', 'Failed to delete device', error instanceof Error ? error : new Error(String(error)));
      toast.error('Erro ao excluir dispositivo');
    }
  };

  const getDeviceForZone = (zoneId: string): IoTDevice | null => {
    // Each zone has exactly ONE device
    return devices.find(d => d.zone_id === zoneId) || null;
  };

  const handleOpenDoorClick = (zone: Zone, device: IoTDevice) => {
    setDoorTriggeringZone(zone);
    setShowDoorConfirmModal(true);
  };

  const handleTriggerZoneDoor = async () => {
    if (!doorTriggeringZone) return;
    const device = getDeviceForZone(doorTriggeringZone.id);
    if (!device) { toast.error('Nenhum dispositivo vinculado a esta zona'); return; }
    setIsDoorTriggering(true);
    try {
      await triggerDeviceDoor(device, fetchWithAuth);
      const isGate = isVehicleListDevice(device.device_type);
      toast.success(isGate ? 'Portão acionado com sucesso!' : 'Porta acionada com sucesso!');
      setShowDoorConfirmModal(false);
      setDoorTriggeringZone(null);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Erro ao acionar porta/portão';
      toast.error(msg);
    } finally {
      setIsDoorTriggering(false);
    }
  };

  const handleRestartDevice = async (device: IoTDevice) => {
    setRestartingDeviceId(device.id);
    setOpenMenuZoneId(null);
    const isRFID = device.device_type === 'rfid_reader' || device.device_type === 'controlid_rfid';
    const isControlID = device.manufacturer?.toLowerCase().includes('control') || device.device_type === 'controlid_face' || device.device_type === 'controlid_rfid';
    const vendor = isControlID ? 'controlid' : 'hikvision';
    const result = await deviceCommunicationService.rebootDevice({
      id: device.id,
      ip_address: device.ip_address,
      local_ip_address: device.local_ip_address,
      username: device.username || '',
      password: device.password || '',
      vendor,
      port: device.port?.toString(),
    }, isRFID);
    if (result.success) {
      toast.success('Reinício solicitado com sucesso');
    } else {
      toast.error('Erro ao reiniciar dispositivo');
    }
    setRestartingDeviceId(null);
  };

  const handleSetDeviceTime = async (device: IoTDevice) => {
    setSettingTimeDeviceId(device.id);
    setOpenMenuZoneId(null);
    const isRFID = device.device_type === 'rfid_reader' || device.device_type === 'controlid_rfid';
    const isControlID = device.manufacturer?.toLowerCase().includes('control') || device.device_type === 'controlid_face' || device.device_type === 'controlid_rfid';
    const vendor = isControlID ? 'controlid' : 'hikvision';
    const result = await deviceCommunicationService.setDeviceTime({
      id: device.id,
      ip_address: device.ip_address,
      local_ip_address: device.local_ip_address,
      username: device.username || '',
      password: device.password || '',
      vendor,
      port: device.port?.toString(),
    }, isRFID);
    if (result.success) {
      toast.success('Horário sincronizado com sucesso');
    } else {
      toast.error('Erro ao sincronizar horário');
    }
    setSettingTimeDeviceId(null);
  };

  /**
   * Load existing users from device and return Set of device registration values.
   * Direct mode stores registration as raw UUID; backend mode stores MD5 hash.
   * The duplicate check in handleSyncZone compares both formats.
   */
  const loadExistingDeviceUsers = async (device: any): Promise<{ hashes: Set<string>, names: Set<string> }> => {
    const empty = { hashes: new Set<string>(), names: new Set<string>() };
    console.log(' [Sync] Loading existing device users...');

    try {
      const isElectron = window.electronAPI !== undefined;
      const hasIP = device.local_ip_address || device.ip_address;
      const isControlID = device.manufacturer?.toLowerCase() === 'controlid';

      if (isElectron && hasIP && isControlID) {
        // Use DIRECT mode with only the local IP so the 15s timeout applies
        // (HYBRID's local IP step only gives 2s — not enough for session + 200-user response)
        // If no local IP, skip pre-load (cloud returns 503 for ControlID)
        const localIp = device.local_ip_address;
        if (!localIp) {
          console.warn(' [Sync] No local IP — skipping device pre-load');
          return empty;
        }
        const result = await DeviceIntegration.instance.execute(
          {
            id: device.id,
            ip_address: localIp,          // DIRECT uses ip_address; set to local IP
            local_ip_address: undefined,  // prevent HYBRID from using local as override
            username: device.username || '',
            password: device.password || '',
            vendor: 'controlid',
            port: device.port?.toString(),
          },
          DeviceOperations.LOAD_PEOPLE,
          {},
          CommunicationMode.DIRECT        // 15s timeout — plenty for session + bulk response
        );

        console.log(` [Sync] LOAD_PEOPLE result:`, {
          success: result.success,
          source: result.source,
          hasData: !!result.data,
          dataKeys: result.data ? Object.keys(result.data) : [],
          usersCount: result.data?.users?.length,
          error: result.error,
        });

        if (result.success && result.data) {
          // Device returns { users: [...] } — fallback to direct array if shape differs
          const rawUsers: any[] = result.data?.users || (Array.isArray(result.data) ? result.data : []);
          if (rawUsers.length > 0) {
            console.log(` [Sync] Device user sample (raw):`, JSON.stringify(rawUsers[0], null, 2));
          }

          const hashes = new Set<string>();
          const names = new Set<string>();

          // Normalize: lowercase + remove accents + collapse spaces
          // e.g. "ANDRE FERNANDO   SILVA" (3 spaces) → "andre fernando silva"
          const normalizeName = (s: string) =>
            s.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');

          for (const user of rawUsers) {
            // Index by registration UUID or MD5 hash (if set)
            const reg = user.registration?.toString() || '';
            if (reg) hashes.add(reg.toLowerCase());
            // ALWAYS index by name — reliable even when registration is null
            const name = normalizeName(user.name?.toString() || '');
            if (name) names.add(name);
          }

          console.log(` [Sync] Pre-loaded ${hashes.size} hashes, ${names.size} names from device`);
          console.log(` [Sync] Device name samples:`, Array.from(names).slice(0, 5));
          return { hashes, names };
        } else {
          console.warn(` [Sync] LOAD_PEOPLE failed or no data - will sync all people`);
        }
      } else {
        console.log(` [Sync] Skipping device user pre-load:`, { isElectron, hasIP, isControlID });
      }

      return empty;
    } catch (error) {
      console.error(' [Sync] Error loading device users:', error);
      return empty;
    }
  };

  // Sync zone devices - HYBRID: Try local first, fallback to cloud
  const handleSyncZone = async (zone: Zone) => {
    const device = devices.find(d => d.zone_id === zone.id);
    if (!device) {
      toast.error('Nenhum dispositivo vinculado a esta zona');
      return;
    }

    setIsSyncingZone(zone.id);

    const toastId = toast.loading('Iniciando sincronização...');

    try {
      const isElectron = window.electronAPI !== undefined;
      const hasIP = device.local_ip_address || device.ip_address;
      const isControlID = device.manufacturer?.toLowerCase() === 'controlid';

      // Try direct mode if in Electron and has IP
      if (isElectron && hasIP) {
        console.log(` [Sync] Trying DIRECT mode for ${zone.name}`);

        // ── RFID Vehicle Device (controlid_rfid / rfid_reader / iDUHF): force re-sync RFID cards ──
        // Deletes all existing cards per user and recreates with correct decimal value (LOCAL SYNC ONLY)
        // An LPR camera holds PLATES, not people and not tags. Without this branch it fell
        // through to the people sync below and reported "Nenhuma pessoa autorizada".
        if (device.device_type === 'hikvision_lpr' || device.device_type === 'lpr') {
          const lprCameraId = device.config?.hikvision_lpr_camera_id;
          if (!lprCameraId) {
            toast.error('Dispositivo LPR não está vinculado a uma câmera', { id: toastId });
            return;
          }
          toast.loading('Buscando veículos com placa...', { id: toastId });
          const cidLpr = selectedCustomer?.id || device.customer_id;
          const vResp = await fetchWithAuth(`/api/v1/customers/${cidLpr}/vehicles?page=1&page_size=5000`);
          if (!vResp.ok) throw new Error('Failed to fetch vehicles');
          const vData = await vResp.json();
          const plated = (vData.vehicles || vData.data || []).filter((v: any) => (v.plate || '').trim());

          if (plated.length === 0) {
            toast.success('Nenhum veículo com placa encontrado', { id: toastId });
            return;
          }

          const { syncAllPlatesToCamera } = await import('../services/lprPlateSync');
          const lprBuf: SyncProgressItem[] = [];
          setSyncProgressData({ zoneName: zone.name, total: plated.length, done: 0, ok: 0, fail: 0, recentItems: [], isComplete: false });

          const res = await syncAllPlatesToCamera(cidLpr, lprCameraId, plated, undefined, {
            onProgress: (pr) => {
              lprBuf.push({ label: pr.label, owner: 'LPR', status: pr.status });
              if (lprBuf.length > 30) lprBuf.shift();
              setSyncProgressData({ zoneName: zone.name, total: pr.total, done: pr.done, ok: pr.ok, fail: pr.fail, recentItems: [...lprBuf], isComplete: pr.done === pr.total });
            },
          });

          toast.success(
            res.fail > 0 ? `${res.ok} placa(s) sincronizada(s), ${res.fail} falha(s)` : `${res.ok} placa(s) sincronizada(s)`,
            { id: toastId, duration: 4000 },
          );
          return;
        }

        if (device.device_type === 'controlid_rfid' || device.device_type === 'rfid_reader') {
          toast.loading('Buscando veículos com TAG RFID...', { id: toastId });
          const cid = selectedCustomer?.id || device.customer_id;
          const vehiclesResp = await fetchWithAuth(`/api/v1/customers/${cid}/vehicles?page=1&page_size=5000&has_rfid=true`);
          if (!vehiclesResp.ok) throw new Error('Failed to fetch vehicles');
          const vehiclesData = await vehiclesResp.json();
          const vehicles = (vehiclesData.vehicles || vehiclesData.data || [])
            .filter((v: any) => v.rfid_tag)
            .map((v: any) => ({
              vehicle_id: v.id,
              rfid_tag: v.rfid_tag,
              plate: v.plate,
              person_name: v.owner_name || v.person?.first_name,
            }));

          if (vehicles.length === 0) {
            toast.success(`Nenhum veículo com TAG RFID encontrado`, { id: toastId });
            return;
          }

          // Probe local vs public IP — same as HYBRID mode in executeOperation
          const rfidProbe = await smartSelectDeviceIp(
            { ip_address: device.ip_address, local_ip_address: device.local_ip_address, port: device.port },
            { timeoutLocal: 400, timeoutPublic: 1500 }
          );
          const rfidWorkingIp = rfidProbe?.ip || device.local_ip_address || device.ip_address;

          const rfidDeviceInfo = {
            id: device.id,
            ip_address: rfidWorkingIp,
            local_ip_address: device.local_ip_address,
            username: device.username || '',
            password: device.password || '',
            vendor: 'controlid' as 'hikvision' | 'controlid' | 'rfid',
            port: device.port?.toString(),
          };

          const vehiclesMapped = vehicles.map((v: any) => ({
            vehicle_id: v.vehicle_id,
            rfid_tag: v.rfid_tag,
            plate: v.plate,
            person_name: v.person_name,
          }));

          // Both controlid_rfid and rfid_reader are ControlID iDUHF devices — always use 'controlid' adapter
          const rfidDeviceInfoFinal = { ...rfidDeviceInfo, vendor: 'controlid' as 'hikvision' | 'controlid' | 'rfid' };

          // Open progress modal
          const zoneName1 = zone.name;
          setSyncProgressData({ zoneName: zoneName1, total: vehiclesMapped.length, done: 0, ok: 0, fail: 0, recentItems: [], isComplete: false });

          const recentBuf1: SyncProgressItem[] = [];
          let rfidOk = 0;
          let rfidFail = 0;

          // Sync each vehicle one-by-one using the same SYNC_PERSON path as the vehicle modal
          for (let i = 0; i < vehiclesMapped.length; i++) {
            const v = vehiclesMapped[i];
            const personSyncData = {
              person_id: v.vehicle_id,
              first_name: v.plate || 'Veículo',
              last_name: v.person_name || '',
              identity_photo_url: undefined,
              rfid_tags: v.rfid_tag ? [v.rfid_tag] : [],
              vehicle_plate: v.plate,
              isAllowed: true,
            };

            const result = await DeviceIntegration.instance.execute(
              rfidDeviceInfoFinal,
              DeviceOperations.SYNC_PERSON,
              personSyncData,
              CommunicationMode.HYBRID,
            );

            if (result.success) rfidOk++; else rfidFail++;
            const itemStatus: 'ok' | 'failed' = result.success ? 'ok' : 'failed';
            recentBuf1.push({ label: v.plate || v.vehicle_id, owner: v.person_name || '', status: itemStatus });
            if (recentBuf1.length > 30) recentBuf1.shift();

            // Update progress after every vehicle (no throttle)
            // Progress lives in the modal below — a second counter in the toast layer is
            // the same run reported twice, in two different corners of the screen.
            setSyncProgressData({ zoneName: zoneName1, total: vehiclesMapped.length, done: i + 1, ok: rfidOk, fail: rfidFail, recentItems: [...recentBuf1], isComplete: i + 1 === vehiclesMapped.length });

            // Small delay between vehicles to avoid overwhelming the ControlID device rate limiter
            if (i < vehiclesMapped.length - 1) await new Promise(r => setTimeout(r, 200));
          }

          const rfidMsg1 = rfidFail > 0
            ? `${rfidOk} sincronizado(s), ${rfidFail} falha(s)`
            : `${rfidOk} veículo(s) sincronizado(s)`;
          toast.success(rfidMsg1, { id: toastId, duration: 4000 });
          return;
        }

        toast.loading('Buscando pessoas autorizadas...', { id: toastId });

        const peopleResponse = await fetchWithAuth(`/api/v1/zones/${zone.id}/authorized-people`);
        if (!peopleResponse.ok) {
          throw new Error('Failed to fetch people');
        }

        const peopleData = await peopleResponse.json();
        const people = peopleData.data || [];

        if (people.length === 0) {
          toast.success(`Nenhuma pessoa autorizada para ${zone.name}`, { id: toastId });
          return;
        }

        // Probe local vs public IP — try local first (400ms), fall back to public (1500ms)
        const probe = await smartSelectDeviceIp(
          { ip_address: device.ip_address, local_ip_address: device.local_ip_address, port: device.port },
          { timeoutLocal: 400, timeoutPublic: 1500 }
        );
        const workingIp = probe?.ip || device.local_ip_address || device.ip_address;

        const deviceInfo = {
          id: device.id,
          ip_address: workingIp,
          local_ip_address: device.local_ip_address,
          username: device.username || '',
          password: device.password || '',
          vendor: (device.manufacturer?.toLowerCase() || 'hikvision') as 'hikvision' | 'controlid' | 'rfid',
          port: device.port?.toString(),
        };

        // Load all existing device users using the SAME deviceInfo + DIRECT mode that SYNC_PERSON uses
        toast.loading('Verificando pessoas já cadastradas...', { id: toastId });
        const existingHashes = new Set<string>(); // registration UUIDs
        const existingNames = new Set<string>();   // normalized names (fallback)

        if (isControlID) {
          try {
            const preLoad = await DeviceIntegration.instance.execute(
              deviceInfo,
              DeviceOperations.LOAD_PEOPLE,
              {},
              CommunicationMode.DIRECT  // 15s timeout — same as SYNC_PERSON, needed for bulk 200-user response
            );

            console.log(' [Sync] Pre-load result:', {
              success: preLoad.success,
              source: preLoad.source,
              error: preLoad.error,
              usersCount: preLoad.data?.users?.length,
              dataKeys: preLoad.data ? Object.keys(preLoad.data) : [],
            });

            if (preLoad.success && preLoad.data) {
              const rawUsers: any[] = preLoad.data?.users || [];
              const norm2 = (s: string) =>
                s.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');

              for (const u of rawUsers) {
                if (u.registration) existingHashes.add(u.registration.toLowerCase());
                const n = norm2(u.name || '');
                if (n) existingNames.add(n);
              }
              console.log(` [Sync] Pre-loaded: ${existingHashes.size} UUIDs, ${existingNames.size} names from device`);
              toast.loading(`${existingHashes.size} já no dispositivo`, { id: toastId });
            } else {
              console.warn(` [Sync] Pre-load failed — syncing all`);
              toast.loading(`Não foi possível verificar dispositivo — sincronizando todos`, { id: toastId });
            }
          } catch (e) {
            console.warn(' [Sync] Pre-load error:', e);
            toast.loading(`Erro ao verificar dispositivo — sincronizando todos`, { id: toastId });
          }
        }

        const isHikvision = device.manufacturer?.toLowerCase() === 'hikvision';

        if (isHikvision) {
          try {
            const preLoad = await DeviceIntegration.instance.execute(
              deviceInfo, DeviceOperations.LOAD_PEOPLE, {}, CommunicationMode.DIRECT
            );
            if (preLoad.success && preLoad.data) {
              const rawUsers: any[] = preLoad.data?.UserInfoSearch?.UserInfo || [];
              const norm2 = (s: string) =>
                s.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');
              for (const u of rawUsers) {
                if (u.employeeNo) existingHashes.add(u.employeeNo.toLowerCase());
                const n = norm2(u.name || '');
                if (n) existingNames.add(n);
              }
              toast.loading(`${existingHashes.size} já no dispositivo`, { id: toastId });
            }
          } catch (e) {
            console.warn('[Sync] Hikvision pre-load error:', e);
          }
        }

        const norm = (s: string) =>
          s.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');

        // Filter: skip people already on device (by ID or name)
        const peopleToSync: typeof people = [];
        const peopleAlreadyExist: typeof people = [];

        for (const person of people) {
          const personKey = isHikvision ? md5(person.id) : person.id;
          const byId = personKey && existingHashes.has(personKey.toLowerCase());
          const byName = existingNames.has(norm(`${person.first_name} ${person.last_name}`));
          if (byId || byName) {
            peopleAlreadyExist.push(person);
          } else {
            peopleToSync.push(person);
          }
        }

        console.log(` [Sync] ${people.length} total | ${peopleAlreadyExist.length} já no dispositivo | ${peopleToSync.length} para sincronizar`);

        if (peopleToSync.length === 0) {
          toast.success(
            `Todas as ${peopleAlreadyExist.length} pessoas já estão cadastradas no dispositivo`,
            { id: toastId, duration: 5000 }
          );
          return;
        }

        toast.loading(
          `Sincronizando ${peopleToSync.length} pessoa(s)... (${peopleAlreadyExist.length} já cadastradas)`,
          { id: toastId }
        );

        // ── ControlID: ensure group / time zone / access rule exists once ──
        let controlIDGroupId: number | null = null;
        if (isControlID) {
          toast.loading('Configurando grupos e horários no dispositivo...', { id: toastId });
          controlIDGroupId = await deviceCommunicationService.ensureControlIDDefaultAccess(deviceInfo);
          console.log(`[SyncZone] ControlID default group id=${controlIDGroupId}`);
        }

        let successCount = 0;
        let failureCount = 0;
        const syncRecentBuf: SyncProgressItem[] = [];
        const syncTotal = people.length;

        /** A person row for someone the device already had — counted, and now also listed. */
        const alreadyOnDeviceItem = (person: any): SyncProgressItem => {
          const photo = person.identity_photo?.photo_url || person.identity_photo_url || person.profile_picture_url;
          return {
            label: `${person.first_name} ${person.last_name}`.trim(),
            owner: '',
            status: 'ok',
            note: 'Já estava cadastrado no dispositivo',
            photoUrl: photo || undefined,
            personId: person.id,
          };
        };

        // These count as successes, so they belong in the list too — otherwise the modal
        // shows "Sucesso 103" above 16 rows and the other 102 are unaccounted for.
        peopleAlreadyExist.forEach(person => syncRecentBuf.push(alreadyOnDeviceItem(person)));

        // Open progress modal
        setSyncProgressData({ zoneName: zone.name, total: syncTotal, done: peopleAlreadyExist.length, ok: peopleAlreadyExist.length, fail: 0, recentItems: [...syncRecentBuf], isComplete: false });

        // Loop only through people not caught by pre-load
        for (let i = 0; i < peopleToSync.length; i++) {
          // Pause support — poll ref every 200ms while paused
          while (syncPauseRef.current) {
            await new Promise(r => setTimeout(r, 200));
          }
          // Stop support
          if (syncStopRef.current) break;

          const person = peopleToSync[i];

          // Extract photo URL from nested structure or flat field
          const identityPhotoUrl = person.identity_photo?.photo_url || person.identity_photo_url;
          const profilePictureUrl = person.profile_picture_url;
          const photoUrl = identityPhotoUrl || profilePictureUrl;

          // Check if person already exists on device before adding
          // Hikvision: check by employeeNo (MD5 hash); ControlID: check by name
          try {
            const checkPayload = isHikvision
              ? { person_id: person.id }
              : { name: `${person.first_name} ${person.last_name}`.trim() };
            const checkResult = await DeviceIntegration.instance.execute(
              deviceInfo,
              DeviceOperations.LOAD_PEOPLE,
              checkPayload,
              CommunicationMode.DIRECT
            );
            const parsedCheck = typeof checkResult.data === 'string'
              ? (() => { try { return JSON.parse(checkResult.data); } catch { return checkResult.data; } })()
              : checkResult.data;
            const foundCount = isHikvision
              ? (parsedCheck?.UserInfoSearch?.UserInfo?.length ?? 0)
              : (parsedCheck?.users?.length ?? 0);
            if (checkResult.success && foundCount > 0) {
              peopleAlreadyExist.push(person);
              syncRecentBuf.push(alreadyOnDeviceItem(person));
              if (selectedCustomer?.id) {
                // Audit trail + reconciliation ledger — see utils/deviceSyncOutcome.
                reportDeviceSyncOutcome({
                  customerId: selectedCustomer.id,
                  personId: person.id,
                  deviceId: device.id,
                  device,
                  success: true,
                  source: 'local',
                });
              }
              continue;
            }
          } catch (checkErr: any) {
            console.warn(`[Check] Error for "${person.first_name}":`, checkErr?.message || checkErr);
          }

          try {
            const result = await DeviceIntegration.instance.execute(
              deviceInfo,
              DeviceOperations.SYNC_PERSON,
              {
                person_id: person.id,
                first_name: person.first_name,
                last_name: person.last_name,
                identity_photo_url: identityPhotoUrl,
                profile_picture_url: profilePictureUrl,
                isAllowed: true,
              },
              CommunicationMode.DIRECT
            );

            if (result.success) {
              successCount++;
              if (isControlID && controlIDGroupId !== null) {
                const rawData = typeof result.data === 'string'
                  ? (() => { try { return JSON.parse(result.data); } catch { return result.data; } })()
                  : result.data;
                const deviceUserId: number | undefined = rawData?.ids?.[0];
                if (deviceUserId) {
                  try {
                    await deviceCommunicationService.assignControlIDUserToGroup(deviceInfo, deviceUserId, controlIDGroupId);
                  } catch (groupErr: any) {
                    console.warn(`[SyncZone] Group assign failed for ${person.first_name}:`, groupErr?.message);
                  }
                }
              }
              if (selectedCustomer?.id) {
                // Audit trail + reconciliation ledger — see utils/deviceSyncOutcome.
                reportDeviceSyncOutcome({
                  customerId: selectedCustomer.id,
                  personId: person.id,
                  deviceId: device.id,
                  device,
                  success: true,
                  source: 'local',
                });
              }
              syncRecentBuf.push({ label: `${person.first_name} ${person.last_name}`.trim(), owner: '', status: 'ok', photoUrl: photoUrl || undefined, personId: person.id });
            } else {
              failureCount++;
              syncRecentBuf.push({ label: `${person.first_name} ${person.last_name}`.trim(), owner: '', status: 'failed', error: result.error, photoUrl: photoUrl || undefined, personId: person.id });
            }

            const syncDone = peopleAlreadyExist.length + i + 1;
            setSyncProgressData({ zoneName: zone.name, total: syncTotal, done: syncDone, ok: peopleAlreadyExist.length + successCount, fail: failureCount, recentItems: [...syncRecentBuf], isComplete: false });

          } catch (error: any) {
            failureCount++;
            syncRecentBuf.push({ label: `${person.first_name} ${person.last_name}`.trim(), owner: '', status: 'failed', error: error?.message, photoUrl: photoUrl || undefined, personId: person.id });
            const syncDone = peopleAlreadyExist.length + i + 1;
            setSyncProgressData({ zoneName: zone.name, total: syncTotal, done: syncDone, ok: peopleAlreadyExist.length + successCount, fail: failureCount, recentItems: [...syncRecentBuf], isComplete: false });
          }
          // Inter-person delay for ControlID to avoid overwhelming the device rate limiter
          if (isControlID && i < peopleToSync.length - 1) await new Promise(r => setTimeout(r, 200));
        }

        // ── ControlID: bulk assign pre-existing users to default group ──
        if (isControlID && controlIDGroupId !== null) {
          try {
            const { assigned, skipped } = await deviceCommunicationService.assignAllDeviceUsersToGroup(deviceInfo, controlIDGroupId);
            console.log(`[SyncZone] Bulk group — assigned: ${assigned}, already in group: ${skipped}`);
          } catch (bulkErr: any) {
            console.warn(`[SyncZone] Bulk group assignment error:`, bulkErr?.message);
          }
        }

        // Mark pre-loaded (already-on-device) people as synced in bulk
        if (selectedCustomer?.id && peopleAlreadyExist.length > 0) {
          for (const p of peopleAlreadyExist) {
            // Audit trail + reconciliation ledger — see utils/deviceSyncOutcome.
            reportDeviceSyncOutcome({
              customerId: selectedCustomer.id,
              personId: p.id,
              deviceId: device.id,
              device,
              success: true,
              source: 'local',
            });
          }
        }

        // Mark modal as complete
        setSyncProgressData({ zoneName: zone.name, total: syncTotal, done: syncTotal, ok: peopleAlreadyExist.length + successCount, fail: failureCount, recentItems: [...syncRecentBuf], isComplete: true });

        // Show result of direct sync
        if (successCount > 0 || peopleAlreadyExist.length > 0) {
          toast.success(
            `${successCount} sincronizadas, ${peopleAlreadyExist.length} já existiam no dispositivo`,
            { id: toastId, duration: 5000 }
          );
          return;
        }

        // All direct attempts failed.
        // If we know the device has people, cloud sync would re-send ALL and create duplicates.
        if (isControlID || existingHashes.size > 0 || existingNames.size > 0) {
          toast.error(
            `Falha ao sincronizar ${failureCount} pessoa(s) diretamente no dispositivo`,
            { id: toastId, duration: 6000 }
          );
          return;
        }

        // ControlID: never fall to cloud sync when direct failed — cloud sync doesn't deduplicate
        // by name and would re-add all people, creating duplicates on the device.
        if (isControlID) {
          toast.error(
            `Não foi possível conectar ao dispositivo diretamente. Verifique a rede e tente novamente.`,
            { id: toastId, duration: 7000 }
          );
          return;
        }

        // Electron: no cloud fallback for sync
        if (isElectron) {
          toast.error(`Falha ao sincronizar diretamente em ${zone.name}. Verifique a rede.`, { id: toastId });
          return;
        }
        toast.loading('Modo direto falhou. Tentando via nuvem...', { id: toastId });
      } else if (isElectron) {
        // Electron but no IP — cannot sync directly, do not fall to cloud
        toast.error(`Dispositivo sem IP configurado — não é possível sincronizar ${zone.name} diretamente.`, { id: toastId });
        return;
      } else {
        // Browser: cloud is the primary path
        toast.loading('Sincronizando via nuvem...', { id: toastId });
      }

      // Cloud path — browser only after this point
      const response = await fetchWithAuth(
        `/api/v1/iot-devices/${device.id}/sync`,
        { method: 'POST' }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(
          `${zone.name}: ${result.success_count || 0} pessoa(s) sincronizada(s) (nuvem)`,
          { id: toastId, duration: 5000 }
        );
      } else {
        const errorText = await response.text();
        toast.error(`Erro: ${errorText}`, { id: toastId });
      }
    } catch (error) {
      console.error('Error syncing zone:', error);
      toast.error(`Erro: ${error}`, { id: toastId });
    } finally {
      setIsSyncingZone(null);
      setShowSyncConfirmModal(false);
      setSyncTarget(null);
    }
  };

  // Sync all zones
  const handleSyncAll = async () => {
    setIsSyncingAll(true);
    const zonesWithDevices = zones.filter(z => devices.some(d => d.zone_id === z.id));

    if (zonesWithDevices.length === 0) {
      toast.error('Nenhuma zona com dispositivo vinculado');
      setIsSyncingAll(false);
      setShowSyncConfirmModal(false);
      setSyncTarget(null);
      return;
    }

    const isElectronApp = typeof window !== 'undefined' && !!(window as any).electronAPI;

    if (isElectronApp) {
      // Electron: delegate per-zone sync (uses direct path, no cloud)
      setIsSyncingAll(false);
      setShowSyncConfirmModal(false);
      setSyncTarget(null);
      for (const zone of zonesWithDevices) {
        await handleSyncZone(zone);
      }
      return;
    }

    // Browser: cloud-only path
    let successCount = 0;
    let errorCount = 0;

    for (const zone of zonesWithDevices) {
      const device = devices.find(d => d.zone_id === zone.id);
      if (!device) continue;

      try {
        const response = await fetchWithAuth(
          `/api/v1/iot-devices/${device.id}/sync`,
          { method: 'POST' }
        );

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error(`Error syncing zone ${zone.name}:`, error);
        errorCount++;
      }
    }

    if (successCount > 0 && errorCount === 0) {
      toast.success(`Sincronização iniciada para ${successCount} zona(s)`);
    } else if (successCount > 0 && errorCount > 0) {
      toast.success(`Sincronização iniciada para ${successCount} zona(s), ${errorCount} erro(s)`);
    } else {
      toast.error('Erro ao sincronizar zonas');
    }

    setIsSyncingAll(false);
    setShowSyncConfirmModal(false);
    setSyncTarget(null);
  };

  // Open sync confirmation modal
  const openSyncConfirmModal = (target: 'all' | Zone) => {
    setSyncTarget(target);
    setShowSyncConfirmModal(true);
  };

  // Handle sync confirmation
  const handleSyncConfirm = () => {
    const target = syncTarget;
    setShowSyncConfirmModal(false);
    setSyncTarget(null);
    if (target === 'all') {
      handleSyncAll();
    } else if (target) {
      handleSyncZone(target);
    }
  };

  const getZoneTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'entrance': 'bg-blue-100 text-blue-800',
      'common_area': 'bg-green-100 text-green-800',
      'private': 'bg-blue-100 text-blue-800',
      'restricted': 'bg-red-100 text-red-800',
      'parking': 'bg-yellow-100 text-yellow-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getDeviceStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <Wifi className="w-4 h-4 text-green-600" />;
      case 'offline':
        return <WifiOff className="w-4 h-4 text-gray-400" />;
      case 'checking':
        return <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Monitor className="w-4 h-4 text-gray-400" />;
    }
  };

  const filteredZones = zones.filter(zone => {
    const matchesSearch = zone.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      zone.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesEntryType = entryTypeFilter === 'all' || zone.entry_type === entryTypeFilter;

    return matchesSearch && matchesEntryType;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Browser warning banner */}
        {!electronMode && !webBannerDismissed && (
          <div className="mb-5 flex items-start gap-3 px-4 py-3.5 bg-amber-50 border border-amber-200 rounded-2xl">
            <div className="flex-shrink-0 mt-0.5 p-1.5 bg-amber-100 rounded-lg">
              <WifiOff className="h-4 w-4 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900">Acesso limitado via navegador</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Alguns dispositivos (Hikvision, ControlID, RFID) exigem o app desktop para verificação de status em tempo real.
                Use o Panther Access para acesso completo.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => navigate('/downloads')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-lg transition-colors whitespace-nowrap"
              >
                <Download className="h-3.5 w-3.5" />
                Baixar App
              </button>
              <button
                onClick={() => setWebBannerDismissed(true)}
                className="p-1.5 text-amber-500 hover:text-amber-700 hover:bg-amber-100 rounded-lg transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          {/* Zonas */}
          <div className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-gray-200">
            <MapPin className="h-3.5 w-3.5 text-[#96bf13] flex-shrink-0" />
            {isLoading ? (
              <div className="w-7 h-4 bg-gray-200 rounded animate-pulse" />
            ) : (
              <span className="text-sm font-bold text-gray-900">{zones.length}</span>
            )}
            <span className="text-xs text-gray-400">Zonas</span>
          </div>

          {/* Ativos */}
          {isAdmin ? (
            <button onClick={() => !isLoading && setShowDeviceListModal('active')} className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-gray-200 hover:border-green-300 transition-colors">
              <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
              {isLoading ? (
                <div className="w-7 h-4 bg-gray-200 rounded animate-pulse" />
              ) : (
                <span className="text-sm font-bold text-gray-900">{effectiveOnlineCount}</span>
              )}
              <span className="text-xs text-gray-400">Ativos</span>
            </button>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-gray-200">
              <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
              {isLoading ? (
                <div className="w-7 h-4 bg-gray-200 rounded animate-pulse" />
              ) : (
                <span className="text-sm font-bold text-gray-900">{effectiveOnlineCount}</span>
              )}
              <span className="text-xs text-gray-400">Ativos</span>
            </div>
          )}

          {/* Offline */}
          {isAdmin ? (
            <button onClick={() => !isLoading && setShowDeviceListModal('offline')} className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-gray-200 hover:border-red-200 transition-colors">
              <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
              {isLoading ? (
                <div className="w-7 h-4 bg-gray-200 rounded animate-pulse" />
              ) : (
                <span className="text-sm font-bold text-gray-900">{effectiveOfflineCount}</span>
              )}
              <span className="text-xs text-gray-400">Offline</span>
            </button>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-gray-200">
              <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
              {isLoading ? (
                <div className="w-7 h-4 bg-gray-200 rounded animate-pulse" />
              ) : (
                <span className="text-sm font-bold text-gray-900">{effectiveOfflineCount}</span>
              )}
              <span className="text-xs text-gray-400">Offline</span>
            </div>
          )}

          {/* Dispositivos */}
          {isAdmin ? (
            <button onClick={() => !isLoading && setShowDeviceListModal('all')} className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-gray-200 hover:border-blue-200 transition-colors">
              <Cpu className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
              {isLoading ? (
                <div className="w-7 h-4 bg-gray-200 rounded animate-pulse" />
              ) : (
                <span className="text-sm font-bold text-gray-900">{devices.length}</span>
              )}
              <span className="text-xs text-gray-400">Dispositivos</span>
            </button>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-gray-200">
              <Cpu className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
              {isLoading ? (
                <div className="w-7 h-4 bg-gray-200 rounded animate-pulse" />
              ) : (
                <span className="text-sm font-bold text-gray-900">{devices.length}</span>
              )}
              <span className="text-xs text-gray-400">Dispositivos</span>
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Atualizando...' : 'Atualizar'}
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col gap-3 mb-5">
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              {isLoading ? (
                <div className="pl-9 pr-3 py-2 w-full bg-gray-100 border border-gray-200 rounded-xl animate-pulse h-9" />
              ) : (
                <input
                  type="text"
                  placeholder="Buscar zonas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-2 w-full text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#96bf13] focus:border-[#96bf13] placeholder-gray-400"
                />
              )}
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => setShowFuncionariosReport(true)}
                disabled={isLoading}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FileText className="w-3.5 h-3.5" />
                Relatório
              </button>
              <button
                onClick={() => openSyncConfirmModal('all')}
                disabled={isSyncingAll || isLoading}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isSyncingAll ? 'animate-spin' : ''}`} />
                Sincronizar Todos
              </button>
              {isAdmin && (
                <>
                  <button
                    onClick={() => setShowCreateDeviceModal(true)}
                    disabled={isLoading}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-gray-800 rounded-xl hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Cpu className="w-3.5 h-3.5" />
                    Novo Dispositivo
                  </button>
                  <button
                    onClick={() => setShowCreateZoneModal(true)}
                    disabled={isLoading}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-[#96bf13] rounded-xl hover:bg-[#7a9f10] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Nova Zona
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Entry Type Filters */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {isLoading ? (
              [72, 88, 100, 72].map((w, i) => (
                <div key={i} className={`h-6 bg-gray-200 rounded-lg animate-pulse`} style={{ width: w }} />
              ))
            ) : (
              [
                { key: 'all', label: `Todas (${zones.length})` },
                { key: 'entrada', label: `↓ Entrada (${zones.filter(z => z.entry_type === 'entrada').length})` },
                { key: 'passagem', label: `↔ Passagem (${zones.filter(z => z.entry_type === 'passagem').length})` },
                { key: 'saida', label: `↑ Saída (${zones.filter(z => z.entry_type === 'saida').length})` },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setEntryTypeFilter(key as any)}
                  className={`px-3 py-1 text-xs rounded-lg font-medium transition-colors ${
                    entryTypeFilter === key
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Unlinked Devices Section */}
        {devices.filter(d => !d.zone_id).length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-yellow-900 mb-2">
                  Dispositivos Não Vinculados ({devices.filter(d => !d.zone_id).length})
                </h3>
                <p className="text-sm text-yellow-800 mb-3">
                  Os seguintes dispositivos precisam ser vinculados a uma zona para funcionar:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {devices.filter(d => !d.zone_id).map((device) => (
                    <button
                      key={device.id}
                      onClick={() => {
                        setSelectedDevice(device);
                        setShowDetailsModal(true);
                      }}
                      className="flex items-center gap-2 p-3 bg-white rounded-lg border border-yellow-300 hover:border-yellow-400 hover:shadow transition-all text-left"
                    >
                      <Cpu className="w-5 h-5 text-yellow-600" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate whitespace-nowrap overflow-hidden text-ellipsis" title={device.name}>
                          {device.name?.replace(/[\n\r]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Dispositivo sem nome'}
                        </div>
                        <div className="text-xs text-gray-600">{translateDeviceType(device.device_type)}</div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        device.status === 'online'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {translateStatus(device.status)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Zones Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 flex flex-col animate-pulse">
                {/* Header skeleton */}
                <div className="px-4 pt-4 pb-3 flex items-start gap-3">
                  <div className="w-1 self-stretch rounded-full bg-gray-200 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-1.5">
                      <div className="h-4 w-16 bg-gray-200 rounded-md" />
                      <div className="h-4 w-14 bg-gray-200 rounded-md" />
                    </div>
                    <div className="h-4 w-3/4 bg-gray-200 rounded" />
                    <div className="h-3 w-1/2 bg-gray-100 rounded" />
                  </div>
                  <div className="w-6 h-6 bg-gray-100 rounded-md flex-shrink-0" />
                </div>
                {/* Device row skeleton */}
                <div className="mx-4 mb-3 p-2.5 bg-gray-50 rounded-lg flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full bg-gray-200 flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-2/3 bg-gray-200 rounded" />
                    <div className="h-2.5 w-1/3 bg-gray-100 rounded" />
                  </div>
                  <div className="h-5 w-14 bg-gray-200 rounded-full" />
                </div>
                {/* Footer skeleton */}
                <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between">
                  <div className="h-3 w-20 bg-gray-100 rounded" />
                  <div className="h-3 w-16 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredZones.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-400">
            Nenhuma zona encontrada
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredZones.map((zone) => {
              const device = getDeviceForZone(zone.id);
              const effectiveDeviceStatus = device ? getEffectiveStatus(device) : null;
              const isOnline = effectiveDeviceStatus === 'online';
              const isChecking = effectiveDeviceStatus === 'checking';
              const isWebUnavailable = effectiveDeviceStatus === 'web_unavailable';

              return (
                <div
                  key={zone.id}
                  className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all flex flex-col"
                >
                  {/* Header */}
                  <div className="px-4 pt-4 pb-3 flex items-start gap-3">
                    {/* Entry type accent bar */}
                    <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${
                      zone.entry_type === 'entrada' ? 'bg-green-500' :
                      zone.entry_type === 'saida' ? 'bg-red-400' : 'bg-blue-400'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${
                          zone.entry_type === 'entrada' ? 'bg-green-50 text-green-700' :
                          zone.entry_type === 'saida' ? 'bg-red-50 text-red-600' :
                          'bg-blue-50 text-blue-600'
                        }`}>
                          {zone.entry_type === 'entrada' ? '↓ Entrada' : zone.entry_type === 'saida' ? '↑ Saída' : '↔ Passagem'}
                        </span>
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 truncate leading-tight">{zone.name}</h3>
                      {zone.description && (
                        <p className="text-[11px] text-gray-400 mt-0.5 truncate">{zone.description}</p>
                      )}
                    </div>
                    {/* Sync + Three-dot menu */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {device && (
                        <button
                          onClick={() => openSyncConfirmModal(zone)}
                          disabled={isSyncingZone === zone.id}
                          className="text-gray-400 hover:text-orange-500 hover:bg-orange-50 p-1 rounded-md transition-colors disabled:opacity-40"
                          title="Sincronizar"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${isSyncingZone === zone.id ? 'animate-spin' : ''}`} />
                        </button>
                      )}
                      <div className="relative">
                        <button
                          onClick={() => setOpenMenuZoneId(openMenuZoneId === zone.id ? null : zone.id)}
                          className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1 rounded-md transition-colors"
                          title="Mais opções"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {openMenuZoneId === zone.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenMenuZoneId(null)} />
                            <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                              {device && (
                                <>
                                  <div className="border-t border-gray-100 my-1" />
                                  <button
                                    onClick={() => {
                                      handleOpenDoorClick(zone, device);
                                      setOpenMenuZoneId(null);
                                    }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-green-700 hover:bg-green-50 transition-colors"
                                  >
                                    <DoorOpen className="w-4 h-4" />
                                    Acionar Porta
                                  </button>
                                  <button
                                    onClick={() => handleRestartDevice(device)}
                                    disabled={restartingDeviceId === device.id}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 transition-colors disabled:opacity-50"
                                  >
                                    {restartingDeviceId === device.id
                                      ? <Loader2 className="w-4 h-4 animate-spin" />
                                      : <RotateCcw className="w-4 h-4" />}
                                    Reiniciar Dispositivo
                                  </button>
                                  <button
                                    onClick={() => handleSetDeviceTime(device)}
                                    disabled={settingTimeDeviceId === device.id}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
                                  >
                                    {settingTimeDeviceId === device.id
                                      ? <Loader2 className="w-4 h-4 animate-spin" />
                                      : <Clock className="w-4 h-4" />}
                                    Sincronizar Horário
                                  </button>
                                </>
                              )}
                              {isAdmin && (
                                <>
                                  <div className="border-t border-gray-100 my-1" />
                                  <button
                                    onClick={() => {
                                      setSelectedZone(zone);
                                      setShowCreateZoneModal(true);
                                      setOpenMenuZoneId(null);
                                    }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                    Editar Zona
                                  </button>
                                  <button
                                    onClick={() => {
                                      handleDeleteZone(zone.id);
                                      setOpenMenuZoneId(null);
                                    }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Excluir Zona
                                  </button>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Device row */}
                  <div className="px-4 pb-3">
                    {!device ? (
                      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                        <Monitor className="w-3.5 h-3.5 text-gray-300" />
                        <span className="text-xs text-gray-400">Sem dispositivo vinculado</span>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDevice(device);
                          setShowDetailsModal(true);
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left group"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-gray-800 truncate" title={device.name}>
                              {device.name?.replace(/[\n\r]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Dispositivo sem nome'}
                            </span>
                            {isWebUnavailable
                              ? <Loader2 className="w-2.5 h-2.5 text-gray-300 animate-spin flex-shrink-0" />
                              : <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                  isChecking ? 'bg-amber-400 animate-pulse' : isOnline ? 'bg-green-500' : 'bg-red-400'
                                }`} />
                            }
                          </div>
                          <div className="text-[10px] text-gray-400 mt-0.5 space-y-0.5">
                            <div className="truncate">{translateDeviceType(device.device_type)}</div>
                            {(device.ip_address || device.local_ip_address) && (
                              <div className="grid gap-y-0.5 items-center" style={{ gridTemplateColumns: '3.5rem 1fr 1rem' }}>
                                {device.ip_address && (<>
                                  <span className="text-gray-500">Público</span>
                                  <span className="font-mono text-gray-600 truncate">{device.ip_address}</span>
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => { e.stopPropagation(); window.open(ensureHttpScheme(device.ip_address), '_blank', 'noopener,noreferrer'); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); window.open(ensureHttpScheme(device.ip_address), '_blank', 'noopener,noreferrer'); } }}
                                    className="flex items-center justify-center w-4 h-4 rounded text-gray-300 hover:text-blue-500 hover:bg-blue-50 transition-colors cursor-pointer"
                                    title={`Abrir público: ${device.ip_address}`}
                                  >
                                    <ExternalLink className="w-2.5 h-2.5" />
                                  </span>
                                </>)}
                                {device.local_ip_address && (<>
                                  <span className="text-gray-500">Privado</span>
                                  <span className="font-mono text-gray-600 truncate">{device.local_ip_address}</span>
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => { e.stopPropagation(); window.open(ensureHttpScheme(device.local_ip_address), '_blank', 'noopener,noreferrer'); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); window.open(ensureHttpScheme(device.local_ip_address), '_blank', 'noopener,noreferrer'); } }}
                                    className="flex items-center justify-center w-4 h-4 rounded text-gray-300 hover:text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer"
                                    title={`Abrir local: ${device.local_ip_address}`}
                                  >
                                    <ExternalLink className="w-2.5 h-2.5" />
                                  </span>
                                </>)}
                              </div>
                            )}
                          </div>
                        </div>
                        <Monitor className="w-3 h-3 text-gray-300 flex-shrink-0 group-hover:text-gray-400 transition-colors" />
                      </button>
                    )}
                  </div>

                </div>
              );
            })}
          </div>
        )}

        {/* Zone Details Modal */}
        {showDetailsModal && selectedZone && !selectedDevice && (
          <ZoneDetailsModal
            zone={selectedZone}
            device={getDeviceForZone(selectedZone.id)}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedZone(null);
            }}
          />
        )}

        {/* Device Details Modal */}
        {showDetailsModal && selectedDevice && (
          <DeviceDetailsModal
            device={selectedDevice}
            zone={zones.find(z => z.id === selectedDevice.zone_id) || null}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedDevice(null);
            }}
            onRefresh={() => {
              fetchZones();
              fetchDevices();
            }}
            onDeviceUpdated={(updated) => setSelectedDevice(updated)}
            liveHealth={
              isGuaritaDevice(selectedDevice)
                ? guaritaHealthMap.get(selectedDevice.id)
                : (electronMode ? healthMap.get(selectedDevice.id) : undefined)
            }
            onSyncProgress={setSyncProgressData}
          />
        )}

        {/* Device Cards Modal */}
        {showDeviceCardsModal && selectedDevice && (
          <DeviceCardsModal
            isOpen={showDeviceCardsModal}
            onClose={() => setShowDeviceCardsModal(false)}
            deviceCards={deviceCards}
            isLoading={isLoadingCards}
            deviceName={selectedDevice.name}
            deviceId={selectedDevice.id}
            registeredCount={deviceCards.filter((c: any) => !c.is_unauthorized).length}
            unknownCount={deviceCards.filter((c: any) => c.is_unauthorized).length}
            onRefresh={fetchDeviceCards}
          />
        )}

        {/* Create/Edit Modal */}
        {showCreateZoneModal && (
          <CreateEditZoneModal
            zone={selectedZone}
            devices={devices}
            onClose={() => {
              setShowCreateZoneModal(false);
              setSelectedZone(null);
            }}
            onSuccess={() => {
              fetchZones();
              fetchDevices();
              setShowCreateZoneModal(false);
              setSelectedZone(null);
            }}
          />
        )}

        {/* Create Device Modal */}
        {showCreateDeviceModal && (
          <CreateDeviceModal
            onClose={() => {
              setShowCreateDeviceModal(false);
            }}
            onSuccess={() => {
              fetchDevices();
              setShowCreateDeviceModal(false);
            }}
          />
        )}

        {/* Device List Modal */}
        {showDeviceListModal && (
          <DeviceListModal
            devices={devices}
            filter={showDeviceListModal}
            zones={zones}
            onClose={() => setShowDeviceListModal(null)}
            onDeviceClick={(device) => {
              setSelectedDevice(device);
              setShowDetailsModal(true);
              setShowDeviceListModal(null);
            }}
            onDeleteDevice={handleDeleteDevice}
            healthMap={healthMap}
            isElectronMode={electronMode}
            isChecking={isHealthChecking}
          />
        )}

        {/* Door Trigger Confirmation Modal */}
        {showDoorConfirmModal && doorTriggeringZone && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
              {/* Header */}
              <div className="px-5 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                    <DoorOpen className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold">Acionar Porta</h3>
                    <p className="text-xs text-green-100 mt-0.5">Confirme para abrir a porta</p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="px-5 py-4">
                <p className="text-sm text-gray-500 mb-3">A seguinte porta será aberta imediatamente:</p>
                <div className="flex items-center gap-3 px-3 py-2.5 bg-green-50 rounded-lg border border-green-200">
                  <div className="w-7 h-7 rounded-md bg-green-100 flex items-center justify-center flex-shrink-0">
                    <DoorOpen className="w-3.5 h-3.5 text-green-700" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{doorTriggeringZone.name}</p>
                    {doorTriggeringZone.description && (
                      <p className="text-xs text-gray-500 truncate">{doorTriggeringZone.description}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 pb-5 flex items-center justify-end gap-2.5">
                <button
                  onClick={() => {
                    setShowDoorConfirmModal(false);
                    setDoorTriggeringZone(null);
                  }}
                  disabled={isDoorTriggering}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleTriggerZoneDoor}
                  disabled={isDoorTriggering}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap shadow-sm"
                >
                  {isDoorTriggering ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      Acionando...
                    </>
                  ) : (
                    <>
                      <DoorOpen className="w-3.5 h-3.5" />
                      Confirmar e Acionar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sync Confirmation Modal */}
        <FuncionariosReport
          open={showFuncionariosReport}
          onClose={() => setShowFuncionariosReport(false)}
          customerId={selectedCustomer?.id ?? ''}
          customerName={selectedCustomer?.name}
          initialView="zones"
        />

        {showSyncConfirmModal && syncTarget && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                    <RefreshCw className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">
                      {syncTarget === 'all' ? 'Sincronizar Todas as Zonas' : 'Sincronizar Zona'}
                    </h3>
                    <p className="text-sm text-gray-300">Confirme para iniciar a sincronização</p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-[#96bf13] bg-opacity-10 rounded-lg border border-[#96bf13] border-opacity-30">
                    <Info className="w-5 h-5 text-[#96bf13] flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      {syncTarget === 'all' ? (
                        <>
                          <p className="text-sm font-medium text-gray-900">
                            Você está prestes a sincronizar todas as zonas com dispositivos vinculados.
                          </p>
                          <p className="text-sm text-gray-700 mt-1">
                            {zones.filter(z => devices.some(d => d.zone_id === z.id)).length} zonas serão sincronizadas
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-900">
                            Você está prestes a sincronizar a zona:
                          </p>
                          <p className="text-sm text-gray-700 font-semibold mt-1">
                            {syncTarget.name}
                          </p>
                          {syncTarget.description && (
                            <p className="text-xs text-gray-600 mt-1">
                              {syncTarget.description}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <p className="text-sm text-gray-600">
                    A sincronização enviará todas as pessoas autorizadas para os dispositivos.
                    Isso pode levar alguns minutos. Tem certeza que deseja continuar?
                  </p>
                </div>
              </div>

              {/* Footer.
                  items-stretch + a non-wrapping label: with both buttons on flex-1 the
                  modal was too narrow for "Confirmar e Sincronizar", so that label wrapped
                  to two lines and the green button ended up taller than "Cancelar".
                  Cancelar now takes only the width its word needs. */}
              <div className="p-6 border-t bg-gray-50 flex items-stretch gap-3">
                <button
                  onClick={() => {
                    setShowSyncConfirmModal(false);
                    setSyncTarget(null);
                  }}
                  disabled={isSyncingAll || isSyncingZone !== null}
                  className="flex-none px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSyncConfirm}
                  disabled={isSyncingAll || isSyncingZone !== null}
                  className="flex-1 px-4 py-2.5 bg-[#96bf13] text-white rounded-lg hover:bg-[#7a9f10] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 whitespace-nowrap shadow-md hover:shadow-lg"
                >
                  {(isSyncingAll || isSyncingZone !== null) ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Sincronizando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Confirmar e Sincronizar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Vehicle Removal Confirmation Modal */}
        {showVehicleRemoveModal && vehicleToRemove && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b bg-gradient-to-r from-red-600 to-red-700 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Remover Veículo</h3>
                    <p className="text-sm text-red-100">Confirme para remover do dispositivo</p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                <div className="space-y-4">
                  {/* Vehicle Info Card */}
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                      <Car className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-semibold text-gray-900 truncate">
                        {vehicleToRemove.vehicle.plate || 'Sem placa'}
                      </p>
                      <p className="text-sm text-gray-600 truncate">
                        {vehicleToRemove.vehicle.owner_name || vehicleToRemove.vehicle.name || 'Proprietário desconhecido'}
                      </p>
                      {vehicleToRemove.vehicle.brand && vehicleToRemove.vehicle.model && (
                        <p className="text-xs text-gray-500 truncate">
                          {vehicleToRemove.vehicle.brand} {vehicleToRemove.vehicle.model}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Warning Message */}
                  <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Você está prestes a remover este veículo do dispositivo RFID.
                      </p>
                      <p className="text-sm text-gray-700 mt-1">
                        <span className="font-medium">Dispositivo:</span> {vehicleToRemove.device.name}
                      </p>
                      <p className="text-xs text-gray-600 mt-2">
                        Esta ação removerá o cadastro da tag RFID do leitor. O veículo não terá mais acesso automático por esta zona.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t bg-gray-50 flex items-stretch gap-3">
                <button
                  onClick={() => {
                    setShowVehicleRemoveModal(false);
                    setVehicleToRemove(null);
                  }}
                  disabled={isRemovingVehicle}
                  className="flex-none px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRemoveVehicleFromDevice}
                  disabled={isRemovingVehicle}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 whitespace-nowrap shadow-md hover:shadow-lg"
                >
                  {isRemovingVehicle ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Removendo...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Confirmar Remoção
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

// Create/Edit Zone Modal Component
const CreateEditZoneModal: React.FC<{
  zone: Zone | null;
  devices: IoTDevice[];
  onClose: () => void;
  onSuccess: () => void;
}> = ({ zone, devices, onClose, onSuccess }) => {
  const { token } = useKeycloak();
  const { selectedCustomer } = useCustomer();
  const [formData, setFormData] = useState({
    name: zone?.name || '',
    description: zone?.description || '',
    zone_type: zone?.zone_type || 'common_area',
    entry_type: zone?.entry_type || 'passagem',
    device_id: zone ? getDeviceForZone(zone.id, devices)?.id || '' : '',
    requires_authorization: zone?.requires_authorization ?? true,
    status: zone?.status || 'active',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Helper function to get device for a zone
  function getDeviceForZone(zoneId: string, allDevices: IoTDevice[]): IoTDevice | null {
    return allDevices.find(d => d.zone_id === zoneId) || null;
  }

  // Get available devices (not assigned to other zones, or assigned to current zone)
  const availableDevices = devices.filter(d =>
    !d.zone_id || d.zone_id === '00000000-0000-0000-0000-000000000000' || (zone && d.zone_id === zone.id)
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomer) return;

    if (!formData.device_id) {
      toast.error('Selecione um dispositivo IoT');
      return;
    }

    setIsSaving(true);
    try {
      const url = zone
        ? `/api/v1/zones/${zone.id}`
        : `/api/v1/zones`;

      const payload = {
        customer_id: selectedCustomer.id,
        name: formData.name,
        description: formData.description,
        zone_type: formData.zone_type,
        entry_type: formData.entry_type,
        requires_authorization: formData.requires_authorization,
        status: formData.status,
      };

      const response = await fetchWithAuth(url, {
        method: zone ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const savedZone = await response.json();
        const zoneId = savedZone.zone?.id || savedZone.id;

        // Update device's zone_id
        const deviceUpdateResponse = await fetchWithAuth(
          `/api/v1/iot-devices/${formData.device_id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              zone_id: zoneId,
            }),
          }
        );

        if (!deviceUpdateResponse.ok) {
          toast.error('Zona criada, mas falha ao vincular dispositivo');
        } else {
          toast.success(zone ? 'Zona atualizada com sucesso' : 'Zona criada com sucesso');
          onSuccess();
        }
      } else {
        const data = await response.json();
        toast.error(data.error || 'Falha ao salvar zona');
      }
    } catch (error) {
      logger.error('zones-devices.modal', 'Failed to save zone', error instanceof Error ? error : new Error(String(error)));
      toast.error('Erro ao salvar zona');
    } finally {
      setIsSaving(false);
    }
  };

  const zoneTypes = [
    { value: 'entrance', label: 'Entrada' },
    { value: 'common_area', label: 'Área Comum' },
    { value: 'private', label: 'Privada' },
    { value: 'restricted', label: 'Restrita' },
    { value: 'parking', label: 'Estacionamento' },
    { value: 'elevator', label: 'Elevador' },
    { value: 'gate', label: 'Portão' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {zone ? 'Editar Zona' : 'Nova Zona'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome da Zona *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96bf13] focus:border-transparent"
              placeholder="Ex: Entrada Principal, Portaria, Estacionamento"
              required
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96bf13] focus:border-transparent"
              placeholder="Descrição opcional da zona"
              rows={2}
            />
          </div>

          {/* Zone Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Zona *
            </label>
            <select
              value={formData.zone_type}
              onChange={(e) => setFormData({ ...formData, zone_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96bf13] focus:border-transparent"
              required
            >
              {zoneTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Entry Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Direção *
            </label>
            <select
              value={formData.entry_type}
              onChange={(e) => setFormData({ ...formData, entry_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96bf13] focus:border-transparent"
              required
            >
              <option value="entrada">Entrada - Ponto de acesso inicial</option>
              <option value="passagem">Passagem - Ponto de circulação interna</option>
              <option value="saida">Saída - Ponto de saída final</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Define o propósito da zona no fluxo de acesso
            </p>
          </div>

          {/* Device Selection - REQUIRED */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dispositivo IoT * <span className="text-xs text-gray-500">(obrigatório - 1:1)</span>
            </label>
            <select
              value={formData.device_id}
              onChange={(e) => setFormData({ ...formData, device_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96bf13] focus:border-transparent"
              required
            >
              <option value="">Selecione um dispositivo...</option>
              {availableDevices.length === 0 ? (
                <option disabled>Nenhum dispositivo disponível</option>
              ) : (
                availableDevices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.name} ({translateDeviceType(device.device_type)}) - {translateStatus(device.status)}
                  </option>
                ))
              )}
            </select>
            {availableDevices.length === 0 && (
              <p className="text-xs text-red-600 mt-1">
                Todos os dispositivos já estão vinculados a outras zonas. Crie um novo dispositivo primeiro.
              </p>
            )}
          </div>

          {/* Options */}
          <div className="mb-4 space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.requires_authorization}
                onChange={(e) => setFormData({ ...formData, requires_authorization: e.target.checked })}
                className="w-4 h-4 text-[#96bf13] rounded focus:ring-[#96bf13]"
              />
              <span className="text-sm font-medium text-gray-700">Requer Autorização</span>
            </label>
          </div>

          {/* Status */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96bf13] focus:border-transparent"
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="maintenance">Manutenção</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              disabled={isSaving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#96bf13] text-white rounded-lg hover:bg-[#7a9f10] transition-colors disabled:opacity-50"
              disabled={isSaving || availableDevices.length === 0}
            >
              {isSaving ? 'Salvando...' : zone ? 'Atualizar Zona' : 'Criar Zona'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Device Modal Component
const CreateDeviceModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const { token } = useKeycloak();
  const { selectedCustomer } = useCustomer();
  const [formData, setFormData] = useState({
    name: '',
    device_type: 'facial_reader',
    ip_address: '',
    local_ip_address: '',
    manufacturer: '',
    model: '',
    username: 'admin',
    password: '',
    status: 'offline',
    serial_number: '',
    firmware_version: '',
    mac_address: '',
    guarita_integration_id: '',
    guarita_receptor: 1,
    guarita_saida: 1,
    alphadigi_camera_id: '',
    hikvision_lpr_camera_id: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [deviceModels, setDeviceModels] = useState<any[]>([]);
  const [vendors, setVendors] = useState<string[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(true);
  const [showProbeModal, setShowProbeModal] = useState(false);
  const [showDevicePassword, setShowDevicePassword] = useState(false);
  const [guaritaIntegrations, setGuaritaIntegrations] = useState<any[]>([]);
  const [alphadigiCameras, setAlphadigiCameras] = useState<any[]>([]);
  const [hikvisionLPRCameras, setHikvisionLPRCameras] = useState<any[]>([]);

  // Fetch available device models on mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        // Fetch vendors from probe endpoint (gets from registered detectors)
        const vendorsResponse = await fetchWithAuth(`/api/v1/iot-connect/probe/vendors`);
        if (vendorsResponse.ok) {
          const vendorsData = await vendorsResponse.json();
          const fetchedVendors = vendorsData.vendors || [];
          // Append Nice if not already present (Guarita is not probe-detected)
          if (!fetchedVendors.includes('nice')) fetchedVendors.push('nice');
          // Append Alphadigi if not already present (LPR uses TCP, not probe-detected)
          if (!fetchedVendors.includes('alphadigi')) fetchedVendors.push('alphadigi');
          setVendors(fetchedVendors);
        }

        // Fetch models from probe endpoint (gets from registered detectors)
        const modelsResponse = await fetchWithAuth(`/api/v1/iot-connect/probe/models`);
        if (modelsResponse.ok) {
          const modelsData = await modelsResponse.json();
          // Convert models map to array for backward compatibility
          const modelsArray: any[] = [];
          if (modelsData.models) {
            Object.entries(modelsData.models).forEach(([vendor, models]: [string, any]) => {
              if (Array.isArray(models)) {
                models.forEach((model: string) => {
                  modelsArray.push({
                    vendor: vendor,
                    code: model,
                    model: model,
                    name: model
                  });
                });
              }
            });
          }
          // Add Nice Módulo Guarita IP manually (not probe-detected)
          modelsArray.push({ vendor: 'nice', code: 'modulo_guarita_ip', model: 'modulo_guarita_ip', name: 'Módulo Guarita IP' });
          // Add Alphadigi LPR camera manually (uses TCP, not probe-detected)
          modelsArray.push({ vendor: 'alphadigi', code: 'lpr_camera', model: 'lpr_camera', name: 'Câmera LPR', device_type: 'lpr' });
          // Add Hikvision DS-TCG406 LPR camera manually (uses ANPR webhook, not probe-detected)
          modelsArray.push({ vendor: 'hikvision', code: 'DS-TCG406', model: 'DS-TCG406', name: 'DS-TCG406-E(S) LPR', device_type: 'lpr' });
          setDeviceModels(modelsArray);
        }

      } catch (error) {
        console.error('Failed to fetch device models:', error);
      } finally {
        setIsLoadingModels(false);
      }
    };
    fetchModels();
  }, []);

  useEffect(() => {
    if (!selectedCustomer?.id) return;
    fetchWithAuth(`/api/v1/guarita-integrations?customer_id=${selectedCustomer.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setGuaritaIntegrations(data.data || data || []); })
      .catch(() => {});
  }, [selectedCustomer?.id]);

  useEffect(() => {
    if (!selectedCustomer?.id) return;
    fetchWithAuth(`/api/v1/alphadigi-cameras?customer_id=${selectedCustomer.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setAlphadigiCameras(data.data || data || []); })
      .catch(() => {});
  }, [selectedCustomer?.id]);

  useEffect(() => {
    if (!selectedCustomer?.id) return;
    fetchWithAuth(`/api/v1/hikvision-lpr-cameras?customer_id=${selectedCustomer.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setHikvisionLPRCameras(data.data || data || []); })
      .catch(() => {});
  }, [selectedCustomer?.id]);

  const isGuaritaDevice = formData.manufacturer === 'nice' && formData.model === 'modulo_guarita_ip';
  const isAlphadigiDevice = formData.manufacturer === 'alphadigi' && formData.model === 'lpr_camera';
  const isHikvisionLPRDevice = formData.manufacturer === 'hikvision' && formData.model === 'DS-TCG406';

  const handleDeviceDetected = (deviceInfo: any, credentials?: { username: string; password: string }) => {
    // Auto-fill form with detected device information
    // Probe API now returns database-compatible device types (facial_reader, rfid_reader)
    // Also transfer device metadata (serial, firmware, MAC) for read-only display
    setFormData({
      ...formData,
      ip_address: deviceInfo.ip_address,
      manufacturer: deviceInfo.vendor.toLowerCase(),
      model: deviceInfo.model,
      name: deviceInfo.device_name || `${deviceInfo.vendor} ${deviceInfo.model}`,
      device_type: deviceInfo.device_type || 'facial_reader',
      // Device metadata from probe (displayed as read-only in device modal)
      serial_number: deviceInfo.serial_number || '',
      firmware_version: deviceInfo.firmware_version || '',
      mac_address: deviceInfo.mac_address || '',
      // Auto-fill credentials from probe if provided
      username: credentials?.username || formData.username,
      password: credentials?.password || formData.password,
    });
    toast.success('Informações do dispositivo preenchidas automaticamente!');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomer) return;

    setIsSaving(true);
    try {
      // Ensure HTTP prefix (default to http:// like presetup does)
      let finalIpAddress = formData.ip_address?.trim() || '';
      if (finalIpAddress && !finalIpAddress.match(/^https?:\/\//)) {
        finalIpAddress = `http://${finalIpAddress}`;
      }

      const body = isGuaritaDevice ? {
        customer_id: selectedCustomer.id,
        name: formData.name,
        device_type: 'guarita',
        ip_address: '',
        manufacturer: 'nice',
        model: 'Módulo Guarita IP',
        status: formData.status,
        config: {
          guarita_integration_id: formData.guarita_integration_id,
          guarita_receptor: formData.guarita_receptor,
          guarita_saida: formData.guarita_saida,
        },
      } : isAlphadigiDevice ? {
        customer_id: selectedCustomer.id,
        name: formData.name,
        device_type: 'alphadigi_lpr',
        ip_address: '',
        manufacturer: 'alphadigi',
        model: 'Câmera LPR',
        status: formData.status,
        config: {
          alphadigi_camera_id: formData.alphadigi_camera_id,
        },
      } : isHikvisionLPRDevice ? {
        customer_id: selectedCustomer.id,
        name: formData.name,
        device_type: 'hikvision_lpr',
        ip_address: '',
        manufacturer: 'hikvision',
        model: 'DS-TCG406-E(S) LPR',
        status: formData.status,
        config: {
          hikvision_lpr_camera_id: formData.hikvision_lpr_camera_id,
        },
      } : {
        customer_id: selectedCustomer.id,
        ...formData,
        ip_address: finalIpAddress,
      };

      const response = await fetchWithAuth(`/api/v1/iot-devices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success('Dispositivo criado com sucesso');
        onSuccess();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Falha ao criar dispositivo');
      }
    } catch (error) {
      logger.error('device.modal', 'Failed to create device', error instanceof Error ? error : new Error(String(error)));
      toast.error('Erro ao criar dispositivo');
    } finally {
      setIsSaving(false);
    }
  };

  const deviceTypes = [
    { value: 'facial_reader', label: 'Leitor Facial' },
    { value: 'rfid_reader', label: 'Leitor RFID' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Novo Dispositivo</h2>
              <p className="text-sm text-gray-600 mt-1">
                Depois de criar, vincule-o a uma zona
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Device Probe Button — hidden for Guarita, Alphadigi, and Hikvision LPR */}
          {!isGuaritaDevice && !isAlphadigiDevice && !isHikvisionLPRDevice && <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                  <Wifi className="h-5 w-5" />
                  Detecção Automática de Dispositivo
                </h3>
                <p className="text-xs text-blue-700 mt-1">
                  Digite o endereço IP do dispositivo para detectar automaticamente o fabricante, modelo e outras informações
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowProbeModal(true)}
                className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center gap-2 whitespace-nowrap"
              >
                <Search className="h-4 w-4" />
                Detectar Dispositivo
              </button>
            </div>
          </div>}

          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome do Dispositivo *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: Leitor Entrada Principal"
              required
            />
          </div>

          {/* Device Type - Auto-detected (hidden for Guarita, Alphadigi, and Hikvision LPR) */}
          {!isGuaritaDevice && !isAlphadigiDevice && !isHikvisionLPRDevice && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Dispositivo (Auto-detectado) *
              </label>
              <input
                type="text"
                value={deviceTypes.find(t => t.value === formData.device_type)?.label || 'Use "Detectar Dispositivo" para identificar'}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                placeholder="Use 'Detectar Dispositivo' para identificar automaticamente"
              />
              <input type="hidden" name="device_type" value={formData.device_type} required />
              <p className="text-xs text-gray-500 mt-1">
                Clique em "Detectar Dispositivo" para identificar automaticamente o tipo
              </p>
            </div>
          )}

          {/* IP Addresses — hidden for Guarita, Alphadigi, and Hikvision LPR */}
          {!isGuaritaDevice && !isAlphadigiDevice && !isHikvisionLPRDevice && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Endereço de Nuvem (Cloud) *
                </label>
                <input
                  type="text"
                  value={formData.ip_address}
                  onChange={(e) => setFormData({ ...formData, ip_address: sanitizeDeviceAddress(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="192.168.1.100"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Endereço público/cloud usado para webhooks do dispositivo
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <span>Endereço Privado (Rede Local)</span>
                  <span className="text-xs text-gray-400 font-normal">(Opcional)</span>
                </label>
                <input
                  type="text"
                  value={formData.local_ip_address || ''}
                  onChange={(e) => setFormData({ ...formData, local_ip_address: sanitizeDeviceAddress(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="10.0.0.50"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Endereço IP local para enviar comandos. Deixe vazio para usar endereço de nuvem
                </p>
              </div>
            </>
          )}

          {/* Manufacturer */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fabricante *
            </label>
            <select
              value={formData.manufacturer}
              onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value, model: '' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Selecione o fabricante</option>
              {vendors.map((vendor) => (
                <option key={vendor} value={vendor.toLowerCase()}>
                  {vendor.charAt(0).toUpperCase() + vendor.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Model */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modelo *
            </label>
            <select
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              disabled={!formData.manufacturer || isLoadingModels}
            >
              <option value="">
                {!formData.manufacturer ? 'Selecione o fabricante primeiro' : 'Selecione o modelo'}
              </option>
              {deviceModels
                .filter((model) => model.vendor.toLowerCase() === formData.manufacturer.toLowerCase())
                .map((model) => (
                  <option key={`${model.vendor}-${model.code}`} value={model.code}>
                    {model.name}
                  </option>
                ))}
            </select>
          </div>

          {/* Guarita-specific fields */}
          {isGuaritaDevice && (
            <div className="mb-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Módulo Guarita *
                </label>
                <select
                  required
                  value={formData.guarita_integration_id}
                  onChange={(e) => setFormData({ ...formData, guarita_integration_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione o módulo</option>
                  {guaritaIntegrations.map((g: any) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
                {guaritaIntegrations.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">Nenhum Módulo Guarita cadastrado. Acesse Integrações para adicionar.</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Receptor *</label>
                  <select
                    required
                    value={formData.guarita_receptor}
                    onChange={(e) => setFormData({ ...formData, guarita_receptor: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Saída *</label>
                  <select
                    required
                    value={formData.guarita_saida}
                    onChange={(e) => setFormData({ ...formData, guarita_saida: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Alphadigi LPR-specific fields */}
          {isAlphadigiDevice && (
            <div className="mb-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Câmera LPR *
                </label>
                <select
                  required
                  value={formData.alphadigi_camera_id}
                  onChange={(e) => setFormData({ ...formData, alphadigi_camera_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione a câmera LPR</option>
                  {alphadigiCameras.map((cam: any) => (
                    <option key={cam.id} value={cam.id}>{cam.name} ({cam.device_ip})</option>
                  ))}
                </select>
                {alphadigiCameras.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">Nenhuma câmera LPR cadastrada. Acesse Integrações para adicionar.</p>
                )}
              </div>
            </div>
          )}

          {/* Hikvision LPR-specific fields */}
          {isHikvisionLPRDevice && (
            <div className="mb-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Câmera LPR Hikvision *
                </label>
                <select
                  required
                  value={formData.hikvision_lpr_camera_id}
                  onChange={(e) => setFormData({ ...formData, hikvision_lpr_camera_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione a câmera LPR</option>
                  {hikvisionLPRCameras.map((cam: any) => (
                    <option key={cam.id} value={cam.id}>{cam.name} ({cam.device_ip}:{cam.device_port})</option>
                  ))}
                </select>
                {hikvisionLPRCameras.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">Nenhuma câmera LPR Hikvision cadastrada. Acesse Integrações para adicionar.</p>
                )}
              </div>
            </div>
          )}

          {/* Device Metadata Section - Serial, Firmware, MAC */}
          {!isGuaritaDevice && !isAlphadigiDevice && !isHikvisionLPRDevice && <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              Informações do Dispositivo
            </h4>
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Número de Série:</span>
                <span className="text-gray-900 font-mono">{formData.serial_number || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Versão Firmware:</span>
                <span className="text-gray-900 font-mono">{formData.firmware_version || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Endereço MAC:</span>
                <span className="text-gray-900 font-mono">{formData.mac_address || 'N/A'}</span>
              </div>
            </div>
          </div>}

          {/* Username — hidden for Guarita, Alphadigi, and Hikvision LPR */}
          {!isGuaritaDevice && !isAlphadigiDevice && !isHikvisionLPRDevice && <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Usuário *
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="admin"
              required
            />
          </div>}

          {/* Password — hidden for Guarita, Alphadigi, and Hikvision LPR */}
          {!isGuaritaDevice && !isAlphadigiDevice && !isHikvisionLPRDevice && <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Senha *
            </label>
            <div className="relative">
              <input
                type={showDevicePassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="••••••••"
                required
              />
              {formData.password && (
                <button
                  type="button"
                  onClick={() => setShowDevicePassword(!showDevicePassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showDevicePassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            </div>
          </div>}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={isSaving}
            >
              {isSaving ? 'Criando...' : 'Criar Dispositivo'}
            </button>
          </div>
        </form>

        {/* Device Probe Modal */}
        <DeviceProbe
          show={showProbeModal}
          onClose={() => setShowProbeModal(false)}
          onDeviceDetected={handleDeviceDetected}
          initialIpAddress={formData.ip_address}
        />
      </div>
    </div>
  );
};

// Device Details Modal Component
export const DeviceDetailsModal: React.FC<{
  device: IoTDevice;
  zone: Zone | null;
  onClose: () => void;
  onEdit?: () => void;
  onRefresh: () => void;
  onDeviceUpdated?: (device: IoTDevice) => void;
  liveHealth?: DeviceHealth;
  onSyncProgress?: (data: SyncProgressState | null) => void;
}> = ({ device, zone, onClose, onEdit, onRefresh, onDeviceUpdated, liveHealth, onSyncProgress }) => {
  // Use live health check status when available; fall back to server-reported status
  const effectiveStatus = liveHealth?.checkedAt ? liveHealth.status : device.status;
  const showConfirm = useConfirm();
  const { pauseRef: syncPauseRef, stopRef: syncStopRef } = useSyncProgress();
  const { keycloak, token } = useKeycloak();
  const { selectedCustomer } = useCustomer();
  const { openPersonDetail } = usePersonDetail();
  const [activeTab, setActiveTab] = useState<'info' | 'people' | 'events' | 'sync'>('info');
  const [authorizedPeople, setAuthorizedPeople] = useState<any[]>([]);
  const [authorizedVehicles, setAuthorizedVehicles] = useState<any[]>([]);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehiclePage, setVehiclePage] = useState(1);
  const vehiclePageSize = 20;
  const [vehicleAdvancedOpen, setVehicleAdvancedOpen] = useState(false);
  const [vehicleFilterAmbiguousHex, setVehicleFilterAmbiguousHex] = useState(false);
  const [devicePeople, setDevicePeople] = useState<any[]>([]);
  const [accessEvents, setAccessEvents] = useState<any[]>([]);
  const [accessEventsTotal, setAccessEventsTotal] = useState(0);
  const [accessEventsPage, setAccessEventsPage] = useState(1);
  const [accessEventsLimit, setAccessEventsLimit] = useState(20);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [syncHistory, setSyncHistory] = useState<any[]>([]);
  const [syncHistoryTotal, setSyncHistoryTotal] = useState(0);
  const [syncHistoryPage, setSyncHistoryPage] = useState(1);
  const syncHistoryPageSize = 15;
  const [isLoadingPeople, setIsLoadingPeople] = useState(false);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [isLoadingDevicePeople, setIsLoadingDevicePeople] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [isLoadingSync, setIsLoadingSync] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isForceResyncing, setIsForceResyncing] = useState(false);
  const [showAdvancedSync, setShowAdvancedSync] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);
  const [editedDevice, setEditedDevice] = useState<IoTDevice>(device);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [detectedModel, setDetectedModel] = useState<string | null>(null);
  // Keep editedDevice in sync when the parent pushes an updated device prop
  // (e.g. after a save triggers onDeviceUpdated → setSelectedDevice in parent).
  useEffect(() => {
    if (!isEditing) setEditedDevice(device);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]);

  const autoDetect = async () => {
    const ip = device.local_ip_address || device.ip_address;
    if (!ip || !canTcpProbe()) return;
    const httpApi = (window as any).electronAPI?.deviceHttpRequest;
    if (!httpApi) { toast.error('deviceHttpRequest não disponível — reinicie o app'); return; }
    setAutoDetecting(true);
    try {
      const rawIp = ip.replace(/^https?:\/\//, '');
      const r = await httpApi({
        url: `http://${rawIp}/ISAPI/System/deviceInfo`,
        method: 'GET',
        digestAuth: { username: device.username || 'admin', password: device.password || '' },
        timeout: 5000,
      });
      console.log('[AutoDetect] response:', JSON.stringify({ success: r.success, error: r.error, hasData: !!r.data, dataType: typeof r.data, preview: typeof r.data === 'string' ? r.data.slice(0, 300) : r.data }));
      if (!r.success) { toast.error(friendlyDeviceError(r.error)); return; }
      if (!r.data) { toast.error('Dispositivo não retornou dados'); return; }

      const xml = new DOMParser().parseFromString(typeof r.data === 'string' ? r.data : String(r.data), 'text/xml');
      const get = (tag: string) => xml.getElementsByTagName(tag)[0]?.textContent?.trim() ?? '';
      const model = get('model');
      console.log('[AutoDetect] parsed:', { model, serial: get('serialNumber'), firmware: get('firmwareVersion') });
      if (!model) { toast.error('XML não contém modelo — dispositivo não suportado'); return; }

      const updated = {
        ...editedDevice,
        model,
        serial_number: get('serialNumber') || editedDevice.serial_number,
        firmware_version: get('firmwareVersion') || editedDevice.firmware_version,
        manufacturer: editedDevice.manufacturer || 'hikvision',
      };
      setEditedDevice(updated);

      try {
        const putRes = await fetchWithAuth(`/api/v1/customers/${selectedCustomer!.id}/iot-devices/${device.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updated),
        });
        // Without this the catch below only ran for network errors, so a rejected PUT left the
        // detected model showing as saved when it was not persisted.
        await ensureOk(putRes, 'Falha ao salvar o modelo detectado');
        onDeviceUpdated?.(updated);
      } catch (saveErr) {
        console.warn('[AutoDetect] PUT failed (display still updated):', saveErr);
      }
      setDetectedModel(model);
      setTimeout(() => setDetectedModel(null), 4000);
    } catch (err) {
      console.error('[AutoDetect] error:', err);
      toast.error(`Erro ao detectar: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setAutoDetecting(false);
    }
  };

  useEffect(() => {
    if (!canTcpProbe()) return;
    if (device.model) return;
    autoDetect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all'); // 'all', 'authentication_success', 'door_opened', 'remote_door_trigger', 'unauthorized'

  // People list management states
  const [peopleSearchTerm, setPeopleSearchTerm] = useState<string>('');
  const [peopleFilterType, setPeopleFilterType] = useState<'all' | 'authorized' | 'unauthorized' | 'duplicates'>('all');
  const [peoplePage, setPeoplePage] = useState<number>(1);
  const [peopleGoToInput, setPeopleGoToInput] = useState<string>('');
  const [devicePeopleTotalCount, setDevicePeopleTotalCount] = useState<number>(0);
  const [devicePeopleAuthorizedCount, setDevicePeopleAuthorizedCount] = useState<number>(0);
  const [devicePeopleUnauthorizedCount, setDevicePeopleUnauthorizedCount] = useState<number>(0);
  const [devicePeopleWithPhotoCount, setDevicePeopleWithPhotoCount] = useState<number>(0);
  const [devicePeopleWithoutPhotoCount, setDevicePeopleWithoutPhotoCount] = useState<number>(0);

  // Authorized people list management states
  const [authorizedPeopleSearchTerm, setAuthorizedPeopleSearchTerm] = useState<string>('');
  const [authorizedPeoplePage, setAuthorizedPeoplePage] = useState<number>(1);
  const [authorizedPeopleGoToInput, setAuthorizedPeopleGoToInput] = useState<string>('');
  const [authorizedPeoplePhotoFilter, setAuthorizedPeoplePhotoFilter] = useState<'all' | 'with_photo' | 'without_photo'>('all');
  const [authorizedPeopleDeptFilter, setAuthorizedPeopleDeptFilter] = useState<string[]>([]);
  const [deptFilterOpen, setDeptFilterOpen] = useState(false);
  const [deptFilterSearch, setDeptFilterSearch] = useState('');
  const [zoneDepartments, setZoneDepartments] = useState<any[]>([]);
  const [zoneProfiles, setZoneProfiles] = useState<any[]>([]);
  const [zoneProfileAssignments, setZoneProfileAssignments] = useState<Record<string, any>>({});
  const [zoneProfilePeopleIds, setZoneProfilePeopleIds] = useState<Record<string, Set<string>>>({});
  const [denyProfilePeople, setDenyProfilePeople] = useState<Record<string, any[]>>({});
  const [expandedDenyProfileId, setExpandedDenyProfileId] = useState<string | null>(null);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [activeProfileFilter, setActiveProfileFilter] = useState<string[]>([]);

  // Device people photo filter state
  const [devicePeoplePhotoFilter, setDevicePeoplePhotoFilter] = useState<'all' | 'with_photo' | 'without_photo'>('all');
  const [devicePeopleCloudUnavailable, setDevicePeopleCloudUnavailable] = useState(false);
  const [deviceModels, setDeviceModels] = useState<any[]>([]);
  const [vendors, setVendors] = useState<string[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  // Link person modal states
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [personToLink, setPersonToLink] = useState<any>(null);
  // personToLink is a snapshot taken when "Vincular" was clicked — if that happened
  // before authorization/face-enrollment finished enriching devicePeople, the snapshot
  // is stuck without a photo forever. Re-sync it against the live list whenever it updates.
  useEffect(() => {
    if (!personToLink?.employeeNo) return;
    const fresh = devicePeople.find((p: any) => p.employeeNo === personToLink.employeeNo);
    if (fresh && fresh !== personToLink) {
      setPersonToLink(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devicePeople]);
  const [selectedPeopleToLink, setSelectedPeopleToLink] = useState<Set<string>>(new Set());
  // Map<employeeNo, devicePerson> — persists across page changes so user can select from multiple pages
  const [selectedPeopleToRemove, setSelectedPeopleToRemove] = useState<Map<string, any>>(new Map());
  const [linkSearchTerm, setLinkSearchTerm] = useState('');
  const [selectedSystemPerson, setSelectedSystemPerson] = useState<any>(null);
  const [showLinkConfirmation, setShowLinkConfirmation] = useState(false);
  const [keepOldCadastro, setKeepOldCadastro] = useState(false);
  const [useSystemPersonName, setUseSystemPersonName] = useState(false);
  const [uploadSystemPhoto, setUploadSystemPhoto] = useState(false);
  const [pullDeviceBiometry, setPullDeviceBiometry] = useState(false);
  const [fingerprintImporting, setFingerprintImporting] = useState<Record<string, boolean>>({});

  // ControlID monitor mode state.
  //
  // monitorStatus used to be written ONLY as a side effect of pressing "Configurar", so on open
  // it was always null and the row always read "Não configurado" — regardless of the device's
  // actual state. monitorCheck holds the result of an actual read, and stays null until one has
  // happened, so "not checked" is never rendered as "not configured".
  const [monitorStatus, setMonitorStatus] = useState<{ hostname: string; port: string; path: string; request_timeout: string } | null>(null);
  const [monitorCheck, setMonitorCheck] = useState<ControlIdMonitorComparison | null>(null);
  const [isReadingMonitor, setIsReadingMonitor] = useState(false);
  const [isConfiguringMonitor, setIsConfiguringMonitor] = useState(false);
  const [isConfiguringWebhook, setIsConfiguringWebhook] = useState(false);
  const [isReadingHikvisionConfig, setIsReadingHikvisionConfig] = useState(false);
  const [hikConfigStatus, setHikConfigStatus] = useState<{
    ok: boolean;
    protocol: string; protocolOk: boolean;
    host: string; hostOk: boolean;
    port: string; portOk: boolean;
  } | null>(null);
  const [isTestingHikvisionPush, setIsTestingHikvisionPush] = useState(false);
  const [webhookDebug, setWebhookDebug] = useState<{
    active: boolean;
    port: number;
    lanIP: string;
    events: Array<{ timestamp: string; method: string; url: string; body: string; headers: any }>;
  }>({ active: false, port: 8099, lanIP: '', events: [] });

  // Vehicle removal confirmation modal state (inside DeviceDetailsModal)
  const [showVehicleRemoveModal, setShowVehicleRemoveModal] = useState(false);
  const [vehicleToRemove, setVehicleToRemove] = useState<{
    vehicle: any;
    device: IoTDevice;
    cardNumber: string;
  } | null>(null);
  const [isRemovingVehicle, setIsRemovingVehicle] = useState(false);
  const [allSystemPeople, setAllSystemPeople] = useState<any[]>([]);
  const [isLoadingSystemPeople, setIsLoadingSystemPeople] = useState(false);
  const [totalSystemPeople, setTotalSystemPeople] = useState(0);
  const [linkModalPage, setLinkModalPage] = useState(1);
  const [linkModalPageSize] = useState(20); // Show 20 people per page in modal
  const [showCreateNewUserModal, setShowCreateNewUserModal] = useState(false);
  const [newUserFormData, setNewUserFormData] = useState({
    first_name: '',
    last_name: '',
    cpf: '',
    email: '',
    phone: '',
    birth_date: '',         // Required for underage validation (unless is_minor checked)
    is_minor: false,        // Checkbox to mark as minor without birth date
    role_type: 'visitante', // Default to visitor
    department_id: '',      // For empresa - funcionario
    unit_id: '',            // For condominio - morador
    position: '',           // For empresa - funcionario (cargo)
    employee_no: ''         // For empresa - funcionario (matricula)
  });
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [isLinkingPerson, setIsLinkingPerson] = useState(false);
  const [availableDepartments, setAvailableDepartments] = useState<any[]>([]);
  const [availableUnits, setAvailableUnits] = useState<any[]>([]);

  // Guarita devices don't have IP, credentials, or serial/firmware metadata
  const isGuaritaModalDevice = device.device_type === 'guarita' ||
    (device.manufacturer?.toLowerCase() === 'nice' && device.model?.toLowerCase().includes('guarita'));
  const isAlphadigiModalDevice = device.device_type === 'alphadigi_lpr' ||
    device.manufacturer?.toLowerCase() === 'alphadigi';

  // Fetch guarita integrations for display (name lookup)
  const [guaritaIntegrationsModal, setGuaritaIntegrationsModal] = useState<any[]>([]);
  useEffect(() => {
    if (!isGuaritaModalDevice || !selectedCustomer?.id) return;
    fetchWithAuth(`/api/v1/guarita-integrations?customer_id=${selectedCustomer.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setGuaritaIntegrationsModal(data.data || data || []); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuaritaModalDevice, selectedCustomer?.id]);

  // Guarita: person-tags registered in the system (shown in the people tab)
  const [guaritaTags, setGuaritaTags] = useState<any[]>([]);
  const [isLoadingGuaritaTags, setIsLoadingGuaritaTags] = useState(false);
  const [guaritaTagSearch, setGuaritaTagSearch] = useState('');
  const [hideWrongReceptorTags, setHideWrongReceptorTags] = useState(true);
  const [hideOtherReceptorRegs, setHideOtherReceptorRegs] = useState(true);
  const [guaritaDeviceRegSearch, setGuaritaDeviceRegSearch] = useState('');
  const [guaritaDeviceRegSort, setGuaritaDeviceRegSort] = useState<
    'default' | 'owner-asc' | 'owner-desc' | 'label-asc' | 'label-desc' | 'serial-asc' | 'serial-desc'
  >('default');
  const [showDeviceRegsExportModal, setShowDeviceRegsExportModal] = useState(false);
  const [deviceRegsExportFormat, setDeviceRegsExportFormat] = useState<'csv' | 'pdf'>('csv');

  // Guarita: vehicles with RFID tags (shown when vehicle_whitelist_enabled)
  const [guaritaVehicles, setGuaritaVehicles] = useState<any[]>([]);
  const [isLoadingGuaritaVehicles, setIsLoadingGuaritaVehicles] = useState(false);

  // Guarita: tags physically registered on the device
  const [guaritaDeviceRegs, setGuaritaDeviceRegs] = useState<GuaritaDeviceRecord[]>([]);
  const [isLoadingGuaritaDeviceRegs, setIsLoadingGuaritaDeviceRegs] = useState(false);
  const [isSyncingGuarita, setIsSyncingGuarita] = useState(false);
  const [isSyncingVehiclesToGuarita, setIsSyncingVehiclesToGuarita] = useState(false);

  // Vincular tag modal
  const [vinculateReg, setVinculateReg] = useState<GuaritaDeviceRecord | null>(null);
  const [vinculateSearch, setVinculateSearch] = useState('');
  const [vinculatePeople, setVinculatePeople] = useState<any[]>([]);
  const [vinculatePersonId, setVinculatePersonId] = useState('');
  const [vinculateLabel, setVinculateLabel] = useState('');
  const [isSearchingPeople, setIsSearchingPeople] = useState(false);
  const [isVinculating, setIsVinculating] = useState(false);

  // Fetch departments when modal opens
  useEffect(() => {
    const fetchDepartments = async () => {
      if (showCreateNewUserModal && selectedCustomer?.type === 'empresa') {
        try {
          const response = await fetchWithAuth(
            `/api/v1/customers/${selectedCustomer.id}/departments`
          );

          if (response.ok) {
            const data = await response.json();
            setAvailableDepartments(data.data || []);
          } else {
            logger.error('zones-devices.page', 'Failed to fetch departments', new Error(`Status: ${response.status}`));
            setAvailableDepartments([]);
          }
        } catch (error) {
          logger.error('zones-devices.page', 'Failed to fetch departments', error instanceof Error ? error : new Error(String(error)));
          setAvailableDepartments([]);
        }
      } else if (!showCreateNewUserModal) {
        // Clear departments when modal closes
        setAvailableDepartments([]);
      }
    };

    fetchDepartments();
  }, [showCreateNewUserModal, selectedCustomer]);

  // Fetch units when modal opens for condominio
  useEffect(() => {
    const fetchUnits = async () => {
      if (showCreateNewUserModal && selectedCustomer?.type === 'condominio') {
        try {
          const response = await fetchWithAuth(
            `/api/v1/customers/${selectedCustomer.id}/units`
          );

          if (response.ok) {
            const data = await response.json();
            setAvailableUnits(data.data || data || []);
          } else {
            logger.error('zones-devices.page', 'Failed to fetch units', new Error(`Status: ${response.status}`));
            setAvailableUnits([]);
          }
        } catch (error) {
          logger.error('zones-devices.page', 'Failed to fetch units', error instanceof Error ? error : new Error(String(error)));
          setAvailableUnits([]);
        }
      } else if (!showCreateNewUserModal) {
        // Clear units when modal closes
        setAvailableUnits([]);
      }
    };

    fetchUnits();
  }, [showCreateNewUserModal, selectedCustomer]);

  // Fetch ControlID monitor status on modal open
  useEffect(() => {
    const isControlID = device.manufacturer?.toLowerCase() === 'controlid';
    if (!isControlID) return;
    fetchWithAuth(`/api/v1/iot-connect/facial-recognition/devices/${device.id}/monitor-status`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.monitor) setMonitorStatus(data.monitor); })
      .catch(() => {});
  }, [device.id, device.manufacturer]);

  // Listen for incoming events from local webhook debug server
  useEffect(() => {
    if (!(window as any).electronAPI?.onLocalWebhookRequest) return;
    const off = (window as any).electronAPI.onLocalWebhookRequest((data: any) => {
      setWebhookDebug(prev => ({
        ...prev,
        events: [data, ...prev.events].slice(0, 20),
      }));
    });
    return off;
  }, []);

  // Fetch authorized people for this device
  const fetchAuthorizedPeople = async () => {
    const zoneId = zone?.id || device.zone_id;
    if (!zoneId) return;

    setIsLoadingPeople(true);
    try {
      const response = await fetchWithAuth(
        `/api/v1/zones/${zoneId}/authorized-people`
      );

      if (response.ok) {
        const data = await response.json();
        // Normalize: API returns identity_photo (object), but getPrimaryPhotoUrl expects identity_photo_url (string).
        // Only use the photo if it's a face/primary photo — document scans should not appear in the list.
        const normalized = (data.data || []).map((p: any) => {
          const photo = p.identity_photo;
          const isUsable = photo && photo.is_active !== false && (
            photo.photo_type === 'facial_recognition' || photo.is_primary === true
          );
          return {
            ...p,
            identity_photo_url: p.identity_photo_url || (isUsable ? photo.photo_url : null),
          };
        });
        setAuthorizedPeople(normalized);
      }
    } catch (error) {
      console.error('Error fetching authorized people:', error);
    } finally {
      setIsLoadingPeople(false);
    }
  };

  // Fetch departments for zone authorized-people dept filter and profile chip fallback.
  // Use zone.customer_id (authoritative) and fall back to device.customer_id.
  useEffect(() => {
    const customerId = zone?.customer_id || device?.customer_id;
    if (!customerId) return;
    fetchWithAuth(`/api/v1/customers/${customerId}/departments?page_size=500`)
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setZoneDepartments(d.data || []))
      .catch(() => setZoneDepartments([]));
  }, [zone?.customer_id, device?.customer_id]);

  // Fetch access profiles applied to this zone
  useEffect(() => {
    if (activeTab !== 'people' || !zone || !device?.customer_id) {
      setZoneProfiles([]);
      setZoneProfileAssignments({});
      setExpandedDenyProfileId(null);
      return;
    }
    const customerId = device.customer_id;
    const load = async () => {
      setIsLoadingProfiles(true);
      try {
        const profilesRes = await fetchWithAuth(`/api/v1/access-profiles/customer/${customerId}`);
        if (!profilesRes.ok) return;
        const d = await profilesRes.json();
        const allProfiles: any[] = d.profiles || [];

        // Collect all unique zone group IDs referenced by zone_group profiles
        // Handle both zone_group_ids (array of strings) and zone_groups (array of objects)
        const referencedGroupIds = new Set<string>();
        const zoneGroupProfiles = allProfiles.filter((p: any) => p.is_active && p.applies_to === 'zone_group');
        zoneGroupProfiles.forEach((p: any) => {
          (p.zone_group_ids || []).forEach((id: string) => referencedGroupIds.add(id));
          (p.zone_groups || []).forEach((g: any) => g?.id && referencedGroupIds.add(g.id));
        });


        // Fetch those zone groups individually to check if they contain this zone
        const groupChecks = await Promise.all(
          Array.from(referencedGroupIds).map(async (groupId) => {
            try {
              const r = await fetchWithAuth(`/api/v1/zone-groups/${groupId}`);
              if (!r.ok) return { id: groupId, hasZone: false };
              const data = await r.json();
              // Handle various response shapes
              const grp = data.zone_group || data;
              const zones: any[] = grp.zones || grp.zone_ids || [];
              const hasZone = zones.some((z: any) => (z.id || z) === zone.id);
              return { id: groupId, hasZone };
            } catch {
              return { id: groupId, hasZone: false };
            }
          })
        );
        const validGroupIds = new Set<string>(groupChecks.filter(c => c.hasZone).map(c => c.id));

        const relevant = allProfiles.filter((p: any) => {
          if (!p.is_active) return false;
          if (p.applies_to === 'all_zones') return true;
          if (p.applies_to === 'zone' && p.zone_id === zone.id) return true;
          if (p.applies_to === 'zone_group') {
            const ids = [
              ...(p.zone_group_ids || []),
              ...(p.zone_groups || []).map((g: any) => g?.id).filter(Boolean),
            ];
            return ids.some((gId: string) => validGroupIds.has(gId));
          }
          return false;
        });
        setZoneProfiles(relevant);
        const assignmentMap: Record<string, any> = {};
        const peopleIdsMap: Record<string, Set<string>> = {};
        const denyPeopleMap: Record<string, any[]> = {};
        await Promise.all(relevant.map(async (p: any) => {
          try {
            const [ar, pr] = await Promise.all([
              fetchWithAuth(`/api/v1/access-profiles/${p.id}/assignments?customer_id=${customerId}`),
              fetchWithAuth(`/api/v1/access-profiles/${p.id}/people?customer_id=${customerId}&page_size=500`),
            ]);
            if (ar.ok) assignmentMap[p.id] = await ar.json();
            if (pr.ok) {
              const pdata = await pr.json();
              const people = pdata.data || pdata.people || [];
              const ids: string[] = people.map((person: any) => person.id).filter(Boolean);
              peopleIdsMap[p.id] = new Set(ids);
              if (p.access_type === 'deny') denyPeopleMap[p.id] = people;
            }
          } catch {}
        }));
        setZoneProfileAssignments(assignmentMap);
        setZoneProfilePeopleIds(peopleIdsMap);
        setDenyProfilePeople(denyPeopleMap);
      } catch (e) {
        console.error('Error fetching zone profiles', e);
      } finally {
        setIsLoadingProfiles(false);
      }
    };
    load();
  }, [activeTab, zone?.id, device?.customer_id]);

  // Fetch the vehicles relevant to this device (no zone filtering).
  //
  // An RFID reader is keyed by TAG, an LPR camera by PLATE — so the two need different
  // queries and different "is it on the device" reads. Listing tagged vehicles for an LPR
  // camera showed 428 SEMPLACA-* rows with no plates, none of which that camera can ever act on.
  const isLprDeviceModal = device.device_type === 'hikvision_lpr' || device.device_type === 'lpr';

  const fetchAuthorizedVehicles = async () => {
    const customerId = selectedCustomer?.id || device.customer_id;
    if (!customerId) return;
    setIsLoadingVehicles(true);
    try {
      const response = await fetchWithAuth(
        isLprDeviceModal
          ? `/api/v1/customers/${customerId}/vehicles?page=1&page_size=5000`
          : `/api/v1/customers/${customerId}/vehicles?page=1&page_size=5000&has_rfid=true`
      );

      if (response.ok) {
        const data = await response.json();
        let vehicles = (data.vehicles || data.data || []).map((v: any) => ({
          vehicle_id: v.id,
          rfid_tag: v.rfid_tag,
          plate: v.plate,
          person_name: v.owner_name || (v.person ? `${v.person.first_name || ''} ${v.person.last_name || ''}`.trim() : undefined),
          brand: v.brand,
          model: v.model,
          color: v.color,
          year: v.year,
          unit_number: v.unit?.number,
          unit_block: v.unit?.block?.name || undefined,
          is_unauthorized: false,
          is_on_device: false,
        })).filter((v: any) => (isLprDeviceModal ? !!v.plate : !!v.rfid_tag));

        // Load what's actually on the device to mark is_on_device
        try {
          if (isLprDeviceModal) {
            // The camera's own allow-list, matched by plate.
            const cameraId = device.config?.hikvision_lpr_camera_id;
            if (cameraId) {
              const { listPlatesOnCamera } = await import('../services/lprPlateSync');
              const { normalizePlate } = await import('../services/hikvisionLprPlates');
              const onCamera = new Set(await listPlatesOnCamera(customerId, cameraId));
              vehicles = vehicles.map((v: any) => ({
                ...v,
                is_on_device: onCamera.has(normalizePlate(v.plate || '') || ''),
              }));
            }
            setAuthorizedVehicles(vehicles);
            setVehiclePage(1);
            setVehicleSearch('');
            setVehicleFilterAmbiguousHex(false);
            setVehicleAdvancedOpen(false);
            return;
          }

          const hasElectron = !!(window as any).electronAPI?.deviceHttpRequest;
          const hasIp = device.local_ip_address || device.ip_address;
          if (hasElectron && hasIp) {
            // Direct path: load users+cards from device
            const deviceEntries = await deviceCommunicationService.loadControlIDDeviceCards({
              id: device.id,
              local_ip_address: device.local_ip_address,
              ip_address: device.ip_address,
              username: device.username,
              password: device.password,
              vendor: 'controlid',
            } as any);
            const regSet = new Set(deviceEntries.map(e => e.registration).filter(Boolean));
            vehicles = vehicles.map((v: any) => ({
              ...v,
              is_on_device: regSet.has(v.vehicle_id) || regSet.has(v.plate),
            }));
          } else {
            // Cloud path: /cards endpoint cross-references with DB and returns vehicle_id
            const cardsResp = await fetchWithAuth(`/api/v1/iot-connect/rfid/devices/${device.id}/cards`);
            if (cardsResp.ok) {
              const cardsData = await cardsResp.json();
              const onDeviceIds = new Set(
                (cardsData.cards || [])
                  .filter((c: any) => !c.is_unauthorized)
                  .map((c: any) => c.vehicle_id)
                  .filter(Boolean)
              );
              vehicles = vehicles.map((v: any) => ({
                ...v,
                is_on_device: onDeviceIds.has(v.vehicle_id),
              }));
            }
          }
        } catch { /* non-fatal: device might be offline */ }

        setAuthorizedVehicles(vehicles);
        setVehiclePage(1);
        setVehicleSearch('');
        setVehicleFilterAmbiguousHex(false);
        setVehicleAdvancedOpen(false);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setIsLoadingVehicles(false);
    }
  };

  // Fetch person-tags for the customer (shown in Guarita device's people tab, same source as Cadastros)
  const fetchGuaritaTags = async () => {
    if (!selectedCustomer?.id) return;
    setIsLoadingGuaritaTags(true);
    try {
      const res = await fetchWithAuth(`/api/v1/customers/${selectedCustomer.id}/person-tags?page_size=200`);
      if (res.ok) {
        const data = await res.json();
        setGuaritaTags(data.data || data.tags || data || []);
      }
    } catch {
      // silent
    } finally {
      setIsLoadingGuaritaTags(false);
    }
  };

  // Fetch vehicles with RFID tags for the Guarita customer (backend caps page_size at 100, so paginate)
  const fetchGuaritaVehicles = async () => {
    if (!selectedCustomer?.id) return;
    setIsLoadingGuaritaVehicles(true);
    try {
      const all: any[] = [];
      let page = 1;
      const pageSize = 100;
      while (true) {
        const res = await fetchWithAuth(`/api/v1/customers/${selectedCustomer.id}/vehicles?has_rfid=true&page_size=${pageSize}&page=${page}`);
        if (!res.ok) break;
        const data = await res.json();
        const items: any[] = data.data || data.vehicles || [];
        all.push(...items);
        if (items.length < pageSize) break; // last page
        page++;
      }
      setGuaritaVehicles(all.filter((v: any) => v.is_active !== false));
    } catch {
      // silent
    } finally {
      setIsLoadingGuaritaVehicles(false);
    }
  };

  // Fetch tags physically registered on the Guarita device.
  // force=true blocks until a live read of the device completes (used for an explicit
  // refresh and right after register/delete actions) instead of the fire-and-forget cache
  // used for regular page loads, which can be stale by up to one background-refresh cycle.
  const fetchGuaritaDeviceRegs = async (retryCount = 0, force = false) => {
    const integrationId = device.config?.guarita_integration_id;
    if (!integrationId) return;
    setIsLoadingGuaritaDeviceRegs(true);
    let willRetry = false;
    const url = `${getApiBaseUrl()}/api/v1/iot-connect/guarita/${integrationId}/device-registrations${force ? '?force=true' : ''}`;
    // eslint-disable-next-line no-console
    console.log('[Guarita] fetchGuaritaDeviceRegs →', { url, force, retryCount });
    try {
      // Call the API directly (not via listDeviceRegistrations) so we can inspect the
      // `loading` flag. The backend returns {data:[],loading:true} on first load after a
      // service restart while the background cache fetch is still in progress.
      const res = await fetchWithAuth(url);
      const json = await res.json();
      const records: GuaritaDeviceRecord[] = Array.isArray(json.data) ? json.data : (Array.isArray(json) ? json : []);
      // eslint-disable-next-line no-console
      console.log('[Guarita] fetchGuaritaDeviceRegs ← response', {
        status: res.status,
        cached: json.cached,
        loading: json.loading,
        error: json.error,
        count: records.length,
        serials: records.map(r => r.serial_decimal),
      });
      if (json.loading && records.length === 0 && retryCount < 5) {
        // Cache not ready yet — keep spinner and auto-retry after 20 s
        willRetry = true;
        setTimeout(() => fetchGuaritaDeviceRegs(retryCount + 1), 20000);
        return;
      }
      setGuaritaDeviceRegs(records);
      if (json.error) {
        // Forced refresh fell back to a (possibly stale) cache because the live read failed
        toast.error(`Não foi possível confirmar o estado atual do dispositivo: ${json.error}`);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Guarita] fetchGuaritaDeviceRegs threw', e);
    } finally {
      if (!willRetry) setIsLoadingGuaritaDeviceRegs(false);
    }
  };

  // guaritaCardDecimal now lives in ../utils/guaritaSerial so it can be unit-tested in
  // isolation — this is the exact function that used to collapse two different people's
  // tags onto the same value (see that file's doc comment for the full incident writeup).

  // Add this device's receptor bit to a tag on the Guarita device.
  // If tag is already on the device, OR-merges the new receptor bit into the existing bitmask.
  // If tag is not on the device, registers it with just this receptor's bit.
  // deviceType must reach the registration call — omitting it silently defaults to CT (card),
  // which is hard-capped at 24 bits. A "controle_remoto" tag needs device_type=1 (RF) for the
  // Nice MG3000's real 28-bit range, or a genuinely valid 25-28 bit remote-control value gets
  // wrongly rejected as "exceeds the Nice MG3000's 24-bit serial capacity for device type Cartão".
  const guaritaAddReceptorBit = async (dec: string, label: string, existingReg: GuaritaDeviceRecord | undefined, deviceType?: number) => {
    const integrationId = device.config?.guarita_integration_id;
    if (!integrationId) return;
    const receptor = device.config?.guarita_receptor ?? 1;
    const receptorBit = 1 << (receptor - 1);
    if (existingReg) {
      const mergedMask = existingReg.receivers | receptorBit;
      if (mergedMask === existingReg.receivers) return; // receptor already set, nothing to do
      await deleteFromDevice(integrationId, dec, existingReg.device_type ?? 3);
      // The delete has already committed — retry the re-register so a transient
      // failure (e.g. device busy) doesn't leave the tag stranded/deleted.
      let lastErr: any = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          await registerOnDeviceWithMask(integrationId, dec, label, mergedMask, deviceType);
          return;
        } catch (regErr) {
          lastErr = regErr;
          if (attempt < 3) await new Promise(r => setTimeout(r, 1500 * attempt));
        }
      }
      throw lastErr;
    } else {
      await registerOnDevice(integrationId, dec, label, receptor, deviceType);
    }
  };

  // Maps a person_tag's tag_type to the Guarita device_type byte — omitted/undefined lets
  // the backend default to CT (card, 24-bit cap); 1 = RF/Controle Remoto (28-bit range),
  // 2 = TA/Tag Ativa, 6 = TP/Tag Passiva. Leaving "passiva"/"ativa" unmapped previously
  // fell through to the CT default, registering those tags on the device as cards.
  const guaritaDeviceTypeForTag = (tag: any): number | undefined => {
    switch (tag?.tag_type) {
      case 'controle_remoto':
        return 1;
      case 'ativa':
        return 2;
      case 'passiva':
        return 6;
      default:
        return undefined;
    }
  };

  // Sync active system tags → Guarita device
  const syncGuaritaTagsToDevice = async () => {
    const integrationId = device.config?.guarita_integration_id;
    if (!integrationId) return;
    const activeTags = guaritaTags.filter((t: any) => t.is_active !== false && t.tag_value);
    if (activeTags.length === 0) {
      toast.error('Nenhuma tag ativa para sincronizar');
      return;
    }
    setIsSyncingGuarita(true);
    let ok = 0;
    let fail = 0;
    for (const tag of activeTags) {
      try {
        const dec = guaritaCardDecimal(tag.tag_value);
        if (!dec || dec === '0') { fail++; continue; }
        const label = tag.label || `${tag.person?.first_name || ''} ${tag.person?.last_name || ''}`.trim();
        const existingReg = guaritaDeviceRegs.find(r => r.serial_decimal === dec);
        await guaritaAddReceptorBit(dec, label, existingReg, guaritaDeviceTypeForTag(tag));
        ok++;
      } catch {
        fail++;
      }
    }
    setIsSyncingGuarita(false);
    if (fail === 0) {
      toast.success(`${ok} tag(s) sincronizada(s) com sucesso`);
    } else {
      toast.error(`${ok} sincronizada(s), ${fail} falha(s)`);
    }
    fetchGuaritaDeviceRegs(); // cached refresh — avoid hogging the device connection after every action
  };

  // Sync a single tag to the Guarita device
  const syncSingleGuaritaTag = async (tag: any) => {
    const integrationId = device.config?.guarita_integration_id;
    if (!integrationId) return;
    const dec = guaritaCardDecimal(tag.tag_value);
    if (!dec || dec === '0') {
      toast.error('Tag sem valor configurado');
      return;
    }
    const label = tag.label || `${tag.person?.first_name || ''} ${tag.person?.last_name || ''}`.trim();
    try {
      const existingReg = guaritaDeviceRegs.find(r => r.serial_decimal === dec);
      await guaritaAddReceptorBit(dec, label, existingReg, guaritaDeviceTypeForTag(tag));
      toast.success('Tag enviada ao dispositivo');
      fetchGuaritaDeviceRegs(); // cached refresh — avoid hogging the device connection after every action
    } catch (e: any) {
      toast.error(`Erro: ${e.message}`);
    }
  };

  // Search people for vincular modal
  const searchVinculatePeople = async (q: string) => {
    if (!selectedCustomer?.id || q.trim().length < 2) { setVinculatePeople([]); return; }
    setIsSearchingPeople(true);
    try {
      const res = await fetchWithAuth(`/api/v1/customers/${selectedCustomer.id}/people?search=${encodeURIComponent(q)}&page_size=20`);
      if (res.ok) {
        const data = await res.json();
        setVinculatePeople(data.data || []);
      }
    } catch { /* silent */ } finally {
      setIsSearchingPeople(false);
    }
  };

  // Submit vincular: create a PersonTag for the device serial
  const submitVinculate = async () => {
    if (!selectedCustomer?.id || !vinculatePersonId || !vinculateReg) return;
    setIsVinculating(true);
    try {
      const res = await fetchWithAuth(`/api/v1/customers/${selectedCustomer.id}/person-tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_id: vinculatePersonId,
          tag_value: vinculateReg.serial_decimal,
          label: vinculateLabel || undefined,
        }),
      });
      const data = await okJson<any>(res, 'Erro ao vincular tag');
      toast.success('Tag vinculada com sucesso');
      setVinculateReg(null);
      setVinculateSearch('');
      setVinculatePeople([]);
      setVinculatePersonId('');
      setVinculateLabel('');
      fetchGuaritaTags();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setIsVinculating(false);
    }
  };

  // Remove only this device's receptor bit from a tag on the Guarita device.
  // If other receptors remain, the tag stays on the device with the reduced bitmask.
  // If the bitmask hits 0, the tag is deleted entirely.
  const removeGuaritaDeviceReg = async (cardDecimal: string, deviceType?: number) => {
    const integrationId = device.config?.guarita_integration_id;
    if (!integrationId) return;
    const receptor = device.config?.guarita_receptor ?? 1;
    const receptorBit = 1 << (receptor - 1);
    const existingReg = guaritaDeviceRegs.find(r => r.serial_decimal === cardDecimal);
    // eslint-disable-next-line no-console
    console.log('[Guarita] removeGuaritaDeviceReg → start', {
      cardDecimal,
      deviceTypeParam: deviceType,
      integrationId,
      receptor,
      receptorBit,
      currentListSize: guaritaDeviceRegs.length,
      currentListSerials: guaritaDeviceRegs.map(r => r.serial_decimal),
      existingReg,
    });
    try {
      if (existingReg) {
        // No-op guard: if this receptor's bit was never set on the tag in the first
        // place, there's nothing to change on the device — skip straight to success.
        // (This is exactly what happened with card 15844888/REC2: the mask already
        // excluded it, so the delete+re-register that followed was pure risk for
        // zero benefit — and it's what left the tag stranded when re-register failed.)
        if ((existingReg.receivers & receptorBit) === 0) {
          // eslint-disable-next-line no-console
          console.log('[Guarita] removeGuaritaDeviceReg → no-op, receptor bit was not set', {
            cardDecimal, mask: existingReg.receivers, receptorBit,
          });
          toast.success('Este receptor já não tinha acesso a esta tag');
          return;
        }

        const newMask = existingReg.receivers & ~receptorBit;
        const resolvedDeviceType = existingReg.device_type ?? deviceType ?? 3;
        // eslint-disable-next-line no-console
        console.log('[Guarita] removeGuaritaDeviceReg → existingReg found', {
          oldMask: existingReg.receivers,
          newMask,
          resolvedDeviceType,
          path: newMask > 0 ? 'delete+reregister' : 'delete-only',
        });
        if (newMask > 0) {
          // eslint-disable-next-line no-console
          console.log('[Guarita] deleteFromDevice (pre-reregister) →', { cardDecimal, resolvedDeviceType });
          await deleteFromDevice(integrationId, cardDecimal, resolvedDeviceType);
          // eslint-disable-next-line no-console
          console.log('[Guarita] deleteFromDevice succeeded, now registerOnDeviceWithMask →', { cardDecimal, newMask, label: existingReg.label });
          // The delete has already committed — leaving the tag stranded (deleted but
          // not restored) is worse than being slow, so retry the re-register a few
          // times before giving up.
          let lastErr: any = null;
          let reregistered = false;
          for (let attempt = 1; attempt <= 3 && !reregistered; attempt++) {
            try {
              await registerOnDeviceWithMask(integrationId, cardDecimal, existingReg.label || '', newMask);
              reregistered = true;
            } catch (regErr) {
              lastErr = regErr;
              // eslint-disable-next-line no-console
              console.warn(`[Guarita] registerOnDeviceWithMask attempt ${attempt} failed`, regErr);
              if (attempt < 3) await new Promise(r => setTimeout(r, 1500 * attempt));
            }
          }
          if (!reregistered) {
            // eslint-disable-next-line no-console
            console.error('[Guarita] re-register failed after retries — tag was deleted and NOT restored', { cardDecimal, lastErr });
            fetchGuaritaDeviceRegs();
            toast.error('Receptor removido, mas falha ao restaurar os demais receptores da tag — verifique o dispositivo e tente sincronizar novamente');
            return;
          }
          // eslint-disable-next-line no-console
          console.log('[Guarita] registerOnDeviceWithMask succeeded');
        } else {
          // eslint-disable-next-line no-console
          console.log('[Guarita] deleteFromDevice (mask hit 0) →', { cardDecimal, resolvedDeviceType });
          await deleteFromDevice(integrationId, cardDecimal, resolvedDeviceType);
          // eslint-disable-next-line no-console
          console.log('[Guarita] deleteFromDevice succeeded');
        }
      } else {
        // eslint-disable-next-line no-console
        console.warn('[Guarita] removeGuaritaDeviceReg → existingReg NOT FOUND in current list, deleting with raw params', { cardDecimal, deviceType });
        await deleteFromDevice(integrationId, cardDecimal, deviceType);
        // eslint-disable-next-line no-console
        console.log('[Guarita] deleteFromDevice succeeded (no existingReg path)');
      }
      fetchGuaritaDeviceRegs(); // cached refresh — avoid hogging the device connection after every action
      toast.success('Receptor removido da tag');
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('[Guarita] removeGuaritaDeviceReg FAILED', { cardDecimal, error: e?.message || e, raw: e });
      toast.error('Falha ao remover receptor da tag');
    }
  };

  // Delete the tag entirely from the Guarita device, regardless of receptors.
  const removeEntirelyFromDevice = async (cardDecimal: string, deviceType?: number) => {
    const integrationId = device.config?.guarita_integration_id;
    if (!integrationId) return;
    const existingReg = guaritaDeviceRegs.find(r => r.serial_decimal === cardDecimal);
    const resolvedDeviceType = existingReg?.device_type ?? deviceType ?? 3;
    // eslint-disable-next-line no-console
    console.log('[Guarita] removeEntirelyFromDevice → start', { cardDecimal, deviceTypeParam: deviceType, existingReg, resolvedDeviceType });
    try {
      await deleteFromDevice(integrationId, cardDecimal, resolvedDeviceType);
      // eslint-disable-next-line no-console
      console.log('[Guarita] removeEntirelyFromDevice succeeded', { cardDecimal });
      fetchGuaritaDeviceRegs(); // cached refresh — avoid hogging the device connection after every action
      toast.success('Tag removida do dispositivo');
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('[Guarita] removeEntirelyFromDevice FAILED', { cardDecimal, error: e?.message || e, raw: e });
      toast.error('Falha ao remover tag do dispositivo');
    }
  };

  // Fetch people registered on the physical device
  // Uses HYBRID mode: tries direct device query first, falls back to cloud
  const fetchDevicePeople = async () => {
    // Guarita devices don't have a people/face registry — skip the fetch
    if (device.device_type === 'guarita') {
      setIsLoadingDevicePeople(false);
      setDevicePeople([]);
      return;
    }

    setIsLoadingDevicePeople(true);
    setDevicePeopleCloudUnavailable(false);
    console.log(' [Device People] Starting fetch for device:', device.id);

    // Safety: clear loading state even if an IPC/HTTP call hangs indefinitely
    const devicePeopleSafetyTimer = setTimeout(() => {
      setIsLoadingDevicePeople(false);
      toast.error('Carregamento expirou (30s) — verifique conexão com o dispositivo');
    }, 30000);

    try {
      const isElectron = window.electronAPI !== undefined;
      const hasIP = device.local_ip_address || device.ip_address;
      const isControlID = device.manufacturer?.toLowerCase() === 'controlid';
      const isHikvision = device.manufacturer?.toLowerCase() === 'hikvision';

      let people: any[] = [];
      let directSucceeded = false;

      // Try HYBRID mode for ControlID or Hikvision in Electron
      if (isElectron && hasIP && (isControlID || isHikvision)) {
        console.log(' [Device People] Trying DIRECT mode');

        try {
          if (isControlID) {
            const deviceInfo = {
              id: device.id,
              ip_address: device.ip_address,
              local_ip_address: device.local_ip_address,
              username: device.username || '',
              password: device.password || '',
              vendor: 'controlid' as const,
              port: device.port?.toString(),
            };

            const result = await DeviceIntegration.instance.execute(
              deviceInfo,
              DeviceOperations.LOAD_PEOPLE,
              {},
              CommunicationMode.HYBRID
            );

            if (result.success) {
              const rawUsers = result.data?.users || [];
              const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
              people = rawUsers.map((user: any) => {
                const hasPhoto = (typeof user.image_timestamp === 'number' && user.image_timestamp > 0) ||
                                 (typeof user.image_timestamp === 'string' && user.image_timestamp !== '0' && user.image_timestamp !== '');
                const reg = user.registration?.toString() || '';
                const personId = uuidRegex.test(reg) ? reg : '';
                return {
                  employeeNo: reg || user.id?.toString() || '',
                  name: user.name?.toString() || '',
                  userType: 'normal',
                  person_id: personId,
                  is_unauthorized: !user.registration,
                  _device_id: typeof user.id === 'number' ? user.id : undefined,
                  has_photo: hasPhoto,
                };
              });
              console.log(` [Device People] ${people.filter((p: any) => p.has_photo).length}/${people.length} users have enrolled faces`);

              // Show the raw list immediately — authorization gets resolved next and
              // will refresh it, but the porteiro shouldn't stare at a blank spinner
              // for the whole roundtrip.
              setDevicePeople(people);
              setDevicePeopleTotalCount(people.length);

              // Resolve authorization: cross-reference against zone's authorized people list
              // (ControlID stores actual UUIDs, so compare person_id directly — no MD5 needed)
              try {
                let authList: any[] = authorizedPeople.length > 0
                  ? authorizedPeople
                  : await (zone
                      ? fetchWithAuth(`/api/v1/zones/${zone.id}/authorized-people`)
                          .then(r => r.ok ? r.json().then((d: any) => d.data || []) : [])
                      : Promise.resolve([]));
                const authIds = new Set(authList.map((p: any) => p.id?.toLowerCase()));
                people = people.map((p: any) => ({
                  ...p,
                  is_unauthorized: p.person_id ? !authIds.has(p.person_id.toLowerCase()) : true,
                }));
                console.log(` [Device People] ControlID: ${authIds.size} authorized IDs, ${people.filter((p: any) => !p.is_unauthorized).length} matched`);
              } catch (authErr) {
                console.warn(' [Device People] Could not resolve ControlID authorization:', authErr);
              }

              directSucceeded = true;
            } else {
              console.warn(' [Device People] DIRECT failed, using cloud');
            }
          } else {
            // Hikvision: paginate through UserInfo/Search directly via electronAPI
            // Avoids DeviceCommunicationService overhead (firmware detection, session auth)
            const { smartSelectDeviceIp } = await import('../utils/deviceProbe');
            const hikSelected = await smartSelectDeviceIp(
              { local_ip_address: device.local_ip_address, ip_address: device.ip_address, port: device.port },
              { timeoutLocal: 400, timeoutPublic: 1500 }
            );
            const hikRawIp = (hikSelected?.ip ?? device.ip_address ?? device.local_ip_address ?? '').replace(/^https?:\/\//, '');
            let hikIp = hikRawIp;
            let hikPort = device.port?.toString() || '80';
            if (hikRawIp.includes(':')) { [hikIp, hikPort] = hikRawIp.split(':'); }
            const hikProto = hikPort === '443' ? 'https' : 'http';
            const hikBaseUrl = `${hikProto}://${hikIp}:${hikPort}`;
            const hikDigestAuth = { username: device.username || '', password: device.password || '' };
            console.log(`[Device People] Hikvision using ${hikSelected?.source ?? 'fallback'} IP: ${hikBaseUrl}`);

            let position = 0;
            let totalMatches = Infinity;

            while (people.length < totalMatches) {
              const pageResult = await (window as any).electronAPI.deviceHttpRequest({
                url: `${hikBaseUrl}/ISAPI/AccessControl/UserInfo/Search?format=json`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: {
                  UserInfoSearchCond: {
                    searchID: '1',
                    maxResults: 30,
                    searchResultPosition: position,
                  },
                },
                timeout: 10000,
                digestAuth: hikDigestAuth,
              });

              if (!pageResult.success) {
                console.warn(' [Device People] Hikvision page failed at position', position, pageResult.error);
                break;
              }

              const raw = typeof pageResult.data === 'string' ? JSON.parse(pageResult.data) : pageResult.data;
              const userInfoSearch = raw?.UserInfoSearch ?? raw;
              const numOfMatches: number = userInfoSearch?.numOfMatches ?? 0;
              totalMatches = userInfoSearch?.totalMatches ?? numOfMatches;
              const userList: any[] = userInfoSearch?.UserInfo ?? [];

              for (const user of userList) {
                people.push({
                  employeeNo: user.employeeNo ?? '',
                  name: user.name ?? '',
                  userType: user.userType ?? 'normal',
                  person_id: '',
                  is_unauthorized: true, // resolved after all pages load
                  has_photo: false,
                });
              }

              console.log(` [Device People] Hikvision page pos=${position}: got ${numOfMatches}, total=${totalMatches}, collected=${people.length}`);

              if (numOfMatches === 0) break;
              position += numOfMatches;
              if (totalMatches === 0) break;
            }

            directSucceeded = people.length > 0 || totalMatches === 0;
            console.log(` [Device People] Hikvision DIRECT: ${people.length}/${totalMatches} users loaded`);

            // Show the raw list immediately — authorization and face-enrollment status
            // are resolved next and will refresh it, but with 450+ users each step can
            // take several seconds and the porteiro shouldn't see a blank spinner the
            // whole time.
            setDevicePeople(people);
            setDevicePeopleTotalCount(people.length);

            // Resolve authorization: cross-reference against customer's authorized people
            // Use already-loaded state or fetch fresh (avoids stale-closure race with fetchAuthorizedPeople)
            try {
              let authList: any[] = authorizedPeople.length > 0
                ? authorizedPeople
                : await (zone
                    ? fetchWithAuth(`/api/v1/zones/${zone.id}/authorized-people`)
                        .then(r => r.ok ? r.json().then((d: any) => d.data || []) : [])
                    : Promise.resolve([]));
              const authEmployeeNos = new Set(authList.map((p: any) => md5(p.id)));
              people = people.map((p: any) => ({
                ...p,
                is_unauthorized: !authEmployeeNos.has(p.employeeNo),
              }));
              console.log(` [Device People] ${authEmployeeNos.size} authorized employeeNos, ${people.filter((p: any) => !p.is_unauthorized).length} matched on device`);
              setDevicePeople(people);
            } catch (authErr) {
              console.warn(' [Device People] Could not resolve authorization:', authErr);
            }

            // Fetch face library to mark which users have enrolled photos (reuse probed hikBaseUrl)
            if (people.length > 0) {
              try {
                const baseUrl = hikBaseUrl;
                const digestAuth = { username: device.username || '', password: device.password || '' };

                // Step 1: discover ALL face libraries (FDID + faceLibType) — faces may be
                // split across multiple libraries (e.g. a "blackFD" visible-light library
                // and an "infraredFD" thermal one on bi-spectrum terminals), so searching
                // only the first one can silently miss everyone enrolled in the other.
                // faceLibType is REQUIRED by FDSearch and must match the library being
                // searched — sending the wrong one (or omitting it) gets a 400 citing
                // "faceLibType", regardless of which FDID it's paired with.
                let libraries: { fdid: string; faceLibType: string }[] = [{ fdid: '1', faceLibType: 'blackFD' }];
                try {
                  const libResult = await (window as any).electronAPI.deviceHttpRequest({
                    url: `${baseUrl}/ISAPI/Intelligent/FDLib?format=json`,
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    timeout: 5000,
                    digestAuth,
                  });
                  console.log(' [Face] FDLib response:', JSON.stringify(libResult.data)?.substring(0, 400));
                  const libs = libResult.data?.FDLib?.FDLibBasicInfo ?? libResult.data?.FDLib ?? [];
                  const libArray = Array.isArray(libs) ? libs : (libs?.FDLibBasicInfo ?? []);
                  const discovered = libArray
                    .filter((l: any) => l?.FDID)
                    .map((l: any) => ({ fdid: String(l.FDID), faceLibType: l.faceLibType || 'blackFD' }));
                  if (discovered.length > 0) libraries = discovered;
                } catch (e) {
                  console.warn(' [Face] Could not read FDLib, using FDID=1/blackFD');
                }
                console.log(` [Face] Using libraries=${JSON.stringify(libraries)}`);

                // Step 2: FDSearch — paginated POST per library to get all enrolled FPIDs.
                // Map: FPID/employeeNo → face image URL (for direct display in Electron)
                const faceMap = new Map<string, string>();

                for (const { fdid, faceLibType } of libraries) {
                  let facePos = 0;
                  let faceTotalMatches = Infinity;

                  while (facePos < faceTotalMatches) {
                    const faceResult = await (window as any).electronAPI.deviceHttpRequest({
                      url: `${baseUrl}/ISAPI/Intelligent/FDLib/FDSearch?format=json`,
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: {
                        FDSearchCond: {
                          searchID: '1',
                          maxResults: 30,
                          searchResultPosition: facePos,
                          FDID: fdid,
                          faceLibType,
                        },
                      },
                      timeout: 10000,
                      digestAuth,
                    });

                    console.log(` [Face FDSearch] FDID=${fdid} pos=${facePos}:`, {
                      success: faceResult.success,
                      statusCode: faceResult.statusCode,
                      error: faceResult.error,
                      data: JSON.stringify(faceResult.data)?.substring(0, 500),
                    });

                    if (!faceResult.success) break;

                    const raw = typeof faceResult.data === 'string' ? JSON.parse(faceResult.data) : faceResult.data;
                    const faceSearch = raw?.FDSearch ?? raw;
                    // Hikvision uses FaceDataRecord[] on some firmware, FaceInfo[] on others
                    const faceRecords: any[] = faceSearch?.FaceDataRecord ?? faceSearch?.FaceInfo ?? [];
                    const numFaceMatches: number = faceSearch?.numOfMatches ?? faceRecords.length;
                    faceTotalMatches = faceSearch?.totalMatches ?? numFaceMatches;

                    for (const rec of faceRecords) {
                      // Prefer employeeNo when the device links it directly to the face
                      // record; fall back to FPID (works only if the device happens to
                      // use FPID===employeeNo, which some firmware does).
                      const key = rec.employeeNo ?? rec.EmployeeNo ?? rec.FPID ?? rec.fpid;
                      if (key) {
                        // If FDSearch returned inline base64 image, store it directly
                        const picData = rec.picData ?? rec.PicData;
                        if (picData) {
                          faceMap.set(key, `data:image/jpeg;base64,${picData}`);
                        } else {
                          // Fallback: construct URL for direct image fetch via Electron
                          const fpid = rec.FPID ?? rec.fpid ?? key;
                          const faceUrl = `${baseUrl}/ISAPI/Intelligent/FDLib/FaceDataRecord?format=jpeg&FDID=${fdid}&FPID=${fpid}`;
                          faceMap.set(key, rec.faceURL ?? faceUrl);
                        }
                      }
                    }

                    console.log(` [Face FDSearch] FDID=${fdid} got ${numFaceMatches}, total=${faceTotalMatches}, enrolled so far=${faceMap.size}`);

                    if (numFaceMatches === 0 || faceRecords.length === 0 || faceTotalMatches === 0) break;
                    facePos += numFaceMatches;
                  }
                }

                people = people.map((p: any) => ({
                  ...p,
                  has_photo: faceMap.has(p.employeeNo),
                  // direct_face_url: usable in Electron via electronAPI for image display
                  direct_face_url: faceMap.get(p.employeeNo) ?? null,
                }));
                console.log(` [Device People] ${faceMap.size}/${people.length} users have enrolled faces`);
              } catch (faceErr) {
                console.warn(' [Device People] Could not load face data:', faceErr);
              }
            }
          }

          if (!directSucceeded) {
            console.warn(' [Device People] DIRECT did not succeed, using cloud');
          }
        } catch (directError) {
          console.log(' [Device People] Falling back to cloud due to error:', directError);
        }
      }

      // Cloud fallback (or primary for browsers) — only if direct mode was NOT used
      if (!directSucceeded) {
        console.log(' [Device People] Using cloud API');
        const url = `/api/v1/iot-connect/facial-recognition/devices/${device.id}/people`;
        // Abort cloud fetch after 15 s — backend may proxy to the device and hang if the device
        // is unreachable from the cloud, which would otherwise exhaust the 30 s safety timer.
        const cloudAbort = new AbortController();
        const cloudAbortTimer = setTimeout(() => cloudAbort.abort(), 15000);
        try {
          const response = await fetchWithAuth(url, { signal: cloudAbort.signal });
          if (response.ok) {
            const data = await response.json();
            people = data.people || [];
          } else if (response.status === 503 && isControlID) {
            // Cloud proxy for ControlID not yet implemented — fail silently
            console.log('[Device People] ControlID cloud proxy not yet available (503), showing empty state');
            setDevicePeopleCloudUnavailable(true);
            people = [];
          } else if (response.status >= 500 && (isHikvision || isControlID)) {
            // Cloud proxy unavailable for this device type — show empty state instead of crashing
            console.warn(`[Device People] Cloud proxy returned ${response.status} for ${device.manufacturer}, showing empty state`);
            setDevicePeopleCloudUnavailable(true);
            people = [];
          } else {
            throw new Error(`Cloud API failed: ${response.status}`);
          }
        } catch (cloudErr: any) {
          if (cloudErr.name === 'AbortError') {
            console.warn('[Device People] Cloud API timed out (15 s) — device likely unreachable from cloud, showing empty state');
            setDevicePeopleCloudUnavailable(true);
          } else {
            throw cloudErr;
          }
        } finally {
          clearTimeout(cloudAbortTimer);
        }
      }

      // Detect duplicates by name (same name appearing more than once on device)
      const nameCounts: Record<string, number> = {};
      for (const p of people) {
        const key = (p.name || '').trim().toLowerCase();
        if (key) nameCounts[key] = (nameCounts[key] || 0) + 1;
      }
      const peopleWithDupFlag = people.map((p: any) => ({
        ...p,
        is_duplicate: nameCounts[(p.name || '').trim().toLowerCase()] > 1,
      }));

      // Update state
      setDevicePeople(peopleWithDupFlag);
      setDevicePeopleTotalCount(people.length);

      // Calculate counts
      // Use only is_unauthorized flag — person_id is empty for direct-mode data
      let authorizedCount = 0;
      let unauthorizedCount = 0;
      let withPhotoCount = 0;
      let withoutPhotoCount = 0;
      for (const person of people) {
        if (person.is_unauthorized === true) {
          unauthorizedCount++;
        } else {
          authorizedCount++;
        }
        const personHasPhoto = !!(person.has_photo ||
          person.faceURL || person.face_url || person.photo_url);
        if (personHasPhoto) {
          withPhotoCount++;
        } else {
          withoutPhotoCount++;
        }
      }
      setDevicePeopleAuthorizedCount(authorizedCount);
      setDevicePeopleUnauthorizedCount(unauthorizedCount);
      setDevicePeopleWithPhotoCount(withPhotoCount);
      setDevicePeopleWithoutPhotoCount(withoutPhotoCount);

      console.log(` [Device People] Loaded: ${people.length} total, ${authorizedCount} authorized`);
    } catch (error) {
      console.error(' [Device People] Error:', error);
      toast.error(`Erro ao carregar pessoas: ${error}`);
    } finally {
      clearTimeout(devicePeopleSafetyTimer);
      setIsLoadingDevicePeople(false);
    }
  };

  // Remove a person from the physical device using HYBRID mode (direct → cloud fallback)
  const handleRemoveDevicePerson = async (devicePerson: any) => {
    const toastId = toast.loading(`Removendo "${devicePerson.name}"...`);

    try {
      // Prefer numeric device ID (most reliable for destroy_objects.fcgi ids:[N])
      // Fall back to registration UUID/hash
      const devicePersonId = devicePerson._device_id !== undefined
        ? String(devicePerson._device_id)
        : devicePerson.employeeNo;

      // Safety guard: never proceed without a valid identifier
      if (!devicePersonId || devicePersonId.trim() === '') {
        console.error(' [Remove] Cannot remove person: no valid device identifier found', devicePerson);
        toast.error('Erro: identificador do dispositivo não encontrado', { id: toastId });
        return;
      }

      console.log(` [Remove] Identifier resolved: "${devicePersonId}" (numeric: ${devicePerson._device_id !== undefined})`);

      const isElectron = window.electronAPI !== undefined;
      const hasIP = device.local_ip_address || device.ip_address;
      const isLocalSyncDevice = ['controlid', 'hikvision'].includes(device.manufacturer?.toLowerCase() || '');

      // Try DIRECT mode for ControlID/Hikvision in Electron
      if (isElectron && hasIP && isLocalSyncDevice) {
        console.log(` [Remove] Trying DIRECT mode for person: ${devicePerson.name} (${devicePersonId})`);

        try {
          const result = await DeviceIntegration.instance.execute(
            {
              id: device.id,
              ip_address: device.ip_address,
              local_ip_address: device.local_ip_address,
              username: device.username || '',
              password: device.password || '',
              vendor: (device.manufacturer?.toLowerCase() || 'hikvision') as 'hikvision' | 'controlid' | 'rfid',
              port: device.port?.toString(),
            },
            DeviceOperations.REMOVE_PERSON,
            { devicePersonId },
            CommunicationMode.HYBRID
          );

          if (result.success) {
            console.log(` [Remove] DIRECT succeeded for: ${devicePerson.name}`);
            // Resolve person UUID (ControlID stores it directly; Hikvision: reverse MD5 lookup)
            const personUUID = devicePerson.person_id ||
              authorizedPeople.find((p: any) => md5(p.id) === devicePerson.employeeNo)?.id || '';
            if (personUUID && selectedCustomer?.id) {
              fireAndForget(
                fetchWithAuth(
                  `/api/v1/customers/${selectedCustomer.id}/people/${personUUID}/devices/${device.id}/mark-revoked`,
                  { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }
                ),
                `mark-revoked person=${personUUID} device=${device.id}`
              );
            }
            toast.success('Pessoa removida do dispositivo', { id: toastId });
            fetchDevicePeople();
            return;
          } else {
            console.warn(` [Remove] DIRECT failed for ${devicePerson.name}:`, result.error);
            // Fall through to cloud
          }
        } catch (directError) {
          console.warn(` [Remove] DIRECT error for ${devicePerson.name}:`, directError);
          // Fall through to cloud
        }

        console.log(` [Remove] Falling back to cloud for: ${devicePerson.name}`);
      }

      // Cloud fallback (or primary for browsers)
      console.log(` [Remove] Using cloud API for: ${devicePerson.name} (id: ${devicePersonId})`);
      const response = await fetchWithAuth(
        `/api/v1/iot-connect/facial-recognition/devices/${device.id}/people/${encodeURIComponent(devicePersonId)}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      toast.success('Pessoa removida do dispositivo', { id: toastId });
      fetchDevicePeople();
    } catch (error) {
      console.error(' [Remove] Failed to remove person from device:', error);
      toast.error('Erro ao remover pessoa do dispositivo', { id: toastId });
    }
  };

  // Bulk remove selected persons from the physical device, one by one with progress toast
  const handleBulkRemoveDevicePeople = async () => {
    if (selectedPeopleToRemove.size === 0) return;

    const toRemove = Array.from(selectedPeopleToRemove.values());
    const toastId = toast.loading(`Removendo 1/${toRemove.length}: "${toRemove[0].name}"...`);

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < toRemove.length; i++) {
      const devicePerson = toRemove[i];
      toast.loading(`Removendo ${i + 1}/${toRemove.length}: "${devicePerson.name}"...`, { id: toastId });

      try {
        const devicePersonId = devicePerson._device_id !== undefined
          ? String(devicePerson._device_id)
          : devicePerson.employeeNo;

        if (!devicePersonId || devicePersonId.trim() === '') {
          failCount++;
          continue;
        }

        const isElectronApp = window.electronAPI !== undefined;
        const hasIP = device.local_ip_address || device.ip_address;
        const isLocalSyncDevice = ['controlid', 'hikvision'].includes(device.manufacturer?.toLowerCase() || '');
        let success = false;

        if (isElectronApp && hasIP && isLocalSyncDevice) {
          try {
            const result = await DeviceIntegration.instance.execute(
              {
                id: device.id,
                ip_address: device.ip_address,
                local_ip_address: device.local_ip_address,
                username: device.username || '',
                password: device.password || '',
                vendor: (device.manufacturer?.toLowerCase() || 'hikvision') as 'hikvision' | 'controlid' | 'rfid',
                port: device.port?.toString(),
              },
              DeviceOperations.REMOVE_PERSON,
              { devicePersonId },
              CommunicationMode.HYBRID
            );
            success = result.success;
            if (!success) {
              console.warn(` [BulkRemove] Direct failed for ${devicePerson.name}:`, result.error);
            }
          } catch (directError) {
            console.warn(` [BulkRemove] Direct error for ${devicePerson.name}:`, directError);
          }
        }

        if (!success) {
          const response = await fetchWithAuth(
            `/api/v1/iot-connect/facial-recognition/devices/${device.id}/people/${encodeURIComponent(devicePersonId)}`,
            { method: 'DELETE' }
          );
          success = response.ok;
          if (!success) {
            console.warn(` [BulkRemove] Cloud also failed for ${devicePerson.name}: HTTP ${response.status}`);
          }
        }

        if (success) {
          successCount++;
          const personUUID = devicePerson.person_id ||
            authorizedPeople.find((p: any) => md5(p.id) === devicePerson.employeeNo)?.id || '';
          if (personUUID && selectedCustomer?.id) {
            fireAndForget(
              fetchWithAuth(
                `/api/v1/customers/${selectedCustomer.id}/people/${personUUID}/devices/${device.id}/mark-revoked`,
                { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }
              ),
              `mark-revoked person=${personUUID} device=${device.id}`
            );
          }
        } else {
          failCount++;
        }
      } catch (e) {
        failCount++;
        console.error(` [BulkRemove] Error removing ${devicePerson.name}:`, e);
      }
    }

    if (failCount === 0) {
      toast.success(`${successCount} pessoa(s) removida(s) do dispositivo`, { id: toastId, duration: 5000 });
    } else {
      toast.error(`${successCount} removida(s), ${failCount} com erro`, { id: toastId, duration: 5000 });
    }

    setSelectedPeopleToRemove(new Map());
    fetchDevicePeople();
  };

  // Fetch people from the system for linking with server-side search and pagination
  const fetchSystemPeople = async (searchTerm: string = '', page: number = 1) => {
    if (!selectedCustomer?.id) return;

    try {
      setIsLoadingSystemPeople(true);
      const pageSize = 20; // Fetch 20 at a time for pagination
      const offset = (page - 1) * pageSize;

      // Build URL with search and pagination parameters
      let url = `/api/v1/customers/${selectedCustomer.id}/people?limit=${pageSize}&offset=${offset}`;
      if (searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      console.log('[fetchSystemPeople] Fetching with search:', searchTerm, 'page:', page);

      const response = await fetchWithAuth(url);

      if (response.ok) {
        const data = await response.json();
        const peopleData = data.data || data.people || data || [];
        const total = data.total || data.count || peopleData.length;

        console.log('[fetchSystemPeople] Fetched:', peopleData.length, 'Total:', total);

        setAllSystemPeople(peopleData);
        setTotalSystemPeople(total);
      } else {
        const errorText = await response.text();
        console.error('[fetchSystemPeople] Failed to fetch:', response.status, errorText);
        setAllSystemPeople([]);
        setTotalSystemPeople(0);
      }
    } catch (error) {
      console.error('[fetchSystemPeople] Error:', error);
      setAllSystemPeople([]);
      setTotalSystemPeople(0);
    } finally {
      setIsLoadingSystemPeople(false);
    }
  };

  // Open link modal for a single person
  const openLinkModal = (devicePerson: any) => {
    setPersonToLink(devicePerson);
    setShowLinkModal(true);
    setLinkSearchTerm(''); // Reset search
    setLinkModalPage(1); // Reset to first page
    fetchSystemPeople('', 1); // Fetch first page without search
  };

  // Open link modal for multiple people
  const openBulkLinkModal = () => {
    if (selectedPeopleToLink.size === 0) {
      toast.error('Selecione pelo menos uma pessoa para vincular');
      return;
    }
    setPersonToLink(null); // null indicates bulk mode
    setShowLinkModal(true);
    setLinkSearchTerm(''); // Reset search
    setLinkModalPage(1); // Reset to first page
    fetchSystemPeople('', 1); // Fetch first page without search
  };

  // Debounced search effect - trigger search when user stops typing
  useEffect(() => {
    if (!showLinkModal) return; // Only run when modal is open

    const timeoutId = setTimeout(() => {
      // Reset to page 1 when search term changes
      setLinkModalPage(1);
      fetchSystemPeople(linkSearchTerm, 1);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [linkSearchTerm, showLinkModal]);

  // Fetch new page when page changes (but not on initial mount or search change)
  useEffect(() => {
    if (!showLinkModal) return;
    if (linkModalPage === 1) return; // Skip page 1 (handled by search effect)

    fetchSystemPeople(linkSearchTerm, linkModalPage);
  }, [linkModalPage]);

  // Show confirmation modal with options
  const handleSelectSystemPerson = (person: any) => {
    setSelectedSystemPerson(person);
    setShowLinkConfirmation(true);
    setKeepOldCadastro(false); // Default: don't keep old
    setUseSystemPersonName(false); // Default: keep device name
    setUploadSystemPhoto(false); // Default: don't upload system photo
  };

  // Handle linking a device person to a system person
  const handleLinkPerson = async () => {
    if (!selectedSystemPerson) return;

    setIsLinkingPerson(true);
    try {
      // Single person linking
      const response = await fetchWithAuth(
        `/api/v1/iot-connect/facial-recognition/devices/${device.id}/people/${personToLink.employeeNo}/link`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            person_id: selectedSystemPerson.id,
            use_system_name: useSystemPersonName,
            upload_system_photo: uploadSystemPhoto
          })
        }
      );

      if (response.ok) {
        // Pull fingerprint templates from device after linking (digital ControlID only)
        if (pullDeviceBiometry && device && selectedSystemPerson) {
          const isDigitalCtrl = shouldPullDeviceBiometry(device);
          const customerId = device.customer_id || zone?.customer_id;
          if (isDigitalCtrl && customerId) {
            try {
              // Try fetching directly from the device first (Electron + reachable IP).
              // Devices only reachable on the local LAN can ONLY be imported this way —
              // iot-connect (cloud, AWS) has no route to a private LAN IP. Falls back to
              // the cloud path (condo-manager → iot-connect) whenever direct isn't
              // possible or fails, same as door-open's HYBRID pattern.
              let directTemplates: Array<{ finger_type: number; template_b64: string }> | null = null;
              let directErrMessage: string | null = null;
              const hasIpForDirect = !!(device.ip_address || device.local_ip_address);
              const attemptedDirect = isElectron() && hasIpForDirect;
              try {
                const forcedUserID = resolveForcedUserID(personToLink?.employeeNo);
                directTemplates = await deviceCommunicationService.fetchControlIDFingerprintsDirect(
                  {
                    id: device.id,
                    ip_address: device.ip_address,
                    local_ip_address: device.local_ip_address,
                    username: device.username || '',
                    password: device.password || '',
                    vendor: 'controlid',
                    port: device.port?.toString(),
                    device_type: device.device_type,
                    config: device.config,
                  },
                  // The fallback lookup below matches by the DEVICE's own registration
                  // field, which the backend only renames to the system person's UUID
                  // when the old employeeNo was a plain int (see linkControlIDFingerprintPerson).
                  // For non-numeric registrations that rename is skipped, so search by the
                  // device's original employeeNo/registration, not the system person's id.
                  personToLink?.employeeNo,
                  forcedUserID,
                );
              } catch (directErr: any) {
                directErrMessage = directErr?.message || 'Erro desconhecido na busca direta';
                console.warn('[BiometryPull] Direct fetch failed, falling back to cloud:', directErrMessage);
                directTemplates = null;
              }

              // directTemplates !== null means a direct attempt was made and returned a
              // definitive answer (possibly empty) — no need to also hit the cloud.
              let result: { imported_count: number };
              try {
                result = directTemplates !== null
                  ? await fingerprintService.importDirect(customerId, selectedSystemPerson.id, directTemplates)
                  : await fingerprintService.importFromDevice(customerId, selectedSystemPerson.id, device.id, personToLink?.employeeNo);
              } catch (cloudErr: any) {
                // Surface the DIRECT attempt's error too when it was tried and both failed —
                // the cloud's generic "Falha na comunicação" hides the actual local-network
                // reason (e.g. connection timeout to a LAN-only device), which is the one
                // that matters for Electron users on-site with the device.
                if (attemptedDirect && directErrMessage) {
                  throw new Error(`Direto: ${directErrMessage} | Cloud: ${cloudErr?.message || 'erro desconhecido'}`);
                }
                throw cloudErr;
              }

              if (result.imported_count > 0) {
                toast.success(`Pessoa vinculada com ${result.imported_count} digital(ais) importada(s)`);
              } else {
                toast.success('Pessoa vinculada com sucesso (nenhuma biometria encontrada no dispositivo)');
              }
            } catch (bioErr: any) {
              console.warn('[BiometryPull] Error pulling templates:', bioErr?.message);
              toast('Pessoa vinculada, mas falha ao importar biometria: ' + (bioErr?.message || 'Erro desconhecido'), { duration: 8000 });
            }
          } else {
            toast.success('Pessoa vinculada com sucesso');
          }
        } else {
          toast.success('Pessoa vinculada com sucesso');
        }
        setShowLinkModal(false);
        setShowLinkConfirmation(false);
        setPersonToLink(null);
        setSelectedSystemPerson(null);
        setPullDeviceBiometry(false);
        fetchDevicePeople();
      } else {
        const error = await response.text();
        toast.error(`Erro ao vincular pessoa: ${error}`);
      }
    } catch (error) {
      console.error('Error linking person:', error);
      toast.error('Erro ao vincular pessoa');
    } finally {
      setIsLinkingPerson(false);
    }
  };

  // Open create new user modal
  const openCreateNewUserModal = () => {
    if (!personToLink) return;

    // Pre-fill with device person's name
    const nameParts = personToLink.name.split(' ');
    setNewUserFormData({
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      cpf: '',
      email: '',
      phone: '',
      birth_date: '',
      is_minor: false,
      role_type: 'visitante',
      department_id: '',
      unit_id: '',
      position: '',
      employee_no: ''
    });

    setShowCreateNewUserModal(true);
    setShowLinkModal(false);
  };

  // Handle creating new user and linking
  const handleCreateAndLinkUser = async () => {
    if (!selectedCustomer || !personToLink) return;

    setIsCreatingUser(true);
    try {
      // Create new person in the system
      const createPayload: any = {
        first_name: newUserFormData.first_name,
        last_name: newUserFormData.last_name,
        cpf: newUserFormData.cpf,
        email: newUserFormData.email,
        phone: newUserFormData.phone,
        is_active: true
      };

      // Add birth_date if provided
      if (newUserFormData.birth_date) {
        createPayload.birth_date = newUserFormData.birth_date;
      }

      // Always send is_minor flag explicitly (true or false)
      createPayload.is_minor = newUserFormData.is_minor;

      console.log('[CreateUser] Payload being sent:', createPayload);
      console.log('[CreateUser] is_minor checkbox state:', newUserFormData.is_minor);
      console.log('[CreateUser] birth_date state:', newUserFormData.birth_date);

      const createResponse = await fetchWithAuth(
        `/api/v1/customers/${selectedCustomer.id}/people`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(createPayload)
        }
      );

      if (!createResponse.ok) {
        const error = await createResponse.json().catch(() => ({}));
        toast.error(error.message || error.error || 'Erro ao criar pessoa');
        return;
      }

      const newPerson = await createResponse.json();
      const personId = newPerson.data?.id || newPerson.id;

      // Download device photo and upload to person's profile
      const devicePhotoURL = personToLink.faceURL || personToLink.face_url;
      if (devicePhotoURL) {
        try {
          console.log('Downloading device photo from:', devicePhotoURL);

          // Fetch the image from the device URL
          const imageResponse = await fetch(devicePhotoURL);
          if (imageResponse.ok) {
            const imageBlob = await imageResponse.blob();

            // Create FormData to upload
            const formData = new FormData();
            formData.append('photo', imageBlob, 'device-photo.jpg');
            formData.append('customer_id', selectedCustomer.id);

            // Upload to person's identity photos
            const uploadResponse = await fetchWithAuth(
              `/api/v1/people/${personId}/photos`,
              {
                method: 'POST',
                body: formData
              }
            );

            if (uploadResponse.ok) {
              console.log('Device photo uploaded successfully');
            } else {
              console.warn('Failed to upload device photo:', await uploadResponse.text());
            }
          } else {
            console.warn('Failed to fetch device photo:', imageResponse.status);
          }
        } catch (error) {
          console.error('Error uploading device photo:', error);
          // Don't fail the entire operation if photo upload fails
        }
      }

      // Create person role for the new person
      // Build role_metadata and role payload based on role_type
      const roleMetadata: any = {};
      const rolePayload: any = {
        role_type: newUserFormData.role_type,
        valid_from: new Date().toISOString(),
        role_metadata: roleMetadata,
        is_authorized: true  // Mark role as authorized
      };

      if (newUserFormData.role_type === 'funcionario' && newUserFormData.department_id) {
        roleMetadata.department_id = newUserFormData.department_id;
        roleMetadata.position = newUserFormData.position;
        roleMetadata.employee_no = newUserFormData.employee_no;
        rolePayload.department_id = newUserFormData.department_id; // Also add to root level
      } else if (newUserFormData.role_type === 'morador' && newUserFormData.unit_id) {
        rolePayload.unit_id = newUserFormData.unit_id; // Backend expects unit_id (singular), not unit_ids
        roleMetadata.relation_type = 'proprietario'; // Default relation type
      }

      const roleResponse = await fetchWithAuth(
        `/api/v1/customers/${selectedCustomer.id}/people/${personId}/roles`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(rolePayload)
        }
      );

      if (!roleResponse.ok) {
        const error = await roleResponse.text();
        console.warn(`Pessoa criada mas erro ao criar role: ${error}`);
        // Continue with linking even if role creation fails
      }

      // Now link the device person to the newly created system person
      const linkResponse = await fetchWithAuth(
        `/api/v1/iot-connect/facial-recognition/devices/${device.id}/people/${personToLink.employeeNo}/link`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            person_id: personId,
            use_system_name: false // Keep device name by default
          })
        }
      );

      if (linkResponse.ok) {
        toast.success('Pessoa criada e vinculada com sucesso!');
        setShowCreateNewUserModal(false);
        setPersonToLink(null);
        fetchDevicePeople();

        // Reset form
        setNewUserFormData({
          first_name: '',
          last_name: '',
          cpf: '',
          email: '',
          phone: '',
          birth_date: '',
          is_minor: false,
          role_type: 'visitante',
          department_id: '',
          unit_id: '',
          position: '',
          employee_no: ''
        });
      } else {
        const error = await linkResponse.text();
        toast.error(`Pessoa criada, mas erro ao vincular: ${error}`);
      }
    } catch (error) {
      console.error('Error creating and linking person:', error);
      toast.error('Erro ao criar e vincular pessoa');
    } finally {
      setIsCreatingUser(false);
    }
  };

  // Fetch access events (entries/exits) for this device
  const fetchAccessEvents = async (page: number = 1, limit: number = accessEventsLimit) => {
    if (!selectedCustomer?.id) return;

    setIsLoadingEvents(true);
    try {
      const response = await fetchWithAuth(
        `/api/v1/events/access?customer_id=${selectedCustomer.id}&device_id=${device.id}&limit=${limit}&page=${page}`
      );

      if (response.ok) {
        const data = await response.json();
        setAccessEvents(data.data || []);
        setAccessEventsTotal(data.total || 0);
        setAccessEventsPage(data.page || 1);
      } else {
        console.error('Failed to fetch access events:', response.status);
      }
    } catch (error) {
      console.error('Error fetching access events:', error);
    } finally {
      setIsLoadingEvents(false);
    }
  };

  // Fetch sync status + history from audit logs
  const fetchSyncStatus = async (histPage: number = 1) => {
    setIsLoadingSync(true);
    try {
      const fetches: Promise<Response | null>[] = [
        fetchWithAuth(`/api/v1/iot-devices/${device.id}/sync-status`),
      ];
      if (selectedCustomer?.id) {
        fetches.push(fetchWithAuth(
          `/api/v1/customers/${selectedCustomer.id}/device-audit-logs?device_id=${device.id}&page_size=${syncHistoryPageSize}&page=${histPage}`
        ));
      }
      const [statusResponse, historyResponse] = await Promise.all(fetches);

      if (statusResponse?.ok) {
        setSyncStatus(await statusResponse.json());
      }
      if (historyResponse?.ok) {
        const histData = await historyResponse.json();
        setSyncHistory(histData.data || []);
        setSyncHistoryTotal(histData.total_count || 0);
        setSyncHistoryPage(histPage);
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
    } finally {
      setIsLoadingSync(false);
    }
  };

  // Force sync - HYBRID: Try local first, fallback to cloud
  const handleForceSync = async () => {
    setIsSyncing(true);

    const toastId = toast.loading('Iniciando sincronização...');

    try {
      const isElectron = window.electronAPI !== undefined;
      const hasIP = device.local_ip_address || device.ip_address;

      // Try direct mode if in Electron and has IP
      if (isElectron && hasIP) {
        console.log(' [Sync] Trying DIRECT mode first (local device)');

        if (!device.zone_id) {
          toast.error('Dispositivo não está vinculado a uma zona', { id: toastId });
          return;
        }

        // ── RFID Vehicle Device (controlid_rfid / rfid_reader / iDUHF): force re-sync RFID cards ──
        // Deletes all existing cards per user and recreates with correct decimal value (LOCAL SYNC ONLY)
        // LPR cameras sync PLATES. Falling through to the people lookup below is what made
        // "Sincronizar" report "Nenhuma pessoa autorizada" on an LPR device.
        if (isLprDeviceModal) {
          const lprCameraId = device.config?.hikvision_lpr_camera_id;
          if (!lprCameraId) {
            toast.error('Dispositivo LPR não está vinculado a uma câmera', { id: toastId });
            return;
          }
          const cidLpr = selectedCustomer?.id || device.customer_id;
          toast.loading('Buscando veículos com placa...', { id: toastId });
          const vResp = await fetchWithAuth(`/api/v1/customers/${cidLpr}/vehicles?page=1&page_size=5000`);
          if (!vResp.ok) throw new Error('Failed to fetch vehicles');
          const vData = await vResp.json();
          const plated = (vData.vehicles || vData.data || []).filter((v: any) => (v.plate || '').trim());

          if (plated.length === 0) {
            toast.success('Nenhum veículo com placa encontrado', { id: toastId });
            return;
          }

          const { syncAllPlatesToCamera } = await import('../services/lprPlateSync');
          const lprBuf: SyncProgressItem[] = [];
          const lprZoneName = device.zone_name || device.name || device.id;
          const res = await syncAllPlatesToCamera(cidLpr, lprCameraId, plated, undefined, {
            onProgress: (pr) => {
              lprBuf.push({ label: pr.label, owner: 'LPR', status: pr.status });
              if (lprBuf.length > 30) lprBuf.shift();
              onSyncProgress?.({ zoneName: lprZoneName, total: pr.total, done: pr.done, ok: pr.ok, fail: pr.fail, recentItems: [...lprBuf], isComplete: pr.done === pr.total });
            },
          });

          toast.success(
            res.fail > 0 ? `${res.ok} placa(s) sincronizada(s), ${res.fail} falha(s)` : `${res.ok} placa(s) sincronizada(s)`,
            { id: toastId, duration: 4000 },
          );
          await fetchSyncStatus();
          fetchAuthorizedVehicles();
          return;
        }

        if (device.device_type === 'controlid_rfid' || device.device_type === 'rfid_reader') {
          toast.loading('Buscando veículos autorizados...', { id: toastId });
          const vehiclesResp = await fetchWithAuth(`/api/v1/iot-devices/${device.id}/authorized-people`);
          if (!vehiclesResp.ok) throw new Error('Failed to fetch vehicles');
          const vehiclesData = await vehiclesResp.json();
          // Only sync authorized vehicles (exclude revoked/expired)
          const vehicles = (vehiclesData.vehicles || []).filter((v: any) => !v.is_unauthorized);

          if (vehicles.length === 0) {
            toast.success('Nenhum veículo autorizado', { id: toastId });
            await fetchSyncStatus();
            return;
          }

          // Probe local vs public IP — same as HYBRID mode in executeOperation
          const rfidProbe2 = await smartSelectDeviceIp(
            { ip_address: device.ip_address, local_ip_address: device.local_ip_address, port: device.port },
            { timeoutLocal: 400, timeoutPublic: 1500 }
          );
          const rfidWorkingIp2 = rfidProbe2?.ip || device.local_ip_address || device.ip_address;

          // Both controlid_rfid and rfid_reader are ControlID iDUHF devices — always use 'controlid' adapter
          const rfidDeviceInfo = {
            id: device.id,
            ip_address: rfidWorkingIp2,
            local_ip_address: device.local_ip_address,
            username: device.username || '',
            password: device.password || '',
            vendor: 'controlid' as 'hikvision' | 'controlid' | 'rfid',
            port: device.port?.toString(),
          };

          const vehiclesMapped2 = vehicles.map((v: any) => ({
            vehicle_id: v.vehicle_id,
            rfid_tag: v.rfid_tag,
            plate: v.plate,
            person_name: v.person_name,
          }));

          // Open progress modal (keep toast running in parallel)
          const deviceZoneName = device.zone_name || device.name || device.id;
          onSyncProgress?.({ zoneName: deviceZoneName, total: vehicles.length, done: 0, ok: 0, fail: 0, recentItems: [], isComplete: false });

          const recentBuf2: SyncProgressItem[] = [];
          let rfidOk2 = 0, rfidFail2 = 0;

          // Sync each vehicle one-by-one using the same SYNC_PERSON path as the vehicle modal
          for (let i = 0; i < vehiclesMapped2.length; i++) {
            const v = vehiclesMapped2[i];
            const personSyncData2 = {
              person_id: v.vehicle_id,
              first_name: v.plate || 'Veículo',
              last_name: v.person_name || '',
              identity_photo_url: undefined,
              rfid_tags: v.rfid_tag ? [v.rfid_tag] : [],
              vehicle_plate: v.plate,
              isAllowed: true,
            };
            const res2 = await DeviceIntegration.instance.execute(
              rfidDeviceInfo,
              DeviceOperations.SYNC_PERSON,
              personSyncData2,
              CommunicationMode.HYBRID,
            );
            if (res2.success) rfidOk2++; else rfidFail2++;
            const itemStatus2: 'ok' | 'failed' = res2.success ? 'ok' : 'failed';
            recentBuf2.push({ label: v.plate || v.vehicle_id, owner: v.person_name || '', status: itemStatus2 });
            if (recentBuf2.length > 30) recentBuf2.shift();
            onSyncProgress?.({ zoneName: deviceZoneName, total: vehiclesMapped2.length, done: i + 1, ok: rfidOk2, fail: rfidFail2, recentItems: [...recentBuf2], isComplete: i + 1 === vehiclesMapped2.length });

            // Small delay between vehicles to avoid overwhelming the ControlID device rate limiter
            if (i < vehiclesMapped2.length - 1) await new Promise(r => setTimeout(r, 200));
          }

          onSyncProgress?.({ zoneName: deviceZoneName, total: vehicles.length, done: vehicles.length, ok: rfidOk2, fail: rfidFail2, recentItems: [...recentBuf2], isComplete: true });
          const rfidMsg2 = rfidFail2 > 0
            ? `${rfidOk2} sincronizado(s), ${rfidFail2} falha(s)`
            : `${rfidOk2} veículo(s) sincronizado(s)`;
          toast.success(rfidMsg2, { id: toastId, duration: 4000 });
          await fetchSyncStatus();
          return;
        }

        toast.loading('Buscando pessoas autorizadas...', { id: toastId });

        const peopleResponse = await fetchWithAuth(`/api/v1/zones/${device.zone_id}/authorized-people`);
        if (!peopleResponse.ok) {
          throw new Error('Failed to fetch people');
        }

        const peopleData = await peopleResponse.json();
        const people = peopleData.data || [];

        if (people.length === 0) {
          toast.success('Nenhuma pessoa autorizada', { id: toastId });
          await fetchSyncStatus();
          return;
        }

        // Probe local vs public IP — try local first (400ms), fall back to public (1500ms)
        const probe = await smartSelectDeviceIp(
          { ip_address: device.ip_address, local_ip_address: device.local_ip_address, port: device.port },
          { timeoutLocal: 400, timeoutPublic: 1500 }
        );
        const workingIp = probe?.ip || device.local_ip_address || device.ip_address;

        const deviceInfo = {
          id: device.id,
          ip_address: workingIp,
          local_ip_address: device.local_ip_address,
          username: device.username || '',
          password: device.password || '',
          vendor: (device.manufacturer?.toLowerCase() || 'controlid') as 'hikvision' | 'controlid' | 'rfid',
          port: device.port?.toString(),
        };

        // ── ControlID: ensure group / time zone / access rule exists once ──
        let controlIDGroupId: number | null = null;
        const isControlIDVendor = deviceInfo.vendor === 'controlid';
        if (isControlIDVendor) {
          toast.loading('Configurando grupos e horários no dispositivo...', { id: toastId });
          controlIDGroupId = await deviceCommunicationService.ensureControlIDDefaultAccess(deviceInfo);
          console.log(`[Sync] ControlID default group id=${controlIDGroupId}`);
        }

        toast.loading(`Sincronizando ${people.length} pessoa(s)...`, { id: toastId });

        let successCount = 0;
        let failureCount = 0;
        let skippedCount = 0;
        const fsBuf: SyncProgressItem[] = [];

        onSyncProgress?.({ zoneName: device.name || device.id, total: people.length, done: 0, ok: 0, fail: 0, recentItems: [], isComplete: false });

        for (let i = 0; i < people.length; i++) {
          // Pause / stop support
          while (syncPauseRef.current) {
            await new Promise(r => setTimeout(r, 200));
          }
          if (syncStopRef.current) break;

          const person = people[i];

          // Check if person already exists before adding.
          // Two-step: UUID (registration) first — exact, works for people synced via frontend.
          // Name fallback — catches people added via backend (registration=null).
          // NOTE: result.data may be a JSON string from Electron IPC — always parse before use.
          const parseData = (data: any) => {
            if (typeof data === 'string') {
              try { return JSON.parse(data); } catch { return data; }
            }
            return data;
          };

          const isHikvisionDevice = deviceInfo.vendor === 'hikvision';
          let alreadyExists = false;
          let existingDeviceUserId: number | undefined = undefined;
          try {
            // Step 1: check by employeeNo (Hikvision) or registration UUID (ControlID)
            console.log(`[Check] "${person.id}" (${i + 1}/${people.length}): querying device...`);
            const checkPayload = isHikvisionDevice ? { person_id: person.id } : { registration: person.id };
            const uuidResult = await DeviceIntegration.instance.execute(
              deviceInfo,
              DeviceOperations.LOAD_PEOPLE,
              checkPayload,
              CommunicationMode.DIRECT
            );
            const uuidData = parseData(uuidResult.data);
            const foundByEmployee = isHikvisionDevice
              ? (uuidData?.UserInfoSearch?.UserInfo?.length ?? 0)
              : (uuidData?.users?.length ?? 0);
            console.log(`[Check] "${person.id}": success=${uuidResult.success}, found=${foundByEmployee}`, uuidData);
            if (uuidResult.success && foundByEmployee > 0) {
              alreadyExists = true;
              // existingDeviceUserId only matters for ControlID group assignment
              if (!isHikvisionDevice) existingDeviceUserId = uuidData?.users?.[0]?.id;
            }
          } catch (uuidErr: any) {
            console.warn(`[Check] Error for person ${i + 1}:`, uuidErr?.message || uuidErr);
          }

          if (!alreadyExists && !isHikvisionDevice) {
            try {
              // Step 2: fallback check by name (ControlID only — backend may add users without registration)
              const rawName = `${person.first_name} ${person.last_name}`;
              const checkName = rawName.trim().replace(/\s+/g, ' ');
              console.log(`[Check-Name] "${checkName}" (${i + 1}/${people.length}): querying device...`);
              const nameResult = await DeviceIntegration.instance.execute(
                deviceInfo,
                DeviceOperations.LOAD_PEOPLE,
                { name: checkName },
                CommunicationMode.DIRECT
              );
              const nameData = parseData(nameResult.data);
              const foundByName = nameData?.users?.length ?? 0;
              console.log(`[Check-Name] "${checkName}": success=${nameResult.success}, found=${foundByName}`, nameData);
              if (nameResult.success && foundByName > 0) {
                alreadyExists = true;
                existingDeviceUserId = nameData?.users?.[0]?.id;
              }
            } catch (nameErr: any) {
              console.warn(`[Check-Name] Error for person ${i + 1}:`, nameErr?.message || nameErr);
            }
          }

          if (alreadyExists) {
            skippedCount++;
            toast.loading(`Verificando... ${i + 1}/${people.length} (${skippedCount} já no dispositivo)`, { id: toastId });

            // Even for already-existing users, ensure group assignment
            if (isControlIDVendor && controlIDGroupId !== null && existingDeviceUserId) {
              try {
                await deviceCommunicationService.assignControlIDUserToGroup(deviceInfo, existingDeviceUserId, controlIDGroupId);
                console.log(`[Sync]  Existing user ${existingDeviceUserId} (${person.first_name}) assigned to group ${controlIDGroupId}`);
              } catch (groupErr: any) {
                console.warn(`[Sync]  Group assignment for existing user ${person.first_name}:`, groupErr?.message);
              }
            }
            if (selectedCustomer?.id) {
              // Audit trail + reconciliation ledger — see utils/deviceSyncOutcome.
              reportDeviceSyncOutcome({
                customerId: selectedCustomer.id,
                personId: person.id,
                deviceId: device.id,
                device,
                success: true,
                source: 'local',
              });
            }
          // Extract photo URL from nested structure or flat field (before 'already exists' continue)
          const identityPhotoUrl0 = person.identity_photo?.photo_url || person.identity_photo_url;
          const profilePictureUrl0 = person.profile_picture_url;
          let photoUrl0: string | undefined = identityPhotoUrl0 || profilePictureUrl0 || undefined;
          // Resolved by the shared helper rather than a pasted host: it also rewrites S3
          // presigned URLs onto the stable proxy path, which is what the device sync reads.
          if (photoUrl0?.startsWith('/')) photoUrl0 = getAbsoluteImageUrl(photoUrl0);

            fsBuf.push({ label: `${person.first_name} ${person.last_name}`.trim(), owner: '', status: 'ok', note: 'Já estava cadastrado no dispositivo', photoUrl: photoUrl0, personId: person.id });
            onSyncProgress?.({ zoneName: device.name || device.id, total: people.length, done: i + 1, ok: successCount + skippedCount, fail: failureCount, recentItems: [...fsBuf], isComplete: false });

            continue;
          }

          // Extract photo URL from nested structure or flat field
          const identityPhotoUrl = person.identity_photo?.photo_url || person.identity_photo_url;
          const profilePictureUrl = person.profile_picture_url;
          let photoUrl = identityPhotoUrl || profilePictureUrl;

          // Convert relative URLs to absolute — through the shared helper, so the host comes
          // from the configured API base instead of being pasted in.
          if (photoUrl && photoUrl.startsWith('/')) {
            photoUrl = getAbsoluteImageUrl(photoUrl);
          }

          // Pre-fetch photo with Bearer token and convert to data URI.
          // fetchImageAsBase64 (used inside executeOperation) calls plain fetch() with no
          // auth header, causing silent 401s for backend image URLs in Electron.
          // Passing a data URI bypasses that fetch entirely.
          let photoDataUri = '';
          if (photoUrl) {
            try {
              const photoResp = await fetchWithAuth(photoUrl);
              if (photoResp.ok) {
                const photoBlob = await photoResp.blob();
                photoDataUri = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onloadend = () => res(r.result as string); r.onerror = rej; r.readAsDataURL(photoBlob); });
                console.log(` [Sync] Pre-fetched photo for ${person.first_name}: ${photoDataUri.length} chars`);
              } else {
                console.warn(` [Sync] Photo fetch HTTP ${photoResp.status} for ${person.first_name}`);
              }
            } catch (photoErr: any) {
              console.warn(` [Sync] Photo fetch error for ${person.first_name}:`, photoErr?.message);
            }
          }

          console.log(` [Sync] Person ${person.first_name}: photo=${photoDataUri ? `data URI (${photoDataUri.length} chars)` : 'NONE'}`);

          try {
            const result = await DeviceIntegration.instance.execute(
              {
                id: device.id,
                ip_address: workingIp,
                local_ip_address: device.local_ip_address,
                username: device.username || '',
                password: device.password || '',
                vendor: (device.manufacturer?.toLowerCase() || 'hikvision') as 'hikvision' | 'controlid' | 'rfid',
                port: device.port?.toString(),
              },
              DeviceOperations.SYNC_PERSON,
              {
                person_id: person.id,
                first_name: person.first_name,
                last_name: person.last_name,
                identity_photo_url: photoDataUri || photoUrl,
                profile_picture_url: photoDataUri || photoUrl,
                isAllowed: true,
              },
              CommunicationMode.DIRECT
            );

            if (result.success) {
              successCount++;

              // ── ControlID: assign user to default group (departamento + horário) ──
              if (isControlIDVendor && controlIDGroupId !== null) {
                // result.data may come back as a string from Electron IPC — parse it
                const rawData = typeof result.data === 'string'
                  ? (() => { try { return JSON.parse(result.data); } catch { return result.data; } })()
                  : result.data;
                const deviceUserId: number | undefined = rawData?.ids?.[0];
                console.log(`[Sync] ${person.first_name} — raw response:`, rawData, '→ deviceUserId:', deviceUserId);
                if (deviceUserId) {
                  try {
                    await deviceCommunicationService.assignControlIDUserToGroup(deviceInfo, deviceUserId, controlIDGroupId);
                    console.log(`[Sync]  ${person.first_name} assigned to group ${controlIDGroupId}`);
                  } catch (groupErr: any) {
                    console.warn(`[Sync]  Group assignment failed for ${person.first_name}:`, groupErr?.message || groupErr);
                  }
                } else {
                  console.warn(`[Sync]  No device user ID in response for ${person.first_name} — raw:`, rawData);
                }
              }
              if (selectedCustomer?.id) {
                // Audit trail + reconciliation ledger — see utils/deviceSyncOutcome.
                reportDeviceSyncOutcome({
                  customerId: selectedCustomer.id,
                  personId: person.id,
                  deviceId: device.id,
                  device,
                  success: true,
                  source: 'local',
                });
              }
              fsBuf.push({ label: `${person.first_name} ${person.last_name}`.trim(), owner: '', status: 'ok', photoUrl: photoUrl || undefined, personId: person.id });
            } else {
              failureCount++;
              console.warn(`Direct sync failed for ${person.first_name}:`, result.error);
              fsBuf.push({ label: `${person.first_name} ${person.last_name}`.trim(), owner: '', status: 'failed', error: result.error, photoUrl: photoUrl || undefined, personId: person.id });
            }
            onSyncProgress?.({ zoneName: device.name || device.id, total: people.length, done: i + 1, ok: successCount + skippedCount, fail: failureCount, recentItems: [...fsBuf], isComplete: false });
          } catch (error) {
            failureCount++;
            console.error(`Sync error for ${person.first_name}:`, error);
          }
          // Inter-person delay for ControlID to avoid overwhelming the device rate limiter
          if (isControlIDVendor && i < people.length - 1) await new Promise(r => setTimeout(r, 200));
        }

        // If direct mode had any success OR skipped everyone, show result
        if (successCount > 0 || skippedCount === people.length) {
          onSyncProgress?.({ zoneName: device.name || device.id, total: people.length, done: people.length, ok: successCount + skippedCount, fail: failureCount, recentItems: [...fsBuf], isComplete: true });
          const msg = successCount > 0
            ? `${successCount} adicionadas, ${skippedCount} já estavam no dispositivo`
            : `Todos (${skippedCount}) já estavam no dispositivo`;
          toast.success(msg, { id: toastId, duration: 5000 });
          await fetchSyncStatus();
          return;
        }
        onSyncProgress?.({ zoneName: device.name || device.id, total: people.length, done: people.length, ok: successCount + skippedCount, fail: failureCount, recentItems: [...fsBuf], isComplete: true });

        // Electron: no cloud fallback for sync
        if (isElectron) {
          toast.error(`Falha ao sincronizar diretamente. Verifique a rede.`, { id: toastId });
          return;
        }
        toast.loading('Modo direto falhou. Tentando via nuvem...', { id: toastId });
      } else if (isElectron) {
        toast.error(`Dispositivo sem IP configurado — não é possível sincronizar diretamente.`, { id: toastId });
        return;
      } else {
        toast.loading('Sincronizando via nuvem...', { id: toastId });
      }

      // Cloud path — browser only after this point
      const response = await fetchWithAuth(
        `/api/v1/iot-devices/${device.id}/sync`,
        { method: 'POST' }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(
          `Sincronização concluída (nuvem): ${result.success_count || 0} pessoa(s)`,
          { id: toastId, duration: 5000 }
        );
        await fetchSyncStatus();
      } else {
        const errorText = await response.text();
        toast.error(`Erro: ${errorText}`, { id: toastId });
      }
    } catch (error) {
      console.error('Error syncing device:', error);
      toast.error(`Erro: ${error}`, { id: toastId });
    } finally {
      setIsSyncing(false);
    }
  };

  // Force re-sync ALL people on ControlID — skips duplicate check, re-uploads photos,
  // re-assigns group. Used from the Advanced Options section in the Sync tab.
  const handleForceResync = async () => {
    setIsForceResyncing(true);
    const toastId = toast.loading('Iniciando re-sincronização forçada...');

    try {
      if (!device.zone_id) {
        toast.error('Dispositivo não está vinculado a uma zona', { id: toastId });
        return;
      }

      // Same reasoning as the zone sync: a camera has plates, not people, so the people
      // lookup below would always report "Nenhuma pessoa autorizada" for an LPR device.
      if (isLprDeviceModal) {
        const lprCameraId = device.config?.hikvision_lpr_camera_id;
        if (!lprCameraId) {
          toast.error('Dispositivo LPR não está vinculado a uma câmera', { id: toastId });
          return;
        }
        const cidLpr = selectedCustomer?.id || device.customer_id;
        toast.loading('Buscando veículos com placa...', { id: toastId });
        const vResp = await fetchWithAuth(`/api/v1/customers/${cidLpr}/vehicles?page=1&page_size=5000`);
        if (!vResp.ok) throw new Error('Failed to fetch vehicles');
        const vData = await vResp.json();
        const plated = (vData.vehicles || vData.data || []).filter((v: any) => (v.plate || '').trim());

        if (plated.length === 0) {
          toast.success('Nenhum veículo com placa encontrado', { id: toastId });
          return;
        }

        const { syncAllPlatesToCamera } = await import('../services/lprPlateSync');
        const res = await syncAllPlatesToCamera(cidLpr, lprCameraId, plated);
        toast.success(
          res.fail > 0 ? `${res.ok} placa(s) sincronizada(s), ${res.fail} falha(s)` : `${res.ok} placa(s) sincronizada(s)`,
          { id: toastId, duration: 4000 },
        );
        fetchAuthorizedVehicles();
        return;
      }

      toast.loading('Buscando pessoas autorizadas...', { id: toastId });
      const peopleResponse = await fetchWithAuth(`/api/v1/zones/${device.zone_id}/authorized-people`);
      if (!peopleResponse.ok) throw new Error('Failed to fetch people');

      const peopleData = await peopleResponse.json();
      const people = peopleData.data || [];

      if (people.length === 0) {
        toast.success('Nenhuma pessoa autorizada para sincronizar', { id: toastId });
        return;
      }

      // Probe local vs public IP — try local first (400ms), fall back to public (1500ms)
      const probe = await smartSelectDeviceIp(
        { ip_address: device.ip_address, local_ip_address: device.local_ip_address, port: device.port },
        { timeoutLocal: 400, timeoutPublic: 1500 }
      );
      const workingIp = probe?.ip || device.local_ip_address || device.ip_address;

      const deviceInfo = {
        id: device.id,
        ip_address: workingIp,
        local_ip_address: device.local_ip_address,
        username: device.username || '',
        password: device.password || '',
        vendor: (device.manufacturer?.toLowerCase() || 'controlid') as 'hikvision' | 'controlid' | 'rfid',
        port: device.port?.toString(),
      };

      const isHikvisionResync = deviceInfo.vendor === 'hikvision';

      // ControlID: Force re-setup (ControlID-specific operations)
      let controlIDGroupId: number | null = null;
      if (!isHikvisionResync) {
        deviceCommunicationService.clearControlIDGroupCache(deviceInfo.id);
        toast.loading('Configurando grupos e horários...', { id: toastId });
        controlIDGroupId = await deviceCommunicationService.ensureControlIDDefaultAccess(deviceInfo);
        console.log(`[ForceResync] ControlID group id=${controlIDGroupId}`);
      }

      let successCount = 0;
      let failureCount = 0;
      let skippedCount = 0;
      const frBuf: SyncProgressItem[] = [];

      onSyncProgress?.({ zoneName: device.name || device.id, total: people.length, done: 0, ok: 0, fail: 0, recentItems: [], isComplete: false });

      const parseResync = (data: any) => {
        if (typeof data === 'string') { try { return JSON.parse(data); } catch { return data; } }
        return data;
      };

      for (let i = 0; i < people.length; i++) {
        // Pause / stop support
        while (syncPauseRef.current) {
          await new Promise(r => setTimeout(r, 200));
        }
        if (syncStopRef.current) break;

        const person = people[i];

        toast.loading(
          `Re-sincronizando... ${i + 1}/${people.length} (${successCount} ok, ${skippedCount} já no dispositivo)`,
          { id: toastId }
        );

        try {
          // Check if user already exists on device — avoid creating duplicates
          // Hikvision: check by employeeNo (MD5); ControlID: check by registration UUID
          let existingDeviceUserId: number | undefined = undefined;
          let existsOnDevice = false;
          try {
            const checkPayload = isHikvisionResync ? { person_id: person.id } : { registration: person.id };
            const checkResult = await DeviceIntegration.instance.execute(
              deviceInfo,
              DeviceOperations.LOAD_PEOPLE,
              checkPayload,
              CommunicationMode.DIRECT
            );
            const checkData = parseResync(checkResult.data);
            if (isHikvisionResync) {
              existsOnDevice = (checkData?.UserInfoSearch?.UserInfo?.length ?? 0) > 0;
            } else {
              existingDeviceUserId = checkData?.users?.[0]?.id;
              existsOnDevice = existingDeviceUserId !== undefined;
            }
          } catch (checkErr: any) {
            console.warn(`[ForceResync] Existence check failed for ${person.first_name}:`, checkErr?.message);
          }

          // Resolve photo URL for this person
          const identityPhotoUrl = person.identity_photo?.photo_url || person.identity_photo_url;
          const profilePictureUrl = person.profile_picture_url;
          let photoUrl = identityPhotoUrl || profilePictureUrl;
          if (photoUrl && photoUrl.startsWith('/')) {
            photoUrl = getAbsoluteImageUrl(photoUrl);
          }

          if (existsOnDevice) {
            // User already on device — for ControlID: overwrite photo + assign group; for Hikvision: skip
            console.log(`[ForceResync] ${person.first_name} already exists (id=${existingDeviceUserId})`);

            if (!isHikvisionResync && photoUrl && existingDeviceUserId) {
              try {
                await deviceCommunicationService.reuploadFacePhoto(
                  deviceInfo,
                  existingDeviceUserId,
                  {
                    person_id: person.id,
                    first_name: person.first_name,
                    last_name: person.last_name,
                    identity_photo_url: photoUrl,
                    profile_picture_url: photoUrl,
                    isAllowed: true,
                  }
                );
                console.log(`[ForceResync]  Photo updated for ${person.first_name}`);
              } catch (photoErr: any) {
                console.warn(`[ForceResync] Photo update failed for ${person.first_name}:`, photoErr?.message);
              }
            }

            if (!isHikvisionResync && controlIDGroupId !== null && existingDeviceUserId) {
              try {
                await deviceCommunicationService.assignControlIDUserToGroup(deviceInfo, existingDeviceUserId, controlIDGroupId);
                console.log(`[ForceResync]  ${person.first_name} assigned to group ${controlIDGroupId}`);
              } catch (groupErr: any) {
                console.warn(`[ForceResync] Group assignment failed for ${person.first_name}:`, groupErr?.message);
              }
            }

            skippedCount++;
            if (selectedCustomer?.id) {
              // Audit trail + reconciliation ledger — see utils/deviceSyncOutcome.
              reportDeviceSyncOutcome({
                customerId: selectedCustomer.id,
                personId: person.id,
                deviceId: device.id,
                device,
                success: true,
                source: 'local',
              });
            }
            frBuf.push({ label: `${person.first_name} ${person.last_name}`.trim(), owner: '', status: 'ok', photoUrl: photoUrl || undefined, personId: person.id });
            onSyncProgress?.({ zoneName: device.name || device.id, total: people.length, done: i + 1, ok: successCount + skippedCount, fail: failureCount, recentItems: [...frBuf], isComplete: false });
            continue;
          }

          // User not on device — create and assign group
          const result = await DeviceIntegration.instance.execute(
            deviceInfo,
            DeviceOperations.SYNC_PERSON,
            {
              person_id: person.id,
              first_name: person.first_name,
              last_name: person.last_name,
              identity_photo_url: photoUrl,
              profile_picture_url: photoUrl,
              isAllowed: true,
            },
            CommunicationMode.DIRECT
          );

          if (result.success) {
            successCount++;
            if (controlIDGroupId !== null) {
              const rawData = parseResync(result.data);
              const deviceUserId: number | undefined = rawData?.ids?.[0];
              if (deviceUserId) {
                try {
                  await deviceCommunicationService.assignControlIDUserToGroup(deviceInfo, deviceUserId, controlIDGroupId);
                  console.log(`[ForceResync]  ${person.first_name} created + assigned to group ${controlIDGroupId}`);
                } catch (groupErr: any) {
                  console.warn(`[ForceResync] Group assignment failed for ${person.first_name}:`, groupErr?.message);
                }
              }
            }
            if (selectedCustomer?.id) {
              // Audit trail + reconciliation ledger — see utils/deviceSyncOutcome.
              reportDeviceSyncOutcome({
                customerId: selectedCustomer.id,
                personId: person.id,
                deviceId: device.id,
                device,
                success: true,
                source: 'local',
              });
            }
            frBuf.push({ label: `${person.first_name} ${person.last_name}`.trim(), owner: '', status: 'ok', photoUrl: photoUrl || undefined, personId: person.id });
          } else {
            failureCount++;
            console.warn(`[ForceResync] Failed for ${person.first_name}:`, result.error);
            frBuf.push({ label: `${person.first_name} ${person.last_name}`.trim(), owner: '', status: 'failed', error: result.error, photoUrl: photoUrl || undefined, personId: person.id });
          }
          onSyncProgress?.({ zoneName: device.name || device.id, total: people.length, done: i + 1, ok: successCount + skippedCount, fail: failureCount, recentItems: [...frBuf], isComplete: false });
        } catch (err: any) {
          failureCount++;
          frBuf.push({ label: `${person.first_name} ${person.last_name}`.trim(), owner: '', status: 'failed', error: err?.message, photoUrl: undefined, personId: person.id });
          onSyncProgress?.({ zoneName: device.name || device.id, total: people.length, done: i + 1, ok: successCount + skippedCount, fail: failureCount, recentItems: [...frBuf], isComplete: false });
          console.error(`[ForceResync] Error for ${person.first_name}:`, err?.message || err);
        }
        // Inter-person delay to avoid overwhelming the ControlID device rate limiter
        if (!isHikvisionResync && i < people.length - 1) await new Promise(r => setTimeout(r, 200));
      }

      onSyncProgress?.({ zoneName: device.name || device.id, total: people.length, done: people.length, ok: successCount + skippedCount, fail: failureCount, recentItems: [...frBuf], isComplete: true });
      toast.success(
        `Re-sincronização concluída: ${successCount} adicionados, ${skippedCount} já no dispositivo, ${failureCount} erros`,
        { id: toastId, duration: 6000 }
      );
      await fetchSyncStatus();
    } catch (error) {
      console.error('[ForceResync] Error:', error);
      toast.error(`Erro: ${error}`, { id: toastId });
    } finally {
      setIsForceResyncing(false);
    }
  };

  // Configure ControlID monitor mode.
  // Electron mode: calls the device directly via Electron IPC (login.fcgi + set_configuration.fcgi).
  // Web mode: calls the cloud backend.
  /**
   * Read the board's actual monitor configuration.
   *
   * A ControlID device only pushes events to its configured monitor, so "Online but no events"
   * is almost always an empty or wrong monitor. There was no way to check this from the UI at
   * all — the Hikvision panel had "Ler configuração" and this one had nothing.
   *
   * Prefers the LAN (Electron) like every other direct operation: the cloud route cannot reach
   * a board behind NAT, and its handler answers `{"monitor": null}` on failure — which is
   * indistinguishable from a genuinely unconfigured monitor, so trusting it would turn "I could
   * not check" into a confident, wrong "not configured".
   */
  const handleReadMonitor = async () => {
    setIsReadingMonitor(true);
    try {
      const inElectron = typeof window !== 'undefined' && (window as any).electronAPI !== undefined;
      const hasIp = device.local_ip_address || device.ip_address;

      let monitor: any = null;

      if (inElectron && hasIp) {
        const { smartSelectDeviceIp } = await import('../utils/deviceProbe');
        const selected = await smartSelectDeviceIp(
          { local_ip_address: device.local_ip_address, ip_address: device.ip_address, port: device.port },
          { timeoutLocal: 400, timeoutPublic: 1500, retryOnUnreachable: true },
        );
        const ip = selected?.ip ?? device.local_ip_address ?? device.ip_address ?? null;
        if (!ip) {
          toast.error('Sem endereço IP configurado para este dispositivo');
          return;
        }

        const rawIp = ip.replace(/^https?:\/\//, '');
        let deviceIp = rawIp;
        let devicePort = device.port?.toString() || '80';
        if (rawIp.includes(':')) {
          const [h, p] = rawIp.split(':');
          deviceIp = h;
          devicePort = p;
        }
        const baseUrl = `${devicePort === '443' ? 'https' : 'http'}://${deviceIp}:${devicePort}`;

        // ControlID is session-based: login.fcgi, then get_configuration.fcgi with the session.
        const loginResult = await (window as any).electronAPI.deviceHttpRequest({
          url: `${baseUrl}/login.fcgi`,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ login: device.username || '', password: device.password || '' }),
          timeout: 7000,
        });

        const session = loginResult?.data?.session;
        if (!loginResult?.success || !session) {
          toast.error(`Erro ao autenticar no dispositivo: ${loginResult?.error || 'sem sessão'}`);
          return;
        }

        const configResult = await (window as any).electronAPI.deviceHttpRequest({
          url: `${baseUrl}/get_configuration.fcgi?session=${encodeURIComponent(session)}`,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monitor: ['hostname', 'port', 'path', 'request_timeout'] }),
          timeout: 7000,
        });

        if (!configResult?.success) {
          toast.error(`Erro ao ler configuração: ${configResult?.error || 'sem resposta'}`);
          return;
        }
        monitor = configResult.data?.monitor ?? null;
      } else {
        const resp = await fetchWithAuth(
          `/api/v1/iot-connect/facial-recognition/devices/${device.id}/monitor-status`,
        );
        if (!resp.ok) {
          toast.error('Erro ao ler configuração do dispositivo');
          return;
        }
        monitor = (await resp.json()).monitor ?? null;
      }

      const comparison = compareControlIdMonitor(monitor, device.id);
      console.log('[ControlID Monitor] read:', { monitor, comparison });
      setMonitorCheck(comparison);
      setMonitorStatus(monitor);

      if (comparison.ok) {
        toast.success('Monitor configurado corretamente');
      } else if (!comparison.configured) {
        toast.error('Monitor não configurado — o dispositivo não envia eventos');
      } else {
        toast.error('Monitor configurado para outro destino');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao ler configuração do monitor');
    } finally {
      setIsReadingMonitor(false);
    }
  };

  const handleConfigureMonitor = async () => {
    setIsConfiguringMonitor(true);
    try {
      const inElectron = typeof window !== 'undefined' && (window as any).electronAPI !== undefined;
      const hasIp = device.local_ip_address || device.ip_address;
      // Shared with the reader above, so what is written is exactly what gets verified.
      const webhookPath = expectedControlIdMonitor(device.id).path;

      if (inElectron && hasIp) {
        const result = await DeviceIntegration.instance.execute(
          {
            id: device.id,
            ip_address: device.ip_address,
            local_ip_address: device.local_ip_address,
            username: device.username || '',
            password: device.password || '',
            vendor: 'controlid',
            port: device.port?.toString(),
          },
          DeviceOperations.CONFIGURE_WEBHOOK,
          { hostname: 'access.panther.com.br', port: '80', path: webhookPath, request_timeout: '5000' },
          CommunicationMode.HYBRID,
        );
        if (!result.success) throw new Error(result.error || 'Falha ao configurar');
      } else {
        const response = await fetchWithAuth(
          `/api/v1/iot-connect/facial-recognition/devices/${device.id}/configure-webhook`,
          { method: 'POST' }
        );
        if (!response.ok) throw new Error('Falha ao configurar');
      }

      toast.success('Modo monitor configurado com sucesso!');
      // Reload monitor status from cloud to confirm
      const statusResp = await fetchWithAuth(
        `/api/v1/iot-connect/facial-recognition/devices/${device.id}/monitor-status`
      );
      if (statusResp.ok) {
        const data = await statusResp.json();
        setMonitorStatus(data.monitor || null);
      }
    } catch (error) {
      toast.error('Erro ao configurar modo monitor');
    } finally {
      setIsConfiguringMonitor(false);
    }
  };

  // Configure cloud webhook address on a Hikvision device.
  // Electron mode: calls the device directly via Electron IPC (works even on local-only networks).
  // Web mode: calls the cloud backend which then calls the device.
  const handleConfigureWebhook = async () => {
    setIsConfiguringWebhook(true);
    try {
      const inElectron = typeof window !== 'undefined' && (window as any).electronAPI !== undefined;
      const hasIp = device.local_ip_address || device.ip_address;
      const isLPR = device.device_type === 'hikvision_lpr' || device.device_type === 'lpr';

      if (isLPR) {
        // LPR: configure ANPR event push via ISAPI httpHosts
        // Use hikvision_lpr_camera_id from config if linked, otherwise fall back to device.id
        const lprCameraId = device.config?.hikvision_lpr_camera_id || device.id;
        // CRITICAL: Hikvision <url> must be just the PATH — device builds full URL from host+port+url
        const webhookPath = `/api/v1/iot-connect/hikvision-lpr/${lprCameraId}/push`;
        if (!inElectron || !hasIp) throw new Error('Configure manualmente via interface web da câmera');
        const ip = device.local_ip_address || device.ip_address;
        const hikNS = 'xmlns="http://www.hikvision.com/ver20/XMLSchema"';
        const xmlBody = `<?xml version="1.0" encoding="UTF-8"?><HttpHostNotificationList ${hikNS}><HttpHostNotification><id>1</id><url>${webhookPath}</url><protocolType>HTTP</protocolType><parameterFormatType>XML</parameterFormatType><addressingFormatType>hostname</addressingFormatType><hostName>access.panther.com.br</hostName><portNo>80</portNo><httpAuthenticationMethod>none</httpAuthenticationMethod></HttpHostNotification></HttpHostNotificationList>`;
        const result = await (window as any).electronAPI.deviceHttpRequest({
          url: `${ip}/ISAPI/Event/notification/httpHosts`,
          method: 'PUT',
          headers: { 'Content-Type': 'application/xml' },
          body: xmlBody,
          timeout: 5000,
          digestAuth: { username: device.username || 'admin', password: device.password || '' },
        });
        if (!result.success) throw new Error(result.error || `Falha ao configurar (HTTP ${result.statusCode})`);
        toast.success('Webhook ANPR configurado com sucesso!');
        return;
      }

      // Face / RFID devices
      // CRITICAL: Hikvision <url> field must be just the PATH.
      // The device builds the full URL from protocolType + hostName + portNo + url.
      // Sending the full "https://..." URL here causes the device to POST to a malformed address.
      const webhookPath = `/api/v1/iot-connect/facial-recognition/events/hikvision/${device.id}`;

      if (inElectron && hasIp) {
        const result = await DeviceIntegration.instance.execute(
          {
            id: device.id,
            ip_address: device.ip_address,
            local_ip_address: device.local_ip_address,
            username: device.username || '',
            password: device.password || '',
            vendor: 'hikvision',
            port: device.port?.toString(),
          },
          DeviceOperations.CONFIGURE_WEBHOOK,
          { webhookUrl: webhookPath, hostname: WEBHOOK_HOST, port: Number(EVENT_WEBHOOK_PORT), useHttps: EVENT_WEBHOOK_PROTOCOL === 'HTTPS' },
          CommunicationMode.HYBRID,
        );
        if (!result.success) throw new Error(result.error || 'Falha ao configurar');
      } else {
        const response = await fetchWithAuth(
          `/api/v1/iot-connect/facial-recognition/devices/${device.id}/configure-webhook`,
          { method: 'POST' }
        );
        if (!response.ok) throw new Error('Falha ao configurar');
      }
      toast.success('Endereço público configurado com sucesso!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao configurar endereço público');
    } finally {
      setIsConfiguringWebhook(false);
    }
  };

  // Returns true for Hikvision models that only support HTTP port 80 (cannot negotiate TLS).
  // Both the configurator and the "Ler configuração" validator derive from utils/hikWebhook,
  // so they cannot disagree — that disagreement is what reported a correctly configured LPR
  // camera as "Configuração incorreta".
  const getHikWebhookUrl = (model?: string, deviceId?: string): string =>
    hikWebhookUrl({ id: deviceId ?? '', device_type: device.device_type, model, config: device.config });

  // Read the current HTTP event notification configuration from a Hikvision device.
  // Useful for diagnosing why events are not reaching the cloud.
  const handleReadHikvisionConfig = async () => {
    setIsReadingHikvisionConfig(true);
    // Safety: clear loading state even if an IPC call hangs indefinitely
    const safetyTimer = setTimeout(() => {
      setIsReadingHikvisionConfig(false);
      toast.error('Operação expirou (20s) — verifique a conexão com o dispositivo');
    }, 20000);
    try {
      const inElectron = typeof window !== 'undefined' && (window as any).electronAPI !== undefined;
      if (!inElectron) {
        toast.error('Disponível apenas no modo Electron');
        return;
      }

      // Smart IP selection via shared probe utility (local probe first, then public)
      const { smartSelectDeviceIp } = await import('../utils/deviceProbe');
      const selected = await smartSelectDeviceIp(
        { local_ip_address: device.local_ip_address, ip_address: device.ip_address, port: device.port },
        { timeoutLocal: 400, timeoutPublic: 1500 }
      );
      // selected is null only when no IP is configured at all; use best-effort otherwise
      const ip = selected?.ip ?? device.local_ip_address ?? device.ip_address ?? null;
      if (!ip) {
        toast.error('Sem endereço IP configurado para este dispositivo');
        return;
      }
      console.log(`[Ler Configuração] Probe → ${selected?.source ?? 'best-effort'} (${ip})`);

      const rawIp = ip.replace(/^https?:\/\//, '');
      let deviceIp = rawIp;
      let devicePort = device.port?.toString() || '80';
      if (rawIp.includes(':')) {
        const [h, p] = rawIp.split(':');
        deviceIp = h;
        devicePort = p;
      }
      const protocol = devicePort === '443' ? 'https' : 'http';
      const baseUrl = `${protocol}://${deviceIp}:${devicePort}`;

      // Read HTTP hosts config
      // Digest auth performs 2 round-trips (challenge + authenticated request).
      // Keep per-request timeout ≤ 7 s so 2× stays well under the 20 s safety timer.
      const httpHostsResult = await (window as any).electronAPI.deviceHttpRequest({
        url: `${baseUrl}/ISAPI/Event/notification/httpHosts`,
        method: 'GET',
        headers: { 'Content-Type': 'application/xml' },
        timeout: 7000,
        digestAuth: { username: device.username || '', password: device.password || '' },
      });

      console.log('[Hikvision Diag] HTTP Hosts raw response:', httpHostsResult);

      if (!httpHostsResult.success) {
        toast.error(`Erro ao ler config: ${httpHostsResult.error}`);
        return;
      }

      const body = typeof httpHostsResult.data === 'string'
        ? httpHostsResult.data
        : JSON.stringify(httpHostsResult.data, null, 2);

      const urlMatch    = body.match(/<url>([^<]+)<\/url>/i);
      const hostMatch   = body.match(/<hostName>([^<]+)<\/hostName>/i);
      const portMatch   = body.match(/<portNo>([^<]+)<\/portNo>/i);
      const protoMatch  = body.match(/<protocolType>([^<]+)<\/protocolType>/i);
      const authMatch   = body.match(/<httpAuthenticationMethod>([^<]+)<\/httpAuthenticationMethod>/i);

      const summary = {
        protocol: protoMatch?.[1] ?? 'N/A',
        host: hostMatch?.[1] ?? 'N/A',
        port: portMatch?.[1] ?? 'N/A',
        url: urlMatch?.[1] ?? 'N/A',
        auth: authMatch?.[1] ?? 'N/A',
      };

      const fullConfigured = `${summary.protocol}://${summary.host}:${summary.port}${summary.url}`;
      const want      = expectedHikWebhook({ id: device.id, device_type: device.device_type, model: device.model, config: device.config });
      const expProto  = want.protocol;
      const expPort   = want.port;
      const expected  = `${expProto}://${want.host}:${expPort}${want.path}`;
      const isCorrect = summary.host === want.host
        && summary.port === expPort
        && summary.url === want.path
        && summary.protocol?.toUpperCase() === expProto;

      console.log('[Hikvision Diag] Parsed config:', summary);
      console.log('[Hikvision Diag] Full URL configured on device:', fullConfigured);
      console.log('[Hikvision Diag] Expected:', expected);
      console.log('[Hikvision Diag] Correct?', isCorrect);
      console.log('[Hikvision Diag] Raw XML:\n', body);

      setHikConfigStatus({
        ok: isCorrect,
        protocol: summary.protocol || expProto,
        protocolOk: summary.protocol?.toUpperCase() === expProto,
        host: summary.host,
        hostOk: summary.host === want.host,
        port: summary.port,
        portOk: summary.port === expPort,
      });
    } catch (error: any) {
      toast.error(`Erro: ${error.message}`);
    } finally {
      clearTimeout(safetyTimer);
      setIsReadingHikvisionConfig(false);
    }
  };

  // Ask the Hikvision device to attempt a test push to its configured cloud URL.
  // PUT /ISAPI/Event/notification/httpHosts/1/testHost — device reports success/fail.
  // This is the definitive test for whether the device can reach access.panther.com.br.
  const handleTestHikvisionPush = async () => {
    setIsTestingHikvisionPush(true);
    try {
      const inElectron = typeof window !== 'undefined' && (window as any).electronAPI !== undefined;
      if (!inElectron || (!device.local_ip_address && !device.ip_address)) {
        toast.error('Disponível apenas no modo Electron com dispositivo configurado');
        return;
      }

      const { smartSelectDeviceIp } = await import('../utils/deviceProbe');
      const selected = await smartSelectDeviceIp(
        { local_ip_address: device.local_ip_address, ip_address: device.ip_address, port: device.port },
        { timeoutLocal: 400, timeoutPublic: 1500 }
      );
      const ip = selected?.ip ?? device.local_ip_address ?? device.ip_address ?? null;
      if (!ip) {
        toast.error('Sem endereço IP configurado para este dispositivo');
        return;
      }
      console.log(`[Hikvision TestPush] Probe → ${selected?.source ?? 'best-effort'} (${ip})`);

      const rawIp = ip.replace(/^https?:\/\//, '');
      let deviceIp = rawIp;
      let devicePort = device.port?.toString() || '80';
      if (rawIp.includes(':')) {
        const [h, p] = rawIp.split(':');
        deviceIp = h;
        devicePort = p;
      }
      const protocol = devicePort === '443' ? 'https' : 'http';
      const testUrl = `${protocol}://${deviceIp}:${devicePort}/ISAPI/Event/notification/httpHosts/1/testHost`;

      console.log('[Hikvision TestPush] Calling:', testUrl);

      // testHost is a trigger call — no body, no Content-Type.
      // Sending Content-Type: application/xml with an empty body causes statusCode 5 "Invalid Format".
      const result = await (window as any).electronAPI.deviceHttpRequest({
        url: testUrl,
        method: 'PUT',
        headers: {},
        timeout: 10000,
        digestAuth: { username: device.username || '', password: device.password || '' },
      });

      console.log('[Hikvision TestPush] Response:', result);

      if (!result.success) {
        toast.error(friendlyDeviceError(result.error), { duration: 8000 });
        return;
      }

      const body = typeof result.data === 'string' ? result.data : JSON.stringify(result.data, null, 2);
      console.log('[Hikvision TestPush] Full response body:\n', body);

      // Hikvision returns <ResponseStatus> with <statusCode>1 (OK) or error code
      const statusCode = body.match(/<statusCode>(\d+)<\/statusCode>/i)?.[1];
      const statusStr  = body.match(/<statusString>([^<]+)<\/statusString>/i)?.[1];

      if (statusCode === '1' || statusStr?.toLowerCase().includes('ok')) {
        toast.success('Dispositivo conseguiu alcançar o cloud com sucesso!', { duration: 8000 });
      } else {
        toast.error(`Dispositivo NÃO conseguiu alcançar o cloud. Código: ${statusCode ?? 'N/A'} — ${statusStr ?? body.substring(0, 100)}`, { duration: 10000 });
        console.error('[Hikvision TestPush] Device reported failure:', body);
      }
    } catch (error: any) {
      toast.error(`Erro: ${error.message}`);
    } finally {
      setIsTestingHikvisionPush(false);
    }
  };

  // Start a local HTTP server and temporarily reconfigure the device to push events here.
  // This bypasses cloud/SSL/NAT to determine whether the device sends events at all.
  const handleStartLocalWebhook = async () => {
    const inElectron = typeof window !== 'undefined' && (window as any).electronAPI !== undefined;
    if (!inElectron) { toast.error('Disponível apenas no modo Electron'); return; }

    const result = await (window as any).electronAPI.startLocalWebhook(8099);
    if (!result.success) { toast.error(`Erro ao iniciar listener: ${result.error}`); return; }

    setWebhookDebug(prev => ({ ...prev, active: true, port: result.port, lanIP: result.lanIP || prev.lanIP }));

    // Reconfigure device to push to our local listener
    try {
      const webhookPath = `/api/v1/iot-connect/facial-recognition/events/hikvision/${device.id}`;
      await DeviceIntegration.instance.execute(
        {
          id: device.id,
          ip_address: device.ip_address,
          local_ip_address: device.local_ip_address,
          username: device.username || '',
          password: device.password || '',
          vendor: 'hikvision',
          port: device.port?.toString(),
        },
        DeviceOperations.CONFIGURE_WEBHOOK,
        { webhookUrl: webhookPath, hostname: result.lanIP, port: result.port, useHttps: false },
        CommunicationMode.DIRECT,
      );
      toast.success(`Ouvindo em http://${result.lanIP}:${result.port} — dispositivo reconfigurado`, { duration: 8000 });
    } catch (e: any) {
      toast.error(`Listener ativo mas falha ao reconfigurar dispositivo: ${e.message}`);
    }
  };

  // Stop the local webhook server and restore cloud config on the device.
  const handleStopLocalWebhook = async () => {
    // Restore cloud config first
    try {
      const webhookPath = `/api/v1/iot-connect/facial-recognition/events/hikvision/${device.id}`;
      await DeviceIntegration.instance.execute(
        {
          id: device.id,
          ip_address: device.ip_address,
          local_ip_address: device.local_ip_address,
          username: device.username || '',
          password: device.password || '',
          vendor: 'hikvision',
          port: device.port?.toString(),
        },
        DeviceOperations.CONFIGURE_WEBHOOK,
        { webhookUrl: webhookPath, hostname: WEBHOOK_HOST, port: Number(EVENT_WEBHOOK_PORT), useHttps: EVENT_WEBHOOK_PROTOCOL === 'HTTPS' },
        CommunicationMode.DIRECT,
      );
    } catch (e: any) {
      toast.error(`Aviso: falha ao restaurar config cloud: ${e.message}`);
    }

    await (window as any).electronAPI.stopLocalWebhook();
    setWebhookDebug(prev => ({ ...prev, active: false }));
    toast.success('Listener parado. Config cloud restaurada.');
  };

  // Reboot Hikvision device via ISAPI to flush its notification queue.
  const handleRebootHikvisionDevice = async () => {
    const inElectron = typeof window !== 'undefined' && (window as any).electronAPI !== undefined;
    if (!inElectron) { toast.error('Disponível apenas no modo Electron'); return; }
    const ip = device.local_ip_address || device.ip_address;
    if (!ip) { toast.error('IP do dispositivo não encontrado'); return; }

    const rawIp = ip.replace(/^https?:\/\//, '');
    let deviceIp = rawIp;
    let devicePort = device.port?.toString() || '80';
    if (rawIp.includes(':')) { const [h, p] = rawIp.split(':'); deviceIp = h; devicePort = p; }
    const protocol = devicePort === '443' ? 'https' : 'http';
    const rebootUrl = `${protocol}://${deviceIp}:${devicePort}/ISAPI/System/reboot`;

    const toastId = toast.loading('Reiniciando dispositivo...');
    const result = await (window as any).electronAPI.deviceHttpRequest({
      url: rebootUrl,
      method: 'PUT',
      headers: {},
      timeout: 10000,
      digestAuth: { username: device.username || '', password: device.password || '' },
    });
    if (result.success) {
      toast.success('Dispositivo reiniciando — aguarde ~60s e verifique se eventos chegam com timestamp correto.', { id: toastId, duration: 10000 });
    } else {
      toast.error(friendlyDeviceError(result.error), { id: toastId });
    }
  };

  // Clear Hikvision device event log via ISAPI to stop 22-day backlog of old events.
  // Drain the device's buffered event log by redirecting it to the local listener.
  // The device ACKs each event when it gets HTTP 200. On LAN this drains the entire
  // backlog in seconds, then we restore cloud config.
  const handleClearHikvisionEventLog = async () => {
    const inElectron = typeof window !== 'undefined' && (window as any).electronAPI !== undefined;
    if (!inElectron) { toast.error('Disponível apenas no modo Electron'); return; }

    // 1. Start local listener
    const startResult = await (window as any).electronAPI.startLocalWebhook(8099);
    if (!startResult.success) {
      toast.error(`Falha ao iniciar listener: ${startResult.error}`);
      return;
    }
    const { lanIP, port } = startResult;

    // 2. Redirect device to local listener (plain HTTP, no SSL)
    try {
      const webhookPath = `/api/v1/iot-connect/facial-recognition/events/hikvision/${device.id}`;
      await DeviceIntegration.instance.execute(
        {
          id: device.id,
          ip_address: device.ip_address,
          local_ip_address: device.local_ip_address,
          username: device.username || '',
          password: device.password || '',
          vendor: 'hikvision',
          port: device.port?.toString(),
        },
        DeviceOperations.CONFIGURE_WEBHOOK,
        { webhookUrl: webhookPath, hostname: lanIP, port, useHttps: false },
        CommunicationMode.DIRECT,
      );
    } catch (err: any) {
      toast.error(`Falha ao reconfigurar webhook: ${err.message}`);
      await (window as any).electronAPI.stopLocalWebhook();
      return;
    }

    setWebhookDebug(prev => ({ ...prev, active: true, port, lanIP, events: [] }));
    toast.success(
      `Drenando backlog via http://${lanIP}:${port} — aguarde o fluxo de eventos parar, depois clique "Parar drenagem".`,
      { duration: 15000 }
    );
  };

  // Handle vehicle removal from RFID device (inside DeviceDetailsModal)
  const handleRemoveVehicleFromDevice = async () => {
    if (!vehicleToRemove) return;

    setIsRemovingVehicle(true);
    try {
      const response = await fetchWithAuth(
        `/api/v1/iot-connect/rfid/devices/${vehicleToRemove.device.id}/cards/${vehicleToRemove.cardNumber}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      toast.success('Veículo removido do dispositivo com sucesso');
      setShowVehicleRemoveModal(false);
      setVehicleToRemove(null);
      // Refresh the vehicles list
      fetchAuthorizedVehicles();
    } catch (error) {
      console.error('Failed to remove vehicle from device:', error);
      toast.error('Erro ao remover veículo do dispositivo');
    } finally {
      setIsRemovingVehicle(false);
    }
  };

  // Save device changes
  const handleSaveDevice = async () => {
    if (!selectedCustomer) return;

    setIsSaving(true);
    try {
      const response = await fetchWithAuth(
        `/api/v1/customers/${selectedCustomer.id}/iot-devices/${device.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(editedDevice)
        }
      );

      if (response.ok) {
        toast.success('Dispositivo atualizado com sucesso');
        setIsEditing(false);
        onDeviceUpdated?.(editedDevice);
        onRefresh();
      } else {
        toast.error('Erro ao atualizar dispositivo');
      }
    } catch (error) {
      console.error('Error updating device:', error);
      toast.error('Erro ao atualizar dispositivo');
    } finally {
      setIsSaving(false);
    }
  };

  // Trigger door to open - with smart routing (local first, cloud fallback for Electron)
  const handleTriggerDoor = async () => {
    setIsTriggering(true);
    try {
      await triggerDeviceDoor(device, fetchWithAuth);
      const isGate = isVehicleListDevice(device.device_type);
      toast.success(isGate ? 'Portão acionado com sucesso!' : 'Porta acionada com sucesso!');
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Erro ao acionar porta/portão';
      toast.error(msg);
    } finally {
      setIsTriggering(false);
    }
  };

  // Fetch device models when editing
  useEffect(() => {
    if (isEditing && deviceModels.length === 0) {
      const fetchModels = async () => {
        setIsLoadingModels(true);
        try {
          // Fetch vendors from probe endpoint (gets from registered detectors)
          const vendorsResponse = await fetchWithAuth(`/api/v1/iot-connect/probe/vendors`);
          if (vendorsResponse.ok) {
            const vendorsData = await vendorsResponse.json();
            const v2 = vendorsData.vendors || [];
            if (!v2.includes('nice')) v2.push('nice');
            if (!v2.includes('alphadigi')) v2.push('alphadigi');
            if (!v2.includes('hikvision')) v2.push('hikvision');
            setVendors(v2);
          }

          // Fetch models from probe endpoint (gets from registered detectors)
          const modelsResponse = await fetchWithAuth(`/api/v1/iot-connect/probe/models`);
          if (modelsResponse.ok) {
            const modelsData = await modelsResponse.json();
            // Convert models map to array for backward compatibility
            const modelsArray: any[] = [];
            if (modelsData.models) {
              Object.entries(modelsData.models).forEach(([vendor, models]: [string, any]) => {
                if (Array.isArray(models)) {
                  models.forEach((model: string) => {
                    modelsArray.push({
                      vendor: vendor,
                      code: model,
                      model: model,
                      name: model
                    });
                  });
                }
              });
            }
            modelsArray.push({ vendor: 'nice', code: 'modulo_guarita_ip', model: 'modulo_guarita_ip', name: 'Módulo Guarita IP' });
            modelsArray.push({ vendor: 'alphadigi', code: 'lpr_camera', model: 'lpr_camera', name: 'Câmera LPR', device_type: 'lpr' });
            modelsArray.push({ vendor: 'hikvision', code: 'DS-TCG406', model: 'DS-TCG406', name: 'DS-TCG406-E(S) LPR', device_type: 'lpr' });
            setDeviceModels(modelsArray);
          }
        } catch (error) {
          console.error('Failed to fetch device models:', error);
        } finally {
          setIsLoadingModels(false);
        }
      };
      fetchModels();
    }
  }, [isEditing]);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'people') {
      if (isGuaritaModalDevice) {
        fetchGuaritaTags();
        fetchGuaritaDeviceRegs();
        fetchAuthorizedPeople();
        const _guaritaIntegration = guaritaIntegrationsModal.find(i => i.id === device.config?.guarita_integration_id);
        if (_guaritaIntegration?.vehicle_whitelist_enabled) {
          fetchGuaritaVehicles();
        }
      } else if (isVehicleListDevice(device.device_type)) {
        fetchAuthorizedVehicles();
      } else {
        fetchAuthorizedPeople();
        fetchDevicePeople();
      }
    } else if (activeTab === 'events') {
      fetchAccessEvents(1, accessEventsLimit);
    } else if (activeTab === 'sync') {
      fetchSyncStatus();
    }
  }, [activeTab]);

  const generateAuthorizedPeoplePDF = async () => {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    let y = margin;

    // ── Panther Access header (same style as other PDFs) ─────
    try {
      const logoImg = new Image();
      logoImg.src = '/panther-logo.png';
      await new Promise<void>((resolve) => {
        logoImg.onload = () => { try { doc.addImage(logoImg, 'PNG', margin, 6, 20, 20); } catch {} resolve(); };
        logoImg.onerror = () => resolve();
        setTimeout(() => resolve(), 2000);
      });
    } catch {}

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text('ACCESS', margin + 23, 20);

    doc.setLineWidth(0.5);
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, 30, pageW - margin, 30);

    y = 38;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text('Pessoas Autorizadas', margin, y);

    y += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Dispositivo: ${device.name || '—'}`, margin, y);
    if (zone?.name) {
      doc.text(`Zona: ${zone.name}`, margin + 70, y);
    }
    const dateStr = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    doc.text(dateStr, pageW - margin, y, { align: 'right' });
    y += 10;

    // ── Section 1: Perfis de Acesso ──────────────────────────
    if (zoneProfiles.length > 0) {
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('PERFIS DE ACESSO', margin, y);
      y += 5;

      const typeLabels: Record<string, string> = {
        resident: 'Morador', morador: 'Morador',
        visitor: 'Visitante', visitante: 'Visitante',
        service_provider: 'Prestador', prestador: 'Prestador',
        staff: 'Funcionário', funcionario: 'Funcionário', employee: 'Funcionário',
        delivery: 'Entregador', entregador: 'Entregador',
      };

      const profileRows = zoneProfiles.map((profile: any) => {
        const assignments = zoneProfileAssignments[profile.id];
        const isAllow = profile.access_type === 'allow';
        const deptNames = (assignments?.department_assignments || [])
          .filter((a: any) => a.is_active)
          .map((a: any) => (a.department_name && a.department_name.trim()) || a.name || '')
          .filter(Boolean);
        const typeKeys = (assignments?.person_assignments || [])
          .filter((a: any) => a.is_active)
          .map((a: any) => typeLabels[a.person_type] || a.person_type);
        const groups = [...typeKeys, ...deptNames].join(', ') || '—';
        const scopeMap: Record<string, string> = {
          all_zones: 'Todas as zonas',
          zone_group: 'Grupo de zonas',
          specific_zone: 'Zona específica',
        };
        return [
          profile.name || '—',
          isAllow ? 'Permitir' : 'Negar',
          scopeMap[profile.applies_to] || profile.applies_to || '—',
          groups,
          profile.assignment_count != null ? String(profile.assignment_count) : '—',
        ];
      });

      autoTable(doc, {
        startY: y,
        head: [['Perfil', 'Acesso', 'Escopo', 'Grupos', 'Pessoas']],
        body: profileRows,
        margin: { left: margin, right: margin },
        headStyles: { fillColor: [50, 50, 50], textColor: 255, fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8, textColor: [40, 40, 40] },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 20 },
          2: { cellWidth: 30 },
          3: { cellWidth: 'auto' },
          4: { cellWidth: 18, halign: 'center' },
        },
        didParseCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 1) {
            const val = data.cell.raw as string;
            data.cell.styles.textColor = val === 'Permitir' ? [22, 101, 52] : [185, 28, 28];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });

      y = (doc as any).lastAutoTable.finalY + 10;
    }

    const roleLabels: Record<string, string> = {
      resident: 'Morador', visitor: 'Visitante', service_provider: 'Prestador',
      staff: 'Funcionário', delivery: 'Entregador',
      morador: 'Morador', visitante: 'Visitante', prestador: 'Prestador',
      funcionario: 'Funcionário', entregador: 'Entregador',
    };

    const buildPersonRow = (person: any) => {
      const activeRoles = (person.roles || [])
        .filter((r: any) => r.is_active && r.is_authorized)
        .map((r: any) => roleLabels[r.role_type] || r.role_type)
        .filter((v: any, i: any, a: any) => a.indexOf(v) === i);
      const deptNamesSet = new Set<string>();
      (person.roles || []).forEach((r: any) => {
        if (!r.is_active) return;
        if (Array.isArray(r.role_metadata?.departments)) {
          r.role_metadata.departments.forEach((d: any) => { if (d?.name) deptNamesSet.add(d.name); });
        } else if (r.role_metadata?.department_name) {
          deptNamesSet.add(r.role_metadata.department_name);
        }
      });
      const plates = (person.vehicles || []).map((v: any) => v.plate).filter(Boolean).join(', ');
      return [
        `${person.first_name || ''} ${person.last_name || ''}`.trim(),
        activeRoles.join(', ') || '—',
        Array.from(deptNamesSet).join(', ') || '—',
        plates || '—',
        getPrimaryPhotoUrl(person) ? 'Sim' : 'Não',
      ];
    };

    // ── Section 2: Pessoas Autorizadas ───────────────────────
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`PESSOAS AUTORIZADAS (${authorizedPeople.length})`, margin, y);
    y += 5;

    autoTable(doc, {
      startY: y,
      head: [['Nome', 'Perfil', 'Departamento/Bloco', 'Veículos', 'Foto']],
      body: authorizedPeople.map(buildPersonRow),
      margin: { left: margin, right: margin },
      headStyles: { fillColor: [50, 50, 50], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { cellWidth: 28 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 28 },
        4: { cellWidth: 14, halign: 'center' },
      },
      didParseCell: (data: any) => {
        if (data.section === 'body' && data.column.index === 4) {
          data.cell.styles.textColor = data.cell.raw === 'Sim' ? [22, 101, 52] : [185, 28, 28];
        }
      },
    });

    // ── Section 3: Pessoas Bloqueadas (from deny profiles) ───
    const denyProfiles = zoneProfiles.filter((p: any) => p.access_type === 'deny');
    for (const profile of denyProfiles) {
      const blockedPeople: any[] = denyProfilePeople[profile.id] || [];
      if (blockedPeople.length === 0) continue;

      y = (doc as any).lastAutoTable.finalY + 10;

      doc.setTextColor(185, 28, 28);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`PESSOAS BLOQUEADAS — ${profile.name} (${blockedPeople.length})`, margin, y);
      y += 5;

      autoTable(doc, {
        startY: y,
        head: [['Nome', 'Perfil', 'Departamento/Bloco', 'Veículos', 'Foto']],
        body: blockedPeople.map(buildPersonRow),
        margin: { left: margin, right: margin },
        headStyles: { fillColor: [185, 28, 28], textColor: 255, fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8, textColor: [40, 40, 40] },
        alternateRowStyles: { fillColor: [255, 245, 245] },
        columnStyles: {
          0: { cellWidth: 55 },
          1: { cellWidth: 28 },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 28 },
          4: { cellWidth: 14, halign: 'center' },
        },
        didParseCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 4) {
            data.cell.styles.textColor = data.cell.raw === 'Sim' ? [22, 101, 52] : [185, 28, 28];
          }
        },
      });
    }

    // ── Footer ───────────────────────────────────────────────
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(160, 160, 160);
      doc.text(`Página ${i} de ${totalPages}`, pageW - margin, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
      doc.text('Panther Access', margin, doc.internal.pageSize.getHeight() - 6);
    }

    doc.save(`pessoas-autorizadas-${(device.name || 'dispositivo').replace(/\s+/g, '-').toLowerCase()}.pdf`);
  };

  // Resolves each person to their department (empresa) or unit (condomínio) label, the same
  // way Tags.tsx's getPersonSubtitle does — role_metadata stores it in one of four formats
  // (departments[], department_ids[], legacy department_name, legacy department_id) or, for
  // residents, unit_number. Paginates through the full people list since the API silently
  // caps page_size well below customer sizes for large customers.
  const fetchPersonDeptUnitMap = async (): Promise<Record<string, string>> => {
    const map: Record<string, string> = {};
    if (!selectedCustomer?.id) return map;

    let departmentsList: any[] = [];
    try {
      const deptRes = await fetchWithAuth(`/api/v1/customers/${selectedCustomer.id}/departments`);
      if (deptRes.ok) {
        const deptData = await deptRes.json();
        departmentsList = deptData.data || deptData || [];
      }
    } catch {
      // silent — falls back to showing department_ids resolved to nothing
    }

    const pageSize = 1000;
    let page = 1;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const res = await fetchWithAuth(`/api/v1/customers/${selectedCustomer.id}/people?page=${page}&page_size=${pageSize}&include_inactive_roles=true`);
      if (!res.ok) break;
      const data = await res.json();
      const batch: any[] = data.data || data.people || [];
      batch.forEach((p: any) => {
        for (const role of p.roles || []) {
          const meta = role.role_metadata;
          if (!meta) continue;
          if (meta.unit_number) {
            map[p.id] = meta.block_name ? `${meta.block_name} · ${meta.unit_number}` : meta.unit_number;
            break;
          }
          if (Array.isArray(meta.departments) && meta.departments.length > 0) {
            map[p.id] = meta.departments.map((d: any) => d.name).filter(Boolean).join(', ');
            break;
          }
          if (Array.isArray(meta.department_ids) && meta.department_ids.length > 0) {
            const names = meta.department_ids.map((id: string) => departmentsList.find((d: any) => d.id === id)?.name).filter(Boolean);
            if (names.length > 0) { map[p.id] = names.join(', '); break; }
          }
          if (meta.department_name) {
            map[p.id] = meta.department_name;
            break;
          }
          if (meta.department_id) {
            const name = departmentsList.find((d: any) => d.id === meta.department_id)?.name;
            if (name) { map[p.id] = name; break; }
          }
        }
      });
      const totalPages = data.pagination?.total_pages;
      if (batch.length < pageSize || (totalPages && page >= totalPages) || batch.length === 0) break;
      page++;
    }
    return map;
  };

  // Nº de série / Dono / Nome do controle / Receptor / Departamento/Unidade / Repetido — built
  // from the raw (undeduplicated) device registration list so a serial appearing more than once
  // (same physical card provisioned under two different names/units) surfaces as "Repetido".
  const buildGuaritaDeviceRegsReportRows = (personDeptUnitMap: Record<string, string>) => {
    const serialCounts = new Map<string, number>();
    guaritaDeviceRegs.forEach(r => serialCounts.set(r.serial_decimal, (serialCounts.get(r.serial_decimal) || 0) + 1));

    const receptorLabel = (mask?: number) => {
      if (!mask) return '—';
      const recs: number[] = [];
      for (let i = 0; i < 8; i++) if (mask & (1 << i)) recs.push(i + 1);
      return recs.length ? recs.map(r => `REC ${r}`).join(', ') : '—';
    };

    return guaritaDeviceRegs.map(reg => {
      const matchedTag = guaritaTags.find((t: any) => guaritaCardDecimal(t.tag_value) === reg.serial_decimal);
      const matchedVehicle = guaritaVehicles.find((v: any) => guaritaCardDecimal(v.rfid_tag || '') === reg.serial_decimal);
      const matchedPerson = matchedTag?.person || matchedVehicle?.person;
      const ownerName = matchedTag?.person
        ? `${matchedTag.person.first_name ?? ''} ${matchedTag.person.last_name ?? ''}`.trim()
        : matchedVehicle
          ? (matchedVehicle.person
              ? `${matchedVehicle.person.first_name ?? ''} ${matchedVehicle.person.last_name ?? ''}`.trim()
              : matchedVehicle.owner_name || matchedVehicle.plate || '')
          : (reg.device_type_name || 'Controle');
      const labelName = matchedTag?.label || reg.label || '';
      const deptUnit = matchedPerson ? personDeptUnitMap[matchedPerson.id] : undefined;
      const repetido = (serialCounts.get(reg.serial_decimal) || 0) > 1 ? 'Sim' : 'Não';
      return {
        serial: reg.serial_decimal || '—',
        owner: ownerName || '—',
        label: labelName || '—',
        receptor: receptorLabel(reg.receivers),
        empresa: deptUnit || '—',
        repetido,
      };
    });
  };

  const generateGuaritaDeviceRegsCSV = async () => {
    const personDeptUnitMap = await fetchPersonDeptUnitMap();
    const rows = buildGuaritaDeviceRegsReportRows(personDeptUnitMap);
    const headers = ['Nº de série', 'Dono do controle', 'Nome do controle', 'Receptor', 'Departamento/Unidade', 'Repetido'];
    const escapeCSVValue = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };
    const csvLines = [
      headers.map(escapeCSVValue).join(','),
      ...rows.map(r => [r.serial, r.owner, r.label, r.receptor, r.empresa, r.repetido].map(escapeCSVValue).join(',')),
    ];
    const blob = new Blob(['﻿' + csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tags-dispositivo-${(device.name || 'dispositivo').replace(/\s+/g, '-').toLowerCase()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateGuaritaDeviceRegsPDF = async () => {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const personDeptUnitMap = await fetchPersonDeptUnitMap();
    const rows = buildGuaritaDeviceRegsReportRows(personDeptUnitMap);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    let y = margin;

    try {
      const logoImg = new Image();
      logoImg.src = '/panther-logo.png';
      await new Promise<void>((resolve) => {
        logoImg.onload = () => { try { doc.addImage(logoImg, 'PNG', margin, 6, 20, 20); } catch {} resolve(); };
        logoImg.onerror = () => resolve();
        setTimeout(() => resolve(), 2000);
      });
    } catch {}

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text('ACCESS', margin + 23, 20);

    doc.setLineWidth(0.5);
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, 30, pageW - margin, 30);

    y = 38;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text('Tags no Dispositivo', margin, y);

    y += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Dispositivo: ${device.name || '—'}`, margin, y);
    if (zone?.name) {
      doc.text(`Zona: ${zone.name}`, margin + 80, y);
    }
    const dateStr = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    doc.text(dateStr, pageW - margin, y, { align: 'right' });
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Nº de série', 'Dono do controle', 'Nome do controle', 'Receptor', 'Departamento/Unidade', 'Repetido']],
      body: rows.map(r => [r.serial, r.owner, r.label, r.receptor, r.empresa, r.repetido]),
      margin: { left: margin, right: margin },
      headStyles: { fillColor: [50, 50, 50], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 60 },
        2: { cellWidth: 45 },
        3: { cellWidth: 35 },
        4: { cellWidth: 'auto' },
        5: { cellWidth: 22, halign: 'center' },
      },
      didParseCell: (data: any) => {
        if (data.section === 'body' && data.column.index === 5) {
          data.cell.styles.textColor = data.cell.raw === 'Sim' ? [185, 28, 28] : [100, 100, 100];
          if (data.cell.raw === 'Sim') data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(160, 160, 160);
      doc.text(`Página ${i} de ${totalPages}`, pageW - margin, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
      doc.text('Panther Access', margin, doc.internal.pageSize.getHeight() - 6);
    }

    doc.save(`tags-dispositivo-${(device.name || 'dispositivo').replace(/\s+/g, '-').toLowerCase()}.pdf`);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 border-b bg-gradient-to-r from-gray-900 to-black text-white flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold truncate" title={device.name}>
              {device.name?.replace(/[\n\r]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Dispositivo sem nome'}
            </h2>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span className="text-xs text-gray-400 bg-white/10 px-2 py-0.5 rounded">
                {translateDeviceType(device.device_type)}
              </span>
              {zone && (
                <span className="text-xs text-gray-400 bg-white/10 px-2 py-0.5 rounded flex items-center gap-1">
                  <MapPin className="w-3 h-3" />{zone.name}
                </span>
              )}
              {liveHealth?.checkedAt && effectiveStatus === 'online' && liveHealth.latency !== undefined && (
                <span className="text-xs text-gray-500">{liveHealth.latency}ms</span>
              )}
            </div>
          </div>
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
            effectiveStatus === 'online' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {effectiveStatus === 'online' ? <Activity className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {translateStatus(effectiveStatus)}
          </span>
          <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1.5 transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body: left nav + content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left navigation */}
          <nav className="w-44 flex-shrink-0 border-r border-gray-100 bg-gray-50 py-3">
            {([
              { key: 'info' as const, label: 'Informações' },
              {
                key: 'people' as const,
                label: isGuaritaModalDevice
                  ? 'Tags Autorizadas'
                  : isVehicleListDevice(device.device_type)
                  ? 'Veículos Autorizados'
                  : 'Pessoas Autorizadas',
              },
              { key: 'events' as const, label: 'Eventos' },
              { key: 'sync' as const, label: 'Sincronização' },
            ] as { key: typeof activeTab; label: string }[]).map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`w-full text-left px-5 py-2.5 text-sm transition-colors ${
                  activeTab === key
                    ? 'bg-white text-gray-900 font-semibold border-r-2 border-gray-900'
                    : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>

          {/* Tab Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
          {activeTab === 'people' && (
            <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <span className="text-sm font-semibold text-gray-700">
                {isGuaritaModalDevice
                  ? 'Tags Autorizadas'
                  : isVehicleListDevice(device.device_type)
                  ? 'Veículos Autorizados'
                  : 'Pessoas Autorizadas'}
              </span>
              {isVehicleListDevice(device.device_type) && !isLoadingVehicles && authorizedVehicles.filter((v: any) => !v.is_unauthorized).length > 0 && (
                <span className="text-xs text-gray-400">{authorizedVehicles.filter((v: any) => !v.is_unauthorized).length} cadastrados</span>
              )}
              {!isGuaritaModalDevice && !isVehicleListDevice(device.device_type) && !isLoadingPeople && (
                <div className="flex items-center gap-3">
                  {authorizedPeople.length > 0 && (
                    <span className="text-xs text-gray-400">{authorizedPeople.length} autorizados</span>
                  )}
                  {authorizedPeople.length > 0 && (
                    <button
                      onClick={generateAuthorizedPeoplePDF}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-300 transition-colors shadow-sm"
                    >
                      <Printer className="w-3.5 h-3.5" />
                      Exportar PDF
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {activeTab === 'events' && (
            <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div>
                <span className="text-sm font-semibold text-gray-700">Eventos Recentes</span>
                {accessEventsTotal > 0 && <span className="text-xs text-gray-400 ml-2">{accessEventsTotal} total</span>}
              </div>
              <button
                onClick={() => fetchAccessEvents(1, accessEventsLimit)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Atualizar eventos"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {activeTab === 'sync' && (
            <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <span className="text-sm font-semibold text-gray-700">Sincronização</span>
              <button
                onClick={handleForceSync}
                disabled={isSyncing}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? 'Sincronizando...' : 'Forçar Sincronização'}
              </button>
            </div>
          )}
          {activeTab === 'info' && (
            <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <span className="text-sm font-semibold text-gray-700">Informações Básicas</span>
              <div className="flex gap-2">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    Editar
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => { setIsEditing(false); setEditedDevice(device); }}
                      className="px-2.5 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSaveDevice}
                      disabled={isSaving}
                      className="px-2.5 py-1.5 text-xs bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-60"
                    >
                      {isSaving ? 'Salvando...' : 'Salvar'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-6">
          {/* Info Tab */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div>

                {/* Integration section */}
                {device?.id && (
                  <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Integração</h4>
                    </div>
                    <dl>
                      {/* Device ID */}
                      <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                        <dt className="text-xs text-gray-500 w-24 flex-shrink-0">ID</dt>
                        <dd className="flex-1 flex items-center gap-1.5 min-w-0">
                          <code className="flex-1 text-xs font-mono text-gray-600 truncate">{device.id}</code>
                          <button type="button" onClick={() => { navigator.clipboard.writeText(device.id || ''); toast.success('Copiado!'); }} className="p-1 text-gray-300 hover:text-gray-600 rounded transition-colors flex-shrink-0" title="Copiar">
                            <Copy className="w-3 h-3" />
                          </button>
                        </dd>
                      </div>

                      {/* Hikvision */}
                      {device.manufacturer?.toLowerCase().includes('hikvision') && (<>
                        <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                          <dt className="text-xs text-gray-500 w-24 flex-shrink-0">Webhook</dt>
                          <dd className="flex-1 flex items-center gap-1.5 min-w-0">
                            <code className="flex-1 text-xs font-mono text-gray-600 truncate" title={getHikWebhookUrl(device.model, device.id)}>{getHikWebhookUrl(device.model, device.id)}</code>
                            <button type="button" onClick={() => { navigator.clipboard.writeText(getHikWebhookUrl(device.model, device.id)); toast.success('Copiado!'); }} className="p-1 text-gray-300 hover:text-gray-600 rounded transition-colors flex-shrink-0">
                              <Copy className="w-3 h-3" />
                            </button>
                          </dd>
                        </div>
                        <div className="flex items-center px-4 py-2.5 gap-2">
                          <button type="button" onClick={handleConfigureWebhook} disabled={isConfiguringWebhook} className="px-2.5 py-1 text-xs font-medium border border-gray-200 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-50 transition-colors">
                            {isConfiguringWebhook ? 'Configurando...' : 'Configurar webhook'}
                          </button>
                          <button type="button" onClick={handleReadHikvisionConfig} disabled={isReadingHikvisionConfig} className="px-2.5 py-1 text-xs font-medium border border-gray-200 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-50 transition-colors">
                            {isReadingHikvisionConfig ? 'Lendo...' : 'Ler configuração'}
                          </button>
                        </div>
                        {hikConfigStatus && (
                          <div className={`mx-4 mb-3 rounded-lg border p-3 ${hikConfigStatus.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-1.5">
                                {hikConfigStatus.ok
                                  ? <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                  : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                                <span className={`text-xs font-semibold ${hikConfigStatus.ok ? 'text-green-700' : 'text-red-700'}`}>
                                  {hikConfigStatus.ok ? 'Configuração correta' : 'Configuração incorreta'}
                                </span>
                              </div>
                              <button type="button" onClick={() => setHikConfigStatus(null)} className="text-gray-300 hover:text-gray-500 transition-colors">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Protocolo</p>
                                <p className={`text-xs font-mono font-medium ${hikConfigStatus.protocolOk ? 'text-green-700' : 'text-red-600'}`}>{hikConfigStatus.protocol}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Host</p>
                                <p className={`text-xs font-mono font-medium truncate ${hikConfigStatus.hostOk ? 'text-green-700' : 'text-red-600'}`} title={hikConfigStatus.host}>{hikConfigStatus.host}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Porta</p>
                                <p className={`text-xs font-mono font-medium ${hikConfigStatus.portOk ? 'text-green-700' : 'text-red-600'}`}>{hikConfigStatus.port}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </>)}

                      {/* ControlID */}
                      {device.manufacturer?.toLowerCase() === 'controlid' && (<>
                        <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                          <dt className="text-xs text-gray-500 w-24 flex-shrink-0">Webhook</dt>
                          <dd className="flex-1 flex items-center gap-1.5 min-w-0">
                            {/* Derived from the same helper the monitor is written with, so the
                                URL shown cannot describe a transport nobody configured. */}
                            <code className="flex-1 text-xs font-mono text-gray-600 truncate" title={controlIdWebhookUrl(device.id)}>
                              {controlIdWebhookUrl(device.id)}
                            </code>
                            <button type="button" onClick={() => { navigator.clipboard.writeText(controlIdWebhookUrl(device.id)); toast.success('Copiado!'); }} className="p-1 text-gray-300 hover:text-gray-600 rounded transition-colors flex-shrink-0">
                              <Copy className="w-3 h-3" />
                            </button>
                          </dd>
                        </div>
                        <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                          <dt className="text-xs text-gray-500 w-24 flex-shrink-0">Monitor</dt>
                          <dd className="flex-1 flex items-center justify-between gap-2">
                            {/* Three distinct states. The old row collapsed them into one
                                permanent "Não configurado" because nothing ever read the
                                device — so an operator could not tell an unconfigured board
                                from one nobody had checked. */}
                            {monitorCheck === null ? (
                              <span className="text-xs text-gray-400">Não verificado</span>
                            ) : monitorCheck.ok ? (
                              <span className="text-xs text-green-600 font-medium">
                                {monitorCheck.actual?.hostname}
                              </span>
                            ) : monitorCheck.configured ? (
                              <span className="text-xs text-red-600 font-medium truncate" title={`${monitorCheck.actual?.hostname}:${monitorCheck.actual?.port}/${monitorCheck.actual?.path}`}>
                                Outro destino: {monitorCheck.actual?.hostname || '—'}
                              </span>
                            ) : (
                              <span className="text-xs text-red-600 font-medium">
                                Não configurado — sem eventos
                              </span>
                            )}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button type="button" onClick={handleReadMonitor} disabled={isReadingMonitor} className="px-2.5 py-1 text-xs font-medium border border-gray-200 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-50 transition-colors">
                                {isReadingMonitor ? 'Lendo...' : 'Ler configuração'}
                              </button>
                              <button type="button" onClick={handleConfigureMonitor} disabled={isConfiguringMonitor} className="px-2.5 py-1 text-xs font-medium border border-gray-200 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-50 transition-colors">
                                {isConfiguringMonitor ? 'Configurando...' : 'Configurar'}
                              </button>
                            </div>
                          </dd>
                        </div>

                        {/* Why no events arrive, stated plainly — this is the single most common
                            cause of an Online ControlID board with an empty event list. */}
                        {monitorCheck && !monitorCheck.ok && (
                          <div className="mx-4 mb-3 mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
                            <div className="flex items-start gap-1.5">
                              <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-px" />
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-red-700 mb-1">
                                  {monitorCheck.configured
                                    ? 'Monitor aponta para outro destino'
                                    : 'Monitor não configurado'}
                                </p>
                                <p className="text-[11px] text-red-700 leading-snug">
                                  Este dispositivo só envia eventos para o endereço gravado no
                                  "modo monitor". {monitorCheck.configured
                                    ? 'Como ele está gravado para outro endereço, os eventos não chegam aqui.'
                                    : 'Sem esse endereço ele fica Online e sincroniza normalmente, mas nunca envia evento algum.'}
                                  {' '}Use <strong>Configurar</strong> para apontá-lo para o Panther.
                                </p>
                                {monitorCheck.configured && (
                                  <p className="text-[10px] font-mono text-red-600 mt-1 truncate">
                                    {monitorCheck.actual?.hostname}:{monitorCheck.actual?.port}/{monitorCheck.actual?.path}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </>)}
                    </dl>
                  </div>
                )}

                {/* Dispositivo */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dispositivo</span>
                  </div>
                  <dl>
                    <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                      <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Nome</dt>
                      <dd className="flex-1 text-sm text-gray-900 font-medium">
                        {isEditing ? (
                          <input type="text" value={editedDevice.name} onChange={(e) => setEditedDevice({ ...editedDevice, name: e.target.value })}
                            className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13]" />
                        ) : (
                          <span className="truncate block" title={device.name}>
                            {device.name?.replace(/[\n\r]+/g, ' ').replace(/\s+/g, ' ').trim() || '—'}
                          </span>
                        )}
                      </dd>
                    </div>
                    {(isEditing ? editedDevice.device_type : device.device_type) === 'catraca' && (
                    <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                      <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Modo catraca</dt>
                      <dd className="flex-1 text-sm text-gray-900 font-medium">
                        {isEditing ? (
                          <select
                            value={editedDevice.config?.is_catraca === false ? 'passagem' : 'entrada_saida'}
                            onChange={(e) => setEditedDevice({
                              ...editedDevice,
                              config: { ...editedDevice.config, is_catraca: e.target.value !== 'passagem' }
                            })}
                            className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13]"
                          >
                            <option value="entrada_saida">Entrada e Saída (por portal)</option>
                            <option value="passagem">Somente Passagem</option>
                          </select>
                        ) : (
                          device.config?.is_catraca === false ? 'Somente Passagem' : 'Entrada e Saída'
                        )}
                      </dd>
                    </div>
                    )}
                    <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                      <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Status</dt>
                      <dd className="flex-1 flex items-center gap-2">
                        <StatusDot status={effectiveStatus} />
                        <span className={`text-sm font-medium ${effectiveStatus === 'online' ? 'text-green-700' : effectiveStatus === 'offline' ? 'text-red-700' : effectiveStatus === 'web_unavailable' ? 'text-gray-400' : 'text-gray-900'}`}>
                          {effectiveStatus === 'web_unavailable' ? 'Indisponível no navegador' : translateStatus(effectiveStatus)}
                        </span>
                        {liveHealth?.checkedAt && (
                          <span className="text-xs text-gray-400">
                            {liveHealth.latency ? `${liveHealth.latency}ms` : 'tempo real'}
                          </span>
                        )}
                      </dd>
                    </div>
                    {!isGuaritaModalDevice && (
                    <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                      <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Endereço público</dt>
                      <dd className="flex-1 flex items-center gap-1.5 min-w-0 font-mono">
                        {isEditing ? (
                          <input type="text" value={editedDevice.ip_address || ''} onChange={(e) => setEditedDevice({ ...editedDevice, ip_address: sanitizeDeviceAddress(e.target.value) })}
                            className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13]" placeholder="https://192.168.1.100" />
                        ) : (
                          <>
                            <span className="truncate text-xs text-gray-900">{device.ip_address || '—'}</span>
                            {device.ip_address && (
                              <a href={ensureHttpScheme(device.ip_address)} target="_blank" rel="noopener noreferrer"
                                className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                                title={`Abrir ${device.ip_address}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </>
                        )}
                      </dd>
                    </div>
                    )}
                    {!isGuaritaModalDevice && (
                    <div className="flex items-center px-4 py-2.5">
                      <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Endereço privado</dt>
                      <dd className="flex-1 flex items-center gap-1.5 min-w-0 font-mono">
                        {isEditing ? (
                          <input type="text" value={editedDevice.local_ip_address || ''} onChange={(e) => setEditedDevice({ ...editedDevice, local_ip_address: sanitizeDeviceAddress(e.target.value) })}
                            className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13]" placeholder="10.0.0.50 (opcional)" />
                        ) : (
                          <>
                            <span className="truncate text-xs text-gray-900">{device.local_ip_address || '—'}</span>
                            {device.local_ip_address && (
                              <a href={ensureHttpScheme(device.local_ip_address)} target="_blank" rel="noopener noreferrer"
                                className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                                title={`Abrir ${device.local_ip_address}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </>
                        )}
                      </dd>
                    </div>
                    )}
                  </dl>
                </div>
              </div>

              {/* Authentication — hidden for Guarita */}
              {!isGuaritaModalDevice && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Autenticação</span>
                </div>
                <dl>
                  <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                    <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Usuário</dt>
                    <dd className="flex-1 text-sm text-gray-900 font-medium">
                      {isEditing ? (
                        <input type="text" value={editedDevice.username || ''} onChange={(e) => setEditedDevice({ ...editedDevice, username: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13]" placeholder="admin" />
                      ) : (
                        device.username || '—'
                      )}
                    </dd>
                  </div>
                  <div className="flex items-center px-4 py-2.5">
                    <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Senha</dt>
                    <dd className="flex-1 text-sm text-gray-900 font-medium">
                      {isEditing ? (
                        <input type="password" value={editedDevice.password || ''} onChange={(e) => setEditedDevice({ ...editedDevice, password: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13]" placeholder="••••••••" />
                      ) : (
                        <span className="tracking-widest text-gray-400">••••••••</span>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
              )}

              {/* Hardware */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Hardware</span>
                  <div className="flex items-center gap-2">
                  {!isEditing && canTcpProbe() && (
                    autoDetecting ? (
                      <span className="text-xs text-gray-400 flex items-center gap-1.5">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Detectando...
                      </span>
                    ) : detectedModel ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-xs font-medium text-green-700">
                        <CheckCircle className="w-3 h-3" />
                        {detectedModel}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={autoDetect}
                        title="Reler hardware"
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    )
                  )}
                  </div>
                </div>
                <dl>
                  <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                    <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Tipo</dt>
                    <dd className="flex-1 text-sm text-gray-900 font-medium">
                      {isGuaritaModalDevice ? (
                        'Guarita IP'
                      ) : isEditing ? (
                        <div className="flex rounded overflow-hidden border border-gray-200 w-fit">
                          {([
                            { value: 'facial_recognition', label: 'Facial' },
                            { value: 'rfid_reader', label: 'RFID' },
                            { value: 'catraca', label: 'Catraca' },
                            { value: 'fingerprint', label: 'Digital' },
                            { value: 'lpr', label: 'LPR' },
                          ] as const).map(({ value, label }, i) => {
                            const current = editedDevice.device_type === 'facial_reader' ? 'facial_recognition'
                              : editedDevice.device_type === 'controlid_rfid' ? 'rfid_reader'
                              : editedDevice.device_type;
                            return (
                              <button
                                key={value}
                                type="button"
                                onClick={() => setEditedDevice({ ...editedDevice, device_type: value })}
                                className={`px-2.5 py-1 text-xs font-medium transition-colors ${i > 0 ? 'border-l border-gray-200' : ''} ${current === value ? 'bg-gray-800 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        translateDeviceType(device.device_type)
                      )}
                    </dd>
                  </div>
                  <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                    <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Fabricante</dt>
                    <dd className="flex-1 text-sm text-gray-900 font-medium">
                      {isEditing ? (
                        <select value={editedDevice.manufacturer || ''} onChange={(e) => setEditedDevice({ ...editedDevice, manufacturer: e.target.value, model: '' })}
                          className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13]" disabled={isLoadingModels}>
                          <option value="">Selecione</option>
                          {vendors.map((vendor) => (
                            <option key={vendor} value={vendor.toLowerCase()}>{formatManufacturer(vendor)}</option>
                          ))}
                        </select>
                      ) : (
                        formatManufacturer(editedDevice.manufacturer) || <span className="text-gray-400 text-xs">auto-detectar</span>
                      )}
                    </dd>
                  </div>
                  <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                    <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Modelo</dt>
                    <dd className="flex-1 text-sm text-gray-900 font-medium">
                      {isEditing ? (
                        <select value={editedDevice.model || ''} onChange={(e) => setEditedDevice({ ...editedDevice, model: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13]" disabled={!editedDevice.manufacturer || isLoadingModels}>
                          <option value="">Selecione</option>
                          {deviceModels.filter((m) => {
                            if (m.vendor.toLowerCase() !== editedDevice.manufacturer?.toLowerCase()) return false;
                            const isLPRType = editedDevice.device_type === 'lpr';
                            const isLPRModel = m.device_type === 'lpr';
                            return isLPRType ? isLPRModel : !isLPRModel;
                          }).map((m) => (
                            <option key={`${m.vendor}-${m.code}`} value={m.code}>{m.name}</option>
                          ))}
                        </select>
                      ) : (
                        editedDevice.model || <span className="text-gray-400 text-xs">auto-detectar</span>
                      )}
                    </dd>
                  </div>
                  {!isGuaritaModalDevice && (
                  <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                    <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Série</dt>
                    <dd className="flex-1 text-sm text-gray-900 font-medium">
                      {editedDevice.serial_number || <span className="text-gray-400 text-xs">auto-detectar</span>}
                    </dd>
                  </div>
                  )}
                  {!isGuaritaModalDevice && (
                  <div className="flex items-center px-4 py-2.5">
                    <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Firmware</dt>
                    <dd className="flex-1 text-sm text-gray-900 font-medium">
                      {editedDevice.firmware_version || <span className="text-gray-400 text-xs">auto-detectar</span>}
                    </dd>
                  </div>
                  )}
                </dl>
              </div>

              {/* Guarita-specific fields */}
              {isGuaritaModalDevice && device.config && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Guarita</span>
                  </div>
                  <dl>
                    {device.config.guarita_integration_id && (
                      <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                        <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Módulo</dt>
                        <dd className="flex-1 text-sm text-gray-900 font-medium">
                          {guaritaIntegrationsModal.find(i => i.id === device.config!.guarita_integration_id)?.name
                            || <span className="text-gray-400 text-xs font-mono">{device.config.guarita_integration_id}</span>}
                        </dd>
                      </div>
                    )}
                    <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                      <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Receptor</dt>
                      <dd className="flex-1 text-sm text-gray-900 font-medium">
                        {isEditing ? (
                          <input
                            type="number"
                            min={1}
                            max={8}
                            value={editedDevice.config?.guarita_receptor ?? device.config.guarita_receptor ?? 1}
                            onChange={(e) => setEditedDevice({ ...editedDevice, config: { ...editedDevice.config, guarita_receptor: Math.max(1, Math.min(8, parseInt(e.target.value) || 1)) } })}
                            className="w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13] text-center"
                          />
                        ) : (device.config.guarita_receptor ?? '—')}
                      </dd>
                    </div>
                    <div className="flex items-center px-4 py-2.5">
                      <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Saída</dt>
                      <dd className="flex-1 text-sm text-gray-900 font-medium">
                        {isEditing ? (
                          <input
                            type="number"
                            min={1}
                            max={8}
                            value={editedDevice.config?.guarita_saida ?? device.config.guarita_saida ?? 1}
                            onChange={(e) => setEditedDevice({ ...editedDevice, config: { ...editedDevice.config, guarita_saida: Math.max(1, Math.min(8, parseInt(e.target.value) || 1)) } })}
                            className="w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13] text-center"
                          />
                        ) : (device.config.guarita_saida ?? '—')}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}

              {/* Guarita — Vehicle Whitelist */}
              {isGuaritaModalDevice && device.config?.guarita_integration_id && (() => {
                const integration = guaritaIntegrationsModal.find(i => i.id === device.config!.guarita_integration_id);
                const integrationId = device.config.guarita_integration_id as string;
                const isEnabled = integration?.vehicle_whitelist_enabled ?? false;

                const handleToggle = async () => {
                  try {
                    const updated = await updateGuaritaIntegration(integrationId, { vehicle_whitelist_enabled: !isEnabled });
                    setGuaritaIntegrationsModal(prev => prev.map(i => i.id === integrationId ? { ...i, ...updated } : i));
                    toast.success(updated.vehicle_whitelist_enabled ? 'Lista de veículos ativada' : 'Lista de veículos desativada');
                  } catch (e: any) {
                    toast.error(e.message || 'Erro ao atualizar');
                  }
                };

                return (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                      <Car className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Lista de Veículos</span>
                    </div>
                    {/* Toggle row */}
                    <div className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-800">Habilitar whitelist de veículos</p>
                        <p className="text-xs text-gray-400 mt-0.5">Permite sincronizar tags RFID de veículos ativos nesta Guarita</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-xs font-semibold ${isEnabled ? 'text-[#96bf13]' : 'text-gray-400'}`}>
                          {isEnabled ? 'SIM' : 'NÃO'}
                        </span>
                        <button
                          type="button"
                          onClick={handleToggle}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${isEnabled ? 'bg-[#96bf13]' : 'bg-gray-300'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${isEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ControlID — sec_box + saída config — shown only in edit mode */}
              {isEditing && (editedDevice.manufacturer?.toLowerCase().includes('control') || editedDevice.device_type === 'controlid_rfid' || editedDevice.device_type === 'controlid_face') && (() => {
                // iDUHF/iDBox terminals (device_type=controlid_rfid) default to the "door" action
                // instead of a sec_box relay, since most don't have one wired up — but some
                // installations DO wire an iDUHF through a sec_box relay box, so the toggle must
                // stay available for RFID devices too, not hidden outright. isRFID only changes
                // the DEFAULT when use_sec_box hasn't been explicitly set, preserving existing
                // devices' current behavior. Mirrors deviceCommunication.ts openControlIDDoor().
                const isRFID = editedDevice.device_type === 'controlid_rfid' || editedDevice.device_type === 'rfid_reader';
                const isCatraca = editedDevice.device_type === 'catraca';
                const useSecBox = editedDevice.config?.use_sec_box ?? !isRFID;
                const catracaAllow: 'both' | 'clockwise' | 'anticlockwise' = editedDevice.config?.catraca_allow ?? 'both';

                return (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">ControlID — Acionamento</span>
                    </div>
                    {isCatraca ? (
                      // Turnstile devices actuate via the native "catra" action (execute_actions.fcgi),
                      // NOT sec_box — see openControlIDDoor() in deviceCommunication.ts.
                      <div className="flex items-center px-4 py-2.5">
                        <div className="flex-1">
                          <span className="text-xs text-gray-700 font-medium">Sentido</span>
                          <p className="text-xs text-gray-400">Aciona via execute_actions.fcgi ação "catra" (allow=clockwise/anticlockwise/both)</p>
                        </div>
                        <select
                          value={catracaAllow}
                          onChange={(e) => setEditedDevice({ ...editedDevice, config: { ...editedDevice.config, catraca_allow: e.target.value } })}
                          className="px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13]"
                        >
                          <option value="both">Ambos</option>
                          <option value="clockwise">Horário</option>
                          <option value="anticlockwise">Anti-horário</option>
                        </select>
                      </div>
                    ) : (
                      <>
                        {/* Toggle: Usar sec_box. Note: for face devices (iDFace/iDFlex) this is
                            display-only — openControlIDDoor() always actuates via sec_box for
                            non-RFID devices regardless of this flag, since they have no alternative
                            "door" action. Toggling it off here won't disable door-open. */}
                        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                          <div>
                            <span className="text-xs text-gray-700 font-medium">Usar sec_box</span>
                            <p className="text-xs text-gray-400">Aciona via execute_actions.fcgi (iDFace, iDFlex, ou iDUHF/iDBox com relé sec_box cabeado)</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setEditedDevice({ ...editedDevice, config: { ...editedDevice.config, use_sec_box: !useSecBox } })}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${useSecBox ? 'bg-[#96bf13]' : 'bg-gray-300'}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${useSecBox ? 'translate-x-4' : 'translate-x-0'}`} />
                          </button>
                        </div>
                        {useSecBox ? (
                          <div className="flex items-center px-4 py-2.5">
                            <div className="flex-1">
                              <span className="text-xs text-gray-700 font-medium">Saída (relé local)</span>
                              <p className="text-xs text-gray-400">Relé interno do dispositivo. Não é o endereço RS485. Primário = 1.</p>
                            </div>
                            <input
                              type="number"
                              min={1}
                              max={100}
                              value={editedDevice.config?.controlid_saida ?? 1}
                              onChange={(e) => setEditedDevice({ ...editedDevice, config: { ...editedDevice.config, controlid_saida: Math.max(1, Math.min(100, parseInt(e.target.value) || 1)) } })}
                              className="w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13] text-center"
                            />
                          </div>
                        ) : isRFID && (
                          <div className="flex items-center px-4 py-2.5">
                            <div className="flex-1">
                              <span className="text-xs text-gray-700 font-medium">Saída (output)</span>
                              <p className="text-xs text-gray-400">Aciona via execute_actions.fcgi ação "door" (iDUHF, iDBox). Não usa sec_box.</p>
                            </div>
                            <input
                              type="number"
                              min={1}
                              max={100}
                              value={editedDevice.config?.controlid_door_number ?? 1}
                              onChange={(e) => setEditedDevice({ ...editedDevice, config: { ...editedDevice.config, controlid_door_number: Math.max(1, Math.min(100, parseInt(e.target.value) || 1)) } })}
                              className="w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#96bf13] text-center"
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Webhook URL (when set) */}
              {!isEditing && device.webhook_url && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Webhook</span>
                  </div>
                  <div className="px-4 py-3 flex items-center gap-2">
                    <code className="flex-1 text-xs font-mono text-gray-700 bg-gray-50 px-2.5 py-1.5 rounded border border-gray-200 break-all">{device.webhook_url}</code>
                    <button onClick={() => { navigator.clipboard.writeText(device.webhook_url); toast.success('Copiado!'); }}
                      className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors flex-shrink-0" title="Copiar URL">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Controls */}
              {!isEditing && device.device_type !== 'camera' && device.device_type !== 'sensor' && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Controles</span>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Acionamento de porta</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {(() => {
                          const mfr = device.manufacturer?.toLowerCase() ?? '';
                          const isCtrl = mfr.includes('control') || mfr.includes('idface') || mfr.includes('idbox') ||
                            device.device_type === 'controlid_face' || device.device_type === 'controlid_rfid';
                          if (!isCtrl) return 'Abre remotamente a porta conectada a este dispositivo';
                          const isRFID = device.device_type === 'controlid_rfid' || device.device_type === 'rfid_reader';
                          // Face devices always actuate via sec_box — see openControlIDDoor().
                          const useSecBox = !isRFID || (device.config?.use_sec_box ?? false);
                          if (!useSecBox) {
                            const doorNo = device.config?.controlid_door_number ?? 1;
                            return `door · porta ${doorNo}`;
                          }
                          const saida = device.config?.controlid_saida ?? 1;
                          return `sec_box · saída ${saida} · id=${controlIDSecBoxId(saida)}`;
                        })()}
                      </p>
                    </div>
                    <button
                      onClick={handleTriggerDoor}
                      disabled={isTriggering}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#96bf13] text-white rounded-lg hover:bg-[#82a810] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    >
                      <DoorOpen className={`w-3.5 h-3.5 ${isTriggering ? 'animate-pulse' : ''}`} />
                      {isTriggering ? 'Acionando...' : 'Acionar'}
                    </button>
                  </div>
                </div>
              )}

              {/* Activity + Timestamps */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Atividade</span>
                </div>
                <dl>
                  <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                    <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Último heartbeat</dt>
                    <dd className="flex-1 text-sm text-gray-900 font-medium">
                      {device.last_heartbeat ? new Date(device.last_heartbeat).toLocaleString('pt-BR') : <span className="text-gray-400 text-xs">Nunca</span>}
                    </dd>
                  </div>
                  <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                    <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Criado em</dt>
                    <dd className="flex-1 text-sm text-gray-900 font-medium">
                      {device.created_at ? new Date(device.created_at).toLocaleString('pt-BR') : '—'}
                    </dd>
                  </div>
                  <div className="flex items-center px-4 py-2.5">
                    <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Atualizado em</dt>
                    <dd className="flex-1 text-sm text-gray-900 font-medium">
                      {device.updated_at ? new Date(device.updated_at).toLocaleString('pt-BR') : '—'}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {/* People Tab */}
          {activeTab === 'people' && (
            <div className="space-y-6">
              {/* Guarita: Tags de Acesso + Tags no Dispositivo */}
              {isGuaritaModalDevice && (() => {
                const guaritaIntegration = guaritaIntegrationsModal.find(i => i.id === device.config?.guarita_integration_id);
                const vehicleWhitelistEnabled = guaritaIntegration?.vehicle_whitelist_enabled ?? false;

                // Pre-compute enriched device regs (deduplicated + sorted)
                const seenSerials = new Set<string>();
                const enrichedRegs = guaritaDeviceRegs
                  .filter(reg => { if (seenSerials.has(reg.serial_decimal)) return false; seenSerials.add(reg.serial_decimal); return true; })
                  .map(reg => {
                    const matchedTag = guaritaTags.find((t: any) => guaritaCardDecimal(t.tag_value) === reg.serial_decimal);
                    const matchedVehicle = vehicleWhitelistEnabled
                      ? guaritaVehicles.find((v: any) => guaritaCardDecimal(v.rfid_tag || '') === reg.serial_decimal)
                      : undefined;
                    const isInSystem = !!matchedTag;
                    const isVehicle = !isInSystem && !!matchedVehicle;
                    // Must also have a linked person — matches "Tags de Acesso"'s sysAuthorized
                    // definition (t.is_active !== false && t.person). Without this, an active
                    // but unlinked tag (e.g. a device's own built-in card) showed "Autorizado"
                    // here while correctly showing "Não autorizado" in Tags de Acesso.
                    const isActive = isInSystem && matchedTag?.is_active !== false && !!matchedTag?.person;
                    // 0=active person, 1=vehicle, 2=inactive person, 3=unknown
                    const sortWeight = isActive ? 0 : isVehicle ? 1 : isInSystem ? 2 : 3;
                    // Owner name: the person/vehicle this tag belongs to — empty for unlinked
                    // "Controle" entries so they cluster together instead of interleaving
                    // with real names.
                    const ownerName = matchedTag?.person
                      ? `${matchedTag.person.first_name ?? ''} ${matchedTag.person.last_name ?? ''}`.trim()
                      : isVehicle
                        ? (matchedVehicle.plate || '')
                        : '';
                    // Control/label name: the physical remote's own label (e.g. "CONTROLE 18",
                    // "C42", "00004") — independent of whether it's linked to a person.
                    const labelName = matchedTag?.label || reg.label || '';
                    // Does this registration actually grant access via THIS device's receptor?
                    const deviceReceptorForCover = device?.config?.guarita_receptor ?? 0;
                    const coversReceptor = reg.receivers === undefined || deviceReceptorForCover === 0 ||
                      (reg.receivers & (1 << (deviceReceptorForCover - 1))) !== 0;
                    return { reg, matchedTag, matchedVehicle, isInSystem, isVehicle, isActive, sortWeight, ownerName, labelName, coversReceptor };
                  })
                  .sort((a, b) => {
                    switch (guaritaDeviceRegSort) {
                      case 'owner-asc': return a.ownerName.localeCompare(b.ownerName, 'pt-BR') || a.sortWeight - b.sortWeight;
                      case 'owner-desc': return b.ownerName.localeCompare(a.ownerName, 'pt-BR') || a.sortWeight - b.sortWeight;
                      case 'label-asc': return a.labelName.localeCompare(b.labelName, 'pt-BR', { numeric: true }) || a.sortWeight - b.sortWeight;
                      case 'label-desc': return b.labelName.localeCompare(a.labelName, 'pt-BR', { numeric: true }) || a.sortWeight - b.sortWeight;
                      case 'serial-asc': return (Number(a.reg.serial_decimal) || 0) - (Number(b.reg.serial_decimal) || 0);
                      case 'serial-desc': return (Number(b.reg.serial_decimal) || 0) - (Number(a.reg.serial_decimal) || 0);
                      default: return a.sortWeight - b.sortWeight;
                    }
                  });

                const devTotal = enrichedRegs.length;
                const devAuthorized = enrichedRegs.filter(e => e.isActive).length;
                const devVehicles = enrichedRegs.filter(e => e.isVehicle).length;
                const devNotRegistered = enrichedRegs.filter(e => !e.isInSystem && !e.isVehicle).length;
                const devOtherReceptor = enrichedRegs.filter(e => !e.coversReceptor).length;

                const sysAuthorized = guaritaTags.filter((t: any) => t.is_active !== false && t.person).length;
                const sysUnauthorized = guaritaTags.filter((t: any) => !(t.is_active !== false && t.person)).length;
                const sysConfiguredReceptor = device.config?.guarita_receptor ?? 1;
                const sysExpectedBit = 1 << (sysConfiguredReceptor - 1);
                const sysWrongReceptor = guaritaTags.filter((t: any) => {
                  const dec = guaritaCardDecimal(t.tag_value);
                  const dr = guaritaDeviceRegs.find(r => r.serial_decimal === dec);
                  return !!dr && (dr.receivers & sysExpectedBit) === 0;
                }).length;

                return (
                <div>
                  {/* Vehicle whitelist state indicator */}
                  {guaritaIntegration && (
                    <div className={`flex items-center gap-2 px-3 py-2 mb-3 rounded-lg text-xs ${guaritaIntegration.vehicle_whitelist_enabled ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                      <Car className={`w-3.5 h-3.5 flex-shrink-0 ${guaritaIntegration.vehicle_whitelist_enabled ? 'text-green-500' : 'text-gray-400'}`} />
                      {guaritaIntegration.vehicle_whitelist_enabled
                        ? 'Whitelist de veículos ativa — veículos com RFID também são enviados a esta Guarita'
                        : 'Whitelist de veículos desabilitada — apenas tags de pessoas são enviadas'}
                    </div>
                  )}

                  {/* Section 1: Tags de Acesso (system) */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900 flex-shrink-0">Tags de Acesso</h3>
                      {!isLoadingGuaritaTags && guaritaTags.length > 0 && (
                        <div className="relative flex-1 min-w-0">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                          <input
                            type="text"
                            value={guaritaTagSearch}
                            onChange={e => setGuaritaTagSearch(e.target.value)}
                            placeholder="Buscar por nome ou tag..."
                            className="w-full pl-7 pr-3 py-1 text-xs border border-gray-200 rounded-lg outline-none focus:border-blue-400 bg-white"
                          />
                        </div>
                      )}
                      <button
                        onClick={() => setHideWrongReceptorTags(v => !v)}
                        className={`text-xs px-2 py-1.5 rounded-lg border flex-shrink-0 whitespace-nowrap transition-colors ${
                          hideWrongReceptorTags ? 'text-blue-600 bg-blue-50 border-blue-200' : 'text-gray-500 bg-gray-50 border-gray-200 hover:text-gray-700'
                        }`}
                        title={`Este dispositivo usa REC ${device.config?.guarita_receptor ?? 1} — ocultar tags configuradas em outro receptor`}
                      >
                        {hideWrongReceptorTags ? 'Só REC ' + (device.config?.guarita_receptor ?? 1) : 'Ocultar receptor errado'}
                      </button>
                      <button onClick={fetchGuaritaTags} disabled={isLoadingGuaritaTags} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0">
                        <RefreshCw className={`w-4 h-4 ${isLoadingGuaritaTags ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                    {!isLoadingGuaritaTags && guaritaTags.length > 0 && (
                      <p className="text-xs text-gray-500 mb-3">
                        {guaritaTags.length} total
                        {sysAuthorized > 0 && <> · <span className="text-green-600">{sysAuthorized} autorizada{sysAuthorized !== 1 ? 's' : ''}</span></>}
                        {sysUnauthorized > 0 && <> · <span className="text-red-500">{sysUnauthorized} não autorizada{sysUnauthorized !== 1 ? 's' : ''}</span></>}
                        {hideWrongReceptorTags && sysWrongReceptor > 0 && (
                          <> · <span className="text-yellow-600">{sysWrongReceptor} oculta{sysWrongReceptor !== 1 ? 's' : ''} (receptor errado)</span></>
                        )}
                      </p>
                    )}
                    {isLoadingGuaritaTags ? (
                      <div className="text-center py-12">
                        <RefreshCw className="w-8 h-8 text-[#96bf13] animate-spin mx-auto mb-3" />
                        <p className="text-gray-600">Carregando tags...</p>
                      </div>
                    ) : guaritaTags.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-600">Nenhuma tag de acesso cadastrada</p>
                        <p className="text-sm text-gray-500 mt-1">Cadastre tags em Operacional → Cadastros</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {guaritaTags
                          .map((tag: any) => {
                            const tagDec = guaritaCardDecimal(tag.tag_value);
                            const deviceReg = guaritaDeviceRegs.find(r => r.serial_decimal === tagDec);
                            const isOnDevice = !!deviceReg;
                            const isAuthorized = tag.is_active !== false && !!tag.person;
                            const configuredReceptor = device.config?.guarita_receptor ?? 1;
                            const expectedBit = 1 << (configuredReceptor - 1);
                            const wrongReceptor = isOnDevice && deviceReg && (deviceReg.receivers & expectedBit) === 0;
                            return { tag, deviceReg, isOnDevice, isAuthorized, wrongReceptor };
                          })
                          .filter(({ tag, wrongReceptor }) => {
                            if (hideWrongReceptorTags && wrongReceptor) return false;
                            if (!guaritaTagSearch.trim()) return true;
                            const q = guaritaTagSearch.toLowerCase();
                            const name = `${tag.person?.first_name ?? ''} ${tag.person?.last_name ?? ''}`.toLowerCase();
                            return name.includes(q) || (tag.tag_value ?? '').toLowerCase().includes(q);
                          })
                          .sort((a, b) => {
                            const aAuth = a.tag.is_active !== false && !!a.tag.person ? 0 : 1;
                            const bAuth = b.tag.is_active !== false && !!b.tag.person ? 0 : 1;
                            return aAuth - bAuth;
                          })
                          .map(({ tag, deviceReg, isOnDevice, isAuthorized, wrongReceptor }) => {
                            return (
                              <div key={tag.id} className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${!isAuthorized ? 'bg-gray-50 opacity-60' : wrongReceptor ? 'bg-yellow-50 hover:bg-yellow-100' : 'bg-gray-50 hover:bg-gray-100'}`}>
                                <div className="w-9 h-9 rounded-full bg-[#96bf13]/10 flex items-center justify-center flex-shrink-0">
                                  <CreditCard className="w-4 h-4 text-[#96bf13]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-mono font-medium text-gray-900">{tag.tag_value}</p>
                                  <p className="text-xs text-gray-500">
                                    {tag.person ? (
                                      <button onClick={() => openPersonDetail(tag.person.id)} className="text-blue-600 hover:underline">
                                        {tag.person.first_name} {tag.person.last_name}
                                      </button>
                                    ) : <span className="italic">Sem pessoa vinculada</span>}
                                    {tag.label && <span className="ml-2 text-gray-400">· {tag.label}</span>}
                                  </p>
                                  <ReceptorDots mask={deviceReg?.receivers ?? 0} />
                                </div>
                                {isOnDevice && !wrongReceptor && (
                                  <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 bg-blue-100 text-blue-700">No dispositivo</span>
                                )}
                                {wrongReceptor && (
                                  <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 bg-yellow-100 text-yellow-700">Receptor errado</span>
                                )}
                                <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium ${isAuthorized ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                  {isAuthorized ? 'Autorizado' : 'Não autorizado'}
                                </span>
                                {isAuthorized && device.config?.guarita_integration_id && (
                                  <button onClick={() => syncSingleGuaritaTag(tag)} className="p-1.5 text-gray-400 hover:text-[#96bf13] rounded-lg transition-colors flex-shrink-0" title={wrongReceptor ? 'Corrigir receptor no dispositivo' : 'Enviar ao dispositivo'}>
                                    <Upload className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>

                  {/* Section 2: Tags physically on the device */}
                  <div className="border-t pt-5 mt-5">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900 flex-shrink-0">Tags no Dispositivo</h3>
                      {!isLoadingGuaritaDeviceRegs && enrichedRegs.length > 0 && (
                        <div className="relative flex-1 min-w-0">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                          <input
                            type="text"
                            value={guaritaDeviceRegSearch}
                            onChange={e => setGuaritaDeviceRegSearch(e.target.value)}
                            placeholder="Buscar tag, placa ou pessoa..."
                            className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 text-gray-700 placeholder-gray-400"
                          />
                        </div>
                      )}
                      <div className="relative flex-shrink-0">
                        <ArrowUpDown className={`absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none ${guaritaDeviceRegSort !== 'default' ? 'text-blue-600' : 'text-gray-400'}`} />
                        <select
                          value={guaritaDeviceRegSort}
                          onChange={e => setGuaritaDeviceRegSort(e.target.value as typeof guaritaDeviceRegSort)}
                          className={`pl-7 pr-6 py-1.5 text-xs rounded-lg border appearance-none cursor-pointer transition-colors ${guaritaDeviceRegSort !== 'default' ? 'text-blue-600 bg-blue-50 border-blue-200' : 'text-gray-600 bg-gray-50 border-gray-200'}`}
                          title="Ordenar lista"
                        >
                          <option value="default">Padrão</option>
                          <option value="owner-asc">Titular (A-Z)</option>
                          <option value="owner-desc">Titular (Z-A)</option>
                          <option value="label-asc">Controle (A-Z)</option>
                          <option value="label-desc">Controle (Z-A)</option>
                          <option value="serial-asc">Tag (menor-maior)</option>
                          <option value="serial-desc">Tag (maior-menor)</option>
                        </select>
                      </div>
                      <button
                        onClick={() => setHideOtherReceptorRegs(v => !v)}
                        className={`text-xs px-2 py-1.5 rounded-lg border flex-shrink-0 whitespace-nowrap transition-colors ${
                          hideOtherReceptorRegs ? 'text-blue-600 bg-blue-50 border-blue-200' : 'text-gray-500 bg-gray-50 border-gray-200 hover:text-gray-700'
                        }`}
                        title={`Este dispositivo usa REC ${device.config?.guarita_receptor ?? 1} — ocultar registros de outro receptor`}
                      >
                        {hideOtherReceptorRegs ? 'Só REC ' + (device.config?.guarita_receptor ?? 1) : 'Ocultar outro receptor'}
                      </button>
                      {devTotal > 0 && (
                        <button
                          onClick={() => setShowDeviceRegsExportModal(true)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
                        >
                          <FileDown className="w-3.5 h-3.5" />
                          Exportar
                        </button>
                      )}
                      <button onClick={() => fetchGuaritaDeviceRegs(0, true)} disabled={isLoadingGuaritaDeviceRegs} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0" title="Atualizar lista (leitura ao vivo do dispositivo)">
                        <RefreshCw className={`w-4 h-4 ${isLoadingGuaritaDeviceRegs ? 'animate-spin' : ''}`} />
                      </button>
                    </div>

                    {/* Export modal (Tags no Dispositivo) */}
                    {showDeviceRegsExportModal && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-gray-900">Exportar Tags no Dispositivo</h3>
                            <button onClick={() => setShowDeviceRegsExportModal(false)} className="text-gray-400 hover:text-gray-600">
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                          <div className="space-y-4">
                            <p className="text-sm text-gray-600">
                              Exportar {devTotal} tag{devTotal !== 1 ? 's' : ''}
                            </p>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Formato de Exportação</label>
                              <div className="space-y-2">
                                <label className="flex items-center space-x-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                                  <input
                                    type="radio"
                                    value="csv"
                                    checked={deviceRegsExportFormat === 'csv'}
                                    onChange={(e) => setDeviceRegsExportFormat(e.target.value as 'csv' | 'pdf')}
                                    className="text-black focus:ring-black"
                                  />
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">CSV</p>
                                    <p className="text-xs text-gray-500">Formato compatível com Excel e Google Sheets</p>
                                  </div>
                                </label>
                                <label className="flex items-center space-x-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                                  <input
                                    type="radio"
                                    value="pdf"
                                    checked={deviceRegsExportFormat === 'pdf'}
                                    onChange={(e) => setDeviceRegsExportFormat(e.target.value as 'csv' | 'pdf')}
                                    className="text-black focus:ring-black"
                                  />
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">PDF</p>
                                    <p className="text-xs text-gray-500">Documento formatado para impressão</p>
                                  </div>
                                </label>
                              </div>
                            </div>
                            <div className="flex justify-end space-x-2 pt-4">
                              <Button onClick={() => setShowDeviceRegsExportModal(false)} variant="outline">Cancelar</Button>
                              <Button
                                onClick={async () => {
                                  setShowDeviceRegsExportModal(false);
                                  if (deviceRegsExportFormat === 'csv') await generateGuaritaDeviceRegsCSV();
                                  else await generateGuaritaDeviceRegsPDF();
                                }}
                                disabled={devTotal === 0}
                              >
                                <FileDown className="h-4 w-4 mr-2" />
                                Exportar
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!isLoadingGuaritaDeviceRegs && devTotal > 0 && (
                      <p className="text-xs text-gray-500 mb-3">
                        {devTotal} total
                        {devAuthorized > 0 && <> · <span className="text-green-600">{devAuthorized} pessoa{devAuthorized !== 1 ? 's' : ''}</span></>}
                        {devVehicles > 0 && <> · <span className="text-blue-600">{devVehicles} veículo{devVehicles !== 1 ? 's' : ''}</span></>}
                        {devNotRegistered > 0 && <> · <span className="text-orange-600">{devNotRegistered} não cadastrada{devNotRegistered !== 1 ? 's' : ''}</span></>}
                        {hideOtherReceptorRegs && devOtherReceptor > 0 && (
                          <> · <span className="text-yellow-600">{devOtherReceptor} oculta{devOtherReceptor !== 1 ? 's' : ''} (outro receptor)</span></>
                        )}
                      </p>
                    )}

                    {!device.config?.guarita_integration_id ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Dispositivo sem integração Guarita configurada</p>
                      </div>
                    ) : isLoadingGuaritaDeviceRegs ? (
                      <div className="text-center py-8">
                        <RefreshCw className="w-7 h-7 text-blue-600 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Consultando dispositivo...</p>
                      </div>
                    ) : enrichedRegs.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <Cpu className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Nenhuma tag cadastrada no dispositivo</p>
                        <p className="text-xs text-gray-400 mt-1">Use o botão de envio em cada tag ou a aba Sincronização</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {enrichedRegs.filter(({ reg, matchedTag, matchedVehicle, coversReceptor }) => {
                          if (hideOtherReceptorRegs && !coversReceptor) return false;
                          if (!guaritaDeviceRegSearch.trim()) return true;
                          const q = guaritaDeviceRegSearch.toLowerCase();
                          if ((reg.serial_decimal ?? '').toLowerCase().includes(q)) return true;
                          if ((reg.label ?? '').toLowerCase().includes(q)) return true;
                          if (matchedTag?.person) {
                            const name = `${matchedTag.person.first_name ?? ''} ${matchedTag.person.last_name ?? ''}`.toLowerCase();
                            if (name.includes(q)) return true;
                          }
                          if (matchedVehicle) {
                            if ((matchedVehicle.plate ?? '').toLowerCase().includes(q)) return true;
                            const owner = `${matchedVehicle.person?.first_name ?? ''} ${matchedVehicle.person?.last_name ?? ''} ${matchedVehicle.owner_name ?? ''}`.toLowerCase();
                            if (owner.includes(q)) return true;
                          }
                          return false;
                        }).map(({ reg, matchedTag, matchedVehicle, isInSystem, isVehicle, isActive, coversReceptor }, idx) => {
                            const rowColor = isVehicle ? 'bg-blue-50 border-blue-100 hover:bg-blue-100'
                              : !isInSystem ? 'bg-gray-50 border-gray-200'
                              : !isActive ? 'bg-red-50 border-red-100'
                              : !coversReceptor ? 'bg-yellow-50 border-yellow-100 hover:bg-yellow-100'
                              : 'bg-green-50 border-green-100 hover:bg-green-100';
                            const iconBg = isVehicle ? 'bg-blue-100'
                              : !isInSystem ? 'bg-gray-100' : !isActive ? 'bg-red-100' : !coversReceptor ? 'bg-yellow-100' : 'bg-green-100';
                            const iconColor = isVehicle ? 'text-blue-500'
                              : !isInSystem ? 'text-gray-400' : !isActive ? 'text-red-500' : !coversReceptor ? 'text-yellow-600' : 'text-green-600';
                            const badgeColor = isVehicle ? 'bg-blue-100 text-blue-700'
                              : !isInSystem ? 'bg-orange-100 text-orange-700' : !isActive ? 'bg-red-100 text-red-600' : !coversReceptor ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700';
                            const badgeLabel = isVehicle ? 'Veículo'
                              : !isInSystem ? 'Não cadastrada' : !isActive ? 'Não autorizado' : !coversReceptor ? 'Outro receptor' : 'Autorizado';
                            const vehicleOwner = matchedVehicle?.person
                              ? `${matchedVehicle.person.first_name || ''} ${matchedVehicle.person.last_name || ''}`.trim()
                              : matchedVehicle?.owner_name;
                            return (
                          <div key={`${reg.serial_decimal}-${idx}`} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${rowColor}`}>
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                              {isVehicle ? <Car className={`w-4 h-4 ${iconColor}`} /> : <Cpu className={`w-4 h-4 ${iconColor}`} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-mono font-medium text-gray-900">
                                {isVehicle ? (matchedVehicle.plate || reg.serial_decimal) : reg.serial_decimal}
                                {isVehicle && matchedVehicle.plate && <span className="font-sans text-gray-400 text-xs ml-1.5">({reg.serial_decimal})</span>}
                              </p>
                              <p className="text-xs text-gray-500">
                                {isVehicle ? (
                                  <span>
                                    {[matchedVehicle.model, matchedVehicle.color].filter(Boolean).join(' · ')}
                                    {vehicleOwner && <> · {vehicleOwner}</>}
                                  </span>
                                ) : matchedTag?.person ? (
                                  <>
                                    <button onClick={() => openPersonDetail(matchedTag.person.id)} className="text-blue-600 hover:underline">
                                      {matchedTag.person.first_name} {matchedTag.person.last_name}
                                    </button>
                                    {matchedTag.label && <span className="ml-2 text-gray-400">· {matchedTag.label}</span>}
                                  </>
                                ) : (
                                  <span>{reg.device_type_name}{reg.label && ` · ${reg.label}`}{reg.block_name && ` · ${reg.block_name} un.${reg.unit}`}</span>
                                )}
                              </p>
                              {reg.receivers !== undefined && <ReceptorDots mask={reg.receivers} />}
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium ${badgeColor}`}>
                              {badgeLabel}
                            </span>
                            {!isInSystem && !isVehicle && (
                              <button
                                onClick={() => {
                                  setVinculateReg(reg);
                                  setVinculateLabel(reg.label || '');
                                  setVinculateSearch('');
                                  setVinculatePeople([]);
                                  setVinculatePersonId('');
                                }}
                                className="text-xs px-2 py-0.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 flex-shrink-0 font-medium transition-colors"
                              >
                                Vincular
                              </button>
                            )}
                            <button onClick={() => removeGuaritaDeviceReg(reg.serial_decimal, reg.device_type)} className="p-1.5 text-gray-400 hover:text-orange-500 rounded-lg transition-colors flex-shrink-0" title={`Remover REC ${device?.config?.guarita_receptor ?? 1} desta tag`}>
                              <X className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => removeEntirelyFromDevice(reg.serial_decimal, reg.device_type)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg transition-colors flex-shrink-0" title="Remover tag inteiramente do dispositivo">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                  {/* Section 3: Vehicles with RFID (when vehicle_whitelist_enabled) */}
                  {guaritaIntegration?.vehicle_whitelist_enabled && (
                    <div className="border-t pt-5 mt-5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Car className="w-4 h-4 text-gray-500" />
                          Veículos com RFID
                        </h3>
                        <button onClick={fetchGuaritaVehicles} disabled={isLoadingGuaritaVehicles} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg transition-colors disabled:opacity-50" title="Atualizar">
                          <RefreshCw className={`w-4 h-4 ${isLoadingGuaritaVehicles ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                      {!isLoadingGuaritaVehicles && guaritaVehicles.length > 0 && (() => {
                        const synced = guaritaVehicles.filter(v => {
                          const dec = guaritaCardDecimal(v.rfid_tag || '');
                          return dec && guaritaDeviceRegs.some(r => r.serial_decimal === dec);
                        }).length;
                        return (
                          <p className="text-xs text-gray-500 mb-3">
                            {guaritaVehicles.length} total
                            {synced > 0 && <> · <span className="text-green-600">{synced} no dispositivo</span></>}
                            {guaritaVehicles.length - synced > 0 && <> · <span className="text-amber-600">{guaritaVehicles.length - synced} pendente{guaritaVehicles.length - synced !== 1 ? 's' : ''}</span></>}
                          </p>
                        );
                      })()}
                      {isLoadingGuaritaVehicles ? (
                        <div className="text-center py-8">
                          <RefreshCw className="w-6 h-6 text-[#96bf13] animate-spin mx-auto mb-2" />
                          <p className="text-sm text-gray-500">Carregando veículos...</p>
                        </div>
                      ) : guaritaVehicles.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                          <Car className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">Nenhum veículo com tag RFID ativo</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {[...guaritaVehicles]
                            .sort((a, b) => {
                              const aOn = guaritaDeviceRegs.some(r => r.serial_decimal === guaritaCardDecimal(a.rfid_tag || '')) ? 0 : 1;
                              const bOn = guaritaDeviceRegs.some(r => r.serial_decimal === guaritaCardDecimal(b.rfid_tag || '')) ? 0 : 1;
                              return aOn - bOn;
                            })
                            .map((vehicle: any) => {
                              const dec = guaritaCardDecimal(vehicle.rfid_tag || '');
                              const isOnDevice = dec ? guaritaDeviceRegs.some(r => r.serial_decimal === dec) : false;
                              const ownerName = vehicle.person
                                ? `${vehicle.person.first_name || ''} ${vehicle.person.last_name || ''}`.trim()
                                : vehicle.owner_name || '—';
                              return (
                                <div key={vehicle.id} className="flex items-center gap-3 px-3 py-2.5 border border-gray-100 rounded-lg bg-white">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2">
                                      <span className="text-sm font-semibold text-gray-900 font-mono">{vehicle.plate || '—'}</span>
                                      {vehicle.model && <span className="text-xs text-gray-400 truncate">{vehicle.model}{vehicle.color ? ` · ${vehicle.color}` : ''}</span>}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-xs text-gray-500 truncate">{ownerName}</span>
                                      {vehicle.rfid_tag && <span className="text-xs text-gray-300 font-mono">{vehicle.rfid_tag}</span>}
                                    </div>
                                  </div>
                                  <span className={`text-xs font-medium flex-shrink-0 ${isOnDevice ? 'text-green-600' : 'text-amber-500'}`}>
                                    {isOnDevice ? 'No dispositivo' : 'Pendente'}
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })()}

              {/* RFID / Facial / Other Devices: only shown for non-Guarita */}
              {!isGuaritaModalDevice && (isVehicleListDevice(device.device_type) ? (
                <>
                  {/* Section 1: Authorized Vehicles (from PantherAccess system - database) */}
                  <div>
                    <div className="mb-4">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        Veículos Cadastrados no Sistema
                        {zone && <span className="text-sm text-gray-600 font-normal">para {zone.name}</span>}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">{isLprDeviceModal ? 'Todas as placas cadastradas no sistema' : 'Todos os veículos com TAG RFID cadastrados no sistema'}</p>
                    </div>

                    {!zone ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600">Dispositivo não vinculado a uma zona</p>
                        <p className="text-sm text-gray-500 mt-1">Vincule este dispositivo a uma zona para gerenciar veículos autorizados</p>
                      </div>
                    ) : isLoadingVehicles ? (
                      <div className="text-center py-12">
                        <RefreshCw className="w-8 h-8 text-[#96bf13] animate-spin mx-auto mb-3" />
                        <p className="text-gray-600">Carregando veículos autorizados...</p>
                      </div>
                    ) : authorizedVehicles.filter((v: any) => !v.is_unauthorized).length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <Car className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600">Nenhum veículo cadastrado no sistema</p>
                        <p className="text-sm text-gray-500 mt-1">Configure perfis de acesso e cadastre veículos para esta zona</p>
                      </div>
                    ) : (() => {
                        const q = vehicleSearch.toLowerCase();
                        const isAmbiguousHex = (tag: string) =>
                          !!tag && /^\d+$/.test(tag) && !tag.startsWith('0');
                        const filtered = authorizedVehicles.filter((v: any) => {
                          if (v.is_unauthorized) return false;
                          if (vehicleFilterAmbiguousHex && !isAmbiguousHex(v.rfid_tag || '')) return false;
                          if (!q) return true;
                          return (
                            (v.plate || '').toLowerCase().includes(q) ||
                            (v.person_name || '').toLowerCase().includes(q) ||
                            (v.brand || '').toLowerCase().includes(q) ||
                            (v.model || '').toLowerCase().includes(q) ||
                            (v.rfid_tag || '').toLowerCase().includes(q)
                          );
                        });
                        const totalPages = Math.ceil(filtered.length / vehiclePageSize);
                        const page = Math.min(vehiclePage, totalPages || 1);
                        const pageItems = filtered.slice((page - 1) * vehiclePageSize, page * vehiclePageSize);
                        const totalUnauth = authorizedVehicles.filter((v: any) => !v.is_unauthorized).length;
                        const ambiguousCount = authorizedVehicles.filter((v: any) => !v.is_unauthorized && isAmbiguousHex(v.rfid_tag || '')).length;
                        return (
                          <div className="space-y-3">
                            {/* Search bar */}
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                              <input
                                type="text"
                                placeholder={isLprDeviceModal ? 'Buscar por placa, nome ou marca...' : 'Buscar por placa, nome, marca ou tag...'}
                                value={vehicleSearch}
                                onChange={e => { setVehicleSearch(e.target.value); setVehiclePage(1); }}
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#96bf13]/40 focus:border-[#96bf13]"
                              />
                            </div>

                            {/* Advanced filter — hex-ambiguity is an RFID tag concern; an LPR
                                camera matches plates, so the whole control is irrelevant there. */}
                            {!isLprDeviceModal && (
                            <div>
                              <button
                                onClick={() => setVehicleAdvancedOpen(o => !o)}
                                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
                              >
                                <span className={`transition-transform ${vehicleAdvancedOpen ? 'rotate-90' : ''}`}>▶</span>
                                Avançado
                                {ambiguousCount > 0 && (
                                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                                    {ambiguousCount} hex ambíguo{ambiguousCount !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </button>
                                {vehicleAdvancedOpen && (
                                <div className="mt-2 p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
                                  {ambiguousCount === 0 ? (
                                    <p className="text-xs text-gray-500">Nenhuma tag hex ambígua nesta zona — todos os registros possuem zero inicial ou letras hex.</p>
                                  ) : (
                                    <label className="flex items-start gap-2 cursor-pointer select-none">
                                      <input
                                        type="checkbox"
                                        checked={vehicleFilterAmbiguousHex}
                                        onChange={e => { setVehicleFilterAmbiguousHex(e.target.checked); setVehiclePage(1); }}
                                        className="mt-0.5 accent-amber-500"
                                      />
                                      <span className="text-xs text-gray-700">
                                        <span className="font-medium text-amber-700">Tags hex sem zero inicial ({ambiguousCount})</span>
                                        <span className="text-gray-500 ml-1">— valores numéricos que deveriam ser interpretados como hex (ex: <code className="bg-gray-100 px-1 rounded">989390</code> → hex <code className="bg-gray-100 px-1 rounded">0x989390</code>)</span>
                                      </span>
                                    </label>
                                  )}
                                </div>
                              )}
                            </div>
                            )}

                            {/* Count */}
                            <p className="text-xs text-gray-500">
                              {(q || vehicleFilterAmbiguousHex) && filtered.length !== totalUnauth
                                ? `${filtered.length} de ${totalUnauth} veículos`
                                : `${filtered.length} veículos`}
                            </p>

                            {/* List */}
                            {pageItems.length === 0 ? (
                              <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <p className="text-gray-500 text-sm">
                                  {vehicleFilterAmbiguousHex && !q
                                    ? 'Nenhuma tag hex ambígua encontrada nesta zona'
                                    : `Nenhum resultado para "${vehicleSearch}"`}
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {pageItems.map((vehicle: any) => (
                                  <div
                                    key={vehicle.vehicle_id || vehicle.plate}
                                    className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                                    onClick={() => vehicle.person_id && openPersonDetail(vehicle.person_id)}
                                  >
                                    <div className="w-10 h-10 rounded-full bg-[#96bf13] bg-opacity-20 flex items-center justify-center flex-shrink-0">
                                      <Car className="w-5 h-5 text-[#96bf13]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-gray-900">
                                        {vehicle.plate}
                                        {vehicle.brand && vehicle.model && (
                                          <span className="text-sm text-gray-600 font-normal ml-2">{vehicle.brand} {vehicle.model}</span>
                                        )}
                                      </p>
                                      <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                        <p className="text-xs text-gray-600">
                                          {vehicle.person_name}
                                          {vehicle.unit_number && (
                                            <span className={vehicle.person_name ? 'text-gray-400 ml-1' : 'text-gray-600'}>
                                              {vehicle.person_name ? '· ' : ''}{vehicle.unit_block ? `${vehicle.unit_block} / ` : ''}Apto {vehicle.unit_number}
                                            </span>
                                          )}
                                        </p>
                                        {vehicle.role_type && (
                                          <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">{getRoleLabel(vehicle.role_type)}</span>
                                        )}
                                        {!isLprDeviceModal && vehicle.rfid_tag && (
                                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Tag: {vehicle.rfid_tag}</span>
                                        )}
                                      </div>
                                      {vehicle.color && vehicle.year && (
                                        <p className="text-xs text-gray-500 mt-0.5">{vehicle.color} • {vehicle.year}</p>
                                      )}
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${isLprDeviceModal ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                                      {isLprDeviceModal ? 'PLACA' : 'RFID'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                              <div className="flex items-center justify-between pt-2">
                                <button
                                  onClick={() => setVehiclePage(p => Math.max(1, p - 1))}
                                  disabled={page <= 1}
                                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  ← Anterior
                                </button>
                                <span className="text-xs text-gray-600">
                                  Página {page} de {totalPages}
                                </span>
                                <button
                                  onClick={() => setVehiclePage(p => Math.min(totalPages, p + 1))}
                                  disabled={page >= totalPages}
                                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  Próxima →
                                </button>
                              </div>
                            )}
                          </div>
                        );
                    })()}
                  </div>

                  {/* Section 2: Vehicles on Device (actually enrolled) */}
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        Veículos no Dispositivo
                        <span className="text-sm text-gray-600 font-normal">
                          (cadastrados fisicamente)
                        </span>
                      </h3>
                    </div>

                    {isLoadingVehicles ? (
                      <div className="text-center py-12">
                        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
                        <p className="text-gray-600">Carregando veículos do dispositivo...</p>
                      </div>
                    ) : (
                      <>
                        {/* Statistics Cards */}
                        {authorizedVehicles.filter((v: any) => v.is_on_device).length > 0 && (
                          <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                              <div className="text-2xl font-bold text-blue-700">
                                {authorizedVehicles.filter((v: any) => v.is_on_device).length}
                              </div>
                              <div className="text-xs text-blue-600 font-medium">Total</div>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                              <div className="text-2xl font-bold text-green-700">
                                {authorizedVehicles.filter((v: any) => v.is_on_device && !v.is_unauthorized).length}
                              </div>
                              <div className="text-xs text-green-600 font-medium">Autorizados</div>
                            </div>
                            <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                              <div className="text-2xl font-bold text-red-700">
                                {authorizedVehicles.filter((v: any) => v.is_unauthorized).length}
                              </div>
                              <div className="text-xs text-red-600 font-medium">Não Autorizados</div>
                            </div>
                          </div>
                        )}

                        {/* Vehicles on Device List */}
                        {authorizedVehicles.filter((v: any) => v.is_on_device).length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-8">
                            Nenhum veículo cadastrado no dispositivo
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {authorizedVehicles.filter((v: any) => v.is_on_device).map((vehicle: any) => (
                              <div
                                key={vehicle.vehicle_id || vehicle.card_number || vehicle.plate}
                                className={`flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                                  vehicle.is_unauthorized
                                    ? 'bg-red-50 border border-red-200 hover:bg-red-100'
                                    : 'bg-blue-50 border border-blue-200 hover:bg-blue-100'
                                }`}
                                onClick={() => vehicle.person_id && openPersonDetail(vehicle.person_id)}
                              >
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  vehicle.is_unauthorized ? 'bg-red-100' : 'bg-blue-100'
                                }`}>
                                  <Car className={`w-5 h-5 ${vehicle.is_unauthorized ? 'text-red-600' : 'text-blue-600'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900">
                                    {vehicle.plate || vehicle.vehicle_plate || 'Placa não informada'}
                                    {vehicle.brand && vehicle.model && (
                                      <span className="text-sm text-gray-600 font-normal ml-2">
                                        {vehicle.brand} {vehicle.model}
                                      </span>
                                    )}
                                  </p>
                                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                    {vehicle.person_name && (
                                      <p className="text-xs text-gray-600">
                                        {vehicle.person_name}
                                        {vehicle.unit_number && (
                                          <span className="text-gray-400 ml-1">
                                            · {vehicle.unit_block ? `${vehicle.unit_block} / ` : ''}Apto {vehicle.unit_number}
                                          </span>
                                        )}
                                      </p>
                                    )}
                                    {vehicle.role_type && (
                                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">{getRoleLabel(vehicle.role_type)}</span>
                                    )}
                                    {(vehicle.user_id || vehicle.person_code) && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                                        Código: {vehicle.user_id || vehicle.person_code}
                                      </span>
                                    )}
                                    {!isLprDeviceModal && (vehicle.rfid_tag || vehicle.card_number) && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                        Tag: {vehicle.rfid_tag || vehicle.card_number}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {vehicle.is_unauthorized ? (
                                    <>
                                      <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        Não Autorizado
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const cardNumber = vehicle.card_number || vehicle.rfid_tag || vehicle.user_id;
                                          if (!cardNumber) {
                                            toast.error('Identificador não encontrado para este veículo');
                                            return;
                                          }
                                          setVehicleToRemove({ vehicle, device, cardNumber });
                                          setShowVehicleRemoveModal(true);
                                        }}
                                        className="p-1.5 rounded-md hover:bg-red-100 text-red-600 hover:text-red-700 transition-colors"
                                        title="Remover do dispositivo"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" />
                                        Autorizado
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const cardNumber = vehicle.card_number || vehicle.rfid_tag || vehicle.user_id;
                                          if (!cardNumber) {
                                            toast.error('Identificador não encontrado para este veículo');
                                            return;
                                          }
                                          setVehicleToRemove({ vehicle, device, cardNumber });
                                          setShowVehicleRemoveModal(true);
                                        }}
                                        className="p-1.5 rounded-md hover:bg-gray-200 text-gray-600 hover:text-gray-700 transition-colors"
                                        title="Remover do dispositivo"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Authorized People Section */}
                  <div>
                    {!zone ? (
                      <div className="text-center py-12 text-gray-400">
                        <AlertCircle className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm text-gray-500">Dispositivo não vinculado a uma zona</p>
                        <p className="text-xs text-gray-400 mt-1">Vincule este dispositivo a uma zona para gerenciar pessoas autorizadas</p>
                      </div>
                    ) : (
                    <>
                      {/* ── Access Profiles Panel ─────────────────────────────── */}
                      {(isLoadingProfiles || zoneProfiles.length > 0) && (
                        <div className="mb-5 pb-5 border-b border-gray-100">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Perfis de Acesso</span>
                            {!isLoadingProfiles && zoneProfiles.length > 0 && (
                              <span className="text-xs text-gray-400">{zoneProfiles.length} aplicado{zoneProfiles.length > 1 ? 's' : ''}</span>
                            )}
                          </div>
                          {isLoadingProfiles ? (
                            <div className="flex items-center gap-2 text-xs text-gray-400 py-1">
                              <RefreshCw className="w-3 h-3 animate-spin" />
                              Carregando perfis...
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {zoneProfiles.map((profile: any) => {
                                const assignments = zoneProfileAssignments[profile.id];
                                const deptNames = (assignments?.department_assignments || [])
                                  .filter((a: any) => a.is_active)
                                  .map((a: any) =>
                                    // API returns "" (empty string) when JSONB lookup fails — fall back to local list
                                    (a.department_name && a.department_name.trim()) ||
                                    (a.name && a.name.trim()) ||
                                    zoneDepartments.find((d: any) => d.id === (a.department_id || a.DepartmentID))?.name ||
                                    null
                                  ).filter(Boolean);
                                const typeKeys = (assignments?.person_assignments || [])
                                  .filter((a: any) => a.is_active)
                                  .map((a: any) => a.person_type);
                                const typeLabels: Record<string, string> = {
                                  resident: 'Morador', morador: 'Morador',
                                  visitor: 'Visitante', visitante: 'Visitante',
                                  service_provider: 'Prestador', prestador: 'Prestador',
                                  staff: 'Funcionário', funcionario: 'Funcionário', employee: 'Funcionário',
                                  delivery: 'Entregador', entregador: 'Entregador',
                                };
                                const chips: string[] = [
                                  ...typeKeys.map((k: string) => typeLabels[k] || k),
                                  ...deptNames,
                                ];
                                const visible = chips;
                                const overflow = 0;
                                const isAllow = profile.access_type === 'allow';
                                const scopeLabel = profile.applies_to === 'all_zones'
                                  ? 'Todas as zonas'
                                  : profile.applies_to === 'zone_group'
                                  ? 'Grupo de zonas'
                                  : 'Zona específica';
                                const isProfileActive = activeProfileFilter.includes(profile.id);
                                const isDenyExpanded = !isAllow && expandedDenyProfileId === profile.id;
                                const denyPeople = denyProfilePeople[profile.id] || [];
                                return (
                                  <div key={profile.id}>
                                    <div
                                      onClick={() => {
                                        if (isAllow) {
                                          setActiveProfileFilter(prev => isProfileActive ? prev.filter(id => id !== profile.id) : [...prev, profile.id]);
                                        } else {
                                          setExpandedDenyProfileId(prev => prev === profile.id ? null : profile.id);
                                        }
                                      }}
                                      className={`rounded-lg border p-3 cursor-pointer transition-all ${
                                        isAllow
                                          ? isProfileActive
                                            ? 'border-gray-900 bg-gray-900 text-white'
                                            : 'border-gray-200 bg-white hover:border-gray-400'
                                          : isDenyExpanded
                                            ? 'border-red-300 bg-red-50'
                                            : 'border-red-100 bg-white hover:border-red-300'
                                      }`}
                                    >
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-2.5 min-w-0">
                                          <div className={`mt-[5px] w-2 h-2 rounded-full flex-shrink-0 ${isAllow ? 'bg-green-400' : 'bg-red-400'}`} />
                                          <div className="min-w-0">
                                            <p className={`text-sm font-semibold leading-tight truncate ${isProfileActive ? 'text-white' : 'text-gray-900'}`}>{profile.name}</p>
                                            <p className={`text-xs mt-0.5 ${isProfileActive ? 'text-gray-300' : 'text-gray-400'}`}>{scopeLabel}</p>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
                                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isAllow ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                            {isAllow ? 'Permitir' : 'Negar'}
                                          </span>
                                          {profile.assignment_count != null && (
                                            <span className={`text-xs tabular-nums ${isProfileActive ? 'text-gray-300' : 'text-gray-500'}`}>
                                              {profile.assignment_count} <span className={isProfileActive ? 'text-gray-500' : 'text-gray-300'}>pessoas</span>
                                            </span>
                                          )}
                                          {!isAllow && (
                                            <ChevronDown className={`w-3.5 h-3.5 text-red-400 transition-transform ${isDenyExpanded ? 'rotate-180' : ''}`} />
                                          )}
                                        </div>
                                      </div>
                                      {chips.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-2.5 ml-[18px]">
                                          {visible.map((chip, i) => (
                                            <span key={i} className={`text-xs px-2 py-0.5 rounded-full font-medium ${isProfileActive ? 'bg-white/15 text-gray-100' : 'bg-gray-100 text-gray-600'}`}>{chip}</span>
                                          ))}
                                        </div>
                                      )}
                                      {assignments && chips.length === 0 && (
                                        <p className={`text-xs mt-1.5 ml-[18px] italic ${isProfileActive ? 'text-gray-400' : 'text-gray-400'}`}>Sem grupos atribuídos</p>
                                      )}
                                    </div>
                                    {/* Expanded deny people list */}
                                    {isDenyExpanded && (
                                      <div className="mt-1 rounded-lg border border-red-100 bg-red-50/50 overflow-hidden">
                                        {denyPeople.length === 0 ? (
                                          <p className="text-xs text-gray-400 italic px-3 py-2">Nenhuma pessoa negada</p>
                                        ) : (
                                          <div className="divide-y divide-red-100">
                                            {denyPeople.map((person: any) => {
                                              const photoUrl = person.photo_url ? getAbsoluteImageUrl(person.photo_url) : getPrimaryPhotoUrl(person);
                                              return (
                                                <div
                                                  key={person.id}
                                                  className="flex items-center gap-2.5 px-3 py-2 hover:bg-red-100/50 cursor-pointer transition-colors"
                                                  onClick={(e) => { e.stopPropagation(); openPersonDetail(person.id); }}
                                                >
                                                  <div className="w-6 h-8 rounded-md flex-shrink-0 overflow-hidden bg-red-100 flex items-center justify-center">
                                                    {photoUrl ? (
                                                      <img src={photoUrl} alt="" className="w-6 h-8 object-cover" />
                                                    ) : (
                                                      <User className="w-3.5 h-3.5 text-red-300" />
                                                    )}
                                                  </div>
                                                  <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-medium text-gray-800 truncate">{person.first_name} {person.last_name}</p>
                                                    <p className="text-[11px] text-gray-400 font-mono">{person.id?.substring(0, 8)}…</p>
                                                  </div>
                                                  <span className="text-[11px] font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full flex-shrink-0">Negado</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Authorized People List ────────────────────────────── */}
                      {isLoadingPeople ? (
                      <div className="text-center py-12">
                        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin mx-auto mb-3" />
                        <p className="text-sm text-gray-500">Carregando...</p>
                      </div>
                    ) : authorizedPeople.length === 0 ? (
                      <div className="text-center py-12">
                        <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm text-gray-500">Nenhuma pessoa autorizada</p>
                        <p className="text-xs text-gray-400 mt-1">Configure perfis de acesso para esta zona</p>
                      </div>
                    ) : (
                      <>
                        {(() => {
                          // Profile filter: union across selected profiles (person must appear in at least one)
                          const hasProfileFilter = activeProfileFilter.length > 0;

                          const filteredAuthorizedPeople = authorizedPeople.filter((person: any) => {
                            const fullName = `${person.first_name} ${person.last_name}`.toLowerCase();
                            const matchesSearch = fullName.includes(authorizedPeopleSearchTerm.toLowerCase()) ||
                                                 person.id?.toLowerCase().includes(authorizedPeopleSearchTerm.toLowerCase());
                            const matchesPhotoFilter = authorizedPeoplePhotoFilter === 'all' ||
                                                      (authorizedPeoplePhotoFilter === 'with_photo' && getPrimaryPhotoUrl(person)) ||
                                                      (authorizedPeoplePhotoFilter === 'without_photo' && !getPrimaryPhotoUrl(person));
                            const matchesDept = authorizedPeopleDeptFilter.length === 0 ||
                                               (person.roles || []).some((r: any) =>
                                                 r.is_active && (
                                                   authorizedPeopleDeptFilter.includes(r.role_metadata?.department_id) ||
                                                   (r.role_metadata?.department_ids || []).some((id: string) => authorizedPeopleDeptFilter.includes(id))
                                                 )
                                               );
                            const matchesProfile = !hasProfileFilter || activeProfileFilter.some(profileId => {
                              const ids = zoneProfilePeopleIds[profileId];
                              return ids ? ids.has(person.id) : true;
                            });
                            return matchesSearch && matchesPhotoFilter && matchesDept && matchesProfile;
                          });

                          // Stats based on filtered results
                          const withPhoto = filteredAuthorizedPeople.filter((p: any) => getPrimaryPhotoUrl(p)).length;
                          const withoutPhoto = filteredAuthorizedPeople.length - withPhoto;

                          const itemsPerPage = 10;
                          const totalPages = Math.ceil(filteredAuthorizedPeople.length / itemsPerPage);
                          const startIndex = (authorizedPeoplePage - 1) * itemsPerPage;
                          const paginatedAuthorizedPeople = filteredAuthorizedPeople.slice(startIndex, startIndex + itemsPerPage);

                          return (
                            <>
                              {/* Active profile filter chips */}
                              {hasProfileFilter && (
                                <div className="flex flex-wrap items-center gap-1.5 mb-3">
                                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider shrink-0">Perfis:</span>
                                  {activeProfileFilter.map(profileId => {
                                    const name = zoneProfiles.find((p: any) => p.id === profileId)?.name ?? profileId;
                                    return (
                                      <span key={profileId} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-0.5 rounded-full bg-gray-900 text-white text-xs font-medium">
                                        {name}
                                        <button
                                          onClick={() => setActiveProfileFilter(prev => prev.filter(id => id !== profileId))}
                                          className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-white/20 transition-colors"
                                        >
                                          <X className="w-2.5 h-2.5" />
                                        </button>
                                      </span>
                                    );
                                  })}
                                  {activeProfileFilter.length > 1 && (
                                    <button
                                      onClick={() => setActiveProfileFilter([])}
                                      className="text-[11px] text-gray-400 hover:text-gray-600 underline"
                                    >
                                      Limpar
                                    </button>
                                  )}
                                  <span className="text-xs text-gray-400 ml-1">
                                    {filteredAuthorizedPeople.length} {filteredAuthorizedPeople.length === 1 ? 'pessoa' : 'pessoas'}
                                  </span>
                                </div>
                              )}
                              {/* Summary + Search row */}
                              <div className="flex items-center gap-3 mb-4">
                                <div className="flex-1 relative">
                                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                  <input
                                    type="text"
                                    placeholder="Buscar pessoa..."
                                    value={authorizedPeopleSearchTerm}
                                    onChange={(e) => { setAuthorizedPeopleSearchTerm(e.target.value); setAuthorizedPeoplePage(1); }}
                                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white"
                                  />
                                </div>
                                <select
                                  value={authorizedPeoplePhotoFilter}
                                  onChange={(e) => { setAuthorizedPeoplePhotoFilter(e.target.value as any); setAuthorizedPeoplePage(1); }}
                                  className="py-1.5 px-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white text-gray-700"
                                >
                                  <option value="all">Todas ({filteredAuthorizedPeople.length})</option>
                                  <option value="with_photo">Com Foto ({withPhoto})</option>
                                  <option value="without_photo">Sem Foto ({withoutPhoto})</option>
                                </select>
                                {zoneDepartments.length > 0 && (
                                  <div className="relative">
                                    <button
                                      type="button"
                                      onClick={() => { setDeptFilterOpen(o => !o); setDeptFilterSearch(''); }}
                                      className={`py-1.5 px-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white flex items-center gap-1.5 whitespace-nowrap ${authorizedPeopleDeptFilter.length > 0 ? 'border-blue-400 text-blue-700' : 'border-gray-200 text-gray-700'}`}
                                    >
                                      {authorizedPeopleDeptFilter.length === 0 ? 'Departamento' : `${authorizedPeopleDeptFilter.length} depart.`}
                                      <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                                    </button>
                                    {deptFilterOpen && (
                                      <>
                                        <div className="fixed inset-0 z-10" onClick={() => setDeptFilterOpen(false)} />
                                        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                                          <div className="p-2 border-b border-gray-100">
                                            <div className="relative">
                                              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                              <input
                                                autoFocus
                                                type="text"
                                                placeholder="Buscar departamento..."
                                                value={deptFilterSearch}
                                                onChange={e => setDeptFilterSearch(e.target.value)}
                                                className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
                                              />
                                            </div>
                                          </div>
                                          <div className="max-h-48 overflow-y-auto py-1">
                                            {zoneDepartments
                                              .filter((d: any) => d.name.toLowerCase().includes(deptFilterSearch.toLowerCase()))
                                              .map((d: any) => {
                                                const selected = authorizedPeopleDeptFilter.includes(d.id);
                                                return (
                                                  <button
                                                    key={d.id}
                                                    type="button"
                                                    onClick={() => {
                                                      setAuthorizedPeopleDeptFilter(prev =>
                                                        selected ? prev.filter(id => id !== d.id) : [...prev, d.id]
                                                      );
                                                      setAuthorizedPeoplePage(1);
                                                    }}
                                                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${selected ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                                                  >
                                                    <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${selected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                                                      {selected && <CheckCircle className="w-3 h-3 text-white" />}
                                                    </div>
                                                    <span className="truncate">{d.name}</span>
                                                  </button>
                                                );
                                              })}
                                            {zoneDepartments.filter((d: any) => d.name.toLowerCase().includes(deptFilterSearch.toLowerCase())).length === 0 && (
                                              <p className="text-xs text-gray-400 text-center py-3">Nenhum resultado</p>
                                            )}
                                          </div>
                                          {authorizedPeopleDeptFilter.length > 0 && (
                                            <div className="border-t border-gray-100 p-2">
                                              <button
                                                type="button"
                                                onClick={() => { setAuthorizedPeopleDeptFilter([]); setAuthorizedPeoplePage(1); }}
                                                className="w-full text-xs text-gray-500 hover:text-gray-700 py-1"
                                              >
                                                Limpar filtro
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Stats bar */}
                              <div className="flex items-center gap-4 mb-3 text-xs text-gray-400">
                                <span><span className="font-semibold text-gray-700">{filteredAuthorizedPeople.length}</span> total{(hasProfileFilter || authorizedPeopleDeptFilter.length > 0) && <span className="text-gray-400"> de {authorizedPeople.length}</span>}</span>
                                <span className="text-gray-200">|</span>
                                <span><span className="font-semibold text-gray-700">{withPhoto}</span> com foto</span>
                                <span className="text-gray-200">|</span>
                                <span><span className={withoutPhoto > 0 ? 'font-semibold text-amber-600' : 'font-semibold text-gray-700'}>{withoutPhoto}</span> sem foto</span>
                                {zone && <span className="ml-auto text-gray-400">via {zone.name}</span>}
                              </div>

                              {/* Person list */}
                              {filteredAuthorizedPeople.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-8">Nenhuma pessoa encontrada</p>
                              ) : (
                                <>
                                  <div className="border border-gray-100 rounded-lg overflow-hidden divide-y divide-gray-100">
                                    {paginatedAuthorizedPeople.map((person: any) => {
                                      const activeRoles = person.roles
                                        ?.filter((r: any) => r.is_active && r.is_authorized)
                                        .map((r: any) => r.role_type)
                                        .filter((v: any, i: any, a: any) => a.indexOf(v) === i) || [];
                                      const roleLabels: Record<string, string> = {
                                        resident: 'Morador', visitor: 'Visitante', service_provider: 'Prestador',
                                        staff: 'Funcionário', delivery: 'Entregador',
                                        morador: 'Morador', visitante: 'Visitante', prestador: 'Prestador',
                                        funcionario: 'Funcionário', entregador: 'Entregador',
                                      };
                                      const photoUrl = getPrimaryPhotoUrl(person);
                                      const hasPhoto = !!photoUrl;
                                      // Collect department names from active roles.
                                      // Priority: enriched departments[] array > department_name > ID lookup.
                                      const deptNamesSet = new Set<string>();
                                      const fallbackIds: string[] = [];
                                      (person.roles || []).forEach((r: any) => {
                                        if (!r.is_active) return;
                                        // Multi-dept enriched array (new format)
                                        if (Array.isArray(r.role_metadata?.departments) && r.role_metadata.departments.length > 0) {
                                          r.role_metadata.departments.forEach((d: any) => {
                                            if (d?.name) deptNamesSet.add(d.name);
                                          });
                                        } else if (r.role_metadata?.department_name) {
                                          // Single enriched name
                                          deptNamesSet.add(r.role_metadata.department_name);
                                        } else {
                                          // Fall back to ID lookup via zoneDepartments
                                          if (r.role_metadata?.department_id) fallbackIds.push(r.role_metadata.department_id);
                                          (r.role_metadata?.department_ids || []).forEach((id: string) => fallbackIds.push(id));
                                        }
                                      });
                                      fallbackIds
                                        .filter((id, i, a) => a.indexOf(id) === i)
                                        .forEach((id: string) => {
                                          const name = zoneDepartments.find((d: any) => d.id === id)?.name;
                                          if (name) deptNamesSet.add(name);
                                        });
                                      const deptNames = Array.from(deptNamesSet);

                                      return (
                                        <div
                                          key={person.id}
                                          className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                                          onClick={() => openPersonDetail(person.id)}
                                        >
                                          {/* Avatar */}
                                          <div className="w-7 h-9 rounded-md flex-shrink-0 overflow-hidden bg-gray-100 flex items-center justify-center">
                                            {hasPhoto ? (
                                              <img
                                                src={getAbsoluteImageUrl(photoUrl!)}
                                                alt={`${person.first_name} ${person.last_name}`}
                                                className="w-7 h-9 object-cover"
                                                onError={(e) => {
                                                  e.currentTarget.style.display = 'none';
                                                  const fallback = e.currentTarget.nextElementSibling;
                                                  if (fallback) (fallback as HTMLElement).style.display = 'flex';
                                                }}
                                              />
                                            ) : null}
                                            <User className={`w-4 h-4 text-gray-400 ${hasPhoto ? 'hidden' : ''}`} />
                                          </div>

                                          {/* Info */}
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <span className="text-sm font-medium text-gray-900 truncate">
                                                {person.first_name} {person.last_name}
                                              </span>
                                              {!hasPhoto && (
                                                <span className="flex items-center gap-1 text-xs text-amber-600 flex-shrink-0">
                                                  <AlertCircle className="w-3 h-3" />
                                                  Sem foto
                                                </span>
                                              )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                              <span className="text-xs text-gray-400 font-mono">{person.id.substring(0, 8)}…</span>
                                              {activeRoles.map((role: string) => (
                                                <span key={role} className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                                  {roleLabels[role] || role}
                                                </span>
                                              ))}
                                              {deptNames.map((name: any) => (
                                                <span key={name} className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100">
                                                  {name}
                                                </span>
                                              ))}
                                              {person.vehicles?.map((vehicle: any) => (
                                                <span key={vehicle.id} className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                                                  {vehicle.plate}
                                                </span>
                                              ))}
                                            </div>
                                          </div>

                                          {/* Status */}
                                          <span className="text-xs text-green-700 font-medium flex-shrink-0">Autorizado</span>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Pagination */}
                                  {totalPages > 1 && (
                                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                                      <span className="text-xs text-gray-400">
                                        {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredAuthorizedPeople.length)} de {filteredAuthorizedPeople.length}
                                      </span>
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => setAuthorizedPeoplePage(Math.max(1, authorizedPeoplePage - 1))}
                                          disabled={authorizedPeoplePage === 1}
                                          className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40"
                                        >←</button>
                                        {(() => {
                                          const pages: (number | 'ellipsis')[] = [];
                                          pages.push(1);
                                          if (authorizedPeoplePage - 2 > 2) pages.push('ellipsis');
                                          for (let p = Math.max(2, authorizedPeoplePage - 1); p <= Math.min(totalPages - 1, authorizedPeoplePage + 1); p++) pages.push(p);
                                          if (authorizedPeoplePage + 2 < totalPages - 1) pages.push('ellipsis');
                                          if (totalPages > 1) pages.push(totalPages);
                                          return pages.map((p, i) =>
                                            p === 'ellipsis' ? (
                                              <span key={`e${i}`} className="px-1 text-xs text-gray-400">…</span>
                                            ) : (
                                              <button
                                                key={p}
                                                onClick={() => setAuthorizedPeoplePage(p)}
                                                className={`w-7 h-7 text-xs rounded border ${authorizedPeoplePage === p ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`}
                                              >{p}</button>
                                            )
                                          );
                                        })()}
                                        <button
                                          onClick={() => setAuthorizedPeoplePage(Math.min(totalPages, authorizedPeoplePage + 1))}
                                          disabled={authorizedPeoplePage === totalPages}
                                          className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40"
                                        >→</button>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}
                    </>
                  )}
                  </div>

              {/* Device People Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-sm font-semibold text-gray-700">Pessoas no Dispositivo</span>
                    <span className="text-xs text-gray-400 ml-2">registradas fisicamente</span>
                  </div>
                  <button
                    onClick={fetchDevicePeople}
                    disabled={isLoadingDevicePeople}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Atualizar lista de pessoas"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isLoadingDevicePeople ? 'animate-spin' : ''}`} />
                    Atualizar
                  </button>
                </div>

                {isLoadingDevicePeople && devicePeople.length === 0 ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-5 h-5 text-gray-400 animate-spin mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Carregando pessoas do dispositivo...</p>
                  </div>
                ) : (
                  <>
                    {isLoadingDevicePeople && (
                      <div className="flex items-center gap-1.5 mb-2 text-xs text-gray-400">
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Atualizando autorização e fotos...
                      </div>
                    )}
                    {/* Stats bar */}
                    {(devicePeopleTotalCount > 0 || devicePeople.length > 0) && (
                      <div className="flex items-center gap-4 mb-3 text-xs text-gray-400">
                        <span><span className="font-semibold text-gray-700">{devicePeopleTotalCount}</span> total</span>
                        <span className="text-gray-200">|</span>
                        <span><span className="font-semibold text-green-700">{devicePeopleAuthorizedCount}</span> autorizadas</span>
                        {devicePeopleUnauthorizedCount > 0 && (
                          <>
                            <span className="text-gray-200">|</span>
                            <span><span className="font-semibold text-red-600">{devicePeopleUnauthorizedCount}</span> não autorizadas</span>
                          </>
                        )}
                        <span className="text-gray-200">|</span>
                        <span><span className="font-semibold text-gray-700">{devicePeopleWithPhotoCount}</span> com foto</span>
                        {devicePeopleWithoutPhotoCount > 0 && (
                          <>
                            <span className="text-gray-200">|</span>
                            <span><span className="font-semibold text-amber-600">{devicePeopleWithoutPhotoCount}</span> sem foto</span>
                          </>
                        )}
                      </div>
                    )}

                    {/* Search and Filter */}
                    {(devicePeopleTotalCount > 0 || devicePeople.length > 0) && (
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1 relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Buscar pessoa..."
                            value={peopleSearchTerm}
                            onChange={(e) => setPeopleSearchTerm(e.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white"
                          />
                        </div>
                        <select
                          value={peopleFilterType}
                          onChange={(e) => { setPeopleFilterType(e.target.value as any); setPeoplePage(1); }}
                          className="py-1.5 px-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white text-gray-700"
                        >
                          <option value="all">Todas ({devicePeopleTotalCount})</option>
                          <option value="authorized">Autorizadas ({devicePeopleAuthorizedCount})</option>
                          <option value="unauthorized">Não Autorizadas ({devicePeopleUnauthorizedCount})</option>
                          <option value="duplicates">Duplicados ({devicePeople.filter((p: any) => p.is_duplicate).length})</option>
                        </select>
                        <select
                          value={devicePeoplePhotoFilter}
                          onChange={(e) => { setDevicePeoplePhotoFilter(e.target.value as any); setPeoplePage(1); }}
                          className="py-1.5 px-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white text-gray-700"
                        >
                          <option value="all">Fotos: Todas</option>
                          <option value="with_photo">Com Foto ({devicePeopleWithPhotoCount})</option>
                          <option value="without_photo">Sem Foto ({devicePeopleWithoutPhotoCount})</option>
                        </select>
                      </div>
                    )}

                    {/* Bulk actions bar */}
                    {selectedPeopleToLink.size > 0 && (
                      <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-900">
                          {selectedPeopleToLink.size} pessoa{selectedPeopleToLink.size > 1 ? 's' : ''} selecionada{selectedPeopleToLink.size > 1 ? 's' : ''}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={openBulkLinkModal}
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Vincular Selecionados
                          </button>
                          <button
                            onClick={() => setSelectedPeopleToLink(new Set())}
                            className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Bulk remove bar */}
                    {selectedPeopleToRemove.size > 0 && (
                      <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                        <span className="text-sm font-medium text-red-900">
                          {selectedPeopleToRemove.size} pessoa{selectedPeopleToRemove.size > 1 ? 's' : ''} selecionada{selectedPeopleToRemove.size > 1 ? 's' : ''} para remoção
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const filtered = devicePeople.filter((person: any) => {
                                const matchesSearch = person.name?.toLowerCase().includes(peopleSearchTerm.toLowerCase()) ||
                                                     person.employeeNo?.toString().includes(peopleSearchTerm);
                                const matchesFilter = peopleFilterType === 'all' ||
                                                     (peopleFilterType === 'authorized' && !person.is_unauthorized) ||
                                                     (peopleFilterType === 'unauthorized' && person.is_unauthorized) ||
                                                     (peopleFilterType === 'duplicates' && person.is_duplicate === true);
                                const hasPhoto = person.faceURL || person.face_url || person.photo_url || person.has_photo;
                                const matchesPhotoFilter = devicePeoplePhotoFilter === 'all' ||
                                                           (devicePeoplePhotoFilter === 'with_photo' && hasPhoto) ||
                                                           (devicePeoplePhotoFilter === 'without_photo' && !hasPhoto);
                                return matchesSearch && matchesFilter && matchesPhotoFilter;
                              });
                              const newSelected = new Map(selectedPeopleToRemove);
                              filtered.forEach((p: any) => newSelected.set(p.employeeNo, p));
                              setSelectedPeopleToRemove(newSelected);
                            }}
                            className="px-3 py-1.5 text-sm bg-red-100 text-red-700 border border-red-300 rounded-lg hover:bg-red-200 transition-colors"
                          >
                            Selecionar Todos
                          </button>
                          <button
                            onClick={handleBulkRemoveDevicePeople}
                            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Remover Selecionados
                          </button>
                          <button
                            onClick={() => setSelectedPeopleToRemove(new Map())}
                            className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                    {(() => {
                      // Filter people based on search, authorization filter, photo filter, and duplicates
                      const filteredPeople = devicePeople
                        .filter((person: any) => {
                          const matchesSearch = person.name?.toLowerCase().includes(peopleSearchTerm.toLowerCase()) ||
                                               person.employeeNo?.toString().includes(peopleSearchTerm);
                          const matchesFilter = peopleFilterType === 'all' ||
                                               (peopleFilterType === 'authorized' && !person.is_unauthorized) ||
                                               (peopleFilterType === 'unauthorized' && person.is_unauthorized) ||
                                               (peopleFilterType === 'duplicates' && person.is_duplicate === true);

                          // Photo filter
                          const hasPhoto = person.faceURL || person.face_url || person.photo_url || person.has_photo;
                          const matchesPhotoFilter = devicePeoplePhotoFilter === 'all' ||
                                                     (devicePeoplePhotoFilter === 'with_photo' && hasPhoto) ||
                                                     (devicePeoplePhotoFilter === 'without_photo' && !hasPhoto);

                          return matchesSearch && matchesFilter && matchesPhotoFilter;
                        })
                        // Always sort by name ascending
                        .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' }));

                      // Pagination
                      const itemsPerPage = 10;
                      const totalPages = Math.ceil(filteredPeople.length / itemsPerPage);
                      const startIndex = (peoplePage - 1) * itemsPerPage;
                      const paginatedPeople = filteredPeople.slice(startIndex, startIndex + itemsPerPage);

                      if (devicePeople.length === 0) {
                        return (
                          <div className="text-center py-12 bg-gray-50 rounded-lg">
                            <Cpu className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            {devicePeopleCloudUnavailable ? (
                              <>
                                {window.electronAPI ? (
                                  <>
                                    <p className="text-gray-600">Não foi possível conectar ao dispositivo</p>
                                    <p className="text-sm text-gray-500 mt-1">
                                      Verifique se o dispositivo está acessível na rede e se o endereço IP está correto
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <p className="text-gray-600">Visualização de pessoas no dispositivo não disponível no navegador</p>
                                    <p className="text-sm text-gray-500 mt-1">
                                      Use o aplicativo desktop para acessar esta funcionalidade
                                    </p>
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                <p className="text-gray-600">Nenhuma pessoa registrada no dispositivo</p>
                                <p className="text-sm text-gray-500 mt-1">
                                  Use o botão "Sincronizar" na aba Sincronização para enviar pessoas autorizadas
                                </p>
                              </>
                            )}
                          </div>
                        );
                      }

                      if (filteredPeople.length === 0) {
                        return (
                          <p className="text-sm text-gray-500 text-center py-8">
                            Nenhuma pessoa encontrada com os filtros aplicados
                          </p>
                        );
                      }

                      return (
                        <>
                          <div className="space-y-2">
                            {paginatedPeople.map((devicePerson: any, index: number) => {
                              const isUnauthorized = devicePerson.is_unauthorized === true;
                              const isDuplicate = devicePerson.is_duplicate === true;
                              const hasPhoto = devicePerson.faceURL || devicePerson.face_url || devicePerson.photo_url || devicePerson.has_photo;
                              const isFaceDevice = device.device_type === 'facial_reader' || device.device_type === 'facial_recognition' || device.device_type === 'controlid_face';
                              const isDigitalControlID = !isFaceDevice && (device.manufacturer?.toLowerCase().includes('control') || device.device_type === 'controlid_rfid' || device.device_type === 'fingerprint');

                              const isSelected = selectedPeopleToLink.has(devicePerson.employeeNo);
                              const isSelectedForRemoval = selectedPeopleToRemove.has(devicePerson.employeeNo);

                              return (
                                <div
                                  key={`${devicePerson.employeeNo || ''}-${index}`}
                                  className={`flex items-center gap-3 p-3 rounded-lg transition-opacity ${
                                    isDuplicate
                                      ? 'bg-orange-50 border-2 border-orange-400'
                                      : isUnauthorized
                                        ? 'bg-red-50 border border-red-200'
                                        : 'bg-blue-50 border border-blue-200'
                                  } ${isSelected ? 'ring-2 ring-blue-500' : ''} ${isSelectedForRemoval ? 'ring-2 ring-red-400' : ''}`}
                                >
                                  {/* Checkbox for bulk link selection (unauthorized persons only) */}
                                  {isUnauthorized && (
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        const newSelected = new Set(selectedPeopleToLink);
                                        if (e.target.checked) {
                                          newSelected.add(devicePerson.employeeNo);
                                        } else {
                                          newSelected.delete(devicePerson.employeeNo);
                                        }
                                        setSelectedPeopleToLink(newSelected);
                                      }}
                                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                  )}

                                  {/* Checkbox for bulk remove selection (all persons) */}
                                  <input
                                    type="checkbox"
                                    checked={isSelectedForRemoval}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      const newSelected = new Map(selectedPeopleToRemove);
                                      if (e.target.checked) {
                                        newSelected.set(devicePerson.employeeNo, devicePerson);
                                      } else {
                                        newSelected.delete(devicePerson.employeeNo);
                                      }
                                      setSelectedPeopleToRemove(newSelected);
                                    }}
                                    className="w-4 h-4 text-red-600 rounded focus:ring-red-500 accent-red-600"
                                    title="Selecionar para remoção"
                                  />

                                  {/* User photo avatar from device - with smart local/cloud routing */}
                                  {hasPhoto && device?.id && devicePerson.employeeNo && (
                                    <div className="flex-shrink-0">
                                      <DevicePersonPhoto
                                        deviceId={device.id}
                                        employeeNo={devicePerson.employeeNo}
                                        device={device}
                                        alt={devicePerson.name || 'Foto'}
                                        directFaceUrl={devicePerson.direct_face_url}
                                      />
                                    </div>
                                  )}

                                  <div
                                    className={`flex-1 min-w-0 ${!isUnauthorized && devicePerson.person_id ? 'cursor-pointer hover:opacity-75' : 'cursor-default'}`}
                                    onClick={() => !isUnauthorized && devicePerson.person_id && openPersonDetail(devicePerson.person_id)}
                                  >
                                    <p className="font-medium text-gray-900 truncate">
                                      {devicePerson.name || 'Sem nome'}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                      {devicePerson.employeeNo && (
                                        <span className="text-xs text-gray-600">
                                          Nº Dispositivo: {devicePerson.employeeNo}
                                        </span>
                                      )}
                                      {devicePerson.person_id && (
                                        <span className="text-xs text-blue-600">
                                          ID Sistema: {devicePerson.person_id.substring(0, 8)}...
                                        </span>
                                      )}
                                      {hasPhoto && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                                           Com foto
                                        </span>
                                      )}
                                      {isDuplicate && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium flex items-center gap-1">
                                          <AlertCircle className="w-3 h-3" />
                                          Duplicado
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {isUnauthorized ? (
                                      <>
                                        <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 flex items-center gap-1">
                                          <AlertCircle className="w-3 h-3" />
                                          Não Autorizado
                                        </span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openLinkModal(devicePerson);
                                          }}
                                          className="px-2 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                                          title="Vincular ao sistema"
                                        >
                                          Vincular
                                        </button>
                                        <button
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            if (!await showConfirm({ message: `Remover "${devicePerson.name}" do dispositivo?`, variant: 'danger', confirmLabel: 'Remover' })) return;
                                            await handleRemoveDevicePerson(devicePerson);
                                          }}
                                          className="p-1.5 rounded-md hover:bg-red-100 text-red-600 hover:text-red-700 transition-colors"
                                          title="Remover do dispositivo"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 flex items-center gap-1">
                                          <CheckCircle className="w-3 h-3" />
                                          Autorizado
                                        </span>
                                        {hasPhoto && isFaceDevice && (
                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              if (!await showConfirm({ message: `Deseja forçar a atualização da foto de "${devicePerson.name}" neste dispositivo?`, variant: 'warning', confirmLabel: 'Forçar' })) return;

                                              const isControlIDDevice = device.manufacturer?.toLowerCase() === 'controlid';
                                              try {
                                                let directSuccess = false;
                                                if (window.electronAPI) {
                                                  try {
                                                    // Resolve person from system.
                                                    // Hikvision: person_id is always '' (device doesn't return it),
                                                    // so look up via MD5 match: md5(person.id) === employeeNo
                                                    let personData: any;
                                                    if (devicePerson.person_id) {
                                                      const personResponse = await fetchWithAuth(`/api/v1/people/${devicePerson.person_id}`);
                                                      if (!personResponse.ok) throw new Error(`Erro ao buscar pessoa: ${personResponse.status}`);
                                                      personData = await personResponse.json();
                                                    } else {
                                                      if (!device.zone_id) throw new Error('Dispositivo sem zona configurada');
                                                      const authResp = await fetchWithAuth(`/api/v1/zones/${device.zone_id}/authorized-people`);
                                                      if (!authResp.ok) throw new Error(`Erro ao buscar pessoas autorizadas: ${authResp.status}`);
                                                      const authData = await authResp.json();
                                                      const matched = (authData.data || []).find((p: any) => md5(p.id) === devicePerson.employeeNo);
                                                      if (!matched) throw new Error(`Pessoa não encontrada no sistema (employeeNo=${devicePerson.employeeNo})`);
                                                      personData = matched;
                                                    }
                                                    // Pre-fetch photo with Bearer token — fetchImageAsBase64 uses plain
                                                    // fetch() (no auth), which silently fails with 401 on the backend.
                                                    const rawPhotoUrl = personData.identity_photo?.photo_url || personData.identity_photo_url || personData.profile_picture_url;
                                                    if (!rawPhotoUrl) throw new Error('Nenhuma foto cadastrada para esta pessoa');
                                                    const fullPhotoUrl = rawPhotoUrl.startsWith('/') ? `https://access.panther.com.br${rawPhotoUrl}` : rawPhotoUrl;
                                                    const photoResp = await fetchWithAuth(fullPhotoUrl);
                                                    if (!photoResp.ok) throw new Error(`Erro ao buscar foto: HTTP ${photoResp.status}`);
                                                    const photoBlob = await photoResp.blob();
                                                    const photoDataUri = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onloadend = () => res(r.result as string); r.onerror = rej; r.readAsDataURL(photoBlob); });
                                                    const photoProbe = await smartSelectDeviceIp(
                                                      { ip_address: device.ip_address, local_ip_address: device.local_ip_address, port: device.port },
                                                      { timeoutLocal: 400, timeoutPublic: 1500 }
                                                    );
                                                  const photoWorkingIp = photoProbe?.ip || device.local_ip_address || device.ip_address;
                                                  const deviceInfo = {
                                                      id: device.id,
                                                      ip_address: photoWorkingIp,
                                                      local_ip_address: device.local_ip_address,
                                                      public_ip_address: device.ip_address,
                                                      username: device.username || '',
                                                      password: device.password || '',
                                                      vendor: isControlIDDevice ? 'controlid' as const : 'hikvision' as const,
                                                      port: device.port?.toString(),
                                                    };
                                                    if (isControlIDDevice) {
                                                      const loadResult = await DeviceIntegration.instance.execute(deviceInfo, DeviceOperations.LOAD_PEOPLE, { registration: devicePerson.employeeNo }, CommunicationMode.DIRECT);
                                                      const loadData = typeof loadResult.data === 'string' ? (() => { try { return JSON.parse(loadResult.data); } catch { return loadResult.data; } })() : loadResult.data;
                                                      const numericId: number | undefined = loadData?.users?.[0]?.id;
                                                      if (!numericId) throw new Error('Usuário não encontrado no dispositivo');
                                                      await deviceCommunicationService.reuploadFacePhoto(deviceInfo, numericId, { person_id: personData.id, first_name: personData.first_name || '', last_name: personData.last_name || '', identity_photo_url: photoDataUri, profile_picture_url: photoDataUri, isAllowed: true });
                                                      const groupId = await deviceCommunicationService.ensureControlIDDefaultAccess(deviceInfo);
                                                      if (groupId !== null) await deviceCommunicationService.assignControlIDUserToGroup(deviceInfo, numericId, groupId);
                                                    } else {
                                                      await deviceCommunicationService.reuploadFacePhoto(deviceInfo, devicePerson.employeeNo, { person_id: personData.id, first_name: personData.first_name || '', last_name: personData.last_name || '', identity_photo_url: photoDataUri, profile_picture_url: photoDataUri, isAllowed: true });
                                                    }
                                                    directSuccess = true;
                                                    toast.success('Foto atualizada com sucesso');
                                                    await fetchDevicePeople();
                                                  } catch (directErr: any) {
                                                    console.warn('[ForcePhoto] Direct upload failed:', directErr?.message);
                                                    toast('Envio direto falhou: ' + (directErr?.message || 'Erro desconhecido'), { duration: 5000 });
                                                  }
                                                }
                                                if (!directSuccess) {
                                                  // If running in Electron and device is on local network only,
                                                  // the cloud server (ECS) cannot reach it — skip the cloud call.
                                                  const rawIp = (device.local_ip_address || device.ip_address || '').replace(/^https?:\/\//, '').split(':')[0];
                                                  const isLocalOnlyDevice = window.electronAPI && (
                                                    rawIp.startsWith('192.168.') || rawIp.startsWith('10.') ||
                                                    /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(rawIp) ||
                                                    rawIp === 'localhost' || rawIp === '127.0.0.1'
                                                  );
                                                  if (isLocalOnlyDevice) {
                                                    throw new Error('Dispositivo não acessível. Verifique se está na mesma rede local que o dispositivo e tente novamente.');
                                                  }
                                                  const response = await fetchWithAuth(
                                                    `/api/v1/iot-connect/facial-recognition/devices/${device.id}/people/${devicePerson.employeeNo}/force-photo-update`,
                                                    { method: 'POST' }
                                                  );
                                                  if (!response.ok) throw new Error(`HTTP ${response.status}: ${await response.text()}`);
                                                  const result = await response.json();
                                                  toast.success(result.message || 'Foto atualizada com sucesso');
                                                  await fetchDevicePeople();
                                                }
                                              } catch (error: any) {
                                                console.error('Erro ao atualizar foto:', error);
                                                toast.error(error.message || 'Erro ao atualizar foto');
                                              }
                                            }}
                                            className="p-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                                            title="Forçar atualização da foto"
                                          >
                                            <RefreshCw className="w-3 h-3" />
                                          </button>
                                        )}
                                        {isFaceDevice && <button
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            if (!await showConfirm({ message: `Deseja enviar a foto do sistema de "${devicePerson.name}" para o dispositivo?`, variant: 'warning', confirmLabel: 'Enviar' })) return;

                                            const isControlIDDevice = device.manufacturer?.toLowerCase() === 'controlid';
                                            try {
                                              let directSuccess = false;
                                              if (window.electronAPI) {
                                                try {
                                                  // Resolve person from system (Hikvision: person_id is '' → MD5 lookup)
                                                  let personData: any;
                                                  if (devicePerson.person_id) {
                                                    const personResponse = await fetchWithAuth(`/api/v1/people/${devicePerson.person_id}`);
                                                    if (!personResponse.ok) throw new Error(`Erro ao buscar pessoa: ${personResponse.status}`);
                                                    personData = await personResponse.json();
                                                  } else {
                                                    if (!device.zone_id) throw new Error('Dispositivo sem zona configurada');
                                                    const authResp = await fetchWithAuth(`/api/v1/zones/${device.zone_id}/authorized-people`);
                                                    if (!authResp.ok) throw new Error(`Erro ao buscar pessoas autorizadas: ${authResp.status}`);
                                                    const authData = await authResp.json();
                                                    const matched = (authData.data || []).find((p: any) => md5(p.id) === devicePerson.employeeNo);
                                                    if (!matched) throw new Error(`Pessoa não encontrada no sistema (employeeNo=${devicePerson.employeeNo})`);
                                                    personData = matched;
                                                  }
                                                  // Pre-fetch photo with Bearer token
                                                  const rawPhotoUrl = personData.identity_photo?.photo_url || personData.identity_photo_url || personData.profile_picture_url;
                                                  if (!rawPhotoUrl) throw new Error('Nenhuma foto cadastrada para esta pessoa');
                                                  const fullPhotoUrl = rawPhotoUrl.startsWith('/') ? `https://access.panther.com.br${rawPhotoUrl}` : rawPhotoUrl;
                                                  const photoResp = await fetchWithAuth(fullPhotoUrl);
                                                  if (!photoResp.ok) throw new Error(`Erro ao buscar foto: HTTP ${photoResp.status}`);
                                                  const photoBlob = await photoResp.blob();
                                                  const photoDataUri = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onloadend = () => res(r.result as string); r.onerror = rej; r.readAsDataURL(photoBlob); });
                                                  const photoProbe2 = await smartSelectDeviceIp(
                                                    { ip_address: device.ip_address, local_ip_address: device.local_ip_address, port: device.port },
                                                    { timeoutLocal: 400, timeoutPublic: 1500 }
                                                  );
                                                  const photoWorkingIp2 = photoProbe2?.ip || device.local_ip_address || device.ip_address;
                                                  const deviceInfo = {
                                                    id: device.id,
                                                    ip_address: photoWorkingIp2,
                                                    local_ip_address: device.local_ip_address,
                                                    public_ip_address: device.ip_address,
                                                    username: device.username || '',
                                                    password: device.password || '',
                                                    vendor: isControlIDDevice ? 'controlid' as const : 'hikvision' as const,
                                                    port: device.port?.toString(),
                                                  };
                                                  if (isControlIDDevice) {
                                                    const loadResult = await DeviceIntegration.instance.execute(deviceInfo, DeviceOperations.LOAD_PEOPLE, { registration: devicePerson.employeeNo }, CommunicationMode.DIRECT);
                                                    const loadData = typeof loadResult.data === 'string' ? (() => { try { return JSON.parse(loadResult.data); } catch { return loadResult.data; } })() : loadResult.data;
                                                    const numericId: number | undefined = loadData?.users?.[0]?.id;
                                                    if (!numericId) throw new Error('Usuário não encontrado no dispositivo');
                                                    await deviceCommunicationService.reuploadFacePhoto(deviceInfo, numericId, { person_id: personData.id, first_name: personData.first_name || '', last_name: personData.last_name || '', identity_photo_url: photoDataUri, profile_picture_url: photoDataUri, isAllowed: true });
                                                    const groupId = await deviceCommunicationService.ensureControlIDDefaultAccess(deviceInfo);
                                                    if (groupId !== null) await deviceCommunicationService.assignControlIDUserToGroup(deviceInfo, numericId, groupId);
                                                  } else {
                                                    await deviceCommunicationService.reuploadFacePhoto(deviceInfo, devicePerson.employeeNo, { person_id: personData.id, first_name: personData.first_name || '', last_name: personData.last_name || '', identity_photo_url: photoDataUri, profile_picture_url: photoDataUri, isAllowed: true });
                                                  }
                                                  directSuccess = true;
                                                  toast.success('Foto do sistema enviada com sucesso');
                                                  await fetchDevicePeople();
                                                } catch (directErr: any) {
                                                  console.warn('[UploadSystemPhoto] Direct upload failed:', directErr?.message);
                                                  toast('Envio direto falhou: ' + (directErr?.message || 'Erro desconhecido'), { duration: 5000 });
                                                }
                                              }
                                              if (!directSuccess) {
                                                const rawIp = (device.local_ip_address || device.ip_address || '').replace(/^https?:\/\//, '').split(':')[0];
                                                const isLocalOnlyDevice = window.electronAPI && (
                                                  rawIp.startsWith('192.168.') || rawIp.startsWith('10.') ||
                                                  /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(rawIp) ||
                                                  rawIp === 'localhost' || rawIp === '127.0.0.1'
                                                );
                                                if (isLocalOnlyDevice) {
                                                  throw new Error('Dispositivo local não pode ser acessado pela nuvem. Verifique se está na mesma rede e tente novamente.');
                                                }
                                                const response = await fetchWithAuth(
                                                  `/api/v1/iot-connect/facial-recognition/devices/${device.id}/people/${devicePerson.employeeNo}/upload-system-photo`,
                                                  { method: 'POST' }
                                                );
                                                if (!response.ok) throw new Error(`HTTP ${response.status}: ${await response.text()}`);
                                                const result = await response.json();
                                                toast.success(result.message || 'Foto do sistema enviada com sucesso');
                                                await fetchDevicePeople();
                                              }
                                            } catch (error: any) {
                                              console.error('Erro ao enviar foto do sistema:', error);
                                              toast.error(error.message || 'Erro ao enviar foto do sistema');
                                            }
                                          }}
                                          className="p-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                                          title="Enviar foto do sistema para o dispositivo"
                                        >
                                          <Upload className="w-3 h-3" />
                                        </button>}
                                        {isDigitalControlID && devicePerson.person_id && (
                                          <button
                                            disabled={!!fingerprintImporting[devicePerson.person_id]}
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              const customerId = device.customer_id || zone?.customer_id;
                                              if (!customerId) { toast.error('Cliente não identificado'); return; }
                                              setFingerprintImporting(prev => ({ ...prev, [devicePerson.person_id]: true }));
                                              try {
                                                const result = await fingerprintService.importFromDevice(customerId, devicePerson.person_id, device.id);
                                                if (result.imported_count === 0) {
                                                  toast('Nenhuma digital encontrada no dispositivo para esta pessoa', { duration: 4000 });
                                                } else {
                                                  toast.success(`${result.imported_count} digital(ais) vinculada(s) para ${devicePerson.name}`);
                                                }
                                              } catch (err: any) {
                                                toast.error(err.message || 'Erro ao importar digital');
                                              } finally {
                                                setFingerprintImporting(prev => ({ ...prev, [devicePerson.person_id]: false }));
                                              }
                                            }}
                                            className="p-1.5 text-xs rounded-md bg-teal-600 text-white hover:bg-teal-700 transition-colors disabled:opacity-50"
                                            title="Vincular digital do dispositivo"
                                          >
                                            {fingerprintImporting[devicePerson.person_id]
                                              ? <RefreshCw className="w-3 h-3 animate-spin" />
                                              : <Fingerprint className="w-3 h-3" />
                                            }
                                          </button>
                                        )}
                                        {isDigitalControlID && devicePerson.person_id && (
                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              const customerId = device.customer_id || zone?.customer_id;
                                              if (!customerId) { toast.error('Cliente não identificado'); return; }
                                              try {
                                                const fps = await fingerprintService.list(customerId, devicePerson.person_id);
                                                const withTemplate = fps.filter(fp => !!fp.template_b64);
                                                if (withTemplate.length === 0) {
                                                  toast('Nenhuma biometria cadastrada para esta pessoa', { duration: 4000 });
                                                  return;
                                                }
                                                if (!await showConfirm({ message: `Enviar ${withTemplate.length} template(s) de biometria de "${devicePerson.name}" para o dispositivo?`, variant: 'warning', confirmLabel: 'Enviar' })) return;
                                                const personResponse = await fetchWithAuth(`/api/v1/people/${devicePerson.person_id}`);
                                                if (!personResponse.ok) throw new Error(`Erro ao buscar pessoa: ${personResponse.status}`);
                                                const personData = await personResponse.json();
                                                const fpProbe = await smartSelectDeviceIp(
                                                  { ip_address: device.ip_address, local_ip_address: device.local_ip_address, port: device.port },
                                                  { timeoutLocal: 400, timeoutPublic: 1500 }
                                                );
                                                const fpWorkingIp = fpProbe?.ip || device.local_ip_address || device.ip_address;
                                                const deviceInfo = {
                                                  id: device.id,
                                                  ip_address: fpWorkingIp,
                                                  local_ip_address: device.local_ip_address,
                                                  public_ip_address: device.ip_address,
                                                  username: device.username || '',
                                                  password: device.password || '',
                                                  vendor: 'controlid' as const,
                                                  port: device.port?.toString(),
                                                };
                                                const payload = {
                                                  person_id: personData.id,
                                                  first_name: personData.first_name || '',
                                                  last_name: personData.last_name || '',
                                                  cpf: personData.cpf,
                                                  identity_photo_url: personData.identity_photo_url,
                                                  profile_picture_url: personData.profile_picture_url,
                                                  isAllowed: true,
                                                  rfid_tags: personData.rfid_tags || [],
                                                  vehicle_plates: [],
                                                  fingerprint_templates: withTemplate.map(fp => ({ templateB64: fp.template_b64! })),
                                                };
                                                const result = await DeviceIntegration.instance.execute(deviceInfo, DeviceOperations.SYNC_PERSON, payload, CommunicationMode.DIRECT);
                                                if (result.success) {
                                                  const pushed = result.data?.fpPushed ?? withTemplate.length;
                                                  const failed = result.data?.fpFailed ?? 0;
                                                  if (failed > 0) toast(`Biometria enviada parcialmente: ${pushed} ok, ${failed} falha(s)`, { duration: 5000 });
                                                  else toast.success(`${pushed} template(s) de biometria enviado(s) com sucesso`);
                                                  await fetchDevicePeople();
                                                } else {
                                                  throw new Error(result.error || 'Falha ao sincronizar biometria');
                                                }
                                              } catch (err: any) {
                                                console.error('[BiometryPush] Error:', err);
                                                toast.error(err.message || 'Erro ao enviar biometria');
                                              }
                                            }}
                                            className="p-1.5 text-xs rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                                            title="Enviar biometria para o dispositivo"
                                          >
                                            <Fingerprint className="w-3 h-3" />
                                          </button>
                                        )}
                                        <button
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            if (!await showConfirm({ message: `Remover "${devicePerson.name}" do dispositivo?\n\nATENÇÃO: Esta pessoa está autorizada no sistema e será removida apenas fisicamente do dispositivo.`, variant: 'danger', confirmLabel: 'Remover' })) return;
                                            await handleRemoveDevicePerson(devicePerson);
                                          }}
                                          className="p-1.5 rounded-md hover:bg-red-100 text-red-600 hover:text-red-700 transition-colors"
                                          title="Remover do dispositivo"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Pagination */}
                          {totalPages > 1 && (
                            <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-200 flex-wrap gap-2">
                              <div className="text-xs text-gray-500">
                                Mostrando {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredPeople.length)} de {filteredPeople.length}
                              </div>
                              <div className="flex gap-1 items-center flex-wrap">
                                <button
                                  onClick={() => setPeoplePage(Math.max(1, peoplePage - 1))}
                                  disabled={peoplePage === 1}
                                  className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                  Anterior
                                </button>
                                {(() => {
                                  const pages: (number | 'ellipsis-left' | 'ellipsis-right')[] = [];
                                  const delta = 2;
                                  const left = peoplePage - delta;
                                  const right = peoplePage + delta;

                                  pages.push(1);
                                  if (left > 2) pages.push('ellipsis-left');

                                  for (let p = Math.max(2, left); p <= Math.min(totalPages - 1, right); p++) {
                                    pages.push(p);
                                  }

                                  if (right < totalPages - 1) pages.push('ellipsis-right');
                                  if (totalPages > 1) pages.push(totalPages);

                                  return pages.map((p) =>
                                    p === 'ellipsis-left' || p === 'ellipsis-right' ? (
                                      <span key={p} className="px-1 text-xs text-gray-400 select-none">…</span>
                                    ) : (
                                      <button
                                        key={p}
                                        onClick={() => setPeoplePage(p)}
                                        className={`px-2 py-1 text-xs border rounded ${
                                          peoplePage === p
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'border-gray-300 hover:bg-gray-50'
                                        }`}
                                      >
                                        {p}
                                      </button>
                                    )
                                  );
                                })()}
                                <button
                                  onClick={() => setPeoplePage(Math.min(totalPages, peoplePage + 1))}
                                  disabled={peoplePage === totalPages}
                                  className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                  Próxima
                                </button>

                                {/* Go to page */}
                                <div className="flex items-center gap-1 ml-2 border-l border-gray-200 pl-2">
                                  <span className="text-xs text-gray-500 whitespace-nowrap">Ir para</span>
                                  <input
                                    type="number"
                                    min={1}
                                    max={totalPages}
                                    value={peopleGoToInput}
                                    onChange={(e) => setPeopleGoToInput(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        const page = parseInt(peopleGoToInput, 10);
                                        if (!isNaN(page) && page >= 1 && page <= totalPages) {
                                          setPeoplePage(page);
                                        }
                                        setPeopleGoToInput('');
                                      }
                                    }}
                                    placeholder={`${peoplePage}`}
                                    className="w-12 px-1 py-1 text-xs border border-gray-300 rounded text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                  <button
                                    onClick={() => {
                                      const page = parseInt(peopleGoToInput, 10);
                                      if (!isNaN(page) && page >= 1 && page <= totalPages) {
                                        setPeoplePage(page);
                                      }
                                      setPeopleGoToInput('');
                                    }}
                                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                  >
                                    Ir
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
                </>
              ))}
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div>
              {isLoadingEvents ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-5 h-5 text-gray-400 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Carregando eventos...</p>
                </div>
              ) : accessEvents.length === 0 ? (
                <div className="text-center py-10">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">Nenhum evento registrado</p>
                </div>
              ) : (
                <>
                  <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                    {accessEvents.map((event) => {
                        const eventLabels: { [key: string]: string } = {
                          'rfid_access': 'Acesso por Tag',
                          'authentication_success': 'Autenticação',
                          'authentication_failed': 'Falha na Autenticação',
                          'door_opened': 'Porta Aberta',
                          'door_closed': 'Porta Fechada',
                          'exit_button_pressed': 'Botão de Saída',
                          'remote_door_trigger': 'Abertura Remota',
                          'door_open_timeout': 'Porta Aberta Muito Tempo',
                          'door_forced_open': 'Porta Forçada',
                        };
                        const directionLabels: { [key: string]: string } = {
                          'entrada': 'Entrada', 'saida': 'Saída', 'passagem': 'Passagem',
                        };
                        const isWarning = ['door_forced_open', 'door_open_timeout'].includes(event.event_type);
                        const isDoor = ['door_opened', 'remote_door_trigger', 'exit_button_pressed'].includes(event.event_type);
                        const dotColor = isWarning ? 'bg-orange-400' : isDoor ? 'bg-blue-400' : event.is_authorized ? 'bg-green-500' : 'bg-red-400';
                        return (
                          <div
                            key={event.id}
                            className={`flex items-center gap-3 px-3 py-2.5 bg-white hover:bg-gray-50 transition-colors ${event.person_id ? 'cursor-pointer' : ''}`}
                            onClick={() => event.person_id && openPersonDetail(event.person_id)}
                          >
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-0.5 ${dotColor}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-2">
                                <span className="text-sm font-medium text-gray-900 truncate">
                                  {event.person_name || (event.employee_no ? `Tag ${event.employee_no}` : 'Desconhecido')}
                                </span>
                                <span className="text-xs text-gray-400 flex-shrink-0 ml-auto">
                                  {new Date(event.event_timestamp).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-400">
                                <span>{eventLabels[event.event_type] ?? event.event_type}</span>
                                {event.direction && directionLabels[event.direction] && (
                                  <span>· {directionLabels[event.direction]}</span>
                                )}
                                {!event.is_authorized && !isWarning && !isDoor && (
                                  <span className="text-red-500 font-medium">· Não autorizado</span>
                                )}
                                {event.event_type === 'remote_door_trigger' && (event.triggered_by_email || event.raw_event_data?.triggered_by?.email) && (
                                  <span className="text-gray-500">· {event.triggered_by_email || event.raw_event_data?.triggered_by?.email}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  {/* Pagination */}
                  {accessEventsTotal > accessEventsLimit && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-400">
                        {((accessEventsPage - 1) * accessEventsLimit) + 1}–{Math.min(accessEventsPage * accessEventsLimit, accessEventsTotal)} de {accessEventsTotal}
                      </span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { const p = Math.max(1, accessEventsPage - 1); setAccessEventsPage(p); fetchAccessEvents(p, accessEventsLimit); }} disabled={accessEventsPage === 1 || isLoadingEvents} className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40">←</button>
                        {Array.from({ length: Math.min(5, Math.ceil(accessEventsTotal / accessEventsLimit)) }, (_, i) => {
                          const total = Math.ceil(accessEventsTotal / accessEventsLimit);
                          const start = Math.max(1, Math.min(accessEventsPage - 2, total - 4));
                          const pg = start + i;
                          if (pg > total) return null;
                          return <button key={pg} onClick={() => { setAccessEventsPage(pg); fetchAccessEvents(pg, accessEventsLimit); }} disabled={isLoadingEvents} className={`w-7 h-7 text-xs border rounded ${accessEventsPage === pg ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`}>{pg}</button>;
                        })}
                        <button onClick={() => { const total = Math.ceil(accessEventsTotal / accessEventsLimit); const p = Math.min(total, accessEventsPage + 1); setAccessEventsPage(p); fetchAccessEvents(p, accessEventsLimit); }} disabled={accessEventsPage >= Math.ceil(accessEventsTotal / accessEventsLimit) || isLoadingEvents} className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40">→</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Sync Tab */}
          {activeTab === 'sync' && (
            <div>
              {isLoadingSync ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-5 h-5 text-gray-400 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Carregando...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {syncStatus && (
                    <>
                      {/* Status row */}
                      <div className="border border-gray-100 rounded-lg overflow-hidden">
                        <dl>
                          <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
                            <dt className="text-xs text-gray-500 w-40 flex-shrink-0">Última sincronização</dt>
                            <dd className="text-sm text-gray-900 font-medium">
                              {syncStatus.last_sync ? new Date(syncStatus.last_sync).toLocaleString('pt-BR') : <span className="text-gray-400">Nunca</span>}
                            </dd>
                          </div>
                          <div className="flex items-center px-4 py-2.5">
                            <dt className="text-xs text-gray-500 w-40 flex-shrink-0">Status</dt>
                            <dd>
                              {(() => {
                                const s = syncStatus.status;
                                const cfg = s === 'success' || s === 'completed'
                                  ? { label: 'Concluído com sucesso', dot: 'bg-green-500', text: 'text-green-700' }
                                  : s === 'failed' || s === 'error'
                                  ? { label: 'Falha na sincronização', dot: 'bg-red-500', text: 'text-red-600' }
                                  : s === 'pending' || s === 'queued'
                                  ? { label: 'Aguardando execução', dot: 'bg-amber-500 animate-pulse', text: 'text-amber-700' }
                                  : s === 'running' || s === 'in_progress'
                                  ? { label: 'Sincronizando...', dot: 'bg-blue-500 animate-pulse', text: 'text-blue-700' }
                                  : s === 'partial'
                                  ? { label: 'Concluído parcialmente', dot: 'bg-amber-400', text: 'text-amber-700' }
                                  : null;
                                return cfg ? (
                                  <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${cfg.text}`}>
                                    <span className={`h-2 w-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                                    {cfg.label}
                                  </span>
                                ) : (
                                  <span className="text-sm text-gray-400">Desconhecido</span>
                                );
                              })()}
                            </dd>
                          </div>
                        </dl>
                      </div>

                      {syncStatus.error && (
                        <div className="flex items-start gap-2 px-3 py-2.5 border border-red-100 rounded-lg bg-red-50/50">
                          <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-red-700 mb-0.5">Erro de Sincronização</p>
                            <p className="text-xs text-red-600">{syncStatus.error}</p>
                          </div>
                        </div>
                      )}

                      {syncStatus.details && (
                        <div className="border border-gray-100 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-500 mb-1.5">Detalhes</p>
                          <pre className="text-xs text-gray-600 overflow-x-auto">{JSON.stringify(syncStatus.details, null, 2)}</pre>
                        </div>
                      )}
                    </>
                  )}

                  {/* Sync History */}
                  {(() => {
                    const SYNC_OP_LABELS: Record<string, string> = {
                      sync: 'Sincronização',
                      manual_sync: 'Sincronização Manual',
                      sync_photo: 'Sincronização de Foto',
                      remove: 'Remoção',
                      manual_revoke: 'Revogação Manual',
                      auto_revoke_entrada: 'Revogação Auto. (Entrada)',
                      auto_revoke_saida: 'Revogação Auto. (Saída)',
                    };
                    const totalPages = Math.ceil(syncHistoryTotal / syncHistoryPageSize);
                    return (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Histórico
                            {syncHistoryTotal > 0 && <span className="font-normal normal-case ml-1 text-gray-400">({syncHistoryTotal})</span>}
                          </span>
                          {totalPages > 1 && (
                            <div className="flex items-center gap-1">
                              <button onClick={() => fetchSyncStatus(Math.max(1, syncHistoryPage - 1))} disabled={syncHistoryPage === 1 || isLoadingSync} className="px-2 py-0.5 text-xs border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50">←</button>
                              <span className="text-xs text-gray-400">{syncHistoryPage}/{totalPages}</span>
                              <button onClick={() => fetchSyncStatus(Math.min(totalPages, syncHistoryPage + 1))} disabled={syncHistoryPage >= totalPages || isLoadingSync} className="px-2 py-0.5 text-xs border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50">→</button>
                            </div>
                          )}
                        </div>
                        {syncHistory.length === 0 ? (
                          <p className="text-sm text-gray-400 py-6 text-center">Nenhuma operação registrada</p>
                        ) : (
                          <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                            {syncHistory.map((entry) => {
                              const isSuccess = entry.status === 'success';
                              const isFailed = entry.status === 'failed';
                              const personName = entry.person
                                ? `${entry.person.first_name} ${entry.person.last_name}`
                                : 'Desconhecido';
                              return (
                                <div
                                  key={entry.id}
                                  className={`flex items-center gap-3 px-3 py-2.5 bg-white transition-colors ${entry.person ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                                  onClick={() => entry.person && openPersonDetail(entry.person.id)}
                                >
                                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isSuccess ? 'bg-green-500' : isFailed ? 'bg-red-400' : 'bg-yellow-400'}`} />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2">
                                      <span className="text-sm font-medium text-gray-900 truncate">{personName}</span>
                                      <span className="text-xs text-gray-400 flex-shrink-0 ml-auto">
                                        {new Date(entry.performed_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                      <span>{SYNC_OP_LABELS[entry.operation_type] || entry.operation_type}</span>
                                      {entry.duration_ms != null && <span>· {entry.duration_ms}ms</span>}
                                      {isFailed && entry.error_message && <span className="text-red-500 truncate max-w-[180px]">· {entry.error_message}</span>}
                                    </div>
                                  </div>
                                  <span className={`text-xs font-medium flex-shrink-0 ${isSuccess ? 'text-green-700' : isFailed ? 'text-red-600' : 'text-yellow-600'}`}>
                                    {isSuccess ? 'Sucesso' : isFailed ? 'Falhou' : entry.status}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Guarita — Vehicle Sync */}
                  {isGuaritaModalDevice && (() => {
                    const integrationId = device.config?.guarita_integration_id as string | undefined;
                    const integration = integrationId ? guaritaIntegrationsModal.find(i => i.id === integrationId) : undefined;
                    const isVehicleWhitelistEnabled = integration?.vehicle_whitelist_enabled ?? false;

                    const handleSyncVehicles = async () => {
                      if (!integrationId) return;
                      setIsSyncingVehiclesToGuarita(true);
                      try {
                        const result = await syncVehiclesToGuarita(integrationId);
                        if (result.synced === 0 && result.errors && result.errors.length > 0) {
                          toast.error(`Falha ao sincronizar: ${result.errors[0]}`);
                        } else if (result.errors && result.errors.length > 0) {
                          toast.success(`${result.synced} veículo(s) sincronizado(s) · ${result.errors.length} com erro`);
                        } else if (result.synced === 0) {
                          toast.success('Nenhum veículo com tag RFID encontrado para sincronizar');
                        } else {
                          toast.success(`${result.synced} veículo(s) sincronizado(s) com sucesso`);
                        }
                      } catch (e: any) {
                        toast.error(e.message || 'Erro ao sincronizar veículos');
                      } finally {
                        setIsSyncingVehiclesToGuarita(false);
                      }
                    };

                    return (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                          <Car className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Veículos Guarita</span>
                        </div>
                        {!isVehicleWhitelistEnabled ? (
                          <div className="px-4 py-3">
                            <p className="text-xs text-gray-400">Whitelist de veículos desabilitada. Habilite em <span className="font-medium text-gray-500">Informações → Lista de Veículos</span>.</p>
                          </div>
                        ) : (
                          <div className="px-4 py-3 flex items-center justify-between gap-3">
                            <p className="text-xs text-gray-500">Sincroniza todos os veículos com tag RFID ativa nesta Guarita.</p>
                            <button
                              type="button"
                              onClick={handleSyncVehicles}
                              disabled={isSyncingVehiclesToGuarita}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#96bf13] text-white rounded-lg hover:bg-[#82a810] disabled:opacity-50 transition-colors flex-shrink-0"
                            >
                              <RefreshCw className={`w-3.5 h-3.5 ${isSyncingVehiclesToGuarita ? 'animate-spin' : ''}`} />
                              {isSyncingVehiclesToGuarita ? 'Sincronizando...' : 'Sincronizar Veículos'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Advanced Options — ControlID only */}
                  {device.manufacturer?.toLowerCase() === 'controlid' && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setShowAdvancedSync(v => !v)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700 transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <Settings className="w-4 h-4 text-gray-500" />
                          Opções Avançadas
                        </span>
                        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showAdvancedSync ? 'rotate-180' : ''}`} />
                      </button>

                      {showAdvancedSync && (
                        <div className="p-4 space-y-4 border-t border-gray-200">
                          {/* Group info */}
                          <div className="border border-gray-100 rounded-lg overflow-hidden">
                            <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Configuração de Acesso Padrão (ControlID)</p>
                            </div>
                            <dl>
                              <div className="flex px-3 py-2 border-b border-gray-100">
                                <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Grupo</dt>
                                <dd className="text-xs text-gray-700">Acesso Geral</dd>
                              </div>
                              <div className="flex px-3 py-2 border-b border-gray-100">
                                <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Horário</dt>
                                <dd className="text-xs text-gray-700">Sempre (24/7)</dd>
                              </div>
                              <div className="flex px-3 py-2">
                                <dt className="text-xs text-gray-500 w-32 flex-shrink-0">Regra</dt>
                                <dd className="text-xs text-gray-700">Acesso Total</dd>
                              </div>
                            </dl>
                          </div>

                          {/* Force full re-sync */}
                          <div className="border border-gray-200 rounded-lg p-3">
                            <p className="text-xs font-semibold text-gray-700 mb-1">Re-sincronizar Todos</p>
                            <p className="text-xs text-gray-500 mb-3">
                              Re-envia todas as pessoas para o dispositivo, mesmo as já registradas. Use se houver registros corrompidos ou após resetar o dispositivo.
                            </p>
                            <button
                              onClick={handleForceResync}
                              disabled={isForceResyncing || isSyncing}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <RefreshCw className={`w-3.5 h-3.5 ${isForceResyncing ? 'animate-spin' : ''}`} />
                              {isForceResyncing ? 'Re-sincronizando...' : 'Re-sincronizar Todos'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          </div>
          </div>
        </div>


        {/* Link Person Modal */}
        {showLinkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {personToLink ? 'Vincular Pessoa' : `Vincular ${selectedPeopleToLink.size} Pessoas`}
                  </h3>
                  {personToLink && (
                    <p className="text-sm text-gray-600 mt-1">
                      Vinculando: <span className="font-medium">{personToLink.name}</span>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {personToLink && (
                    <button
                      onClick={openCreateNewUserModal}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      Criar Novo Usuário
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowLinkModal(false);
                      setPersonToLink(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Device Person Photo (if single person) */}
                {personToLink && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Foto no Dispositivo</h4>
                    <div className="flex items-center gap-4">
                      {personToLink.faceURL || personToLink.face_url ? (
                        <img
                          src={getAbsoluteImageUrl(personToLink.faceURL || personToLink.face_url)}
                          alt={personToLink.name}
                          className="w-24 h-24 rounded-lg object-cover border-2 border-gray-300"
                        />
                      ) : (personToLink.has_photo || personToLink.direct_face_url) && device?.id && personToLink.employeeNo ? (
                        <DevicePersonPhoto
                          deviceId={device.id}
                          employeeNo={personToLink.employeeNo}
                          device={device}
                          alt={personToLink.name}
                          directFaceUrl={personToLink.direct_face_url}
                          className="w-24 h-24 rounded-lg object-cover border-2 border-gray-300"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                          <User className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{personToLink.name}</p>
                        <p className="text-sm text-gray-600">Nº: {personToLink.employeeNo}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Search System People */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Selecione a pessoa no sistema para vincular
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por nome, CPF, email..."
                      value={linkSearchTerm}
                      onChange={(e) => setLinkSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* System People List */}
                <div className="space-y-2 min-h-[400px]">
                  {isLoadingSystemPeople ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                      <span className="ml-3 text-gray-600">Carregando pessoas...</span>
                    </div>
                  ) : (
                    <>
                      {allSystemPeople.map((person) => (
                      <div
                        key={person.id}
                        onClick={() => handleSelectSystemPerson(person)}
                        className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-colors"
                      >
                        {/* System Person Photo */}
                        {person.identity_photo_url ? (
                          <img
                            src={getAbsoluteImageUrl(person.identity_photo_url)}
                            alt={person.name}
                            className="w-16 h-16 rounded-lg object-cover border-2 border-gray-300"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center border-2 border-gray-300">
                            <User className="w-8 h-8 text-gray-400" />
                          </div>
                        )}

                        {/* Person Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">
                            {person.first_name && person.last_name
                              ? `${person.first_name} ${person.last_name}`
                              : person.name || 'Nome não disponível'}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {person.cpf && (
                              <span className="text-xs text-gray-600">
                                CPF: {person.cpf}
                              </span>
                            )}
                            {person.email && (
                              <span className="text-xs text-gray-600">
                                {person.email}
                              </span>
                            )}
                            {person.phone && (
                              <span className="text-xs text-gray-600">
                                {person.phone}
                              </span>
                            )}
                          </div>
                          {person.person_type && (
                            <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                              {person.person_type === 'resident' ? 'Morador' :
                               person.person_type === 'visitor' ? 'Visitante' :
                               person.person_type === 'employee' ? 'Funcionário' :
                               person.person_type}
                            </span>
                          )}
                        </div>

                        {/* Link Action */}
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                          Vincular
                        </button>
                      </div>
                      ))}

                      {/* Empty State */}
                      {allSystemPeople.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <User className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                          <p>Nenhuma pessoa encontrada</p>
                          {linkSearchTerm && (
                            <p className="text-sm mt-2">Tente outro termo de busca</p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Pagination Controls */}
                {!isLoadingSystemPeople && (() => {
                  const totalPages = Math.ceil(totalSystemPeople / linkModalPageSize);

                  if (totalPages <= 1) return null;

                  const startIndex = (linkModalPage - 1) * linkModalPageSize + 1;
                  const endIndex = Math.min(linkModalPage * linkModalPageSize, totalSystemPeople);

                  return (
                    <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
                      <div className="text-sm text-gray-600">
                        Mostrando {startIndex} - {endIndex} de {totalSystemPeople} pessoas
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setLinkModalPage(1)}
                          disabled={linkModalPage === 1}
                          className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          Primeira
                        </button>
                        <button
                          onClick={() => setLinkModalPage(p => Math.max(1, p - 1))}
                          disabled={linkModalPage === 1}
                          className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          Anterior
                        </button>
                        <span className="px-3 py-1 text-sm text-gray-700">
                          Página {linkModalPage} de {totalPages}
                        </span>
                        <button
                          onClick={() => setLinkModalPage(p => Math.min(totalPages, p + 1))}
                          disabled={linkModalPage === totalPages}
                          className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          Próxima
                        </button>
                        <button
                          onClick={() => setLinkModalPage(totalPages)}
                          disabled={linkModalPage === totalPages}
                          className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          Última
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowLinkModal(false);
                    setPersonToLink(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Link Confirmation Modal */}
        {showLinkConfirmation && selectedSystemPerson && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Confirmar Vinculação</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Vincular pessoa do dispositivo ao sistema
                </p>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6 relative">
                {/* Loading Overlay */}
                {isLinkingPerson && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                      <p className="text-sm font-medium text-gray-700">Vinculando...</p>
                    </div>
                  </div>
                )}

                {/* Device Person Info */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Pessoa no Dispositivo</h4>
                  <div className="flex items-center gap-4">
                    {personToLink && (personToLink.faceURL || personToLink.face_url) ? (
                      <img
                        src={getAbsoluteImageUrl(personToLink.faceURL || personToLink.face_url)}
                        alt={personToLink.name}
                        className="w-20 h-20 rounded-lg object-cover border-2 border-gray-300"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                        <User className="w-10 h-10 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{personToLink?.name}</p>
                      <p className="text-sm text-gray-600">Nº: {personToLink?.employeeNo}</p>
                    </div>
                  </div>
                </div>

                {/* System Person Info */}
                <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                  <h4 className="text-sm font-semibold text-blue-700 mb-3">Pessoa no Sistema</h4>
                  <div className="flex items-center gap-4">
                    {selectedSystemPerson.identity_photo_url ? (
                      <img
                        src={getAbsoluteImageUrl(selectedSystemPerson.identity_photo_url)}
                        alt={selectedSystemPerson.name}
                        className="w-20 h-20 rounded-lg object-cover border-2 border-blue-300"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-blue-100 flex items-center justify-center border-2 border-blue-300">
                        <User className="w-10 h-10 text-blue-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {selectedSystemPerson.first_name && selectedSystemPerson.last_name
                          ? `${selectedSystemPerson.first_name} ${selectedSystemPerson.last_name}`
                          : selectedSystemPerson.name}
                      </p>
                      {selectedSystemPerson.cpf && (
                        <p className="text-sm text-gray-600">CPF: {selectedSystemPerson.cpf}</p>
                      )}
                      {selectedSystemPerson.email && (
                        <p className="text-sm text-gray-600">{selectedSystemPerson.email}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Options */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-700">Opções de Vinculação</h4>

                  {/* Use System Person Name Option */}
                  <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="useSystemPersonName"
                      checked={useSystemPersonName}
                      onChange={(e) => setUseSystemPersonName(e.target.checked)}
                      disabled={isLinkingPerson}
                      className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1">
                      <label htmlFor="useSystemPersonName" className="font-medium text-gray-900 cursor-pointer">
                        Usar nome do cadastro do sistema
                      </label>
                      <p className="text-xs text-gray-600 mt-1">
                        Se marcado, atualiza o nome no dispositivo para: <strong>{selectedSystemPerson.first_name} {selectedSystemPerson.last_name || selectedSystemPerson.name}</strong>.
                        Se desmarcado (padrão), mantém o nome atual: <strong>{personToLink?.name}</strong>
                      </p>
                    </div>
                  </div>

                  {/* Upload System Photo Option */}
                  <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="uploadSystemPhoto"
                      checked={uploadSystemPhoto}
                      onChange={(e) => setUploadSystemPhoto(e.target.checked)}
                      disabled={isLinkingPerson}
                      className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1">
                      <label htmlFor="uploadSystemPhoto" className="font-medium text-gray-900 cursor-pointer">
                        Enviar foto do sistema para o dispositivo
                      </label>
                      <p className="text-xs text-gray-600 mt-1">
                        Se marcado, envia a foto cadastrada no sistema para o dispositivo.
                        Se desmarcado (padrão), mantém a foto atual do dispositivo {selectedSystemPerson.identity_photo_url ? '(ou envia a foto do sistema se disponível)' : ''}
                      </p>
                    </div>
                  </div>

                  {/* Pull Biometry Option — only for digital ControlID devices */}
                  {device && device.manufacturer?.toLowerCase().includes('control') &&
                   device.device_type !== 'controlid_face' &&
                   device.device_type !== 'facial_reader' &&
                   device.device_type !== 'facial_recognition' && (
                    <div className="flex items-start gap-3 p-4 border border-purple-200 rounded-lg bg-purple-50">
                      <input
                        type="checkbox"
                        id="pullDeviceBiometry"
                        checked={pullDeviceBiometry}
                        onChange={(e) => setPullDeviceBiometry(e.target.checked)}
                        disabled={isLinkingPerson}
                        className="mt-1 w-4 h-4 text-purple-600 rounded focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <label htmlFor="pullDeviceBiometry" className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                          <Fingerprint className="w-4 h-4 text-purple-600" />
                          Importar biometria do dispositivo
                        </label>
                        <p className="text-xs text-gray-600 mt-1">
                          Se marcado, busca os templates de digital cadastrados no dispositivo e salva no perfil desta pessoa no sistema.
                          Útil para importar biometrias que já foram cadastradas fisicamente no leitor.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Info about what will happen */}
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex gap-2">
                      <AlertCircle className="w-5 h-5 flex-shrink-0 text-blue-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          Como funciona a vinculação
                        </p>
                        <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                          <li>A pessoa no dispositivo será atualizada com o ID do sistema: <strong>{selectedSystemPerson.id}</strong></li>
                          <li>A foto do dispositivo será mantida (não será alterada)</li>
                          <li>Nome: {useSystemPersonName ? `será atualizado para "${selectedSystemPerson.first_name} ${selectedSystemPerson.last_name || selectedSystemPerson.name}"` : `será mantido como "${personToLink?.name}"`}</li>
                          <li>A pessoa será marcada como autorizada no sistema</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowLinkConfirmation(false);
                    setSelectedSystemPerson(null);
                  }}
                  disabled={isLinkingPerson}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Voltar
                </button>
                <button
                  onClick={handleLinkPerson}
                  disabled={isLinkingPerson}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLinkingPerson ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Vinculando...
                    </>
                  ) : (
                    'Confirmar Vinculação'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create New User Modal */}
        {showCreateNewUserModal && personToLink && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Criar Novo Usuário</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Criar novo cadastro no sistema e vincular com: <span className="font-medium">{personToLink.name}</span>
                </p>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-6 relative">
                {/* Loading Overlay */}
                {isCreatingUser && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                      <p className="text-sm font-medium text-gray-700">Criando...</p>
                    </div>
                  </div>
                )}

                <form className="space-y-4">
                  {/* Name Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newUserFormData.first_name}
                        onChange={(e) => setNewUserFormData({...newUserFormData, first_name: e.target.value})}
                        disabled={isCreatingUser}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sobrenome
                      </label>
                      <input
                        type="text"
                        value={newUserFormData.last_name}
                        onChange={(e) => setNewUserFormData({...newUserFormData, last_name: e.target.value})}
                        disabled={isCreatingUser}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* CPF */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CPF {!['funcionario', 'morador'].includes(newUserFormData.role_type) && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={newUserFormData.cpf}
                      onChange={(e) => setNewUserFormData({...newUserFormData, cpf: e.target.value})}
                      disabled={isCreatingUser}
                      placeholder="000.000.000-00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required={!['funcionario', 'morador'].includes(newUserFormData.role_type)}
                    />
                    {['funcionario', 'morador'].includes(newUserFormData.role_type) && (
                      <p className="text-xs text-gray-500 mt-1">
                        Opcional para {newUserFormData.role_type === 'funcionario' ? 'funcionários' : 'moradores'}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={newUserFormData.email}
                      onChange={(e) => setNewUserFormData({...newUserFormData, email: e.target.value})}
                      disabled={isCreatingUser}
                      placeholder="email@exemplo.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      value={newUserFormData.phone}
                      onChange={(e) => setNewUserFormData({...newUserFormData, phone: e.target.value})}
                      disabled={isCreatingUser}
                      placeholder="(00) 00000-0000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  {/* Minor Checkbox */}
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="is_minor"
                      checked={newUserFormData.is_minor}
                      disabled={isCreatingUser}
                      onChange={(e) => {
                        const isChecked = e.target.checked;

                        if (newUserFormData.birth_date) {
                          const birthDate = new Date(newUserFormData.birth_date);
                          const today = new Date();
                          const age = today.getFullYear() - birthDate.getFullYear() -
                            (today.getMonth() < birthDate.getMonth() ||
                             (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate()) ? 1 : 0);

                          // If checking the box and age >= 18, prevent
                          if (isChecked && age >= 18) {
                            toast.error('Não é possível marcar como menor de idade. A data de nascimento indica idade maior ou igual a 18 anos.');
                            return;
                          }

                          // If unchecking the box and age < 18, prevent
                          if (!isChecked && age < 18) {
                            toast.error('Não é possível desmarcar. A data de nascimento indica que a pessoa é menor de idade.');
                            return;
                          }
                        }

                        setNewUserFormData({...newUserFormData, is_minor: isChecked});
                      }}
                      className="mt-0.5 w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <label htmlFor="is_minor" className="flex-1 text-sm text-gray-700 cursor-pointer">
                      <span className="font-medium">Menor de idade</span>
                      <span className="block text-xs text-gray-600 mt-0.5">
                        Marque se a pessoa tiver menos de 18 anos. A data de nascimento é opcional.
                      </span>
                    </label>
                  </div>

                  {/* Birth Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Nascimento
                    </label>
                    <input
                      type="date"
                      value={newUserFormData.birth_date}
                      disabled={isCreatingUser}
                      onChange={(e) => {
                        const newBirthDate = e.target.value;

                        // Calculate age if date is provided
                        if (newBirthDate) {
                          const birthDate = new Date(newBirthDate);
                          const today = new Date();
                          const age = today.getFullYear() - birthDate.getFullYear() -
                            (today.getMonth() < birthDate.getMonth() ||
                             (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate()) ? 1 : 0);

                          // Auto-check "menor de idade" if age < 18
                          if (age < 18) {
                            setNewUserFormData({...newUserFormData, birth_date: newBirthDate, is_minor: true});
                          } else {
                            // Auto-uncheck if age >= 18
                            setNewUserFormData({...newUserFormData, birth_date: newBirthDate, is_minor: false});
                          }
                        } else {
                          // If clearing birth date, keep is_minor as-is
                          setNewUserFormData({...newUserFormData, birth_date: newBirthDate});
                        }
                      }}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    {newUserFormData.birth_date ? (() => {
                      const birthDate = new Date(newUserFormData.birth_date);
                      const today = new Date();
                      const age = today.getFullYear() - birthDate.getFullYear() -
                        (today.getMonth() < birthDate.getMonth() ||
                         (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate()) ? 1 : 0);

                      if (age < 18) {
                        return (
                          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Pessoa menor de idade ({age} anos). Checkbox "Menor de idade" marcado automaticamente.
                          </p>
                        );
                      } else {
                        return (
                          <p className="text-xs text-gray-500 mt-1">
                            Idade: {age} anos
                          </p>
                        );
                      }
                    })() : newUserFormData.is_minor ? (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Marcado como menor de idade sem data de nascimento informada.
                      </p>
                    ) : null}
                  </div>

                  {/* Role Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Pessoa <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newUserFormData.role_type}
                      onChange={(e) => setNewUserFormData({...newUserFormData, role_type: e.target.value, department_id: '', unit_id: '', position: '', employee_no: ''})}
                      disabled={isCreatingUser}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                    >
                      {selectedCustomer?.type === 'empresa' ? (
                        <>
                          <option value="funcionario">Funcionário</option>
                          <option value="visitante">Visitante</option>
                        </>
                      ) : (
                        <>
                          <option value="morador">Morador (Residente)</option>
                          <option value="visitante">Visitante</option>
                          <option value="prestador">Prestador de Serviço</option>
                          <option value="entregador">Entregador</option>
                        </>
                      )}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Define como esta pessoa será autorizada no sistema
                    </p>
                  </div>

                  {/* Department (for empresa + funcionario) */}
                  {selectedCustomer?.type === 'empresa' && newUserFormData.role_type === 'funcionario' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Departamento <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newUserFormData.department_id}
                        onChange={(e) => setNewUserFormData({...newUserFormData, department_id: e.target.value})}
                        disabled={isCreatingUser}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        required
                      >
                        <option value="">Selecione um departamento</option>
                        {availableDepartments.map((dept: any) => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Position/Cargo (for empresa + funcionario) */}
                  {selectedCustomer?.type === 'empresa' && newUserFormData.role_type === 'funcionario' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cargo
                      </label>
                      <input
                        type="text"
                        value={newUserFormData.position}
                        onChange={(e) => setNewUserFormData({...newUserFormData, position: e.target.value})}
                        disabled={isCreatingUser}
                        placeholder="Ex: Engenheiro, Supervisor, Operador"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>
                  )}

                  {/* Unit (for condominio + morador) */}
                  {selectedCustomer?.type === 'condominio' && newUserFormData.role_type === 'morador' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unidade <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newUserFormData.unit_id}
                        onChange={(e) => setNewUserFormData({...newUserFormData, unit_id: e.target.value})}
                        disabled={isCreatingUser}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        required
                      >
                        <option value="">Selecione uma unidade</option>
                        {availableUnits.map((unit: any) => (
                          <option key={unit.id} value={unit.id}>
                            {unit.block_name ? `${unit.block_name} - ` : ''}{unit.number || unit.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex gap-2">
                      <AlertCircle className="w-5 h-5 flex-shrink-0 text-blue-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          O que será feito
                        </p>
                        <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                          <li>Novo cadastro será criado no sistema com os dados informados</li>
                          {newUserFormData.is_minor && (
                            <li className="text-amber-700 font-medium">
                              Pessoa será marcada como <strong>menor de idade</strong>
                              {newUserFormData.birth_date ? ' (idade calculada)' : ' (sem data de nascimento)'}
                            </li>
                          )}
                          <li>A pessoa será registrada como <strong>{newUserFormData.role_type}</strong> autorizada</li>
                          <li>A pessoa no dispositivo será vinculada ao novo cadastro</li>
                          <li>O nome no dispositivo será mantido: <strong>{personToLink.name}</strong></li>
                          <li>A foto do dispositivo será mantida</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowCreateNewUserModal(false);
                    setShowLinkModal(true);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isCreatingUser}
                >
                  Voltar
                </button>
                <button
                  onClick={handleCreateAndLinkUser}
                  disabled={
                    isCreatingUser ||
                    !newUserFormData.first_name ||
                    (!['funcionario', 'morador'].includes(newUserFormData.role_type) && !newUserFormData.cpf) ||
                    (selectedCustomer?.type === 'empresa' && newUserFormData.role_type === 'funcionario' && !newUserFormData.department_id) ||
                    (selectedCustomer?.type === 'condominio' && newUserFormData.role_type === 'morador' && !newUserFormData.unit_id)
                  }
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isCreatingUser ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      Criar e Vincular
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Vehicle Removal Confirmation Modal (inside DeviceDetailsModal) */}
        {showVehicleRemoveModal && vehicleToRemove && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b bg-gradient-to-r from-red-600 to-red-700 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Remover Veículo</h3>
                    <p className="text-sm text-red-100">Confirme para remover do dispositivo</p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                <div className="space-y-4">
                  {/* Vehicle Info Card */}
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                      <Car className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-semibold text-gray-900 truncate">
                        {vehicleToRemove.vehicle.plate || 'Sem placa'}
                      </p>
                      <p className="text-sm text-gray-600 truncate">
                        {vehicleToRemove.vehicle.owner_name || vehicleToRemove.vehicle.name || 'Proprietário desconhecido'}
                      </p>
                      {vehicleToRemove.vehicle.brand && vehicleToRemove.vehicle.model && (
                        <p className="text-xs text-gray-500 truncate">
                          {vehicleToRemove.vehicle.brand} {vehicleToRemove.vehicle.model}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Warning Message */}
                  <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Você está prestes a remover este veículo do dispositivo RFID.
                      </p>
                      <p className="text-sm text-gray-700 mt-1">
                        <span className="font-medium">Dispositivo:</span> {vehicleToRemove.device.name}
                      </p>
                      <p className="text-xs text-gray-600 mt-2">
                        Esta ação removerá o cadastro da tag RFID do leitor. O veículo não terá mais acesso automático por esta zona.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t bg-gray-50 flex items-stretch gap-3">
                <button
                  onClick={() => {
                    setShowVehicleRemoveModal(false);
                    setVehicleToRemove(null);
                  }}
                  disabled={isRemovingVehicle}
                  className="flex-none px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRemoveVehicleFromDevice}
                  disabled={isRemovingVehicle}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 whitespace-nowrap shadow-md hover:shadow-lg"
                >
                  {isRemovingVehicle ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Removendo...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Confirmar Remoção
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Vincular Tag Modal */}
        {vinculateReg && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
              <div className="p-5 border-b flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">Vincular Tag</h3>
                  <p className="text-xs text-gray-500 mt-0.5 font-mono">{vinculateReg.serial_decimal}</p>
                </div>
                <button onClick={() => setVinculateReg(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pessoa</label>
                  <input
                    type="text"
                    placeholder="Buscar pessoa..."
                    value={vinculateSearch}
                    onChange={e => {
                      setVinculateSearch(e.target.value);
                      setVinculatePersonId('');
                      searchVinculatePeople(e.target.value);
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {isSearchingPeople && <p className="text-xs text-gray-400 mt-1">Buscando...</p>}
                  {vinculatePeople.length > 0 && !vinculatePersonId && (
                    <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto shadow-sm">
                      {vinculatePeople.map((p: any) => (
                        <button
                          key={p.id}
                          onClick={() => {
                            setVinculatePersonId(p.id);
                            setVinculateSearch(`${p.first_name} ${p.last_name}`);
                            setVinculatePeople([]);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0"
                        >
                          <span className="font-medium text-gray-900">{p.first_name} {p.last_name}</span>
                          {p.email && <span className="text-gray-400 ml-2 text-xs">{p.email}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {vinculatePersonId && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> Pessoa selecionada
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Etiqueta (opcional)</label>
                  <input
                    type="text"
                    placeholder="Ex: Controle remoto, Cartão principal..."
                    value={vinculateLabel}
                    onChange={e => setVinculateLabel(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="p-5 border-t flex gap-3">
                <button
                  onClick={() => setVinculateReg(null)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={submitVinculate}
                  disabled={!vinculatePersonId || isVinculating}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isVinculating ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Salvando...</> : 'Vincular'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Zone Details Modal Component
const ZoneDetailsModal: React.FC<{
  zone: Zone;
  device: IoTDevice | null;
  onClose: () => void;
}> = ({ zone, device, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-900">{zone.name}</h2>
          {zone.description && (
            <p className="text-gray-600 mt-1">{zone.description}</p>
          )}
        </div>

        <div className="p-6">
          {/* Zone Details */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Informações da Zona</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Tipo</p>
                <p className="font-medium text-gray-900">{translateZoneType(zone.zone_type)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="font-medium text-gray-900">{translateStatus(zone.status)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Requer Autorização</p>
                <p className="font-medium text-gray-900">
                  {zone.requires_authorization ? 'Sim' : 'Não'}
                </p>
              </div>
            </div>
          </div>

          {/* Device Details */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">
              Dispositivo IoT Vinculado
            </h3>

            {!device ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <Cpu className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Nenhum dispositivo vinculado a esta zona</p>
                <p className="text-xs text-gray-400 mt-1">
                  Adicione um dispositivo para habilitar o controle de acesso
                </p>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg border border-[#96bf13] border-opacity-30 p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#96bf13] bg-opacity-20 flex items-center justify-center flex-shrink-0">
                    <Cpu className="w-6 h-6 text-[#96bf13]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-3 gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 text-lg truncate whitespace-nowrap overflow-hidden" title={device.name}>
                          {device.name?.replace(/[\n\r]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Dispositivo sem nome'}
                        </h4>
                        <p className="text-sm text-gray-600">{translateDeviceType(device.device_type)}</p>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium flex-shrink-0 whitespace-nowrap ${
                        device.status === 'online'
                          ? 'bg-green-100 text-green-800'
                          : device.status === 'offline'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {translateStatus(device.status)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {device.ip_address && (
                        <div>
                          <p className="text-xs text-gray-600">IP Cloud</p>
                          <p className="font-medium text-sm text-gray-900">{device.ip_address}</p>
                        </div>
                      )}
                      {device.local_ip_address && (
                        <div>
                          <p className="text-xs text-gray-600">IP Privado</p>
                          <p className="font-medium text-sm text-gray-900">{device.local_ip_address}</p>
                        </div>
                      )}
                      {device.last_heartbeat && (
                        <div>
                          <p className="text-xs text-gray-600">Último Heartbeat</p>
                          <p className="font-medium text-sm text-gray-900">
                            {new Date(device.last_heartbeat).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

// Device List Modal Component
// Status dot for live health display
const StatusDot: React.FC<{ status: 'online' | 'offline' | 'checking' | 'web_unavailable' | string }> = ({ status }) => {
  if (status === 'checking') {
    return <span title="Verificando..." className="flex-shrink-0"><Loader2 className="w-3 h-3 text-amber-400 animate-spin" /></span>;
  }
  if (status === 'web_unavailable') {
    return <span title="Inacessível via navegador" className="flex-shrink-0"><Loader2 className="w-3 h-3 text-gray-300 animate-spin" /></span>;
  }
  if (status === 'online') {
    return <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0" title="Online" />;
  }
  return <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0" title="Offline" />;
};

const DeviceListModal: React.FC<{
  devices: IoTDevice[];
  filter: 'all' | 'active' | 'offline';
  zones: Zone[];
  onClose: () => void;
  onDeviceClick: (device: IoTDevice) => void;
  onDeleteDevice: (deviceId: string, deviceName: string) => void;
  healthMap?: Map<string, DeviceHealth>;
  isElectronMode?: boolean;
  isChecking?: boolean;
}> = ({ devices, filter, zones, onClose, onDeviceClick, onDeleteDevice, healthMap, isElectronMode, isChecking }) => {
  const getEffectiveStatus = (device: IoTDevice): string => {
    if (isElectronMode && healthMap?.has(device.id)) {
      return healthMap.get(device.id)!.status;
    }
    return device.status;
  };

  const filteredDevices = devices.filter(device => {
    if (filter === 'active') return device.status === 'online';
    if (filter === 'offline') return device.status === 'offline';
    return true; // 'all'
  });

  const lastCheckTime = (() => {
    if (!isElectronMode || !healthMap) return null;
    let latest: Date | undefined;
    healthMap.forEach(h => {
      if (h.checkedAt && (!latest || h.checkedAt > latest)) latest = h.checkedAt;
    });
    return latest;
  })();

  const getTitle = () => {
    if (filter === 'active') return 'Dispositivos Ativos';
    if (filter === 'offline') return 'Dispositivos Offline';
    return 'Todos os Dispositivos';
  };

  const getZoneName = (zoneId: string) => {
    const zone = zones.find(z => z.id === zoneId);
    return zone?.name || 'Sem zona';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold">{getTitle()}</h2>
                {isElectronMode && (
                  <span className="text-xs bg-white bg-opacity-20 px-2 py-0.5 rounded-full font-medium flex items-center gap-1.5">
                    {isChecking
                      ? <RefreshCw className="w-3 h-3 animate-spin" />
                      : <span className="w-1.5 h-1.5 rounded-full bg-green-300 inline-block" />
                    }
                    {isChecking ? 'Verificando...' : 'Verificação em tempo real'}
                  </span>
                )}
              </div>
              <p className="text-indigo-200 text-sm mt-0.5">
                {filteredDevices.length} {filteredDevices.length === 1 ? 'dispositivo' : 'dispositivos'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Device List */}
        <div className="flex-1 overflow-y-auto p-3">
          {filteredDevices.length === 0 ? (
            <div className="text-center py-12">
              <Cpu className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg font-medium">Nenhum dispositivo encontrado</p>
              <p className="text-gray-500 text-sm mt-2">
                {filter === 'active' && 'Todos os dispositivos estão offline'}
                {filter === 'offline' && 'Todos os dispositivos estão online'}
                {filter === 'all' && 'Nenhum dispositivo cadastrado'}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredDevices.map((device) => {
                const effectiveStatus = getEffectiveStatus(device);
                const health = isElectronMode ? healthMap?.get(device.id) : undefined;
                return (
                  <div
                    key={device.id}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-100 hover:border-indigo-300 hover:bg-indigo-50 transition-all group"
                  >
                    <StatusDot status={effectiveStatus} />

                    {/* Main info - clickable */}
                    <button
                      onClick={() => onDeviceClick(device)}
                      className="flex-1 min-w-0 text-left flex items-center gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900 text-sm truncate" title={device.name}>
                            {device.name?.replace(/[\n\r]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Dispositivo sem nome'}
                          </span>
                          <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded flex-shrink-0">
                            {translateDeviceType(device.device_type)}
                          </span>
                          {device.zone_id && (
                            <span className="text-xs text-gray-500 flex items-center gap-0.5 flex-shrink-0">
                              <MapPin className="w-3 h-3" />
                              {getZoneName(device.zone_id)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          {device.ip_address && (
                            <span className="text-xs text-gray-400 font-mono">{device.ip_address}</span>
                          )}
                          {device.last_heartbeat && (
                            <span className="text-xs text-gray-400 flex items-center gap-0.5">
                              <Activity className="w-3 h-3" />
                              {new Date(device.last_heartbeat).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                          {isElectronMode && health?.latency !== undefined && effectiveStatus === 'online' && (
                            <span className="text-xs text-green-600 font-medium">{health.latency}ms</span>
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Delete button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteDevice(device.id, device.name);
                      }}
                      className="p-1.5 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                      title="Excluir dispositivo"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
          {isElectronMode && lastCheckTime ? (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Verificado às {lastCheckTime.toLocaleTimeString('pt-BR')}
            </span>
          ) : (
            <span />
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

// Device Cards Modal Component
const DeviceCardsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  deviceCards: any[];
  isLoading: boolean;
  deviceName: string;
  deviceId: string;
  registeredCount: number;
  unknownCount: number;
  onRefresh: () => void;
}> = ({ isOpen, onClose, deviceCards, isLoading, deviceName, deviceId, registeredCount, unknownCount, onRefresh }) => {
  const [deletingCard, setDeletingCard] = useState<string | null>(null);
  const confirm = useConfirm();

  const handleDeleteCard = async (cardNumber: string) => {
    if (!await confirm({ message: `Tem certeza que deseja remover o cartão ${cardNumber} do dispositivo?`, variant: 'danger', confirmLabel: 'Remover' })) {
      return;
    }

    setDeletingCard(cardNumber);
    try {
      const response = await fetchWithAuth(
        `/api/v1/iot-connect/rfid/devices/${deviceId}/cards/${cardNumber}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        toast.success('Cartão removido com sucesso');
        onRefresh(); // Refresh the card list
      } else {
        const error = await response.json();
        toast.error(error.error || 'Falha ao remover cartão');
      }
    } catch (error) {
      logger.error('Error deleting card:', error);
      toast.error('Erro ao remover cartão do dispositivo');
    } finally {
      setDeletingCard(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Car className="w-6 h-6 text-[#96bf13]" />
                Cartões no Dispositivo RFID
              </h2>
              <p className="text-sm text-gray-600 mt-1">{deviceName}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          {/* Stats */}
          <div className="flex gap-4 mt-4">
            <div className="flex items-center gap-2 px-3 py-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-900">
                {registeredCount} Registrados
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-100 rounded-lg">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-900">
                {unknownCount} Desconhecidos
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-[#96bf13] animate-spin mx-auto mb-3" />
              <p className="text-gray-600">Carregando cartões do dispositivo...</p>
            </div>
          ) : deviceCards.length === 0 ? (
            <div className="text-center py-12">
              <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg font-medium">Nenhum cartão encontrado</p>
              <p className="text-gray-500 text-sm mt-2">
                O dispositivo não possui cartões RFID registrados
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {deviceCards.map((card: any, index: number) => (
                <div
                  key={index}
                  className={`p-4 border-2 rounded-lg ${
                    card.is_unauthorized
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-green-300 bg-green-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          card.is_unauthorized
                            ? 'bg-amber-200 text-amber-900'
                            : 'bg-green-200 text-green-900'
                        }`}>
                          {card.is_unauthorized ? 'Desconhecido' : 'Registrado'}
                        </span>
                        <span className="text-sm font-mono text-gray-700">
                          {card.card_number}
                        </span>
                      </div>
                      {(card.card_holder_name || card.person_name) && (
                        <p className="text-sm text-gray-900 font-medium mb-1">
                          {card.card_holder_name || card.person_name}
                        </p>
                      )}
                      {(card.vehicle_plate || card.plate) && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Car className="w-4 h-4" />
                          <span className="font-semibold">Placa: {card.vehicle_plate || card.plate}</span>
                          {(card.brand || card.model) && (
                            <span className="text-gray-500">
                              ({card.brand} {card.model})
                            </span>
                          )}
                          {card.color && (
                            <span className="px-2 py-0.5 bg-gray-200 rounded text-xs">
                              {card.color}
                            </span>
                          )}
                        </div>
                      )}
                      {card.owner_name && (
                        <p className="text-xs text-gray-500 mt-1">
                          Proprietário: {card.owner_name}
                        </p>
                      )}
                      {card.valid_until && (
                        <p className="text-xs text-gray-500 mt-1">
                          Válido até: {new Date(card.valid_until).toLocaleDateString('pt-BR')}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCard(card.card_number);
                        }}
                        disabled={deletingCard === card.card_number}
                        className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        title="Remover cartão do dispositivo"
                      >
                        {deletingCard === card.card_number ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                        Remover
                      </button>
                      {card.is_unauthorized ? (
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ZonesDevices;
